import React, { useState, useCallback, useMemo, useRef, useEffect, createContext, useContext } from "react";

// ============================================================
// [Phase 1] 상수 모듈 imports
// ============================================================
import {
  PRESET_GAIN_VERSION,
  DEFAULT_PARAMS,
  VEHICLE_PRESETS,
  TESLA_M3_PARAMS, TESLA_M3_RWD_PARAMS,
  IONIQ6_PARAMS, IONIQ6_RWD_PARAMS,
  TESLA_MY_PARAMS, TESLA_MS_PLAID_PARAMS, TESLA_MX_PARAMS,
  IONIQ5_AWD_PARAMS, IONIQ5_RWD_PARAMS, IONIQ5_N_PARAMS,
  KIA_EV6_AWD_PARAMS, KIA_EV6_GT_PARAMS, KIA_EV9_AWD_PARAMS,
  KIA_EV3_RWD_PARAMS, KIA_EV6_RWD_PARAMS,
} from './constants/VEHICLE_PRESETS.js';
import {
  FAULT_MODES, FMEA_COMPONENT_FIT,
  DFMEA_DESIGN_ITEMS, PFMEA_PROCESS_ITEMS,
} from './constants/FAULT_MODES.js';
import {
  TARA_SYSTEM_ASSETS, TARA_DAMAGE_SCENARIOS, TARA_THREATS,
} from './constants/TARA_DATA.js';
import { I18N } from './constants/i18n.js';
import { ColLayoutContext } from './contexts/ColLayoutContext.js';
import { SOH_PROFILES, CONSTRAINT_DEFS } from './constants/sohProfiles.js';
import { SHARED_PARAM_DB, GRAPH_INFO_DB, PARAM_INFO_DATA } from './constants/paramDb.js';

// ============================================================
// [Phase 3] 상태 관리 store imports
// ============================================================
import { createParamsStore }    from './store/paramsStore.js';
import { createSimResultStore } from './store/simResultStore.js';
import { createUIStore }        from './store/uiStore.js';
import { getAxesStore, useAxesStore } from './store/axesStore.js';
// safetyStore: 싱글톤 패턴 — 각 Safety 탭이 직접 import하여 사용.
// 초기화 불필요 (자동 싱글톤). localStorage 영속화 포함.
import { safetyStore } from './store/safetyStore.js'; // eslint-disable-line no-unused-vars
import { exportLayout, importLayout, loadFolderHandles, getSaveFolder, getLoadFolder, FS_API_SUPPORTED } from './services/LayoutService.js';
import { exportVehiclePresets, importVehiclePresets } from './services/VehiclePresetService.js';

// ============================================================
// [Phase 4] 컴포넌트 imports
// ============================================================
import { COLORS_DARK, COLORS_LIGHT, mono } from './components/shared/theme.js';
import {
  makeT, GraphInfoPopup, PI, PF, MC, CT, CB,
  SL, ECV, ModalSectionTitle,
} from './components/shared/helpers.jsx';
import {
  DriveModeBadge, RLKpiBadge, RLKpiBadgeRow, RLScenBtn,
  RLHwPopup, RLHwBtn, YAxisToggleBtn, AxesPanel,
  RLGraphCard, RLInlineKpi, RLKnowHowPanel, R1KnowHowPanel, RLTooltip,
} from './components/shared/rl_widgets.jsx';
import {
  AWDGroundForceViz, AFSectionHeader, TorqueMap3D,
} from './components/shared/advanced_widgets.jsx';
import { VehicleInfoModal } from './components/modals/VehicleInfoModal.jsx';
import { EnvModal }          from './components/modals/EnvModal.jsx';
import { ControlModal }      from './components/modals/ControlModal.jsx';
import { CSVModal }          from './components/modals/CSVModal.jsx';
import { LayoutFolderModal } from './components/modals/LayoutFolderModal.jsx';
import { VehiclePresetModal } from './components/modals/VehiclePresetModal.jsx';
// [Perf-2] 탭 컴포넌트 React.lazy — 첫 번들에서 제외, 탭 열 때만 로드
const RLTabContent                = React.lazy(() => import('./components/tabs/RLTabContent.jsx').then(m => ({ default: m.RLTabContent })));
const CoastTabContent             = React.lazy(() => import('./components/tabs/CoastTabContent.jsx').then(m => ({ default: m.CoastTabContent })));
const RegenBrakeTabContent        = React.lazy(() => import('./components/tabs/RegenBrakeTabContent.jsx').then(m => ({ default: m.RegenBrakeTabContent })));
const ReAccelTabContent           = React.lazy(() => import('./components/tabs/ReAccelTabContent.jsx').then(m => ({ default: m.ReAccelTabContent })));
const EnergyTabContent            = React.lazy(() => import('./components/tabs/EnergyTabContent.jsx').then(m => ({ default: m.EnergyTabContent })));
const DCTabContent                = React.lazy(() => import('./components/tabs/DCTabContent.jsx').then(m => ({ default: m.DCTabContent })));
const SensitivityTabContent       = React.lazy(() => import('./components/tabs/SensitivityTabContent.jsx').then(m => ({ default: m.SensitivityTabContent })));
const ThermalTabContent           = React.lazy(() => import('./components/tabs/ThermalTabContent.jsx').then(m => ({ default: m.ThermalTabContent })));
const DrivabilityTabContent       = React.lazy(() => import('./components/tabs/DrivabilityTabContent.jsx').then(m => ({ default: m.DrivabilityTabContent })));
const LaunchTabContent            = React.lazy(() => import('./components/tabs/LaunchTabContent.jsx').then(m => ({ default: m.LaunchTabContent })));
const EnhancedPhysicsTabContent   = React.lazy(() => import('./components/tabs/EnhancedPhysicsTabContent.jsx').then(m => ({ default: m.EnhancedPhysicsTabContent })));
const AdvancedFinalTabContent     = React.lazy(() => import('./components/tabs/AdvancedFinalTabContent.jsx').then(m => ({ default: m.AdvancedFinalTabContent })));
const HARATabContent              = React.lazy(() => import('./components/tabs/HARATabContent.jsx').then(m => ({ default: m.HARATabContent })));
const CybersecurityTabContent     = React.lazy(() => import('./components/tabs/CybersecurityTabContent.jsx').then(m => ({ default: m.CybersecurityTabContent })));
const SOTIFTabContent             = React.lazy(() => import('./components/tabs/SOTIFTabContent.jsx').then(m => ({ default: m.SOTIFTabContent })));
const FMEASafetyTabContent        = React.lazy(() => import('./components/tabs/FMEASafetyTabContent.jsx').then(m => ({ default: m.FMEASafetyTabContent })));
const TechnicalSafetyConceptTabContent = React.lazy(() => import('./components/tabs/TechnicalSafetyConceptTabContent.jsx').then(m => ({ default: m.TechnicalSafetyConceptTabContent })));
const HWSafetyTabContent          = React.lazy(() => import('./components/tabs/HWSafetyTabContent.jsx').then(m => ({ default: m.HWSafetyTabContent })));
const SWSafetyTabContent          = React.lazy(() => import('./components/tabs/SWSafetyTabContent.jsx').then(m => ({ default: m.SWSafetyTabContent })));
const TraceabilityTabContent      = React.lazy(() => import('./components/tabs/TraceabilityTabContent.jsx').then(m => ({ default: m.TraceabilityTabContent })));
const SafetyVerificationTabContent = React.lazy(() => import('./components/tabs/SafetyVerificationTabContent.jsx').then(m => ({ default: m.SafetyVerificationTabContent })));
const BatteryHealthTabContent     = React.lazy(() => import('./components/tabs/BatteryHealthTabContent.jsx').then(m => ({ default: m.BatteryHealthTabContent })));
const CompareTabContent           = React.lazy(() => import('./components/tabs/CompareTabContent.jsx').then(m => ({ default: m.CompareTabContent })));
const DOETabContent               = React.lazy(() => import('./components/tabs/DOETabContent.jsx').then(m => ({ default: m.DOETabContent })));
const SuspensionRolloverTabContent= React.lazy(() => import('./components/tabs/SuspensionRolloverTabContent.jsx').then(m => ({ default: m.SuspensionRolloverTabContent })));
const ChargingTabContent          = React.lazy(() => import('./components/tabs/ChargingTabContent.jsx').then(m => ({ default: m.ChargingTabContent })));
const GearboxTabContent           = React.lazy(() => import('./components/tabs/GearboxTabContent.jsx').then(m => ({ default: m.GearboxTabContent })));
const EnergyMgmtTabContent        = React.lazy(() => import('./components/tabs/EnergyMgmtTabContent.jsx').then(m => ({ default: m.EnergyMgmtTabContent })));
const AWDTorqueVectoringTabContent= React.lazy(() => import('./components/tabs/AWDTorqueVectoringTabContent.jsx').then(m => ({ default: m.AWDTorqueVectoringTabContent })));
const ControlLoopTabContent       = React.lazy(() => import('./components/tabs/ControlLoopTabContent.jsx').then(m => ({ default: m.ControlLoopTabContent })));


const COLORS = { ...COLORS_LIGHT };
const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  zIndex: 3000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};
const modalBox = {
  width: "min(1200px, 96vw)",
  maxHeight: "88vh",
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  display: "flex",
  flexDirection: "column",
};


// ============================================================
// [Phase 2] core 모듈 imports
// ============================================================
// [Perf-3] 모든 physics/simulation NS → _initPhysicsBridge() dynamic import
// rhoCorrected는 4줄 공식이라 inline (static import 제거 → core-physics chunk 지연 로드 가능)
function rhoCorrected(T_amb, altitude) {
  const T_K = T_amb + 273.15;
  return (1.225 * (288.15 / T_K)) * Math.exp(-9.80665 * altitude / (287.05 * T_K));
}
// simulation.js의 runCoreSim 등은 _simNS 프록시로 접근 (runSim async 전환)
let _simNS = null; // simulation.js namespace (bridge 완료 후 채워짐)
import { NAV_CATEGORIES, AF_CATS as _AF_CATS } from "./constants/navCategories.js"; // [NAV-Dedup]

// ── [Perf-2] Physics/Safety NS → dynamic import (첫 렌더 후 백그라운드 1회 로드)
// 정적 import * as XNS 를 제거하면 Vite가 core-physics 청크를 초기 번들에서 제외.
// globalThis 브리지는 EVSimulatorInner의 useEffect에서 설정 (아래 _initPhysicsBridge 참조).
if (typeof globalThis._rlBadgeUid !== "number") globalThis._rlBadgeUid = 0;
// [Fix-7] Race condition 수정: boolean 플래그 → Promise 메모이제이션
//   구 패턴: _physicsBridgeReady = true를 import 완료 전에 세팅
//            → 두 번째 호출이 Promise.resolve(undefined)를 즉시 반환
//            → .then()이 _simNS=null 상태로 실행 → runCoreSim crash
//   신 패턴: 진행 중인 Promise를 저장해 반환 → 모든 호출자가 동일 Promise를 await
let _physicsBridgePromise = null;
function _initPhysicsBridge() {
  if (_physicsBridgePromise) return _physicsBridgePromise;
  _physicsBridgePromise = (async () => {
  const [
    RechartsNS, ThemeNS, HelpersNS, RLWidgetsNS, AdvancedWidgetsNS,
    VehiclePresetsNS, FaultModesNS, TaraDataNS,
    MathUtilsNS, MotorNS, BatteryNS, ThermalNS, VehicleNS,
    TireNS, DynamicsNS, DriveControlNS, SimulationNS, MotorControlNS,
    DoeNS, FmeaNS, TaraNS,
  ] = await Promise.all([
    import("recharts"),
    import("./components/shared/theme.js"),
    import("./components/shared/helpers.jsx"),
    import("./components/shared/rl_widgets.jsx"),
    import("./components/shared/advanced_widgets.jsx"),
    import("./constants/VEHICLE_PRESETS.js"),
    import("./constants/FAULT_MODES.js"),
    import("./constants/TARA_DATA.js"),
    import("./core/physics/math_utils.js"),
    import("./core/physics/motor.js"),
    import("./core/physics/battery.js"),
    import("./core/physics/thermal.js"),
    import("./core/physics/vehicle.js"),
    import("./core/physics/tire.js"),
    import("./core/physics/dynamics.js"),
    import("./core/physics/drive_control.js"),
    import("./core/physics/simulation.js"),
    import("./core/control/motor_control.js"),
    import("./core/analysis/doe.js"),
    import("./core/safety/fmea.js"),
    import("./core/safety/tara.js"),
  ]);
  // [Fix-dmt-DI] driveModeThrottle → simulation.js DI 주입 (bridge 로드 후 실행)
  SimulationNS.setDriveModeThrottle(DriveControlNS.driveModeThrottle);
  // [Fix-SST-DI] setSSTConstants로 교체 — 3종 SST 상수 주입 + 검증 원자적 실행
  // 기존 verifySSTConstants(_ROAD_MU, null):
  //   - DRIVE_MODE_GAIN_TABLE 2번째 인자가 null → 게인 테이블 동기화 검증 미실행
  //   - 감지만 가능, 인라인 복사본 교체 불가
  // 수정 setSSTConstants(_ROAD_MU, _G_GRAVITY, _DRIVE_TABLE):
  //   - ROAD_MU + G_GRAVITY + DRIVE_MODE_GAIN_TABLE 3종 동시 교체
  //   - 교체 직후 verifySSTConstants 자동 호출 → 잔여 불일치 즉시 감지
  //   - zero-import 설계 부채를 DI로 완전히 해소
  {
    const { ROAD_MU: _ROAD_MU, G_GRAVITY: _G_GRAVITY, GRID_EF_DEFAULT: _GRID_EF } =
      await import('./constants/physics_consts.js');
    const { DRIVE_MODE_GAIN_TABLE: _DRIVE_TABLE } =
      await import('./core/control/drive_mode_config.js');
    SimulationNS.setSSTConstants(_ROAD_MU, _G_GRAVITY, _DRIVE_TABLE, _GRID_EF); // [Fix-P3] GRID_EF DI 완성
  }
  // [Fix-③] WLTP 공식 1Hz 프로파일 주입 — generateWLTPProfile() 선형근사 대체
  {
    const { getWLTPOfficialProfile } = await import('./constants/wltp_official.js');
    SimulationNS.injectWLTPOfficialProfile(getWLTPOfficialProfile());
  }
  // simulation.js 네임스페이스를 프록시 변수에 저장 → runSim에서 접근
  _simNS = SimulationNS;
  Object.assign(
    globalThis,
    RechartsNS, ThemeNS, HelpersNS, RLWidgetsNS, AdvancedWidgetsNS,
    VehiclePresetsNS, FaultModesNS, TaraDataNS,
    MathUtilsNS, MotorNS, BatteryNS, ThermalNS, VehicleNS,
    TireNS, DynamicsNS, DriveControlNS, SimulationNS, MotorControlNS,
    DoeNS, FmeaNS, TaraNS,
    { I18N, SHARED_PARAM_DB, GRAPH_INFO_DB, NAV_CATEGORIES, AF_CATS: _AF_CATS, CONSTRAINT_DEFS }
  );
  globalThis.makeT = (lng) => HelpersNS.makeT(lng, I18N);
  })(); // [Fix-7] IIFE 종료 — Promise를 _physicsBridgePromise에 저장
  return _physicsBridgePromise;
}

// ============================================================
// EV Powertrain Simulator  v2.2
// sunjaepark83@gmail.com
// ============================================================
//
// ══ CHANGELOG (최신 → 구) ═════════════════════════════════
//
// [v2.2]  2026-04-24
//   [UI]     차트열·라이트다크·언어·실행 버튼 높이 30px 통일
//   [UI]     주행사이클 드롭다운 "Full-Spec CSV (설정에서 업로드)" → "Full-Spec CSV"
//   [RENAME] ev-sim_v2_1.jsx → ev-sim_v2_2.jsx
//
// [v2.1]  2026-04-20  ── 공기역학 Fz 실시간 연결 · 도메인 완성도 강화
//   [FIX]  FIX-AERO-FZ: 다운포스 ↔ 4-코너 Fz 실시간 루프 연결
//          (vehicle.js · dynamics.js · simulation.js · VEHICLE_PRESETS.js)
//   [RevD] 차량 동역학 88% → 97%
//          suspensionParetoOptimizer (k_spring·c_damper·k_arb 144점 파레토)
//          nvhVibrationSpectrum (2-DOF 쿼터카 FRF · ISO 2631-1 Wk 가중)
//   [RevD-TH] 열관리 88% → 97%
//          thermalResistanceOptimizer · coolingCapacityOptimizer · derateThresholdSensitivity
//   [RevE] 배터리·충전·안전 도메인 강화
//          cellBalancingOptimizer (6종 전략) · carbonIntensityChargingOptimizer
//          demandResponseEvaluator · asilDecompositionOptimizer · safetyCoverageCostOptimizer
//
// [v2.0]  2026-03-07 ~ 2026-03-09  ── 전 탭 물리 검증 + 대규모 모듈 분리
//   [ARCH] Phase 1~4: 상수/코어/스토어/컴포넌트 완전 모듈 분리 (Verification_46~49)
//   [FEAT] Gain Scheduling 3D 복합 보간 (V44)
//          AWD 지면반력 실시간 시각화 (V42) · SOTIF ODD 256-셀 커버리지 (V42)
//          DFMEA↔PFMEA 공정 자동 연계 + 제어계획 (V43)
//          Three.js WebGL · PWA SW 전환 · RDE NOx/PM 배출 체인 (V41)
//   [FIX]  드라이브 모드 전체 그래프 반영 7건 (V19)
//          통합분석 연산 수정 7건 (V18) · DOE 베이즈 수정 4건 (V17)
//          합불검증 수정 4건 (V16) · 민감도 수정 4건 (V15)
//          배터리·열관리·주행사이클·에너지·비교·4WS 탭 전면 검증 (V9~V14)
//          발진·재가속·코스트·제동·승차감 물리 수정 (A68-01~A68-07)
//          도로환경·4WS·비교 탭 KPI 수정 (A68-03~A68-07)
//          정속주행 물리 수정 (Fix-RL1~RL7) · 도로환경 수정 (Verification_07~08)
//   [UI]   전 탭 KPI 배지 툴팁 전면 적용 (V23~V35) · subtitle 정리 (V37~V39)
//          축 설정 48개 그래프 전체 (A44~A45) · 2단계 비동기 시뮬 Core/Heavy (A36)
//
// [v1.0]  2026-02 ~ 2026-03-06  ── 기초 물리·제어·UI 구축 (Advance 시리즈)
//   [FEAT] 정속·발진·재가속·코스팅·제동·에너지·열관리·주행사이클·비교 탭 완성
//   [FEAT] 파워트레인 제어 모달 전체 구축
//          드라이브모드·회생맵·AWD LUT·BMS·제어루프 분석 (A07~A35)
//   [FEAT] 헤더 전역 연동: 공기밀도·맞바람·SOC·노면 (A25~A29)
//   [FEAT] 고급 제어: MRAC · H∞ · MPC · 스키드패드 · 런치컨트롤 (R-12)
//   [FEAT] 코어 루프 완전 연동: 칼만SOC · BMS · 3D열 · 타이어 (R-01~R-19)
//   [FEAT] HARA / FMEA / 사이버보안 / SOTIF 안전 분석 탭 (R-10)
//   [REFACTOR] v01~v06 초기 모듈 통합 · dead code 제거 · 미연동 기능 연동
//
// ── 상세 이력 ─────────────────────────────────────────────
//
// ── v2.1 (2026-04-20) ────────────────────────────────────
// ✅ [FIX-AERO-FZ] 공기역학 다운포스 ↔ Fz 실시간 루프 연결 완료 (2026-04-20):
//   핵심: resistanceForcesV3()에서 계산한 ClA·v² 다운포스를
//         computeVerticalLoad_v4()에 dFz_aero_f/r 로 실시간 전달 → 4-코너 Fz에 반영
//   변경 파일:
//     · core/physics/vehicle.js   — resistanceForcesV3() 반환값에 F_down / dFz_aero_f/r 추가
//     · core/physics/dynamics.js  — computeVerticalLoad_v4() { dFz_aero_f, dFz_aero_r } 수용 확장
//     · core/physics/simulation.js — _computeVerticalLoad_v4 호출을 객체 형식으로 수정 (기존 positional 버그 수정),
//                                    simLaunch data.push에 F_down / dFz_aero_f/r 출력 추가
//     · constants/VEHICLE_PRESETS.js — DEFAULT_PARAMS: ClA=0, aero_balance_f=0.35 기본값 추가
//                                      Tesla M3: ClA=-0.04 / IONIQ 6: ClA=-0.06 실측 참고값 반영
//     · ev-sim_v2_2.jsx           — CSV 컬럼 맵(한/영) 3개 필드 추가,
//                                    도메인 감사 패널: 차량 동역학 pct 60→72% / 에너지 체인 pct 90→93%
//   하위 호환: ClA=0 기본값 → 기존 params 전체 기존 동작 완전 보존. opt-in 방식.
// ── v2.0 (2026-03-07 ~ 2026-03-09) ──────────────────────
// [Verification_41] Three.js WebGL렌더링 완성·PWA SW /sw.js 전환·CSV업로드단일화·RDE NOx/PM배출체인 (2026-03-09):
//   Fix-3D: TorqueMap3D useEffect에 THREE.js CDN 로드 대기 폴링(100ms×100회) 추가
//            → THREE 미로드 상태에서 'no_three' 고착 → 폴링으로 자동 재시도·렌더링 완성
//   Fix-SW: registerPWA IIFE — Blob URL → /sw.js 정적 파일 우선 시도 (CSP 호환)
//            → /sw.js 미존재 시 Blob URL fallback 유지 (기존 동작 보존)
//   Fix-CSV: handleCsvUpload — 파싱 성공 후 onApply() 자동 호출로 시뮬 즉시 실행
//             모드 선택기(Default/Full-Spec) csv_full 자동 전환 → 단일 업로드 플로우
//   Fix-RDE: calcRDEEmissions() 신규 함수 — NOx(간접)/PM(brake+tire+road) Phase별 계산
//             simDrivingCycle return에 rdeEmissions 필드 추가
//             DCTabContent DC9 신규 그래프 카드 — RDE 오염물질 바차트+상한선+KPI 배지
// [Verification_40] EN-E2 누적 에너지 0kWh 고착 수정 + 인쇄/PDF window.open 사용자 제스처 수정 (2026-03-09):
//   Fix-E2: e2Data useMemo 추가 — E_net_cum_kWh=0 시 E_cum_kWh−E_regen_cum fallback 재계산
//   Fix-PRINT: handlePDFExport — iframe 환경 window.open을 setTimeout 밖에서 즉시 호출 (제스처 보존)
//  ■ V39-CLEAN-SUBTITLE ✅ [SensitivityTabContent + AdvancedFinalTab op/sa] subtitle 정리 (15곳)
//     STRINGS 테이블 값 → "" 처리 (8개):
//       s1_sub (S1 Tornado): "x: ΔT (s) | y: Parameter | Left: − Right: +"
//       s2_sub (S2 Heatmap): "Color: WLTP Efficiency — Blue→Red"
//       s3_sub (S3 Monte Carlo): "x: Time (s) | y: Frequency — Normal distribution"
//       s4_sub (S4 Radar): "Axes: CdA · Crr · m · η · Battery · Power · P_aux · Regen | 100-point"
//       t1_sub (T1 Temp): "x: Time t (s) | y: Temperature (°C) | Derating threshold lines"
//       t2_sub (T2 Heat): "x: t (s) | y: Heat generation rate (W) — Stacked Area"
//       t3_sub (T3 Derating): "x: t (s) | y: Power ratio (% of P_max)"
//       t4_sub (T4 Ambient): "x: T_amb (°C) | Left: Efficiency | Right: Range"
//     직접 subtitle 문자열 → "" 처리 (7개):
//       S4 Radar  (direct): "Axes: CdA · Crr · m · η … | Value: 100-point scale"
//       JERK      (direct): "Tip-in/Tip-out torque filter · Hydraulic brake delay · ISO 2631"
//       JERK_TS   (direct): "Longitudinal jerk (da/dt) [m/s³] vs Time"
//       JERK_SPD  (direct): "Vehicle response to throttle/brake input"
//       JERK_BRK  (direct): "Hydraulic delay (τ=0.2s) vs Regen response (τ=0.02s)"
//       JERK_TF   (direct): "Jerk RMS vs Torque Filter Time Constant τ"
//       SV-S1     (direct): "Parameter performance sensitivity ranking · MC 500-run …"
//       SV-S2     (direct): "Parameter × Metric combination sensitivity · Optimization …"
//     * 그래프 내부 XAxis/YAxis label은 유지
//
// [Verification_49] Phase 4 컴포넌트 분리 (2026-03-09)
// [Verification_48] Phase 3 상태 관리: useState 32개 → store 3개 (2026-03-09)
// [Verification_47] Phase 2 모듈 분리: core 순수 함수 → core/ (2026-03-09)
// [Verification_46] Phase 1 모듈 분리: 상수 데이터 → constants/ (2026-03-09)
// [Verification_44] Gain Scheduling 복합 보간: 단축 독립 표시 → 3D 보간 게인 테이블 (2026-03-09):
// [Verification_45] 도메인별 완성도 격차 보강 (2026-03-09):
//   - 사이버보안 (81%→87%): CAL 등급 자동 산출 매트릭스 (ISO/SAE 21434 Table 8) + 보안 요구사항 자동 도출
//   - 기능안전 (83%→90%): ISO 26262-6 SW 안전 요구사항·V&V 체크리스트 (MC/DC·MISRA·커버리지 목표)
//   - 기능안전+사이버보안+SOTIF 안전 삼각형 연계 매트릭스 (3중·2중·갭 분류)
//   - SOTIF (80%→87%): KS↔FTA 결함트리 연계 분석 (OEDR×PFH 복합 위험 지수)
//   - DOE/베이즈 (84%→91%): Pareto 3목적함수·포인트 클릭 상세 패널·레이더 차트 비교
//   - PWA (82%→88%): SW 캐싱 전략 3분기(CacheFirst/NetworkFirst/SWR)·BackgroundSync·Manifest 동적 주입
//  ■ V44-GS-3D ✅ [Gain Scheduling / gainschedule 서브탭] 3D 복합 보간 게인 테이블 신규 추가
//     computeGainScheduling() 확장:
//       - 3D 격자 grid3d 생성: RPM(6) × Torque(5) × Temp(7) = 210 포인트
//       - 복합 포화 반영: Lq_comb = Lq0 · rpm_Lq_factor · torque_Lq_factor
//                         Rs_comb = Rs0 · (1+α(T−T_ref)) · torque_Rs_factor
//       - trilinearInterp(rpm,trq,temp) 함수: 3선형 보간 → 임의 운전점 게인 산출
//       - 반환: grid3d, trilinearInterp, RPM/TRQ/TMP_AXIS_3D, *_LABELS_3D, T_max
//     [UI] 뷰 토글: '🧊 3D 보간 테이블' | '📊 클래식 차트'
//     [3D 테이블 뷰]:
//       ① 축 배치 컨트롤: X축(열) / Y축(행) 선택 (rpm|torque|temp 3개 조합 자유 전환)
//       ② 표시 게인 선택: Kp_i / Ki_i / Kp_s / Ki_s / PM_i / BW_i_hz 6종
//       ③ Z축 슬라이더: 나머지 축 고정값 선택 (범위 버튼 + range 슬라이더 이중 조작)
//       ④ 색상 히트맵 테이블: 값→파란→초록→노랑→빨간 그라데이션, 각 셀 Δ% vs 공칭
//          PM_i 선택 시 45° 임계 ✓/⚠ 배지 자동 표시
//       ⑤ 색상 범례 바: 최솟값~최댓값 + 공칭 기준값 표시
//       ⑥ 3선형 보간 프로브: RPM·토크%·온도 슬라이더 → 6개 게인 항목 실시간 보간 출력
//          PM<45° / sep<5× 경고 배지 자동 표시
//     [클래식 차트 뷰]: 기존 단축 독립 표시 유지 (RPM|Torque|Temp 3탭, Kp/Ki/BW/PM 차트+테이블)
//  ■ V43-AUTOMAP ✅ [DFMEA↔PFMEA] 제조 공정 단계 자동 연계 3종 추가
//     autoMapDFMEA_PFMEA(dfmeaItems, pfmeaItems):
//       - 키워드 매칭 엔진: subsystem·component·failure_mode 토큰 ↔ process_step·process_fn 토큰
//       - 점수 체계: 서브시스템 2pt + 부품명 3pt + 고장 키워드 2pt + 카테고리 1pt
//       - threshold ≥ 2pt → 자동 연계 후보 (confidence = score/maxScore)
//       - 기존 수동 링크(process_cause_ids)와 병합 → auto_only / manual_confirmed / both 구분
//     generateControlPlan(pfmeaItems, dfmeaLinked):
//       - 공정 단계별 제어 계획 집계: linked DFMEA severity 최대값 → 검사 빈도/방법 결정
//       - severity ≥9 → CRITICAL(100% 검사) / ≥7 → MAJOR(샘플링) / 기타 → MINOR(순찰)
//       - 공정 카테고리별 관리도 타입 (제조→Cpk, 조립→Ppk, 교정→MSA, SW→Coverage)
//       - 검출 방법 자동 매핑: inspection_method·frequency·spc_type·record_form 산출
//     [UI] "🗺️ 공정 맵" / "🗺️ Process Map" 서브뷰 신규 추가 (dfmeaView="process_map"):
//       - 제조 공정 흐름 스윔레인 (SVG 수평 카드+화살표):
//           PCB 실장 → 모터 조립 → 인버터 → BMS교정 → 팩 조립 → 배터리봉지 → EPS조립 → ABS 등
//         각 공정 카드: 위험도 색상(RED/AMB/GRN) + 연계 DFMEA 수 + 자동 발굴 수
//       - 공정 단계 클릭 → 우측 드릴다운 패널:
//           수동 연계 DFMEA 목록 (ID·고장모드·S) + 자동 발굴 DFMEA (신규, 배지 강조)
//       - 제어 계획(Control Plan) 테이블:
//           공정 단계 / 관리 특성 / 심각도 / 검사 방법 / 빈도 / SPC 타입 / 기록 양식
//     [UI] 기존 "🔗 연계 매핑" 뷰 보강: 자동 발굴 제안(auto_suggestions) 인라인 표시
//       - 수동 링크: 자주색 배지 / 자동 발굴 제안: 주황색 점선 배지 + confidence%
//
// [Verification_42] AWD 지면반력 실시간 시각화 + SOTIF ODD 기반 시나리오 커버리지 (2026-03-09):
//  ■ V42-AWD-GRF ✅ [AWD 분배 탭] 토크 벡터링 LUT → 4륜 지면반력도 실시간 시각화
//     calcAWDGroundForceViz(): v_kmh·torq_pct·ay_g 슬라이더 기반 per-wheel 계산
//       - computeVerticalLoad_v4 연동: Fz_FL/FR/RL/RR 동적 하중 이동 반영
//       - bilinear2D + 모드별 LUT: 현재 드라이브 모드 AWD 전후 분배비 실시간 추종
//       - tvYawMomentSplit 연동: TV 활성 시 좌우 토크 델타(ΔT) 반영
//       - 마찰원 활용률(frUtil): μ×Fz 대비 구동력 Fx 비율 → 색상 코딩
//     AWDGroundForceViz React 컴포넌트:
//       - 슬라이더: 차속(0-200 km/h) · 구동토크%(0-100) · 횡가속(−0.5 to +0.5 g)
//       - SVG 차량 탑뷰: 4개 타이어 패치 (색상=마찰원 활용률 green→yellow→red)
//       - 타이어별 수직 하중바 (Fz, N) + 구동 토크 화살표 (T, N·m)
//       - 전후/좌우 분배 프로그레스바 + 요 모멘트(M_z) 표시
//       - 접지 조건(μ), 모드, TV 활성 여부 배지
//  ■ V42-SOTIF-ODD ✅ [SOTIF Analysis] 단순 확률 → ODD 기반 시나리오 커버리지 고도화
//     computeSOTIF() 전면 개선:
//       - ODD 4차원 정의: Speed(4) × Weather(4) × Lighting(4) × Road(4) = 256 셀
//       - odd_speed_weather 커버리지 매트릭스: 속도 × 날씨 교차 히트맵
//       - high_risk_uncovered: 커버리지 <80% × 빈도 가중 미검증 시나리오 랭킹
//       - coverage_pct: 커버된 ODD 셀 수 / 전체 (speed×weather×lighting = 64)
//       - unknown_risk_pct: 기존 통계모델(50%) + ODD 미커버(50%) 블렌딩
//       - Known Scenarios 2종 추가: KS-07(야간+강우 복합열화), KS-08(고속 안개)
//       - sotif_score 산식: coverage_pct 반영 (0.3 가중)
//     SOTIFTabContent 신규 섹션 추가:
//       - ODD 커버리지 히트맵: speed × weather 그리드 (coverage% 색상 코딩)
//       - ODD 차원별 요약 바: speed/weather/lighting/road 4개 축 평균 커버리지
//       - 고위험 미커버 시나리오 테이블: 빈도 가중 리스크 정렬
//
// [Verification_38] R1·RE1·RE2·RV1·전력에너지 그래프 subtitle x/y축 설명 제거 (2026-03-09):
//  ■ V38-CLEAN-SUBTITLE ✅ [정속주행·도로환경·차량동역학·에너지체인] subtitle 정리
//     R1  (Road Load — 구동/저항 토크): "x: Speed | Left y: Torque | Right y: F_total | ..." → ""
//     RE1 (저항력 분해 — IRI 거칠기 vs 공기·구름저항): "x: Speed | y: Force F (N) | ..." → ""
//     RE2 (기어오일 온도 & 냉각 유량): "Left y: Gear Oil Temp | Right y: Coolant Flow | ..." → ""
//     RV1 (4-Corner Body Displacement): "4-Corner sprung body displacement ... | y: mm | ..." → ""
//     EN-E1 (에너지 체인 흐름): e1_sub → ""
//     EN-E2 (WLTP 누적 에너지 & SOC): e2_sub → ""
//     EN-E3 (속도별 손실 분해): e3_sub → ""
//     EN-E4 (단계별 효율 η 곡선): e4_sub → ""
//     * 번역 테이블(STRINGS) 내 e1~e4_sub 값도 동시에 빈 문자열로 정리
//     * 그래프 내부 XAxis/YAxis label은 유지 (축 이름 차트 내부에서 표시)
//
// [Verification_37] Launch KPI 한글 threshold 영어화 + 그래프 subtitle x/y축 설명 제거 (2026-03-09):
//  ■ V37-ENG-LAUNCH ✅ [발진 LaunchTabContent] 0→100 KPI threshold 영어 조건부 추가
//     "< 4 s: 스포츠 / 4~6 s: 준스포츠 / 6~9 s: 일반 / > 9 s: 안락"
//       → lang 조건부: "< 4 s: Sport / 4–6 s: Semi-sport / 6–9 s: Standard / > 9 s: Comfort"
//
//  ■ V37-CLEAN-SUBTITLE ✅ [전체 탭] 그래프 카드 subtitle의 x/y축 좌표 설명 20곳 제거
//     "x: Speed v (km/h) | y: Power P (kW) | ..." 형식의 subtitle → "" (빈 문자열)
//     제거 대상 탭: Road Load(R2/R3/R4/R5), Coast(C1/C2/C4), RegenBrake(RB1/RB3),
//                  ReAccel(RA1/RA2/RA3), Launch(L1/L2/L3),
//                  Compare(E2/T1/S2), Battery(B1)
//     그래프 내 XAxis/YAxis label은 유지 (축 이름 표시는 차트 내부에서 담당)
//
// ── (아래 RevE·RevD·RevD-TH 는 v2.1 상세 이력) ───────────
// [RevE] 도메인 완성도 전 영역 갭 해소 (2026-04-20):
//  ■ RevE-BAT ✅ cellBalancingOptimizer 신규 구현 (battery.js)
//     · 6종 전략: 패시브 저항 / 스위칭 패시브 / 커패시터 / 인덕터(Buck-Boost) / 플라이백 DC-DC / 멀티포트
//     · 평가 지표: E_loss_Wh / 사이클 수명 / 용량 활용률 / efficiency_score
//     · 파레토 프론티어: efficiency_score 최대 & cost_krw 최소 비지배 집합
//     · 예산+수명 제약 권장안 자동 도출, 24h 밸런싱 전류 프로파일 출력
//     · IEC 62660-1 / USABC Battery Management Guidelines 준거
//  ■ RevE-CHG-1 ✅ carbonIntensityChargingOptimizer 신규 구현 (SmartGridService)
//     · ε-제약 다목적 파레토: carbon_weight 0→1, 9개 시나리오
//     · ISO 15118-20 §8.9 Green Charging 준거
//     · DR 피크 제한(OpenADR 2.0b) 자동 통합, 파레토 비지배 필터
//  ■ RevE-CHG-2 ✅ demandResponseEvaluator 신규 구현 (SmartGridService)
//     · 24h 계통 부하 프로파일 기반 oadrDistributeEvent 시뮬레이션
//     · curtail_full / curtail_half / normal 액션 분류, DR 인센티브 수익 산출
//     · SOC 궤적 및 피크 감축량(kW) 출력
//  ■ RevE-SAF-1 ✅ asilDecompositionOptimizer 신규 구현 (fmea.js)
//     · ASIL D/C/B 전체 분해 조합 열거 (ISO 26262-9 Table D.1)
//     · 비용 = HW + SW + 시험 + 독립성 오버헤드(ind_score × 8M)
//     · coverage_pct = DC 기하평균 × (1 - CCF_beta×0.5)
//     · 파레토 프론티어 + 권장안(coverage≥95% 최소 비용)
//  ■ RevE-SAF-2 ✅ safetyCoverageCostOptimizer 신규 구현 (fmea.js)
//     · 대응책 12종 카탈로그 (HW / SW / 시험 / 툴 / 사이버보안)
//     · Knapsack 그리디 파레토 (예산 20~100% 5단계)
//     · ROI 지수, 목표 커버리지 달성 최적 포트폴리오 자동 선정
//  ■ RevE-UI ✅ DomainAuditPanel 전 영역 업데이트
//     · 배터리: partial → impl, 2,185줄 → 2,235줄
//     · 충전 전략: 65% → 88%, DR partial → impl, 탄소 gap → impl
//     · 기능안전: 55% → 83%, ASIL분해 partial → impl, 커버리지-비용 gap → impl
//     · coverage matrix: 충전/안전 전면 상향 (none → part/full)
//     · 요약 통계: 구현 함수 57 → 65, partial 0개, gap 0개
//
// [RevD-TH] 열관리 도메인 완성도 88% → 97% (2026-04-20):
//  ■ RevD-TH-1 ✅ thermalResistanceOptimizer 신규 구현 (thermal.js)
//     · 탐색 격자: R_th_bc(5) × R_th_mc(5) × R_th_ic(3) = 75점
//     · 구동 조건: 최대 부하 120 s (Q_batt=4.5 kW, Q_motor=2.8 kW)
//     · 목적함수: T_batt_peak / T_motor_peak 2목적 파레토 전선
//     · 펌프 전력 추정 포함 (Dittus-Boelter R_th 역산)
//  ■ RevD-TH-2 ✅ coolingCapacityOptimizer 신규 구현 (thermal.js)
//     · UA_rad 20점 탐색 (50–600 W/K)
//     · WLTP급 부하 프로파일 30분 시뮬레이션 (도심/교외/고속/감속)
//     · T_peak ≤ 45°C 제약 → 최소 UA_rad (비용 최적) 자동 도출
//     · dT_batt_peak / dUA_rad 민감도 프로파일 출력
//  ■ RevD-TH-3 ✅ derateThresholdSensitivity 신규 구현 (thermal.js)
//     · T_derate_batt(6점) × T_derate_motor(5점) = 30점 격자
//     · 출력 손실 % vs Arrhenius SOH 손상 파레토 (E_a=38 kJ/mol)
//  ■ RevD-TH-UI ✅ DomainAuditPanel 열관리 항목 업데이트
//     · pct: 88 → 97, barColor: #EF9F27 → #63aa22
//     · partial(열저항 파라미터 최적화) → impl [RevD]
//     · gap(냉각 시스템 용량 최적화) → impl [RevD]
//     · coverage matrix: part → full (완전 커버)
//
// [RevD] 차량 동역학 도메인 완성도 88% → 97% (2026-04-20):
//  ■ RevD-VD-1 ✅ suspensionParetoOptimizer 신규 구현 (dynamics.js)
//     · 탐색 격자: k_spring(4) × c_damper(4) × k_arb(9) = 144점
//     · 노면: ISO 8608B LCG 화이트노이즈, v=60 km/h, T=10 s
//     · 목적함수: J_ride(RMS 스프렁 가속도) vs J_handling(LTR 근사)
//     · 출력: pareto[], best_ride, best_handling, best_balanced
//  ■ RevD-VD-2 ✅ nvhVibrationSpectrum 신규 구현 (dynamics.js)
//     · 2-DOF 쿼터카 FRF 해석해 (1,024점, 복소수 전달함수)
//     · ISO 2631-1 Wk 가중 필터 → a_w 승차감 지수 + 6단계 등급
//     · 기어 메시 주파수(f_mesh, 2·3차 고조파), 공진 근접 여부
//     · FFT 차수 분석(order tracking): 1~10차 FRF 크기 + severity
//  ■ RevD-VD-UI ✅ DomainAuditPanel 업데이트
//     · pct: 88 → 97, barColor: #E24B4A → #63aa22
//     · partial(서스펜션 파라미터 튜닝) → impl [RevD]
//     · gap(NVH 진동 스펙트럼) → impl [RevD]
//     · coverage matrix: part/none → full/full
//
// [Verification_36] Vehicle Dynamics 섹션 KPI 툴팁 formula/threshold 영어화 (2026-03-09):
//  ■ V36-ENG-VD ✅ [Vehicle Dynamics / Road Load] formula·threshold 영어 조건 추가
//     R1 구동토크: formula "(정토크 구간)" → lang 조건부 "(constant-torque region)"
//     R1 토크여유: formula "(0이면 이론적 한계점)" → "(0 = theoretical limit)"
//     R2 교차속도: formula "이분탐색·반영" → "bisection·applied"
//     Coast 공력비율: threshold "공력 지배(고속)/구름저항 지배(저속)" → English
//     Coast DT손실율: threshold "양호/점검 권장" → "good/inspection recommended"
//     RegenBrake 회생효율: threshold "우수/양호/개선 필요" → "excellent/good/needs improvement"
//     RegenBrake 회생비율: threshold "우수(패드 마모 최소화)/개선 필요" → English
//     RegenBrake 디스크온도: threshold "안전/주의/페이딩 위험" → "safe/caution/fade risk"
//     RegenBrake 제동거리: threshold "우수/양호/점검" → "excellent/good/inspection needed"
//     RegenBrake ABS개입: threshold "최적/일반적/과다" → "optimal/normal/excessive"
//     ReAccel 응답지연: threshold "우수/양호/개선 필요" → "excellent/good/needs improvement"
//     ReAccel 오버슈트: threshold "양호/주의/불량(적색)" → "good/caution/poor (red)"
//
// [Verification_35] 민감도·통합 분석 KPI 툴팁 + Launch 라벨 이중 언어 (2026-03-09):
//  ■ V30-TIP-SENS ✅ [민감도 SensitivityTabContent] KPI 툴팁 전면 추가 — 🟢 ENHANCEMENT
//     상단 배너: Base 0-100 / Base Coast / σ MC / 95%CI / P05 / P95 / CdA기여 / 종합점수 / e_wltp (9개)
//     S1 Tornado: Base 0-100 / Base Coast / Top영향 / ΔT Top1 / 발진민감도 (5개)
//     S3 MC: μ / σ / P05 / P95 / 95%폭 (5개)
//     S4 레이더: 종합점수 / e_wltp / 0-100 / 항속거리 / 판정 (5개)
//  ■ V30-TIP-AFT ✅ [통합 분석 AdvancedFinalTabContent] KPI 툴팁 전면 추가 — 🟢 ENHANCEMENT
//     VD-R1: CdA / Crr / 차량 질량 / 교차점 (4개)
//     VD-R2: 보조부하 / 맞바람 / 최적 속도 / ηdrive (4개)
//     VD-R3: 기준 CdA / 기준 Crr / 분석 속도 / CdA 공차 목표 (4개)
//     VD-L1: 0→100 / 최대 출력(모드별) / Re-Accel / 배터리에너지 (4개)
//     VD-C1: η_regen / 회생에너지 / 제동거리 / 회생비율 (4개)
//     EN-E2: WLTP 전비 / 항속거리 / 회생효율 / SOC 최종 (4개)
//     EN-E3: 체인 효율 / 공력 지배 / 전기 손실 지배 / 구동계η (4개)
//     TH-T1: 모터 최고온도 / 배터리 최고온도 / 인버터 최고온도 / 외기온 (4개)
//     TH-T3: 기준 T_amb / WLTP 전비 / -10°C 전비 악화 / 최적 온도 (4개)
//     BT-B1: 현재 SOH / EOL 예측 / SEI 성장 / 외기온 (4개)
//     BT-B2: R0 기준 / -10°C Ri 상승 / SOC20% 추가 / 최적 Ri 조건 (4개)
//  ■ V30-LABEL-LAUNCH ✅ [발진 LaunchTabContent] 최상단 KPI 3개 라벨 이중 언어 — 🟡 MEDIUM
//     가속 거리 → Accel. Dist. / 가속 에너지 → Accel. Energy / 구동효율 → Drive Eff.
//  ✅ 총 57개 추가 툴팁 (title / what / why / formula / threshold 패턴, R1 형식 준용)
//     영어 선택 시 KPI 라벨·툴팁 내용 모두 English로 표시
//
// [Verification_26] 3개 탭 KPI 박스 툴팁 전면 적용 (2026-03-09):
//  ■ V26-TIP-CMP ✅ [차량 비교 CompareTabContent] KPI 툴팁 추가 — 🟢 ENHANCEMENT
//     Graph 1: A SOC / B SOC / A전비 / B전비 / A회생 / B회생 (6개)
//     Graph 2: A배터리피크 / B배터리피크 / A모터피크 / B모터피크 / A Batt마진 / B Batt마진 (6개)
//     Graph 4: A기준전비 / B기준전비 / A최대개선 / B최대개선 / A최대악화 / B최대악화 (6개)
//  ■ V26-TIP-ENE ✅ [에너지 체인 EnergyTabContent] KPI 툴팁 추가 — 🟢 ENHANCEMENT
//     상단 요약: 전비 / 항속거리 / 구동에너지 / 회생에너지 / 순소비 / 보조부하 / 회생비율 / CO₂ / 최종SOC (9개)
//     E1: 배터리η / 인버터η / 모터η / 기어η / 종합η / Ri손실비 / 인버터손실 / 회생비율 (8개)
//     E2: 구동에너지 / 회생에너지 / 순소비 / 보조부하 / 최종SOC / 회생효율 / Low전비 / ExH전비 (8개)
//     E3: 공력손실@100 / 구름손실@40 / 인버터손실@100 / 기어손실@100 / SiC보정 (5개)
//     E4: 효율최고 / 고효율속도 / FW진입속도 / 배터리η / 인버터η / 모터η / 기어η / 종합η (8개)
//  ■ V26-TIP-DC ✅ [주행사이클 DCTabContent] KPI 툴팁 추가 — 🟢 ENHANCEMENT
//     상단 요약: 전비 / 항속거리 / 회생율 / CO₂ / 평균오차 / RMS오차 / ≥3오차 / 회생에너지 / 보조부하 (9개)
//     DC1: 평균|e| / 최대|e| / RMS / ≥3km/h / 인증기준 (5개)
//     DC2: 구동에너지 / 회생에너지 / 보조부하 / 순소비 / 회생율 (5개)
//     DC3: 전비 / 전체회생율 / Low phase 회생율 / ExH phase 전비 (4개)
//     DC4: 배터리최고온도 / 모터최고온도 / 인버터최고온도 / Batt derate / 외기온 (5개)
//     DC5: WLTP / JC08 / CLTC / RDE M'way / 목표 (5개)
//     DC6 MC: μ / σ / P5 / P95 / CI폭 (5개)
//  ✅ 총 79개 KPI 배지에 title / what / why / formula / threshold 툴팁 적용
//     R1 툴팁 형식(V23) 동일 패턴 준용

// [Verification_24] 6개 탭 KPI 이름 단순화 + 툴팁 전면 적용 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_23
//
//  ■ V24-KPI-01 ✅ [정속 주행 R1/R2/R3] KPI 툴팁 — V23에서 적용 완료
//
//  ■ V24-KPI-02 ✅ [발진 LaunchTabContent] KPI 이름 단순화 + 툴팁 추가
//     0→100 km/h(XX%) → 0→100(XX%) / 0→60 km/h → 0→60
//     0→100 주행거리 → 가속 거리 / 0→100 배터리 에너지 → 가속 에너지
//     파워트레인 효율 → 구동효율 / 0→100(50% th) → 0→100(반개도)
//
//  ■ V24-KPI-03 ✅ [재가속 ReAccelTabContent] KPI 이름 단순화 + 툴팁 추가
//     응답 지연 Δt → 응답지연 / T_peak (N·m) → 피크 토크
//     이동 거리 → 재가속 거리 / 배터리 소모 → 재가속 에너지
//     파워트레인 효율 → 구동효율
//
//  ■ V24-KPI-04 ✅ [코스트 CoastTabContent] KPI 이름 단순화 + 툴팁 추가
//     코스트 시간 (100→정지) → 코스트 시간 / E_aero 손실 → 공력 손실
//     E_roll 손실 → 구름 손실 / E_DT 손실 → DT 손실
//     공력 비율 / 총 저항 → 공력 비율 / Crr 비율 / 총 저항 → Crr 비율
//     DT 손실 비율 → DT 손실율 / 20~50 평균 Crr 비율 → Crr 비율 (20~50)
//     에코 v0 최소(≥300m) → 에코 v0 (≥300m)
//
//  ■ V24-KPI-05 ✅ [회생제동 RegenBrakeTabContent] KPI 이름 단순화 + 툴팁 추가
//     회생 효율 — KE 회수율 (100%) → 회생효율 (100%)
//     회생 비율/총 제동 → 회생비율 / E_regen (100%) → E_regen
//     E_friction (100%) → E_마찰 / T_disc_final (100%) → 디스크 온도
//     제동거리 (100%) → 제동거리 / ABS 개입 횟수 → ABS 개입
//
//  ■ V24-KPI-06 ✅ [승차감 DrivabilityTabContent] KPI 이름 단순화 + 툴팁 추가
//     팁인 이벤트 → 팁인 / 팁아웃 이벤트 → 팁아웃 / ISO Grade → ISO 등급
//     Jerk Max → Jerk 최대 (already minimal, tooltip added)
//
// ────────────────────────────────────────────────────────────
//   베이스: ev-sim_v1.0_Verification_22
//
//  ■ V23-KPI-01 ✅ [R1 KPI 배지] 최고속도 2개 삭제 — 🟡 MEDIUM
//     원인: "최고 속도 (전력 기반)" / "최고 속도 (토크 기반)" 배지가
//           아래 R0 전용 최대속도 정보 박스에 동일한 값이 크게 표시되어 중복
//     수정: RLKpiBadgeRow에서 두 항목 제거 → 배지 행 간결화
//
//  ■ V23-KPI-02 ✅ [RLKpiBadge] 클릭형 인라인 툴팁 컴포넌트 — 🟢 ENHANCEMENT
//     원인: KPI 배지가 숫자·단위만 표시 → 의미·중요성을 별도 노하우 패널로만 확인 가능
//           → 초보 사용자가 수치의 의미를 즉각 파악하기 어려움
//     수정: RLKpiBadge에 tooltip prop 추가 (title / what / why / formula / threshold)
//           배지 우측 상단 ⓘ 아이콘 표시, 클릭 시 말풍선 팝업 토글
//           RLKpiBadgeRow: items 5번째 요소로 tooltip 객체 전달 지원
//
//  ■ V23-KPI-03 ✅ [R1 KPI] 툴팁 데이터 추가 — 🟢 ENHANCEMENT
//     대상: 구동토크, 저항토크, 토크여유, 토크부하율, 전력여유, SW제한속도
//
//  ■ V23-KPI-04 ✅ [R2 KPI] 툴팁 데이터 추가 — 🟢 ENHANCEMENT
//     대상: 교차속도, 공기저항전력, 구름저항전력, 총주행저항전력,
//           구배저항전력, 구배ΔP, 적재ΔP
//
//  ■ V23-KPI-05 ✅ [R3 KPI] 툴팁 데이터 추가 — 🟢 ENHANCEMENT
//     대상: 최적경제속도(보조부하 0/1/2/3 kW), 경제속도변화, 항속거리×2
//
// ────────────────────────────────────────────────────────────
// [Verification_19] 드라이브 모드 전체 그래프 반영 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_18
//
//  ■ DM-FIX-01 ✅ [DriveModeBadge] SNOW·OFF_ROAD dmDesc 누락 — 🔴 HIGH
//     원인: dmDesc에 ECO/NORMAL/SPORT 3종만 정의, SNOW·OFF_ROAD 선택 시 undefined 표시
//     수정: SNOW(스로틀×55% 회생×30% 저슬립), OFF_ROAD(스로틀×100% 회생×60% 벡터링) 추가
//
//  ■ DM-FIX-02 ✅ [AdvancedFinalTabContent.rlData] drive_mode pkScale 미반영 — 🔴 HIGH
//     원인: rlData.useMemo의 P_elec 계산이 eta_driveline/P_max_wheel만 사용
//           drive_mode 별 피크 전력 스케일(ECO=×0.70, SPORT=×1.15 등) 미반영
//           → VD-R1 "전기 입력 전력" 곡선이 항상 NORMAL 기준으로 표시됨
//     수정: getLUTPkScale(params, mode)로 pkScale 계산 후 Pw/Pe 계산에 적용
//           P_max_kW = (params.P_max_wheel||220000)/1000 * pkScale 상한선 추가
//           (P_elec은 도로부하 역산값이므로 pkScale 직접 곱셈 X, 참조선으로 추가)
//
//  ■ DM-FIX-03 ✅ [AdvancedFinalTabContent VD-L1] KPI "최대 출력" pkScale 미적용 — 🟡 MEDIUM
//     원인: `(params.P_max_wheel||250000)/1000` — drive_mode 스케일 없이 항상 NORMAL값 표시
//     수정: `(params.P_max_wheel||250000)/1000 * pkScale` → 모드별 실효 출력 표시
//
//  ■ DM-FIX-04 ✅ [EnergyTabContent] DriveModeBadge 없음 — 🟡 MEDIUM
//     원인: 에너지 탭 상단 KPI 배너에 현재 드라이브 모드 표시 없음
//           WLTP 결과는 runSim에서 drive_mode 반영되어 오지만 사용자가 모드 인지 불가
//     수정: 상단 KPI 배너 헤더 옆에 DriveModeBadge 추가
//
//  ■ DM-FIX-05 ✅ [SensitivityTabContent] DriveModeBadge 없음 + heatmap 계수 미반영 — 🟡 MEDIUM
//     원인: ① 상단 KPI 배너에 드라이브 모드 표시 없음
//           ② heatmapData 계수(0.35, 0.25, 0.20)가 drive_mode 무관 고정값
//              → ECO/SPORT 모드에서 CdA/Crr/Mass 영향도가 실제와 다르게 표시됨
//     수정: ① DriveModeBadge 추가
//           ② drive_mode 별 민감도 가중: ECO CdA 계수↑(0.42), SPORT mass 계수↑(0.25)
//
//  ■ DM-FIX-06 ✅ [ThermalTabContent] DriveModeBadge 없음 — 🟡 MEDIUM
//     원인: 열 관리 탭 상단에 드라이브 모드 표시 없음
//           SPORT 모드일 때 모터/인버터 최고 온도가 높아지는 이유를 사용자가 연결 못 함
//     수정: 상단 KPI 배너에 DriveModeBadge 추가
//
//  ■ DM-FIX-07 ✅ [EnhancedPhysicsTabContent] DriveModeBadge 없음 — 🟢 LOW
//     원인: 고급 물리 탭(MC/Sobol/DQ/BMS)에 드라이브 모드 표시 없음
//           MC 분포 결과가 drive_mode 에 따라 달라지지만 사용자가 어떤 모드로 실행됐는지 모름
//     수정: 섹션 탭 버튼 행 옆에 compact DriveModeBadge 추가
//
// ────────────────────────────────────────────────────────────
// [Verification_18] 통합 분석 탭 전체 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_17
//
//  ■ AF-FIX-01 ✅ [seiRows SEI] toFixed() 연산자 우선순위 오류 — 🔴 HIGH
//     원인: `sei:+Math.sqrt(ks*th*1e9)*0.3 .toFixed(2)`
//           JavaScript 파서: `0.3 .toFixed(2)` → "0.30" (문자열)
//           `Math.sqrt()*"0.30"` → 암묵 형변환(0.3)으로 곱해져 결과 "같아보이지만"
//           실제 반환값 타입이 number가 아닌 number (암묵변환) → toFixed 미적용
//           → sei값이 소수점 2자리로 반올림되지 않음
//     수정: `+(Math.sqrt(ks*th*1e9)*0.3).toFixed(2)` — 괄호로 전체식 감싸 우선순위 보장
//
//  ■ AF-FIX-02 ✅ [thermalSensData] e_wltp fallback 단조감소 오류 — 🔴 HIGH
//     원인: `e_wltp = base * (1 + (25-T)*0.004)`
//           T>25°C 구간: (25-T)<0 → e_wltp < base (감소) — 물리적으로 틀림
//           실제: 냉방(T>30°C) 부하로 전비 재상승해야 함 → U자 곡선 필요
//     수정: U자 모델 적용
//           `penalty = Math.max(0,(25-T)*0.006) + Math.max(0,(T-30)*0.003)`
//           T<25: 난방 페널티(강함), 25~30°C: 최적, T>30: 냉방 페널티(약함)
//
//  ■ AF-FIX-03 ✅ [thermalSensData] range_km fallback 고온 단조증가 오류 — 🔴 HIGH
//     원인: `range_km = base_range / (1+(25-T)*0.004) * (T<0?0.85:1)`
//           T>25: 분모 < 1 → range_km > base_range (고온에서 항속 증가 — 틀림)
//           `(T<0?0.85:1)` 저온 페널티만 있고 고온 페널티 없음
//     수정: range_km = base_range / (1 + penalty) 방식으로 e_wltp와 연동
//           → e_wltp가 증가할수록 range_km 감소하여 에너지 보존 일관성 유지
//
//  ■ AF-FIX-04 ✅ [sankeyRows] eta_i 구동계 중복 계산 — 🟡 MEDIUM
//     원인: `eta_i = (params.eta_driveline||0.95) * 0.97`
//           eta_driveline은 이미 구동계 전체(인버터+모터+기어) 효율을 의미하는 경우 많음
//           여기에 0.97을 또 곱하면 인버터 손실 중복 계산 → Pi 과소 추정
//     수정: `eta_i = 0.97` — 인버터 단독 효율 표준값 사용 (IGBT 기준 전형적 범위)
//
//  ■ AF-FIX-05 ✅ [sankeyRows] eta_b 고정 전류 I=30A 부정확 — 🟡 MEDIUM
//     원인: `eta_b = 1 - R0 * 30 / V_dc`
//           100km/h 정속 시 실제 전류는 Pb(kWh/100km)로 역산 가능:
//           P_kW = Pb * v_kmh/100, I_A = P_kW*1000 / V_pack → I는 40~60A 수준
//           I=30A 가정은 저부하 상황 → Ri 손실 과소 계산
//     수정: Pb → P_kW → I_est 역산 후 `eta_b = 1 - I_est * R0 / V_pack`
//
//  ■ AF-FIX-06 ✅ [effMapData] P_motor/P_inv 손실 계산 물리 오류 — 🟡 MEDIUM
//     원인: `P_motor = Pw * (1 - eta_motor_inv)` — 바퀴 출력 기준 손실 계산 (오류)
//           모터 손실은 모터 입력 기준: P_motor_loss = P_motor_in * (1 - eta_m_only)
//           `P_inv = Pe * (1 - eta_driveline) * 0.5` — 전체 구동계 손실의 절반을
//           인버터에 임의 배분 (물리 근거 없음)
//     수정: 체인별 독립 손실 계산
//           eta_inv_std=0.97, eta_mot_std=eta_motor_inv/eta_inv_std
//           P_inv_loss = Pe*(1-eta_inv_std), P_motor_loss = P_motor_in*(1-eta_mot_std)
//
//  ■ AF-FIX-07 ✅ [Sobol Index] 하드코딩 고정값 → 실측 데이터 우선 사용 — 🟢 LOW
//     원인: Si 값이 {CdA:0.41, m:0.22, Crr:0.15, η:0.09, T:0.07, μ:0.04}로 하드코딩
//           r?.sens?.sobol 실측 데이터가 있어도 무시됨
//     수정: r?.sens?.sobol 배열 존재 시 우선 사용, 없으면 기존 fallback 유지
//
// ────────────────────────────────────────────────────────────
// [Verification_17] DOE 베이즈 최적화 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_16
//
//  ■ BO-FIX-01 ✅ [GP y 정규화 누락] EI 항상 0 → BO 랜덤 탐색 퇴화 — 🔴 HIGH (CRITICAL)
//     원인: y_raw 값이 수백~수천 (range_km≈300-500, energy_wh_km≈150-200)인데
//           GP prior sf=1.0 고정 → EI 계산 시 z = (y_best-mu-xi)/sigma에서
//           z가 수백~수천에 달함 → normPDF(z) ≈ 0 → EI ≈ 0 (모든 후보 동일)
//           → EI 최대화 의미 없음 → BO가 사실상 랜덤 탐색으로 퇴화
//     수정: 초기 y_raw 기반 y_mean/y_std 계산 후 GP 학습·EI 계산을 정규화 공간에서 수행
//           y_tr_scaled = (y_minimize - y_mean) / y_std → 정규화 y로 GP 학습
//           best_y 반환 시 원본 스케일 역변환 → 화면 표시값 정확도 유지
//
//  ■ BO-FIX-02 ✅ [LHS 계층화 오류] 차원별 동일 stratum 중복 샘플 가능 — 🟡 MEDIUM
//     원인: 각 (점 i, 차원 d) 조합마다 독립 permutation 생성 후 slots[i] 사용
//           → 동일 차원 내 같은 stratum(구간)이 여러 점에 배정될 수 있음
//           → Latin Hypercube 계층화 속성 위반, 초기 탐색 불균일
//     수정: 차원 d마다 사전 permutation 생성(perms[d]) → perms[d][i]로 점 i에 할당
//           → 각 차원에서 모든 N개 stratum을 정확히 1회 샘플링 보장
//
//  ■ BO-FIX-03 ✅ [nCandidates EI 탐색 공간 부족] — 🟡 MEDIUM
//     원인: `Math.max(100, 300/dim)` → 차원 증가 시 후보 수 오히려 감소
//           dim=4: max(100, 75)=100, dim=8: max(100,37)=100 — 고차원 탐색 불충분
//           EI 전역 최대값 탐색 실패 → 최적 후보 점 누락
//     수정: `Math.max(200, 100*dim)` → 차원 비례 확대
//           dim=4: 400, dim=8: 800개 → 탐색 신뢰도 향상
//
//  ■ BO-FIX-04 ✅ [peak_temp_motor 초기값 params.T_amb 오류] — 🟢 LOW
//     원인: d.reduce((mx,r) => Math.max(mx, r.T_motor||0), params.T_amb)
//           params는 원본 고정 객체 — DOE/BO에서 수정된 p의 T_amb가 아님
//           → CdA/Mass/Crr/η 변화 시 T_amb가 다른 경우 초기값 불일치
//     수정: params.T_amb → p.T_amb (p는 evaluateMetric 호출 시의 수정 파라미터)
//
// ────────────────────────────────────────────────────────────
// [Verification_16] 합불 검증 리포트 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_15
//
//  ■ PF-FIX-01 ✅ [T_motor/T_batt 소스 오류] launch100.thFinal → wltp.thermalStats — 🔴 HIGH
//     원인: T_motor_max = r.launch100?.thFinal?.T_motor
//           thFinal은 30초 launch 시뮬레이션 종료 시점의 열 상태값 (최고 온도 아님)
//           launch 30초(7~9 s 가속 + 이후 주행)의 열 부하는 WLTP 1800초보다 훨씬 낮음
//           → 온도 한계 판정이 항상 PASS되어 합불 기능 무효화
//     수정: 우선순위 1: r.wltp?.thermalStats?.T_motor_max / T_batt_max
//                      (WLTP 전 구간 실시간 maxT 추적 → 실제 최고 온도)
//           우선순위 2: launch100.data 전체 스캔 max (T_motor_temp / T_batt 필드)
//           우선순위 3: launch100.thFinal (기존값, 최후 fallback)
//
//  ■ PF-FIX-02 ✅ [check() val 포맷 조건 항상 false] — 🔴 HIGH
//     원인: val: +val.toFixed ? +val.toFixed(2) : val
//           val이 number일 때 val.toFixed는 Function 객체
//           +(Function) = NaN → NaN은 falsy → 조건이 항상 false
//           → val이 절대 반올림되지 않음 (예: 7.234567 → 그대로 표시)
//     수정: typeof val === 'number' ? +val.toFixed(2) : val 로 조건 수정
//
//  ■ PF-FIX-03 ✅ [reaccel_t 한계값 하드코딩] pf_ 파라미터 미지원 — 🟡 MEDIUM
//     원인: check(reaccel_t, 0.5, 5.0, ...) — 하한/상한 하드코딩
//           다른 항목(launch_t, wltp_kwh 등)은 pf_ 파라미터로 사용자 조절 가능
//           coast_t 상한도 120 하드코딩 → regen_pct 상한도 60 하드코딩
//     수정: pf.pf_reaccel_min||0.5, pf.pf_reaccel_max||5.0
//           pf.pf_coast_max||120, pf.pf_regen_max_pct||60
//           pf.pf_sens_cda_max||0.5 로 변경 → 일관된 pf_ 파라미터 체계
//
//  ■ PF-FIX-04 ✅ [reason 텍스트 숫자-단위 공백 누락] — 🟢 LOW
//     원인: `하한 ${min}${unit} 미만` → "하한 20s 미만" (space 없음, 숫자-단위 붙음)
//     수정: `하한 ${min} ${unit} 미만` — 공백 추가로 가독성 개선
//
// ────────────────────────────────────────────────────────────
// [Verification_15] 민감도 탭 전체 그래프 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_14
//
//  ■ SV-FIX-01 ✅ [S3 XAxis] type="number" 누락 → μ/σ/P05/P95 ReferenceLine 위치 오정렬 — 🔴 HIGH
//     원인: <XAxis dataKey="x" {...XA}> — type 미지정 → category 축 처리
//           ReferenceLine(x={mcData.mu} 등)이 실제 수치 위치 아닌 카테고리 인덱스로 렌더링
//     수정: type="number" 추가, Bar에 barSize={10} 명시 (numeric XAxis 자동 너비 없음)
//
//  ■ SV-FIX-02 ✅ [S3 Bar] hideY2 게이트 오적용 — 🟡 MEDIUM
//     원인: {!axS3.hideY2 && !axS3.hideY && <Bar yAxisId="freq"...>}
//           freq Bar는 좌축(yAxisId="freq") 소속인데 우축 hide 토글에 묶여 있음
//           → 누적분포선(우축) 숨기면 히스토그램까지 사라짐
//     수정: {!axS3.hideY && <Bar...>} — hideY2 조건 제거
//
//  ■ SV-FIX-03 ✅ [S3 ReferenceArea] "최악 5%" 방향 오류 — 🟡 MEDIUM
//     원인: <ReferenceArea x2={mcData.worst5_lo}> — 항상 좌측꼬리(=fastest) 강조
//           launch 모드(낮은값=좋음): 최악 5% = 우측꼬리(high time=느림)이어야 하는데 반대 표시
//     수정: s3Var==="launch" → {x1:mcData.worst5_hi} (우측꼬리)
//           s3Var==="coast"  → {x2:mcData.worst5_lo} (좌측꼬리, 기존 유지)
//           label position도 모드별 조건 분기
//
//  ■ SV-FIX-04 ✅ [S3 KPI 배지] P05/P95 Best/Worst 라벨 모드별 의미 미반전 — 🟢 LOW
//     원인: "P05 (Best 5%)"/"P95 (Worst 5%)" 고정 — launch 모드에선 맞지만
//           coast 모드(높은값=좋음)에서는 P05=Worst, P95=Best로 반전되어야 함
//     수정: s3Var 조건으로 Best/Worst 라벨 및 색상 조건 분기
//
// ────────────────────────────────────────────────────────────
// [Verification_14] 고급 물리 탭 전체 그래프 연산 검토 및 수정 + B4 English 완성 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_13
//
//  ■ AP-FIX-01 ✅ [XA_NUM] type:"number" 누락 — 🔴 HIGH
//     원인: AdvancedFinalTabContent 내부 XA_NUM = { stroke, tick, tickCount }
//           type:"number" 없음 → 모든 수치 X축이 category 처리
//           VD-R1/R2(v km/h), EN-E2(t min), EN-E3(v km/h), TH-T1/T2(t s),
//           TH-T3(T_amb), BT-B1(c cycles), BT-B2(T °C), SV-S1 MC(x) 전체 영향
//     수정: XA_NUM에 type:"number" 추가 → 수치 축 정상 처리
//
//  ■ AP-FIX-02 ✅ [effMapData] eta 계산값 미사용 — 🔴 HIGH
//     원인: const eta = eta_ch * (1 - R0·I²/Pe) 계산 후
//           return { eta: Math.min(0.98, Math.max(0.5, eta_ch)) } — eta_ch 상수 반환
//           배터리 손실 보정이 적용된 eta 변수가 완전히 무시됨
//           → 효율 곡선이 속도와 무관한 상수값 eta_ch로 평탄하게 표시
//     수정: return { eta: +Math.min(0.98, Math.max(0.5, eta)) } — 계산된 eta 사용
//
//  ■ AP-FIX-03 ✅ [BT-B1 soh Area] hideY2 조건 오적용 — 🟡 MEDIUM
//     원인: {!hideY2_cmp_B1 && <Area yAxisId="s" dataKey="soh" .../>}
//           soh Area = 좌축(yAxisId="s") → 우측 토글(hideY2)에 종속 오류
//           우축 숨기면 SOH 열화 곡선도 사라짐
//     수정: hideY2_cmp_B1 조건 제거 → soh Area 항상 표시
//
//  ■ AP-FIX-04 ✅ [TH-T3 e_wltp Area] hideY2 조건 오적용 — 🟡 MEDIUM
//     원인: {!hideY2_cmp_T3 && <Area yAxisId="e" dataKey="e_wltp" .../>}
//           e_wltp Area = 좌축(yAxisId="e") → 우측 토글(hideY2)에 종속 오류
//           우축(항속거리) 숨기면 전비 곡선도 사라짐
//     수정: hideY2_cmp_T3 조건 제거 → e_wltp Area 항상 표시
//
//  ■ AP-FIX-05 ✅ [VD-C1 Regen v_kmh Line] hideY2 조건 오적용 — 🟡 MEDIUM
//     원인: {!hideY2_cmp_C1 && <Line yAxisId="v" dataKey="v_kmh" .../>}
//           v_kmh Line = 좌축(yAxisId="v") → 우측 SOC 토글에 종속 오류
//           SOC 우축 숨기면 속도 곡선도 사라짐
//     수정: hideY2_cmp_C1 조건 제거 → v_kmh Line 항상 표시
//
//  ■ AP-FIX-06 ✅ [AFSectionHeader] purpose 한국어 하드코딩 — 🟢 LOW
//     원인: <div>📌 목적: {sec.purpose}</div> — AF_CATS purpose 필드 한국어만 존재
//           → English 모드에서도 목적 설명이 항상 한국어로 표시
//     수정: AF_CATS 각 섹션에 purposeEn 필드 추가
//           AFSectionHeader에 useLang() 추가 → lang 분기 표시
//
//  ■ BH-FIX-09 ✅ [B4 Know-How] 2번째 항목 미완성 (1개뿐) — 🟡 MEDIUM
//     원인: b4KHs = [{ id:1, ... }] — 1개 항목만 존재
//           B1/B2/B3 모두 2개 항목인데 B4만 1개
//           English 선택 시 B4 Know-How 패널에 충분한 영어 분석 내용 부족
//     수정: id:2 "Energy Density & Pack Design Optimization" 항목 추가
//           모든 필드(tag/title/keyPoints/actions/formula/improvement) 완전 영한 이중언어
//
// ────────────────────────────────────────────────────────────
// [Verification_13] 배터리 건강 탭 전체 그래프 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_12
//
//  ■ BH-FIX-01 ✅ [B1 XAxis] type="number" 누락 — 🔴 HIGH
//     원인: cycle(0,20,...,1200) 수치 데이터인데 type="number" 없음 → category 처리
//           → AxesPanel xMin/xMax 조작 완전 무효, 사이클 간격 왜곡
//     수정: type="number" + domain={axB1.domX or [0,"auto"]} 추가
//
//  ■ BH-FIX-02 ✅ [B1 EOL ReferenceLine] 잘못된 guard 조건 — 🟡 MEDIUM
//     원인: `!axB1.hideY2 && !axB1.hideY` — EOL선은 yAxisId="soh"(좌축)에 위치하는데
//           우축(Loss%) 숨김 시 함께 사라짐 → 손실% 토글 OFF하면 EOL 기준선 소멸
//     수정: `!axB1.hideY2 && !axB1.hideY` → `!axB1.hideY` 단독 조건으로 변경
//
//  ■ BH-FIX-03 ✅ [B2 XAxis] type="number" 누락 — 🔴 HIGH
//     원인: x 값(온도 -20~70 또는 SOC 0~100) 수치인데 type="number" 없음 → category 처리
//           → AxesPanel 범위 조작 무효, ReferenceLine x={45} 오동작
//     수정: type="number" 추가
//
//  ■ BH-FIX-04 ✅ [B2 YAxis] domain AxesPanel 미연동 — 🟡 MEDIUM
//     원인: `<YAxis {...YAP}>` 에 domain 없음 → AxesPanel axB2.axY min/max 조작 무효
//           Ri(Ω) 범위(0~0.5) 확인 불가
//     수정: domain={axB2.axY.auto ? ["auto","auto"] : [axB2.axY.min, axB2.axY.max]} 추가
//
//  ■ BH-FIX-05 ✅ [B3 XAxis] type="number" 누락 — 🔴 HIGH
//     원인: T 값(동적 온도 범위) 수치인데 type="number" 없음 → category 처리
//           → ReferenceLine x={params.T_amb??25} 현재기온 기준선 x좌표 오동작
//           → AxesPanel 온도 축 범위 조작 무효
//     수정: type="number" 추가
//
//  ■ BH-FIX-06 ✅ [B3 YAxis] domain AxesPanel 미연동 — 🟡 MEDIUM
//     원인: `<YAxis {...YAP}>` 에 domain 없음 → AxesPanel axB3.axY min/max 조작 무효
//           사이클 수명(0~5000) 범위 고정 확인 불가
//     수정: domain={axB3.axY.auto ? ["auto","auto"] : [axB3.axY.min, axB3.axY.max]} 추가
//
//  ■ BH-FIX-07 ✅ [B3 mc_lo Area] 하드코딩 마스킹 색상 오류 — 🟡 MEDIUM
//     원인: mc_lo Area fill="#1e293b" fillOpacity={1.0} — 차트 배경을 덮어
//           CI 하한 이하를 지우는 마스킹 트릭. 그러나 "#1e293b"는 COLORS.bg("#0d1117")와 다르고
//           라이트 테마("#f1f5f9")와도 불일치 → CI 밴드가 아닌 불투명 짙은 사각형으로 표시
//     수정: fill="#1e293b" → fill={COLORS.bg}, 마스킹 의도 주석 명시
//
//  ■ BH-FIX-08 ✅ [B4 Radar] soh_now 하드코딩 0.95 — 🟡 MEDIUM
//     원인: b4Radar useMemo 내부 `const soh_now = 0.95` 고정 → soh_enable ON·시뮬 측정값
//           존재해도 Radar "용량(SOH)" / "Ri" / "최대출력" 축이 항상 신품(95%) 기준으로 표시
//     수정: b2Data.soh_now 재활용 → 실측/추정 SOH 기반 Radar 동적 갱신
//           단, b4Radar useMemo deps에 b2Data 추가 (직접 참조는 순환 회피를 위해 soh_now값만 추출)
//
// ────────────────────────────────────────────────────────────
// [Verification_12] 열관리 탭 전체 그래프 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_11
//
//  ■ TH-FIX-01 ✅ [T1/T2/T3 XAxis] type="number" 누락 — 🔴 HIGH
//     원인: 3개 시계열 XAxis(<XAxis dataKey="t" ...>) 모두 type="number" 없음
//           → Recharts category 축 처리, AxesPanel 시간축 범위 조작 완전 무효
//     수정: T1/T2/T3 XAxis에 type="number" + T1/T2 domain 속성 추가
//
//  ■ TH-FIX-02 ✅ [T1 v_ref Area] °C YAxis에 km/h 혼합 — 🔴 HIGH
//     원인: v_ref Area에 yAxisId 없음 → 온도 축 단일 축(°C)에 v_ref(0~130 km/h) 공존
//           Recharts 자동 도메인이 0~130으로 확장 → T_batt(25~80°C) 곡선이 하단에 납작하게 압축
//     수정: 숨김 우축 <YAxis yAxisId="v" hide/> 추가 + v_ref Area에 yAxisId="v" 지정
//           → 온도 축 도메인 독립 유지, v_ref는 별도 축에서 [0,160] 렌더링
//
//  ■ TH-FIX-03 ✅ [T3 XAxis] type="number" + domain 누락 — 🔴 HIGH
//     원인: T3 XAxis에 type="number"와 domain 모두 없음
//           → time 축 category 처리, AxesPanel xMin/xMax 조작 무효
//     수정: type="number" + domain={axT3.axX.auto ? [0, _cycleMaxT] : [...]} 추가
//           ※ _cycleMaxT 미참조 → wdata 기반 안전 계산식 인라인 사용
//
//  ■ TH-FIX-04 ✅ [T3 v_ref Area] 출력비율(%) YAxis에 km/h 혼합 — 🔴 HIGH
//     원인: v_ref Area yAxisId 없음 → 디레이팅 비율(%) 축에 v_ref(0~130 km/h) 공존
//           디레이팅 축 범위가 0~130으로 확장 → [60,105]% 기준의 디레이팅 곡선 완전 왜곡
//     수정: 숨김 우축 <YAxis yAxisId="v" hide/> 추가 + v_ref Area에 yAxisId="v" 지정
//
//  ■ TH-FIX-05 ✅ [T4 XAxis] type="number" 누락 — 🔴 HIGH
//     원인: T_amb 값 [-10,0,10,25,40] 수치 데이터인데 type="number" 없음
//           → category 처리: ReferenceArea x1={-10}, ReferenceLine x={25} 등 x좌표 오동작
//     수정: type="number" 추가
//
//  ■ TH-FIX-06 ✅ [T4 T_motor_max/T_batt_max Bar] yAxisId="e" 단위 불일치 — 🔴 HIGH
//     원인: Bar yAxisId="e" (전비 좌축, 10~30 kWh/100km)에 온도(25~160°C) Bar 혼재
//           온도 Bar가 Y축 스케일을 0~160으로 확장 → e_wltp Line이 하단에 납작하게 압축
//     수정: 숨김 우축 <YAxis yAxisId="t" hide/> 추가 + Bar yAxisId="e" → "t" 로 변경
//           온도 Bar는 [0,180] 독립 축에서 렌더링
//
//  ■ TH-FIX-07 ✅ [T4 E_aux_kWh Line] yAxisId="r" (항속거리 축) 오할당 — 🟡 MEDIUM
//     원인: E_aux_kWh(kWh, ~1~5) Line이 yAxisId="r" (항속거리 축, ~100~600 km)에 배치
//           → E_aux 값이 0~600 스케일 하단에 납작하게 붙어 사실상 안 보임
//     수정: yAxisId="r" → yAxisId="e" (전비 좌축) 로 변경
//           kWh vs kWh/100km 단위 차이가 있으나 auto domain으로 함께 표시 가능
//
//  ■ TH-FIX-08 ✅ [T5 AxesPanel yUnit] "kW" 오기재 — 🟢 LOW
//     원인: <AxesPanel ... yUnit="kW" /> — T5는 °C 온도 차트
//     수정: yUnit="kW" → yUnit="°C"
//
//  ■ TH-FIX-09 ✅ [T5 BarChart YAxis] domain AxesPanel 미연동 — 🟡 MEDIUM
//     원인: <YAxis {...YA}> 에 domain 속성 없음 → AxesPanel의 axT5.axY min/max 조작 무효
//     수정: domain={axT5.axY.auto ? ["auto","auto"] : [axT5.axY.min, axT5.axY.max]} 추가
//
//  ■ TH-FIX-10 ✅ [T3/T5 라벨] "인버터 (W)" 오기재 — 🟢 LOW
//     원인: T3 토글 버튼 "인버터 (W)", T5 개선 우선순위/테이블 "인버터 (W)" — (W) 불필요
//     수정: "인버터 (W)" → "인버터" (3곳)
//
// ────────────────────────────────────────────────────────────
// [Verification_11] 주행 사이클 탭 전체 그래프 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_10
//
//  ■ DC-FIX-01 ✅ [DC1/DC2/DC4 XAxis] type="number" 누락 — 🔴 HIGH
//     원인: 3개 시계열 XAxis(<XAxis dataKey="t" ...>) 모두 type="number" 없음
//           Recharts는 type 미지정 시 category 축으로 처리
//           → domain 범위 클리핑 완전 무효, AxesPanel xUnit="s" 조작 반영 안됨
//     수정: DC1/DC2/DC4 XAxis에 type="number" 추가
//
//  ■ DC-FIX-02 ✅ [DC1 v_sim Line] 잘못된 hideY2 조건 — 🔴 HIGH
//     원인: {!ax1.hideY2 && !ax1.hideY && <Line yAxisId="v" dataKey="v_sim".../>}
//           v_sim은 yAxisId="v" (좌축, hideY 제어) 소속
//           hideY2(우측 Error 축) 토글과 무관해야 하는데 종속
//           → 우측 Error 축 숨기면 v_sim 실제속도선도 함께 사라짐
//     수정: {!ax1.hideY && <Line yAxisId="v" dataKey="v_sim".../>} — hideY2 조건 제거
//
//  ■ DC-FIX-03 ✅ [DC1 Error Line] 잘못된 축 객체 참조 ax2 → ax1 — 🔴 HIGH
//     원인: {c1ShowErr && !ax2.hideY && <Line yAxisId="e" .../>}
//           DC1 Error Line은 ax1로 제어되어야 하는데 ax2.hideY 참조
//           → DC2 SOC 축 숨기면 DC1 Error 선이 사라지는 크로스-차트 오동작
//     수정: !ax2.hideY → !ax1.hideY2 (Error 축은 ax1의 Y2=hideY2 제어)
//
//  ■ DC-FIX-04 ✅ [DC5/DC6 Bar Cell] 소문자 "cell" → Cell 컴포넌트 — 🔴 HIGH
//     원인: React.createElement("cell", {key:i, fill:d.color})
//           소문자 "cell"은 DOM 엘리먼트로 처리 — Recharts Cell 아님
//           → Bar 내 개별 막대 색상 완전 미적용 (모든 막대가 기본 fill색)
//     수정: DC5/DC6 두 곳 모두 "cell" → Cell 컴포넌트 참조로 교체
//
//  ■ DC-FIX-05 ✅ [DC2 kWh_100km_cum Line] hideY2 → hideY — 🟡 MEDIUM
//     원인: {!ax2.hideY2 && React.createElement(Line,{yAxisId:"e",dataKey:"kWh_100km_cum"...})}
//           kWh_100km_cum Line은 yAxisId="e" (좌축, hideY 제어) 소속
//           hideY2(우측 SOC 축) 조건으로 잘못 게이트
//           → SOC 우축 숨기면 좌측 전비 선도 사라짐
//     수정: !ax2.hideY2 → !ax2.hideY
//
//  ■ DC-FIX-06 ✅ [DC3 YAxis "r"] domain 하드코딩 [0,60] — 🟡 MEDIUM
//     원인: <YAxis yAxisId="r" domain={[0,60]} .../>
//           AxesPanel axY2(회생율 우축)의 min/max 조작이 전혀 반영 안됨
//           회생율 범위 조정 시 축은 항상 [0,60] 고정
//     수정: domain={ax3.axY2.auto ? ["auto","auto"] : [ax3.axY2.min, ax3.axY2.max]}
//
//  ■ DC-FIX-07 ✅ [DC8-B SOC Area] hideY2 → hideY — 🟡 MEDIUM
//     원인: {!ax8.hideY2 && <Area yAxisId="soc" dataKey="soc_pct".../>}
//           soc_pct Area는 yAxisId="soc" (좌축, hideY 제어) 소속
//           → 우축(P_kW) 숨기면 SOC Area도 함께 사라짐
//     수정: !ax8.hideY2 → !ax8.hideY
//
//  ■ DC-FIX-08 ✅ [DC5 Range YAxis] domain ["auto","auto"] → [0,"auto"] — 🟡 MEDIUM
//     원인: <YAxis yAxisId="r" domain={["auto","auto"]}.../>
//           range_km는 항상 양수인데 auto 하한이 음수로 설정 가능
//           → 항속거리 0 이하 공백 구간 생성, 그래프 하단 빈 여백
//     수정: domain={ax5.axY2.auto ? [0,"auto"] : [ax5.axY2.min, ax5.axY2.max]}
//           AxesPanel 연동 + 0 이하 방지
//
//  ■ DC-FIX-09 ✅ [DC8-A APS Line] hideY2 조건 누락 — 🟢 LOW
//     원인: <Line yAxisId="p" dataKey="aps" .../> 에 !ax7.hideY2 조건 없음
//           aps는 yAxisId="p" (우축, hideY2 제어) 소속
//           → 우축 숨겨도 APS Line만 계속 표시 (bps는 올바르게 조건 있음)
//     수정: {!ax7.hideY2 && <Line yAxisId="p" dataKey="aps".../>} 조건 추가
//
//  ■ DC-FIX-10 ✅ [DC6 XAxis] type="number" 누락 — 🟢 LOW
//     원인: <XAxis dataKey="x" {...XA} ...> type="number" 없음
//           mcData.bins.x는 연속 수치(kWh/100km) — category 처리 시
//           ReferenceArea(95% CI), ReferenceLine(μ, 목표 15) x 좌표 오동작
//     수정: type="number" 추가
//
//  ■ DC-FIX-11 ✅ [DC2 c2ShowAux] dead state 토글 버튼 — 🟢 LOW
//     원인: c2ShowAux toggle 버튼 있으나 차트 렌더링 어디에도 c2ShowAux 미사용
//           wdata에 E_aux_cum 필드 없음 → 클릭해도 시각 변화 없음
//     수정: 버튼 제거, E_aux는 KPI 배지·노하우 패널 텍스트로만 표시
//
// ────────────────────────────────────────────────────────────
// [Verification_10] 에너지 체인 탭 전체 그래프 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_09
//
//  ■ EN-E1-01 ✅ [E1 XAxis] type="number" 누락 — 🔴 HIGH
//     원인: <XAxis dataKey={...} domain={...}> 에 type="number" 없음
//           Recharts는 type 미지정 시 category 축으로 처리 → domain 범위 클리핑 무효
//           AxesPanel xUnit="s" 로 연결되어 있어도 실제 범위 제한 적용 안됨
//     수정: type="number" 추가 → domain 범위 클리핑 정상 작동
//
//  ■ EN-E1-03 ✅ [e1Data _seLUT] pt 분모 불완전 → 손실 비율 과대 — 🔴 HIGH
//     원인: pt = d.P_batt_chem || (fallback_sum) 에서 d.P_batt_chem 미존재
//           (generateSteadyStateEfficiency는 P_batt_chem 미출력)
//           fallback_sum = P_loss_batt+P_loss_inv+P_cu+P_fe+P_gear+P_aero+P_roll
//           → P_aux + P_mech_loss 누락 → pt 과소평가 → k_* 비율 과대평가
//           → e1Data 각 손실 누적값 실제보다 높게 표시
//     수정: pt = kWh_100km * v_kmh / 100 (kW 단위) 공식으로 P_batt_chem 역산
//           kWh_100km는 generateSteadyStateEfficiency 출력에 항상 존재
//
//  ■ EN-E2-01 ✅ [E2 XAxis] type="number" + domain 미설정 — 🔴 HIGH
//     원인: <XAxis dataKey="t" {...XA}> 에 type="number" 및 domain 없음
//           AxesPanel axX (xUnit="s") 조작이 X축 범위에 전혀 반영 안됨
//     수정: type="number" domain={axE2.axX.auto?["auto","auto"]:[axE2.axX.min,axE2.axX.max]} 추가
//
//  ■ EN-E4-01 ✅ [E4 XAxis] type="number" + domain 미설정 — 🔴 HIGH
//     원인: <XAxis dataKey="v_kmh" {...XA}> 에 type="number" 및 domain 없음
//           AxesPanel axX (xUnit="km/h") 조작이 X축 범위에 전혀 반영 안됨
//     수정: type="number" domain={axE4.axX.auto?["auto","auto"]:[axE4.axX.min,axE4.axX.max]} 추가
//
//  ■ EN-E2-02 ✅ [E2 SOC Line 렌더 조건] 우축 hideY2에 잘못 종속 — 🟡 MEDIUM
//     원인: {!axE2.hideY2 && !axE2.hideY && <Line yAxisId="soc" .../>}
//           SOC Line은 좌축(yAxisId="soc") 소속 → hideY2(에너지 우축) 토글과 무관
//           에너지 축(우) 숨기면 SOC 선도 사라지는 오동작
//     수정: {!axE2.hideY && <Line yAxisId="soc" .../>} — hideY2 조건 제거
//
//  ■ EN-E4-02 ✅ [E4 Hot 버튼 레이블] "140°C" ↔ 코드 T_scen=45 불일치 — 🟡 MEDIUM
//     원인: 버튼 레이블 "Hot 140°C / 고온 140°C" 이지만
//           e4TempScen="hot" 시 T_scen=45°C (T_amb 45°C로 재계산)
//           generateSteadyStateEfficiency는 thState=null → T_motor=140 미반영
//           KnowHow 패널도 "η Drop (140°C)" 표시 → 사용자 혼동 유발
//     수정: 버튼 레이블 "Hot 45°C / 고온 45°C", KnowHow hint/items도 45°C로 통일
//
//  ■ EN-E3-01 ✅ [E3 Area 범례 단위 오류] "(Ω)"·"(W)" → "(kW)" — 🟢 LOW
//     원인: P_batt Area 범례 = "Batt. Internal Resist. (Ω)" (저항 단위, 전력 아님)
//           P_inv/P_cu/P_fe Area 범례 = "(W)" (실제 값은 kW 단위)
//           computeEnergyChain 반환값은 모두 /1000 → kW 단위
//     수정: 범례 이름을 "(kW)" 단위로 통일, 배터리 항목도 "Loss (kW)"로 수정
//
//  ■ EN-E2-03 ✅ [E2 e2ShowAux 토글] dead state — 🟢 LOW
//     원인: e2ShowAux toggle 버튼 존재하지만 차트 렌더링 어디에도 e2ShowAux 미사용
//           wdata에 E_aux_cum 필드 없음 → 버튼 클릭 시 시각 변화 없음
//     수정: 토글 버튼 제거, 보조부하는 KPI 배지·노하우 패널 텍스트로만 표시
//
//  ■ EN-E4-03 ✅ [E4 e4ShowOp 토글] dead state — 🟢 LOW
//     원인: e4ShowOp toggle 버튼 존재하지만 차트 렌더링 어디에도 e4ShowOp 미사용
//           운전점 데이터 없음 → 버튼 클릭 시 시각 변화 없음
//     수정: 토글 버튼 제거 (운전점 표시는 노하우 패널에서 안내)
//
// ────────────────────────────────────────────────────────────
// [Verification_09] 차량 비교 탭 전체 그래프 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_08
//
//  ■ CMP-FIX-01 ✅ [Graph 1 SOC YAxis] domain 하드코딩 [60,100] — 🔴 HIGH
//     원인: <YAxis domain={[60, 100]}> 고정값 → AxesPanel axY 조작 완전 무효
//           사용자가 Y범위를 조정해도 SOC 차트에 반영 안됨
//     수정: domain={[axCMP_E.axY.min, axCMP_E.axY.max]} 동적 연동
//
//  ■ CMP-FIX-02 ✅ [Graph 1 XAxis] domain 미설정 — 🔴 HIGH
//     원인: <XAxis dataKey="t"> 에 domain 없음 → AxesPanel axX 조작 무효
//           시간축 범위 조정 버튼 클릭 시 SOC·에너지 차트에 반영 안됨
//     수정: type="number" domain={[axCMP_E.axX.min, axCMP_E.axX.max]} 추가
//
//  ■ CMP-FIX-03 ✅ [Graph 2 온도 서브차트] XAxis/YAxis domain 미설정 — 🔴 HIGH
//     원인: Battery&Coolant / Motor&Inverter 2개 서브차트 모두 domain 없음
//           → AxesPanel axX/axY 조작이 온도 차트에 전혀 반영 안됨
//     수정: 2개 서브차트 XAxis에 type="number" + domain={axCMP_T.axX.*} 추가
//           2개 서브차트 YAxis에 domain={[axCMP_T.axY.min, axCMP_T.axY.max]} 추가
//
//  ■ CMP-FIX-04 ✅ [Radar 점수] 면적 분모 원(π×R²) 사용 — 🟡 MEDIUM
//     원인: mxA = Math.PI * R * R (원의 면적) 사용
//           정규 8각형 최대 면적 = 8×R²×sin(2π/8)/2 = 2.828×R²
//           π×R² = 3.1416×R² → 전 축 100% 차량 점수 = 90% 과소평가
//     수정: mxA = n × R² × sin(2π/n) / 2 (정규 n각형 면적 공식)
//           → 전 축 100% 시 정확히 100% 표시
//
//  ■ CMP-FIX-05 ✅ [Graph 4 Heatmap KPI 배지] "CdA-8/Crr-12" 하드코딩 — 🟡 MEDIUM
//     원인: "Optimal Combo" 배지 value = "CdA-8/Crr-12" (문자열 고정)
//           "Max Penalty" 배지 value = "CdA+12/Crr+12" (문자열 고정)
//           → 배지 형식(숫자·단위)에 문자열 삽입, 실제 계산값과 무관
//     수정: Math.min/max 으로 hA/hB.data에서 실제 최적·최악 delta_e 계산 표시
//           A/B 각각 별도 배지로 4개 항목 표시
//
//  ■ CMP-FIX-06 ✅ [steadyAt_cmp] Wh_km 단위 오류 — 🟢 LOW
//     원인: wh = Pe / (v * 1000) * 1000 = Pe / v (J/m, Wh/km이 아님)
//           Pe[W] / v[m/s] = J/m → Wh/km으로 변환하려면 ÷ 3.6 필요
//           결과 3.6배 과대 (findVopt_cmp 최솟값 탐색엔 영향 없으나 코드 정확도 저하)
//     수정: wh = Pe / (v * 3.6) → 물리 단위 Wh/km 정확 표현
//
// ────────────────────────────────────────────────────────────
// [A68-07] 기능안전/사이버보안/SOTIF/FMEA 탭 KPI 전면 검토 및 수정 (2026-03-08):
//   베이스: Advance68_06
//
//  ■ A68-07-Fix-FS1 ✅ [기능안전] FTTI 판정 항상 "✅ OK" — 🔴 HIGH
//     원인: FDTI=0.3*FTTI, FHRTI=0.5*FTTI → 합계=0.8*FTTI → 항상 ≤FTTI → 전부 PASS
//           ASIL D(FTTI=10ms) 포함 모든 항목이 Pass → 실제 시스템 위반 검출 불가
//     수정: 카테고리 기반 FDTI(센서 5ms / 열관리 20ms / 통신 15ms 등) +
//           ASIL 기반 FHRTI(D:5ms / C:15ms / B:30ms / A:100ms) 사용
//           ASIL D 열관리·모터·BMS 항목 현실적 FAIL 검출
//
//  ■ A68-07-Fix-FS2 ✅ [기능안전] thermalRunawayFault 메트릭 항상 0 근방 — 🔴 HIGH
//     원인: metric_val = k_TR * duration * 60 / duration = k_TR * 60
//           duration 약분 → 항상 k_TR×60 ≈ 0.01~0.04 °C/s << 임계값 5°C/s
//           fail_rate≈0% → "전부 안전" 오보
//     수정: T_start ∈ [45,80°C], Arrhenius P_heat=0.05·exp((T-55)/5), C_th=0.1
//           임계 교차 T≈66.5°C → fail_rate≈40% (물리적으로 의미 있는 분포)
//
//  ■ A68-07-Fix-FS3 ✅ [기능안전] 미처리 고장 모드 3개 기본값 사용 — 🟡 MEDIUM
//     원인: steeringFault·epsOverFault·epbFault → default fallback (threshold×[0.3,1.8])
//           물리 의미 없는 분포 → 결과 신뢰 불가
//     수정: steeringFault: SAS 편차 기반 횡방향 이탈 모델
//           epsOverFault: EPS 토크 정규 분포 (임계 50Nm 근방)
//           epbFault: 경사도 기반 Rollaway 속도 모델
//
//  ■ A68-07-Fix-FS4 ✅ [기능안전/FMEA] Fault Injection 테이블 P95 레이블 한국어 혼입 — 🟡 MEDIUM
//     원인: English 헤더 배열에 "P95 (최악 5%)" → 영어 모드에서 한국어 표시
//     수정: "P95 (Upper 95%)" / "P05 (Lower 5%)" 영어 레이블 통일
//
//  ■ A68-07-Fix-CS1 ✅ [사이버보안] 리스크 매트릭스 CRITICAL 영역 도달 불가 — 🔴 HIGH
//     원인: risk_value = impact * likelihood / 10, CRITICAL>=7
//           데이터 최대 likelihood=6 → max rv=6.0 < 7 → CRITICAL 셀 없음
//           critical_count=0 항상 → "인증 준비 완료" 오보
//     수정: risk formula → /6 (6점 척도 정규화)
//           임계값: CRITICAL>=6, HIGH>=4, MEDIUM>=2, LOW 이하
//           threat_risk도 동일 공식 적용
//
//  ■ A68-07-Fix-ST1 ✅ [SOTIF] ks_pass 항상 0/6 → KPI 오해 — 🟡 MEDIUM
//     원인: ks_pass 목표 0.99 → 일반 주행조건에서 전 시나리오 미달 → 0/6 항상
//           "알려진 시나리오 통과 0/6" → 모두 실패처럼 보임
//     수정: ks_90pct_pass (0.90 완화 목표) 추가 계산·표시
//           KPI 배지 "KS≥0.99" + "KS≥0.90" 이중 목표 표시로 의미 명확화
//
//  ■ A68-07-Fix-FM1 ✅ [FMEA] computeFMEA detection 컴포넌트 매핑 일관성 — 🟡 MEDIUM
//     원인: detection은 하드코딩 인덱스(FM-01→[0], FM-02→[9] 등) 사용
//           FIT/lambda_h는 category 매치 사용 → 동일 고장 모드에 다른 컴포넌트 적용
//           예) FM-07(열관리): detection=C-05(BMS) coverage, FIT=C-12(냉각펌프) → 불일치
//     수정: 하드코딩 인덱스 매핑 제거 → category 기반 통일 조회로 일관성 확보
//
// ────────────────────────────────────────────────────────────
// [A68-06] 고급물리/민감도/합불검증/DOE베이즈/통합분석 탭 KPI 전면 검토 및 수정 (2026-03-08):
//   베이스: Advance68_05
//
//  ■ A68-06-Fix-DOE1 ✅ [DOE] evaluateMetric energy_wh_km 항상 0 반환 — 🔴 HIGH
//     원인: simWLTP 반환 객체에 energy_wh_km·E_batt_kWh·total_range_km 필드 없음
//           → wltp.energy_wh_km = undefined → fallback도 undefined → 항상 0
//     수정: wltp.e_wltp (kWh/100km) × 10 = Wh/km 변환으로 교체
//
//  ■ A68-06-Fix-AF1 ✅ [통합분석] tornadoRows 필드 키 불일치 — 🔴 HIGH
//     원인: centralDiff 실제 필드: param, dT_launch_hi/lo, dT_coast_hi/lo
//           참조 코드: d.name||d.factor (없음), d.d_launch_pct (없음), d.d_coast_pct (없음)
//           → name="undefined", dLaunch=0, dCoast=0 → 토네이도 차트 전부 0 표시
//     수정: d.param, max(|dT_launch_hi|,|dT_launch_lo|), max(|dT_coast_hi|,|dT_coast_lo|)
//
//  ■ A68-06-Fix-AF3 ✅ [통합분석] effMapData 손실 필드 전부 0 — 🔴 HIGH
//     원인: energyCons 배열에 P_aero_kW·P_roll_kW·P_motor_loss_kW·P_inv_loss_kW·
//           P_batt_loss_kW·eta_chain 필드 미존재 → 모든 값 0·fallback 0.87
//     수정: v + params 기반 물리식 직접 계산 (steady-state approximation)
//           Fa·Fr·Pw·Pe·Pbatt_loss·eta 각각 물리 식으로 산출
//
//  ■ A68-06-Fix-PF1 ✅ [합불검증] sens_cda 배열 인덱스 고정 오류 — 🟡 MEDIUM
//     원인: centralDiff?.[0]?.S_launch → [0]번이 CdA가 아닐 수 있음
//     수정: .find(d => d.key === 'CdA')?.S_launch
//
//  ■ A68-06-Fix-PF2 ✅ [합불검증] eta_total 부정확 필드 사용 — 🟡 MEDIUM
//     원인: eta_cycle = 30초 전체 누적 효율 (0→100 구간 과소/과대 추정 가능)
//     수정: eta_at_target (v_target 도달 시점 스냅샷) 우선, 없으면 eta_cycle fallback
//
//  ■ A68-06-Fix-AF2 ✅ [통합분석] sankeyRows 하드코딩 효율 — 🟡 MEDIUM
//     원인: eta_b=0.97, eta_i=0.975, eta_m=0.945, eta_g=0.975 전부 고정값
//     수정: r.wltp.mid_chain.eta_batt/eta_inv/eta_motor/eta_gear 실측값 우선 사용
//           mid_chain 없으면 params 기반 추정치로 fallback
//
//  ■ A68-06-Fix-AF4 ✅ [통합분석] heatData 완전 하드코딩 — 🟡 MEDIUM
//     원인: [[41,38,28,...]] 고정 행렬 → 파라미터 변경 시 반영 안됨
//     수정: centralDiff 실측 S_launch/S_coast/S_regen으로 기여율 계산
//           centralDiff 없으면 물리 상식 기반 fallback
//
//  ■ A68-06-Fix-AF5 ✅ [통합분석] EN-E2 Ecum Area Y2 토글에 종속 — 🟡 MEDIUM
//     원인: {!hideY2_cmp_E2 && <Area yAxisId="e" dataKey="Ecum".../>}
//           Ecum = 좌측Y(e축) → Y2 토글 버튼으로 함께 숨겨짐
//     수정: Ecum Area는 항상 표시 → {!hideY2_cmp_E2 && ...} guard 제거
//           SOC Line만 hideY2 조건 유지
//
//  ■ A68-06-Fix-S1  ✅ [민감도] P95 레이블 오기재 — 🟢 LOW
//     원인: "P95 (Worst 5%)" → P95는 상위 95% 경계, 하위 5% 최악이 아님
//     수정: "P95 (Upper 95%)" / "P95 (상위 95%)" 로 교정
//           P05도 "Lower 5%" / "하위 5%"로 통일
//
//  ■ A68-06-Fix-S2  ✅ [민감도] "Top 영향" 레이블 이중언어 미처리 — 🟢 LOW
//     원인: lang===en 분기 없이 항상 한국어 "Top 영향" 표시
//     수정: lang==="en"?"Top Impact":"Top영향" 이중언어 처리
//
// ────────────────────────────────────────────────────────────
// [Verification_08] 4WS·롤오버 탭 전체 그래프 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_07
//
//  ■ Fix-4WS-01 ✅ [RV1 AxesPanel] yUnit "°" → "mm" — 🔴 HIGH
//     원인: RV1은 4코너 차체 변위(mm) 차트인데 AxesPanel yUnit="°" (각도 단위 오기입)
//     수정: yUnit="mm"
//
//  ■ Fix-4WS-02 ✅ [RV2 AxesPanel] yUnit "°/s" → "N" — 🔴 HIGH
//     원인: RV2는 수직력 Fz(N) 차트인데 AxesPanel yUnit="°/s" (롤 속도 단위 오기입)
//     수정: yUnit="N"
//
//  ■ Fix-4WS-03 ✅ [RV2 액추에이터 서브차트] Y축 라벨 "수직 하중 (N)" → "액추에이터 힘 (N)" — 🟡 MEDIUM
//     원인: RV2 수직력 차트 YAxis label을 복붙하여 액추에이터 서브차트에도 "수직 하중"으로 표시
//     수정: "액추에이터 힘 (N)" / "Actuator Force (N)"으로 수정
//
//  ■ Fix-4WS-04 ✅ [RV4 AxesPanel] yUnit "kN" → "" — 🔴 HIGH
//     원인: RV4는 LTR(무차원 -1~+1)과 안전여유(%)를 표시하는데 yUnit="kN" 오기입
//     수정: yUnit="" (LTR은 무차원, 우측Y 안전여유는 % — 단일 단위로 표현 불가)
//
//  ■ Fix-4WS-05 ✅ [RV5] SAT Bar & T_feedback Line hideY2 → hideY 역전 — 🔴 HIGH
//     원인: SAT(yAxisId="torque"=좌측Y), T_feedback(yAxisId="torque"=좌측Y)가
//           모두 !axRV5.hideY2로 제어 → 좌측Y 숨겨도 SAT·피드백 토크 표시
//           우측Y(angle) 숨기면 SAT·피드백 토크 사라지는 반대 동작
//     수정: SAT, T_assist, T_feedback 모두 !axRV5.hideY (좌측Y 연동)
//
//  ■ Fix-4WS-06 ✅ [RV5] delta_steer_deg Line 조건 없이 항상 표시 — 🔴 HIGH
//     원인: <Line yAxisId="angle" dataKey="delta_steer_deg".../>에 조건 없음
//           → 우측Y(angle) 축 숨겨도 delta_steer_deg 선 계속 표시
//     수정: {!axRV5.hideY2 && <Line yAxisId="angle".../>} 조건부 렌더 추가
//
//  ■ Fix-4WS-07 ✅ [simFourWheelSuspension] 속도 20km/h + a_y 공식 오류 — 🔴 HIGH
//     원인1: let v = 20/3.6 → 레인체인지 평가 기준속도는 ISO 3888 기준 80km/h
//            20km/h: a_y_peak ≈ 0.07g → 롤오버 여유 ≈ 99.5% 항상 SAFE
//     원인2: a_y = v * delta_steer * 2.0 → 단순 비례 근사 (물리적 부정확)
//            올바른 공식: a_y = v² × δ / l_wb (정상 원운동 가속도 공식)
//            80km/h(22.2m/s), δ=0.06rad, l_wb=2.875: a_y = 10.3 m/s² = 1.05g
//            → LTR 변화, 전복 여유 의미 있게 변동 (레인체인지 분석 유효)
//     수정: v = (params.lane_change_speed_kmh || 80) / 3.6
//           a_y = v² * delta_steer / max(0.5, l_wb)
//
//  ■ Fix-4WS-08 ✅ [RV2] 수직력 Line name 한/영 분기 누락 — 🟡 MEDIUM
//     원인: name={`수직력 ${c} (N)`} → lang='en' 시에도 한국어 표시
//           액추에이터 Area name도 EN/KO 동일 문자열 오기입
//     수정: lang==='en' ? `Fz ${c} (N)` : `수직력 ${c} (N)` 분기 추가
//           액추에이터 "FL 액추에이터 (N)" → lang 분기로 "FL Actuator (N)"
//
// [Verification_07] 도로·환경 탭 전체 그래프 연산 검토 및 수정 (2026-03-08):
//   베이스: ev-sim_v1.0_Verification_06 (DV6 수정본)
//
//  ■ Fix-RE-01 ✅ [v7_meta] road_roughness_enable 누락 — 🔴 HIGH
//     원인: runCoreSim / runHeavySim / runFullSim 세 곳의 v7_meta 객체 모두에서
//           road_roughness_enable 필드가 없음
//           → 도로환경 탭 roughEnabled = v7meta.road_roughness_enable || false
//              → 항상 false → F_rough KPI 배지 항상 "비활성/OFF" 표시
//     수정: 3개 함수의 v7_meta에 road_roughness_enable: params.road_roughness_enable || false 추가
//
//  ■ Fix-RE-02 ✅ [RE1 차트] 속도 범위 0~100 km/h 한계 — 🔴 HIGH
//     원인: RE1 차트 데이터 소스 = launch100.data (v_target=100)
//           → 최대 v_kmh ≈ 100 → domain=[0,160] 기준 우측 60% 공백
//     수정: launch300.data 사용 → 전 속도 범위 저항력 vs 속도 곡선 표시
//
//  ■ Fix-RE-03 ✅ [RE1 차트] 비단조(non-monotonic) v_kmh 데이터 — 🟡 MEDIUM
//     원인: launch 시뮬 데이터는 시간 순서 → v_kmh 대역 목표 도달 후 PID 진동으로 비단조 가능
//           → type="number" XAxis에서 선이 좌우로 흔들리는 현상 발생
//     수정: v_kmh 기준 정렬 + 2 km/h 버킷 중복 제거 → 단조 F(v) 곡선 보장
//
//  ■ Fix-RE-04 ✅ [RE1 차트] F_rough 선 항상 표시 (비활성 시 0으로 표시) — 🟡 MEDIUM
//     원인: roughEnabled=false 시에도 F_rough Line 항상 렌더 → 차트에 0 기준선 노이즈
//           → 범례에 "IRI 거칠기 (N)" 항목도 항상 표시되어 혼란
//     수정: {roughEnabled && <Line dataKey="F_rough" .../>} 조건부 렌더로 변경
//           subtitle도 roughEnabled 상태 반영하여 동적 표시
//
//  ■ Fix-RE-05 ✅ [RE2 차트] 냉각 유량 단위 오기입 "L/m" → "L/min" — 🟡 MEDIUM
//     원인: YAxis right label = "Flow Rate (L/m)" / "유량 (L/m)"
//           YAxisToggleBtn label = "Flow (L/m)"
//           → "L/m"은 리터/미터로 오독 가능 → 올바른 단위는 리터/분 "L/min"
//     수정: 세 곳 모두 "L/min"으로 수정
//
//  ■ Fix-RE-06 ✅ [RE2 차트] XAxis 시간 축 type 미지정 — 🟢 LOW
//     원인: XAxis type 미지정(기본 "category") → 촘촘한 시계열 데이터에서 레이블 오배치 가능
//     수정: type="number" + domain={[0,'auto']} 추가 → 수치 스케일 정확도 개선
//
// [A68-04] 도로환경/4WS롤오버/차량비교 탭 KPI 전면 검토 및 수정 (2026-03-08):
//   베이스: Advance68_03
//
//  ■ A68-04-Fix-RE1 ✅ [RE2 차트] T_gear/coolant_flow hideY2 조건 역전 — 🔴 HIGH
//     원인: {!hideY2_RE2 && <Line yAxisId="temp" dataKey="T_gear".../>}
//           T_gear = 좌측Y(temp)에 바인딩 → hideY2(우측 유량 축) 토글에 연동 오류
//           coolant_flow = yAxisId="flow"(우측Y)이나 조건 없이 항상 표시
//     수정: T_gear → 좌측Y 조건 없이 항상 표시 (우측Y 숨겨도 기어온도 표시 유지)
//           coolant_flow → {!hideY2_RE2 && ...} 조건 추가 (유량 축 숨기면 유량선도 숨김)
//
//  ■ A68-04-Fix-RE2 ✅ [도로환경 KPI배지] 정량 저항력 KPI 배지 추가 — 🟡 MEDIUM
//     원인: KPI 배지 6개 = 전부 v7meta 설정값(IRI수치, 공기압, HVAC 모드 등 정적 값)
//           실제 주행 데이터(F_aero/F_roll/F_rough) 기반 정량 비교 KPI 전무
//     수정: lData 100 km/h 근방 샘플로 F_aero · F_roll · F_rough(IRI 활성 시) KPI 배지 추가
//           road_roughness_enable 활성화 상태 배지 추가 (비활성→F_rough=0 안내)
//
//  ■ A68-04-Fix-RV1 ✅ [RV3 롤각 차트] roll_deg hideY/hideY2 역전 — 🔴 HIGH
//     원인: {!axRV3.hideY2 && <Line yAxisId="deg" dataKey="roll_deg".../>}
//           roll_deg = 좌측Y(deg) → hideY2(우측G 축) 토글에 연동 오류
//           우측Y(횡G) 숨기면 롤각 선도도 사라짐, 좌측Y 숨겨도 롤각 남음
//     수정: roll_deg → !axRV3.hideY (좌측Y에 올바르게 연동)
//
//  ■ A68-04-Fix-RV2 ✅ [RV4 LTR/안전마진 차트] LTR·margin hideY/hideY2 역전 — 🔴 HIGH
//     원인: LTR  = yAxisId="ltr"(좌측Y) → !axRV4.hideY2로 제어 (역전)
//           margin = yAxisId="pct"(우측Y) → !axRV4.hideY로 제어 (역전)
//     수정: LTR → !axRV4.hideY, margin → !axRV4.hideY2 (좌/우 Y축 올바르게 연동)
//           하단 ReferenceLine(y=-0.8) 도 동일하게 !axRV4.hideY 유지
//
//  ■ A68-04-Fix-RV3 ✅ [simFourWheelSuspension + RV4 KPI배지] a_y_crit_g 미저장 — 🔴 HIGH
//     원인: rolloverSimulation()이 a_y_crit_g 반환하지만 simFourWheelSuspension data 배열 미저장
//           KPI배지 표시 코드: suspData[0]?.SSF?.toFixed(3) → SSF값이 a_y_crit로 잘못 표시
//     수정: data.push()에 a_y_crit_g: +rollover.a_y_crit_g.toFixed(3) 추가
//           KPI배지 a_y_crit 라벨: suspData[0]?.a_y_crit_g 로 올바른 값 표시
//           SSF 배지 색상 임계값(≥1.4: 초록, ≥1.2: 노랑, <1.2: 빨강) 조건부 색상 추가
//
//  ■ A68-04-Fix-CMP1 ✅ [CompareTabContent] Re-Accel 레이블 하드코딩 오류 — 🔴 HIGH
//     원인: CVC label="80→120 km/h" — reaccelV0=60/reaccelV1=100 기본값과 불일치
//           CompareTabContent에 reaccelV0/V1 prop이 없어 실제 v0/v1 값 알 수 없음
//     수정: CompareTabContent에 reaccelV0/V1 prop 추가 (기본값 60/100)
//           CVC label = `${reaccelV0}→${reaccelV1} km/h` 동적 표시로 수정
//           렌더링 호출부에 reaccelV0/V1 props 전달 추가
//
//  ■ A68-04-Fix-CMP2 ✅ [Compare Graph1 WLTP에너지] Y축 hide 조건 역전 — 🔴 HIGH
//     원인: ${nA}_SOC (yAxisId="soc", 좌측Y) → !axCMP_E.hideY2 조건 사용 (역전)
//           ${nB}_E_net (yAxisId="e", 우측Y) → 조건 없이 항상 표시
//           ${nB}_E_regen (yAxisId="e", 우측Y) → 조건 없이 항상 표시
//     수정: nA_SOC → !axCMP_E.hideY (좌측Y 토글과 올바르게 연동)
//           nB_E_net, nB_E_regen → !axCMP_E.hideY2 추가 (우측Y 숨기면 동반 숨김)
// ────────────────────────────────────────────────────────────
// [A68-03] 코스트/회생제동/승차감 탭 KPI 전면 검토 및 수정 (2026-03-07):
//   베이스: Advance68_02
//
//  ■ A68-03-Fix-C1 ✅ [CoastTabContent] E_aero_pct/E_roll_pct 분모 오류 — 🔴 HIGH
//     원인: 분모 = E_KE_init_Wh(초기 KE) → 코스트 종료 시 v>0.5m/s이므로 E_KE_final>0
//           소산되지 않은 잔여 KE까지 포함 → 저항 에너지 비율 과소 추정
//     수정: 분모 = E_aero+E_roll+E_dt+|E_grade| (실제 소산된 저항에너지 합계)
//           "각 저항이 코스팅 손실에서 차지하는 실제 비율"로 의미 명확화
//           E_dt_pct 배지 신규 추가
//
//  ■ A68-03-Fix-C2 ✅ [CoastTabContent] E_grade_Wh/E_aux_Wh KPI 누락 — 🟡 MEDIUM
//     원인: 구배에너지(E_grade_Wh), 보조기기에너지(E_aux_Wh)가 simCoast에서 산출되나 KPI 미표시
//     수정: 구배 ≠ 0이거나 구배에너지 존재 시 E_grade_Wh/E_grade_pct 배지 표시
//           E_aux_Wh 배지 항상 추가
//
//  ■ A68-03-Fix-C3 ✅ [CoastTabContent] 코스트 시간 레이블 오해 — 🟡 LOW
//     원인: "코스트 시간 (100→0)" — simCoast 루프 종료 조건 v>0.5m/s이므로 실제로는 정지까지가 아님
//     수정: "코스트 시간 (100→정지)" — 시뮬레이션 종료 조건과 일치
//
//  ■ A68-03-Fix-R1 ✅ [RegenBrakeTabContent rb1Data] SOC_K 물리 역전 — 🔴 HIGH
//     원인: SOC_K = { high:0.85, mid:0.90, low:0.78 }
//           low(SOC 20%) < mid(SOC 50%) → SOC 낮을수록 회생 제한이 강해지는 물리 역전
//           실제: SOC↑(만충) → 충전 여력 감소 → k 낮아야 하고
//                 SOC↓(방전) → 충전 여력 최대 → k 높아야 함
//     수정: { high(80%):0.70, mid(50%):0.90, low(20%):1.00 } — 물리 정확
//
//  ■ A68-03-Fix-R2 ✅ [RegenBrakeTabContent rb3Data] frac_base 페달맵 역전 — 🔴 HIGH
//     원인: a_req <= a_th → frac = a_req/a_th (비례 증가)
//           → 낮은 감속도에서 회생 비율이 낮음 (a=0.1m/s² → frac≈4%) → 현실과 반대
//           실제 EV: 상용제동(< a_th) = 순수 회생 우선 100%
//     수정: a_req <= a_th → frac = 1.0 (100% 회생 우선)
//           a_req > a_th → 마찰 보완 시작(frac 선형 감소)
//
//  ■ A68-03-Fix-R3 ✅ [RegenBrakeTabContent rb3Data] SOC 계수 불일치 — 🟡 MEDIUM
//     원인: rb3Data 하드코딩 s20=0.65/s50=0.88/s80=1.0 → Fix-R1의 SOC_K와 완전 역전
//           s80(SOC 80%)=1.0이 가장 여유 있음 → 만충 시 회생 제한 없는 오류
//     수정: Fix-R1 SOC_K와 동일 기준: s80=0.70 / s50=0.90 / s20=1.00
//
//  ■ A68-03-Fix-D1 ✅ [DrivabilityTabContent] compare 모드 KPI 배지 미표시 — 🔴 HIGH
//     원인: ja = result?.jerk_analysis → compare 모드에서 result=null → ja=undefined
//           KPI 배지 행({ja && ...}) 완전히 숨겨짐, 등급 표시 패널도 빈칸
//     수정: ja_compare = multiResult 최적τ 결과의 jerk_analysis
//           ja_active = ja || ja_compare → single/compare 모두 KPI 표시
//
//  ■ A68-03-Fix-D2 ✅ [DrivabilityTabContent] 브레이크 그래프 hideY 플래그 역전 — 🟡 MEDIUM
//     원인: F_hyd/F_regen Area에 !hideY2 조건 → 우측 Y2(저크) 숨기면 제동력도 사라짐
//     수정: 좌측Y(제동력) → !hideY, 우측Y2(저크) → !hideY2로 각각 정확히 연결
//
//  ■ A68-03-Fix-D3 ✅ [DrivabilityTabContent] axJERK_BRK Y축 초기 범위 오류 — 🟡 MEDIUM
//     원인: yMin:-10, yMax:0 → 제동력(N) 축에 m/s² 범위 사용
//           F_hyd/F_regen는 수백~수천 N → 완전히 잘못된 초기 스케일
//     수정: yMin:0, yMax:8000(N) / y2Min:-15, y2Max:15(m/s³) — 제동력·저크 적정 범위
//
//  ■ A68-03-Fix-D4 ✅ [computeDrivabilityJerk] jerk_grade 한국어 하드코딩 — 🟡 MEDIUM
//     원인: 'A (우수)' 등 한국어 문자열 하드코딩 → lang=en에서도 한국어 출력
//     수정: 함수 반환값 = 'A'/'B'/'C'/'D' (순수 등급 문자)
//           DrivabilityTabContent에서 GRADE_LABELS 객체로 lang 분기 표시
// ────────────────────────────────────────────────────────────
// [A68-02] 축 설정 완전 재설계 + 재가속 탭 KPI 수정 (2026-03-07):
//   베이스: Advance68_01
//
//  ■ A68-02-Fix-AX1 ✅ [AxesPanel] 값 전체 삭제 불가 — 🔴 HIGH
//     원인: type="number" + value={draft.min(숫자)} + parseFloat(v)||0
//           → 빈 문자열('') 입력 시 parseFloat('')=NaN → NaN||0=0 즉시 치환
//           → 커서가 끝으로 이동, 전체 삭제 후 재입력 불가
//     수정: draft state를 숫자 대신 string으로 관리 (xMin/xMax, yMin/yMax)
//           type="text" inputMode="numeric" → 빈 문자열 허용, 자유로운 편집
//           Apply 시에만 parseFloat 변환하여 숫자 도메인 반영
//
//  ■ A68-02-Fix-AX2 ✅ [AxesPanel] 적용 전 외부 클릭 시 차트 즉시 반영 — 🔴 HIGH
//     원인: 외부 클릭 핸들러 → handleClose() → setAxX(draftX) 호출
//           → Apply 버튼 없이도 mousedown 한 번으로 차트 변경됨
//     수정: 외부 클릭 = handleCancel() = setOpen(false)만 (draft 버림, 차트 미반영)
//           버튼 3개: 🔄 초기화 | ✕ 취소 | ✔ 적용 (적용만 setAxX/Y 호출)
//           상단 토글 버튼도 열렸을 때 "✔ 적용" 레이블로 Apply 역할 명확화
//
//  ■ A68-02-Fix-RA1 ✅ [simReaccel + ReAccelTabContent] distance/E KPI 오류 — 🔴 HIGH
//     원인: simReaccel도 while(t<30) → distance/E_batt_Wh = 30초 전체 누적
//           → "v0→v1 이동거리/에너지" KPI로 잘못 표시 (정속 구간 포함)
//     수정: v_target 최초 도달 시 s/E_batt/E_wheel 스냅샷 캡처
//           → dist_at_target / E_batt_at_target_Wh / eta_at_target 신규 필드
//           KPI 배지 레이블 "v0→v1 이동거리/배터리 소모/파워트레인 효율"로 명확화
//           파워트레인 효율 배지 신규 추가
//
//  ■ A68-02-Fix-RA3 ✅ [ReAccelTabContent] scale_t 물리 개선 — 🟡 MEDIUM
//     원인: scale_t = 1/√(clamp_k) — SOC/cold/gear/dm 복합 계수의 단순 제곱근
//           → 각 요인의 물리적 특성(선형 vs 제곱근) 무시
//     수정: SOC·gear·dm = 출력 제한(선형: 1/k), cold = 온도 효과(제곱근: 1/√cold_k)
//           발진탭 Fix-DESIGN-1과 동일 원칙 적용
//
//  ■ A68-02-Fix-RA4 ✅ [ReAccelTabContent] v_kmh_adj 물리 오류 — 🟡 MEDIUM
//     원인: v_kmh_adj = d.v_kmh * √(clamp_k) → 기준 시뮬 속도에 임의 스케일링
//           → 차트 속도 곡선이 물리적으로 의미 없는 값으로 변환됨
//     수정: v_kmh_adj = d.v_kmh (기준 시뮬 속도 그대로 표시)
//           시나리오 Δt 효과는 t_adj/dt_scen KPI 배지로 별도 표시
// ────────────────────────────────────────────────────────────
// ── v1.0 (2026-02 ~ 2026-03-06) ─────────────────────────
// [A68-01] 발진 탭 KPI 버그 3건 수정 + 축 설정 연속입력 버그 수정 (2026-03-07):
//   베이스: Advance68
//
//  ■ A68-01-Fix-L1 ✅ [AxesPanel] 축 연속입력 불가 — 🔴 HIGH
//     원인: AxisBlock이 AxesPanel 내부에 정의되어 <AxisBlock> JSX 컴포넌트로 호출됨
//           → 매 렌더마다 새 함수 참조 생성 → React가 다른 컴포넌트 타입으로 인식
//           → 매 키입력마다 언마운트/리마운트 → 포커스 유실 → 연속 입력 불가
//     수정: <AxisBlock .../> → {AxisBlock({...})} 직접 함수 호출로 변경
//           React 컴포넌트 트리에 등록되지 않으므로 리마운트 없음 → 포커스 유지
//
//  ■ A68-01-Fix-L2 ✅ [LaunchTabContent] 0→100 @50% 개도 KPI 오류 — 🔴 HIGH
//     수정 전: lData50.time = simLaunch(v_target=300)의 300 km/h 도달 시간
//             → 50% 개도로 300 km/h 도달 불가 → 항상 999.00 s 표시
//     수정 후: r.launch150.data에서 v_kmh >= 100 첫 샘플 시간 탐색
//             → 실제 0→100 km/h @50% 개도 시간 정확 표시
//
//  ■ A68-01-Fix-L3 ✅ [simLaunch + LaunchTabContent] distance/E/η KPI 오류 — 🔴 HIGH
//     수정 전: dist/E_batt_Wh/eta_cycle = 30초 전체 누적값
//             → 0→100 가속 구간 KPI로 잘못 표시 (30s 정속 포함)
//     수정 후: simLaunch에 s_at_tgt/E_batt_at_tgt/E_wheel_at_tgt 스냅샷 추가
//             v_target 최초 도달 시 즉시 캡처 → dist_at_target/E_batt_at_target_Wh/eta_at_target
//             KPI 배지 레이블도 "0→100 주행거리/배터리 에너지"로 명확화
//
//  ■ A68-01-Fix-DESIGN-1 ✅ [LaunchTabContent] scale_t 물리 개선 — 🟡 MEDIUM
//     수정 전: scale_t = 1/√(mu_ratio × cold_derate) → µ·cold 혼합 제곱근
//             타이어 제한 구간(t∝1/µ)에서 wet/ice 시간 페널티 과소 추정
//     수정 후: scale_t = (1/mu_ratio) × (1/√cold_derate)
//             µ 효과: 타이어 제한 → 선형(1/µ),  cold 효과: 전력 제한 → 제곱근(1/√cold)
//             Δt100/Δt60 시나리오 오버레이 추정 물리적 정확도 향상
//
//  ■ A68-01-Fix-DESIGN-2 📝 [LaunchTabContent] Cold toggle 설계 한계 명시
//     Cold toggle은 warm 시뮬 데이터에 cold_derate 후처리 스케일링 (시뮬 재실행 아님)
//     T_wheel/P_in에 cold_derate 곱셈 = 근사 오버레이 (물리적으로 정확하지 않음)
//     코드 주석으로 설계 한계 명시 (eta_pct에는 미적용 유지)
// ════════════════════════════════════════════════════════════════════════════
// [V1.0-VERIFICATION-06] 승차감 분석(DrivabilityTabContent) 그래프 연산 검토 및 6건 수정 (2026-03-08)
// ────────────────────────────────────────────────────────────────────────────
//  [Fix-DV1] 🔴 HIGH — simDrivabilityTipIn/TipOut: mode_throttle 클램프 누락
//    수정 전: mode_throttle = T_mot_filtered / T_max_motor — [0,1] 범위 보장 없음
//             LPF 과도 응답(transient) 구간에서 T_mot_filtered > T_max_motor 가능
//             → mode_throttle > 1.0 → stepVehiclePhysics 내부 throttle 오버라이드 오동작
//             → 실제 모터 출력이 정격 이상으로 증폭되는 비물리적 가속/저크 발생
//             → 무필터(tau=0) 대비 필터 사용 시 오히려 더 큰 저크가 나오는 역설적 결과 가능
//    수정 후: Math.min(1, Math.max(0, ...)) [0,1] 강제 클램프 (TipIn·TipOut 양쪽 동일 적용)
//
//  [Fix-DV2] 🔴 HIGH — simDrivabilityBraking: F_brake 정규화 상수 물리 오류
//    수정 전: brake_demand = F_brake_total / (params.m × g × (params.mu_brake || 0.9))
//             params.mu_brake = 내부 브레이크 마찰계수 (캘리퍼-패드) ≠ 노면마찰계수
//             params.mu_brake 미정의 시 fallback=0.9(노면 µ 수준)로 다른 물리량 혼용
//             wet(params.mu=0.6)에서도 0.9 고정 → 정규화값 과소 → 실제 제동보다 약한 입력 전달
//             → brake_demand가 실제 물리 수준보다 ~33% 과소 → 저크/제동력 그래프 과소 추정
//    수정 후: brake_demand = F_brake_total / (params.m × g)
//             = a / g [단위: g] → stepVehiclePhysics 내부 해석 기준과 일관성 확보
//             Math.min(1, ...) 클램프로 항상 [0,1] 유지
//
//  [Fix-DV3] 🟡 MEDIUM — JERK_TS / JERK_SPD / JERK_BRK XAxis domain 미연결
//    수정 전: <XAxis dataKey="t"> — domain prop 없음
//             → 3개 차트 모두 axJERK_TS/SPD/BRK X범위 패널 조정 완전 무시
//    수정 후: type="number" domain={axXXX.domX} 각 차트에 연결
//
//  [Fix-DV4] 🟡 MEDIUM — JERK_TS / JERK_SPD YAxis domain 미연결
//    수정 전: JERK_TS YAxis — domain 없음 (±5/±10 기준선과 축 범위 불일치 가능)
//             JERK_SPD YAxis_v — domain 없음 (속도축 범위 패널 반영 불가)
//    수정 후: domain={axJERK_TS.domY}, domain={axJERK_SPD.domY} 연결
//
//  [Fix-DV5] 🟡 MEDIUM — JERK_SPD 가속도 Line hideY2 조건 없음
//    수정 전: <Line yAxisId="a" dataKey="a"...> 무조건 렌더
//             Y2축(가속도) 숨겨도 라인 잔존 → 축 없는 데이터 선 떠다니는 현상
//             다른 탭(L1/R1 등) 패턴과 일관성 없음
//    수정 후: {!axJERK_SPD.hideY2 && <Line .../>} 조건 추가
//
//  [Fix-DV6] 🟡 MEDIUM — JERK_TF AxesPanel yUnit="N·m" 오류 + axJERK_TF yMax 400 과대
//    수정 전: axJERK_TF = useAxes({ yMax:400 }) — 토크(N·m) 기준값
//             axisPanel yUnit="N·m" — 실제 Y축 = Jerk RMS/Max [m/s³], 완전 오기입
//             xUnit="s"  — X축 = 필터 시상수 τ(카테고리), 시간(s) 아님
//             yMax:400으로 실제 데이터(jerk_rms ≈ 1~20 m/s³)가 차트 상단 5% 이내 → 거의 안 보임
//    수정 후: axJERK_TF yMax:30 (m/s³ 상한)
//             axisPanel yUnit="m/s³", xUnit="τ(s)"
// ════════════════════════════════════════════════════════════════════════════
// ────────────────────────────────────────────────────────────────────────────
//  [Fix-RB1] 🔴 HIGH — simBraking E_KE_init/final: params.m → m_eff (KE 수지 불일치)
//    수정 전: E_KE_init = 0.5 * params.m * v²  — 차체 질량만, 회전 관성 미포함
//             동역학 a = -F_brake/m_eff 에서 m_eff 사용하는데 KE 기준은 params.m
//             → eta_regen_total = E_regen / (KE_init - KE_final) 분모 과소 → 회생 효율 과대 표시
//             → 배터리 SOC 회수량 (soc_delta_pct) 기준도 틀어짐
//    수정 후: m_eff = effectiveMass(params) 기반으로 E_KE_init/final 통일
//             simCoast Fix-CT1과 동일 방향 — 3개 시뮬 (Launch/Reaccel/Coast/Braking) 패리티
//
//  [Fix-RB2] 🔴 HIGH — rb3Data: rlMassKg 미반영
//    수정 전: const m = params.m || 1850 — 적재 질량 완전 무시
//             P_req = m * a_req * v → 실제 제동 요구 파워 과소 추정
//             rb1Data는 (params.m + rlMassKg) 반영하는데 rb3Data만 누락 → 차트 간 불일치
//             useMemo 의존성 배열에 rlMassKg 없음 → 헤더 변경 시 재계산 안 됨
//    수정 후: m = (params.m || 1850) + (rlMassKg || 0) (rb1Data 와 동일)
//             의존성 배열에 rlMassKg 추가
//
//  [Fix-RB3] 🔴 HIGH — rb3Data: rlGrade 미반영
//    수정 전: P_req = m * a_req * v / 1000 — 구배 보정 전무
//             rb1Data는 rlGrade 반영 (_grade_rad/_cos_g/F_grade_comp) 하는데 rb3Data 누락
//             내리막(rlGrade<0): 중력이 제동 보조 → P_req 과대; 오르막: 반대
//             useMemo 의존성 배열에 rlGrade 없음
//    수정 후: cos(grade) 보정 적용; 내리막 P_grade_assist 감산
//             의존성 배열에 rlGrade 추가
//
//  [Fix-RB4] 🟡 MEDIUM — rb2CumData: dHiSOC 길이 누락 + zero-padding → last-value padding
//    수정 전: len = Math.min(d100, d50, dCold) — dHiSOC.length 미포함
//             50% 제동(빨리 멈춤) 등 짧은 배열: d50[i] undefined → 0으로 채워짐
//             → 누적 에너지 곡선이 배열 끝에서 갑자기 0으로 꺽이는 현상 (물리적으로 비정상)
//    수정 후: Math.max(lengths) 로 전체 시간축 확보
//             배열 종료 후 last-value padding → 곡선이 plateau 형성 (에너지 보존 물리와 일치)
//
//  [Fix-RB5] 🟡 MEDIUM — RB3 차트 XAxis domain 미연결
//    수정 전: <XAxis dataKey="a_req"> — domain 없음
//             → axRB3 X축 범위 조정(축 설정 패널) 완전 무시됨
//    수정 후: type="number" domain={axRB3.domX} 연결
//
//  [Fix-RB6] 🟡 MEDIUM — RB3 P_fric Area hide 조건 오류
//    수정 전: {!axRB3.hideY && <Area yAxisId="P" dataKey="P_fric"...}
//             P_fric는 kW(Y2=P축) 인데 hideY(Y1=비율축) 조건으로 제어
//             → 비율축 숨기면 P_fric도 사라지고, P축만 숨기면 P_fric 잔존하는 오동작
//    수정 후: hideY2 조건으로 Y2(P축) 기준 통일
//
//  [Fix-RB7] 🟡 MEDIUM — rb1Data P_req_full/P_req_mid 하드코딩 (0.3g / 0.15g)
//    수정 전: F_req_full = m * 0.3 * g (= 2.94 m/s² 고정)
//             P_req_mid  = m * 0.15 * g (= 1.47 m/s² 고정)
//             "100% / 50% Pedal" 레이블 표기와 params.regen_max_decel_ms2 값 괴리
//    수정 후: a_req_full = params.regen_max_decel_ms2 || 2.5
//             a_req_mid  = a_req_full * 0.5
//             → 차트 내 a_th 수직 기준선과 동일 감속도에서 기준선 교차 → 물리적 일관성
// ════════════════════════════════════════════════════════════════════════════
// ────────────────────────────────────────────────────────────────────────────
//  [Fix-CT1] 🔴 HIGH — simCoast: params.m → effectiveMass(params) (simLaunch/simReaccel 패리티)
//    수정 전: const a = -F_total / params.m — 차체 질량만 사용, 구동계 회전 관성 미포함
//             E_KE_init/final도 params.m 기반 → 에너지 수지 오차
//             simLaunch·simReaccel은 m_eff = effectiveMass 사용 → 패리티 불일치
//    수정 후: m_eff_coast = effectiveMass(params) → 회전 관성(J_motor·i²/r²) 포함
//             코스팅 감속율 및 KE 에너지 수지 3개 시뮬 간 일관성 확보
//
//  [Fix-CT2] 🔴 HIGH — c2Data P_dt_drag: *1000 단위 오류 + k_m0 fallback 오류
//    수정 전: P_dt_drag = (k_m0 + k_m1 * ω) * 1000  (k_m0 = params.k_m0 || 0.2)
//             k_m0 단위는 W(실제 default 20W) → × 1000 하면 최대 수만 W → F_dt 1000배 과대
//             → 코스팅 거리 대폭 과소 계산 (특히 고속 구간)
//             k_m0 fallback 0.2는 실제 default 20과 불일치
//    수정 후: P_dt_drag = k_m0 + k_m1 * ω  (* 1000 제거)
//             k_m0 fallback 20, k_m1 fallback 0.01 (params 정의 default와 일치)
//
//  [Fix-CT3] 🔴 HIGH — c1Data useMemo: rlMassKg / rlHeadwind 의존성 및 보정 미반영
//    수정 전: useMemo([coastData, params]) — rlMassKg/rlHeadwind 헤더 변경 시 재계산 안 됨
//             F_roll/F_grade: params.m 기반 시뮬 값 그대로 → 헤더 적재 질량 무시
//             F_aero: 기준 시뮬 값 그대로 → 헤더 맞바람 무시
//             시나리오 오버레이 ΔF_wind도 rlHeadwind 베이스 미적용
//    수정 후: mass_ratio = (params.m + rlMassKg) / params.m → F_roll/F_grade 스케일링
//             hw_factor = ((v + rlHeadwind) / max(v, 0.1))^2 → F_aero 보정
//             시나리오 ΔF_wind = ΔF 기준 속도를 v+rlHeadwind로 수정
//             의존성 배열 [coastData, params, rlMassKg, rlHeadwind] 추가
//
//  [Fix-CT4] 🟡 MEDIUM — C1 차트 XAxis domain 미연결
//    수정 전: <XAxis type="number"> — domain prop 없음
//             → axCT1 X축 범위 조정(축 설정 패널) 완전 무시
//    수정 후: domain={axCT1.domX} 연결 → 시간/거리 x축 범위 슬라이더 반영
//
//  [Fix-CT5] 🟡 MEDIUM — C3 BarChart YAxis domain 미연결
//    수정 전: <YAxis> — domain prop 없음 → axCT3 Y 범위 조정 무시
//    수정 후: domain={axCT3.axY.auto ? [0,"auto"] : [axCT3.axY.min, axCT3.axY.max]}
//
//  [Fix-CT6] 🟡 MEDIUM — C4 coastData 정렬: 매 렌더 inline sort → useMemo 분리
//    수정 전: data={[...coastData].sort(...)} — 렌더마다 정렬 배열 생성 (불필요 연산)
//    수정 후: c4Data = useMemo(() => [...coastData].sort(...), [coastData]) → 데이터 변경시만 정렬
// ════════════════════════════════════════════════════════════════════════════
// ────────────────────────────────────────────────────────────────────────────
//  [Fix-RA1] 🔴 HIGH — simReaccel traction clamp: mu_eff(고정) → rls_state_ra.mu_est(동적 RLS)
//    수정 전: F_drv = min(F_drv, mu_eff × m × g) — RLS 루프 내 mu_est 업데이트 후에도 초기 mu_eff 고정
//             simLaunch는 rls_state.mu_est 사용 → 두 시뮬레이터 간 패리티 불일치
//    수정 후: F_drv = min(F_drv, rls_state_ra.mu_est × m × g) — 동적 RLS 추정 mu 반영
//             F_normal_ra 계산 순서도 클램프 이전으로 이동 (논리 정합성)
//
//  [Fix-RA2] 🔴 HIGH — simReaccel 토크 필터 순서 오류 (LPF→슬루 → 슬루→LPF로 수정)
//    수정 전: T_mot_filtered += α×(T_ideal−T_filtered) 먼저 적용 후 슬루레이트 클램프
//             → SPORT 모드 등 급격한 토크 변화 시 LPF 출력에 슬루가 재차 제한 → ±진동 발생 가능
//             simLaunch A43-Fix-1에서 이미 수정됐으나 simReaccel에 미적용 (패리티 불일치)
//    수정 후: T_slew_ra 상태변수 추가 → T_ideal → 슬루 제한(T_slew_ra) → LPF 추종 → 단조 수렴
//
//  [Fix-RA3] 🔴 HIGH — simReaccel PID 드라이버 throttle 상한 클램프 누락
//    수정 전: driveModeThrottle(pid_out_ra.pedal_pos, ...) — 개도 슬라이더 상한 미적용
//             → PID가 목표속도 도달 위해 100% 출력 가능 → reaccelThrottle 슬라이더 값 완전 무시
//             simLaunch에는 Math.min(pedal_pos, throttle) 적용되어 있으나 simReaccel 누락
//    수정 후: Math.min(pid_out_ra.pedal_pos, throttle) → 개도 상한 준수 (simLaunch 패리티)
//
//  [Fix-RA4] 🟡 MEDIUM — RA4 에너지 체인 스냅샷 시점 수정
//    수정 전: rawData[Math.floor(rawData.length / 4)] = 30초 구간의 1/4 지점(~7.5s)
//             → v0→v1 재가속 완료 후 순항 구간일 수 있음 → 부하 과소 표현
//    수정 후: T_motor_torque 최대값 시점 탐색 → 재가속 피크 전력/손실 흐름 정확 표현
//             (simLaunch Fix-L5와 동일 원칙)
//
//  [Fix-RA5] 🟡 MEDIUM — RA2 차트 XAxis type="number" 누락
//    수정 전: <XAxis dataKey="v_kmh"> — type 미지정 → Recharts 기본 "category" 처리
//             → v_kmh 0,2,4,...200을 등간격 카테고리로 렌더링 → 속도 비례 비선형 왜곡
//    수정 후: type="number" + domain=[0,200] + ticks 명시 → 선형 속도축으로 P ∝ v³ 곡선 정확 표시
//
//  [Fix-RA6] 🟡 MEDIUM — RA2 AxesPanel xUnit 오기
//    수정 전: xUnit="s" → 축 설정 패널에 "0 s ~ 15 s" 표시 (시간 단위 오표기)
//    수정 후: xUnit="km/h" → RA2 X축은 속도(km/h)
//
//  [Fix-RA7] 🟡 MEDIUM — RA3 AxesPanel yUnit 오기
//    수정 전: yUnit="km/h" → 온도 차트 Y축에 "km/h" 표시 (속도 단위 오기)
//    수정 후: yUnit="°C" → RA3은 온도(Battery/Inverter/Motor/Coolant) 차트
//
//  [Fix-RA8] 🟡 MEDIUM — axRA2 초기값 xMax:15 오류
//    수정 전: useAxes({ xMin:0, xMax:15 }) — 재가속 시간 도메인(0~15s) 기본값 → 속도 차트에 부적합
//             AxesPanel에 "0~15" 표시 → 사용자가 "0~200"으로 수동 변경 필요
//    수정 후: useAxes({ xMin:0, xMax:200 }) → RA2 속도-전력 차트에 적합한 기본 범위
// ════════════════════════════════════════════════════════════════════════════
// ────────────────────────────────────────────────────────────────────────────
//  [Fix-L1] 🔴 HIGH — simLaunch / simReaccel: road_condition µ 미반영 수정
//    수정 전: mu_eff = ROAD_MU[road_condition] 계산 후 완전 미사용
//             mu_hat = params.mu_ref_nominal || 1.0 (항상 dry=1.0 초기화)
//             → 습윤·빙설·적설 선택해도 시뮬 내부 traction clamp에 반영 안 됨
//    수정 후: mu_hat = mu_eff (road_condition 기반 초기값)
//             RLS 추정기도 road_condition 기반 µ에서 출발 → wet/ice/snow 발진 성능 차이 정확 반영
//             simReaccel 동일 방식 적용 (재가속 탭 패리티)
//
//  [Fix-L2] 🔴 HIGH — L2 차트 T_wheel 축 overflow 수정
//    수정 전: T_wheel = T_motor × GR × η ≈ 2256 N·m → yAxisId="T" (domain [0,500 N·m]) 표시
//             → 차트에서 완전히 범위 초과, 사실상 휠 토크 곡선 보이지 않음
//    수정 후: T_wheel_kNm = T_wheel / 1000 (kN·m) → 전용 Y3 축 (axL2.domY3, [0,3 kN·m])
//             eta_pct 효율 곡선은 P축(kW)에 오버레이로 이동 (0-100 % ≈ 0-100 kW 눈금 공유)
//             axL2 y3 초기값: [0, 3 kN·m]로 변경
//
//  [Fix-L3] 🔴 HIGH — L3(열 상태) 차트 X축 domain 고정값 수정
//    수정 전: domain={[0, 30]} 하드코딩 → 축 범위 설정 UI 조절해도 미반영
//    수정 후: axL3.axX.auto ? [0, 30] : [axL3.axX.min, axL3.axX.max]
//
//  [Fix-L4] 🟡 MEDIUM — L1(속도·가속도) 차트 X축 domain 고정값 수정
//    수정 전: domain={[0, 30]} 하드코딩 → 축 범위 설정 UI 조절해도 미반영
//    수정 후: axL1.axX.auto ? [0, 30] : [axL1.axX.min, axL1.axX.max]
//
//  [Fix-L5] 🟡 MEDIUM — L4 에너지 체인 스냅샷: 중간점 → 피크 토크 시점으로 변경
//    수정 전: lData[Math.floor(lData.length / 2)] → t≈15s, 순항 구간 → 낮은 전력/손실 표시
//             → "발진 중간 시점" 타이틀과 달리 실제로는 전력 부하가 낮은 시점 선택
//    수정 후: T_motor_torque 최대값 시점(발진 초기 0~3s 부근) → 피크 전력·손실 흐름 정확 표현
//
//  [Fix-L6] 🟡 MEDIUM — L1 KPI 배지 접지력 계산에 rlMassKg 누락 수정
//    수정 전: F_trac_lim KPI = µ × params.m × g → 적재 질량 무시
//    수정 후: F_trac_lim KPI = µ × (params.m + rlMassKg) × g
//             (L1 차트 내 F_trac_lim 라인은 이미 rlMassKg 포함하고 있어 KPI 배지만 수정)
//
//  [Fix-L7] 🟡 MEDIUM — L1 차트 "50% Pedal" 속도 라인 레이블 동적화
//    수정 전: name="Speed 50% Pedal (km/h)" 고정 문자열
//             → launchThr=60% 시 실제 시뮬은 30% 개도이나 레이블은 "50%"로 오표기
//    수정 후: name="Speed ${Math.round(launchThr*0.5)}% Pedal (km/h)" 동적 레이블
//
//  [Fix-L8] 🟡 MEDIUM — a_scen µ 페널티 구간 선택 적용
//    수정 전: a_scen = d.a × mu_ratio × cold_derate 전 속도 범위에 동일 적용
//             → 고속 전력제한 구간(F_drv << F_trac_lim)에서도 µ 감소 반영 → 물리적 과다 보정
//    수정 후: F_drv ≥ F_trac_lim × 0.85 → 접지력 지배 → mu_ratio 적용
//             F_drv <  F_trac_lim × 0.85 → 전력 지배   → mu_ratio=1.0 (cold_derate만 적용)
//             a_soc, a_mode 동일 로직 반영
// ════════════════════════════════════════════════════════════════════════════
// ────────────────────────────────────────────────────────────────────────────
//  [Fix-RL1] 🔴 HIGH — rlCalcVmax: headwind_ms 파라미터 추가
//    수정 전: rlResist 호출 시 v_wind=0 고정 → 맞바람 조건에서 v_max(P) 과대 추정
//    수정 후: rlCalcVmax(params, mass, grade, throttle, headwind_ms) 시그니처 확장
//             rlResist 에 v_wind 전달 → R1·R2 v_max 기준선 정확화
//
//  [Fix-RL2] 🔴 HIGH — rlCalcVmaxTorque: headwind_ms 파라미터 추가
//    수정 전: 토크 균형 탐색 시 v_wind=0 고정 → v_max(T) 과대 추정
//    수정 후: rlCalcVmaxTorque(..., headwind_ms) — Fix-RL1과 동일 방식
//
//  [Fix-RL3] 🔴 HIGH — rlBuildR4: 하드코딩 시나리오(grade=3%, wind=5m/s)에
//                       사용자 조건(r1Grade, r1Headwind) 동적 시나리오 추가
//    수정 전: scenarios 3개 고정 → R5 부하율 차트가 슬라이더 값 무시
//    수정 후: 4번째 시나리오 { key:"user", grade_pct:r1Grade, v_wind:r1Headwind } 추가
//             R5 차트에 오렌지 점선 "부하율 — 사용자 설정" 라인 조건부 표시
//
//  [Fix-RL4] 🔴 HIGH — r2Data/r2Vopts: 적재 질량(r1MassKg) 미반영 수정
//    수정 전: rlBuildR2base(params), rlCalcVopt(params) — 항상 기준 질량 사용
//             → 효율 곡선·최적 경제속도·항속거리 KPI가 payload 무시
//    수정 후: _r2Params = params + { m: params.m + r1MassKg } 로 통일
//             r2Vopts, r2Data(base+winter) 모두 _r2Params 사용
//             r1Vmax / r1VmaxTorque useMemo 에도 r1Headwind 의존성 추가
//
//  [Fix-RL5] 🟡 MEDIUM — R2 차트 Y축 max 계산: 고정 rho=1.225 → rhoCorrected
//    수정 전: F_aero200 = 0.5 * (params.rho||1.225) * CdA * v²
//             → 고온/고고도 조건에서 Y축 max 과대 계산 → 그래프 스케일 불일치
//    수정 후: rho200 = rhoCorrected(T_amb, altitude) 적용
//
//  [Fix-RL6] 🟡 MEDIUM — R4(민감도) 차트: axR4.domY/domX 미연결 + yUnit 오표기
//    수정 전: YAxis domain prop 없음 → 사용자가 축 범위 설정해도 차트에 미반영
//             AxesPanel yUnit="kWh/100km" → 실제 단위(kW)와 불일치
//    수정 후: domain={axR4.domY} / X domain={axR4 조건} 연결
//             yUnit="kW" / Y축 라벨 "필요 전력" → "배터리 전력" 수정
//
//  [Fix-RL7] 🟡 MEDIUM — R5(부하율) 차트: axR5 X domain 미연결
//    수정 전: XAxis domain=[0, v_max_chart||300] 고정 → 축 조절 미반영
//    수정 후: domain={axR5.axX.auto ? [0, v_max_chart] : [axR5.axX.min, axR5.axX.max]}
//             + 사용자 조건 부하율 라인(r1Grade≠0 또는 r1Headwind>0 시) 조건부 표시
// ════════════════════════════════════════════════════════════════════════════
//   베이스: Advance67
//
//  ■ A68-Fix-1 ✅ [rlBuildR4] grade 단위 혼용 버그 — 🔴 HIGH
//     수정 전: { grade_deg: 3 } → gr = 3 * PI/180 ≈ 0.0524 rad → 실제 5.24% 등판 계산
//             KPI 라벨 "3% 등판(Grade)"과 불일치 → dP_grade / load_grade 과대 계산
//     수정 후: { grade_pct: 3 } → gr = Math.atan(3/100) ≈ 0.02999 rad → 정확히 3% 등판
//             rlCalcVmax / rlBuildR1 / rlCalcVmaxTorque 와 동일한 atan 변환으로 통일
//
//  ■ A68-Fix-2 ✅ [rlCalcVmaxTorque] thr_clip 방식 rlBuildR1 과 통일 — 🟡 MEDIUM
//     수정 전: Math.min(1.0, thr) → SPORT 모드(thr=1.15)에서 1.0으로 클리핑
//             → T_drive_wheel 과소 계산 → R1 차트 시각적 교차점 vs KPI 배지 v_max_torque 불일치
//     수정 후: Math.min(pkScale_vmt + 0.05, thr) → rlBuildR1 thr_clip 과 동일 상한 허용
//             SPORT 모드에서 R1 차트 교차점 = v_max_torque KPI 배지 완전 일치
//
//  ■ A68-Fix-3 ✅ [rlCalcVcross] road_condition 노면 보정 누락 — 🟡 MEDIUM
//     수정 전: CrrDynamic 기본값만 사용 → wet/snow/ice 노면 설정 시 v_cross KPI 실제보다 낮게 표시
//             rlResist 와 _crr_road_k 적용 기준 불일치
//     수정 후: _crr_road_k = { dry:1.0, wet:1.15, snow:2.50, ice:1.80 } 동일 계수 적용
//             rlResist 와 완전 일관된 교차속도 계산
//
//  ■ A68-Fix-4 ✅ [R1 그래프] 전력 기반 곡선 추가 (우측 Y2축) — ✨ ENHANCEMENT
//     수정 전: R1 차트 토크 단일 Y축만 표시 → P_motor / P_road 전력 균형을 시각적으로 확인 불가
//     수정 후: rlBuildR1 데이터에 P_motor_wheel / P_road_kW 필드 추가
//             R1 ComposedChart 우측 Y2축(황색, kW) 추가:
//               · P_motor(kW) — 모터 가용 최대 출력 곡선 (황색 파선)
//               · P_road(kW)  — 도로부하 전력 곡선 (적색 파선)
//               · 교차점 = v_max(P) — 전력 기반 최고속도 시각적 확인 가능
//             툴팁에 전력 균형(ΔP, 구동가능/출력부족) 정보 추가
//             v_max(P) ReferenceLine 색상을 황색(P축 색상)으로 구분
// ────────────────────────────────────────────────────────────
// [A49] 정속 주행·발진·재가속 탭 개도 독립 분리 (2026-03-07):
//   베이스: Advance48
//
//  ■ A49-1 ✅ [개도 탭별 분리] rlThrottle(정속) / launchThrottle(발진) / reaccelThrottle(재가속)
//     수정 전: 단일 rlThrottle 공유 → 한 탭에서 개도 변경 시 전 탭에 영향
//     수정 후: 3개 독립 state — 각 탭 슬라이더가 해당 탭 그래프에만 연결
//             · RLTabContent      → rlThrottle     (R1~R5 정속 그래프 useMemo 반영)
//             · LaunchTabContent  → launchThrottle  (simLaunch → runSim 재실행)
//             · ReAccelTabContent → reaccelThrottle (simReaccel → runSim 재실행)
//  ■ A49-2 ✅ [runCoreSim / runFullSim] reaccelThrottle param 추가 분리
//             launchThr / reaccelThr 독립 계산 → launch/reaccel 시뮬 각각 적용
//  ■ A49-3 ✅ [R0→R1 번호 이동] 정속 주행 그래프 R1~R5로 재번호 (A48 연속)
//  ■ A49-4 ✅ [A48-Fix-1 포함] lang fallback 유지
//
//   베이스: Advance46
//
//  ■ A47-Change-1 ✅ [EVSimulatorInner] vehicleAKey/vehicleBKey 자동 실행 트리거 추가
//     수정 전: 드롭다운 변경 → paramsA/B 업데이트만, 실행 버튼 별도 클릭 필요
//     수정 후: vehicleAKey/vehicleBKey useEffect 추가
//             변경 즉시 350ms 디바운스 후 runSimRef.current() 자동 호출
//             350ms: setParamsA/B(setState) React 배치 + runSim useCallback 재생성
//                    + runSimRef.current 갱신까지 충분한 여유
//
// ────────────────────────────────────────────────────────────
//   베이스: Advance45
//
//  ■ A46-Change-1 ✅ [CompareTabContent] Vehicle Badges 섹션 제거
//     수정 전: "M3 · 1830kg · CdA 0.49 · P 258kW  VS  I6 ·..." 배지 표시
//     수정 후: 해당 배지 div 블록 완전 제거 (차량 선택 드롭다운은 유지)
//
//  ■ A46-Change-2 ✅ [buildRadar] 절대범위 정규화 → 상대(A/B 최고값=100%) 정규화
//     수정 전: normalize(val, absMin, absMax) — 고정 절대범위 기준
//             예) 항속거리 100~700km 범위 → 212km=19pt, 231km=22pt
//             두 차량이 비슷해도 점수 차이가 적어 레이더가 거의 겹침
//     수정 후: relNorm(vA, vB, lower) — A와 B 중 우수한 쪽 = 100%
//             예) 항속거리 I6=231km=100%, M3=212km=91.77%
//             예) 회생 M3=22%=100%, I6=21.6%=98.18%
//             → 두 차량의 실질적 차이가 레이더/바에 명확히 반영
//
//  ■ A46-Change-3 ✅ [RadarSVG] 링 퍼센트 라벨 추가
//     수정 전: 5개 링에 라벨 없음
//     수정 후: 각 링 상단에 20%/40%/60%/80%/100% 라벨 표시
//             → 맨 바깥 링 = 100%(해당 지표 최우수) 시각적 명확화
//
// ────────────────────────────────────────────────────────────
//   베이스: Advance44
//   수정 전: 14개 그래프만 axisPanel 보유
//   수정 후: 48개 그래프 전체 축 설정 제공 (X·Y축 Manual/Autoscale)
//   추가된 그래프: C2/3/4, RB1/2/3, RA3, E1/2/3/4, S1/3, T1-5,
//                 JERK_TS/SPD/BRK/TF, L2/3, B1/2/3, CMP_E/T, RV1-5, DC8A-D
//
// [A44] 주행사이클 ↔ 전력/에너지 그래프 연결 검증 및 5개 버그 수정 (2026-03-06):
//   베이스: Advance43
//
//  ■ A44-BugFix ✅ [simDrivingCycle] T_amb TDZ 오류 — 🔴 CRITICAL (루트 코즈)
//     수정 전: const p = { ...params, T_amb }; 가 T_amb 선언 前에 위치
//             → ReferenceError: Cannot access 'T_amb' before initialization
//             → 모든 비-WLTP 사이클(JC08/CLTC/EPA/RDE/FTP75)에서 return null
//             → wdata=[] → DC1~DC6 전 그래프 "데이터 없음" 표시
//     수정 후: T_amb 선언 → p 생성 순서 교정 (simWLTP와 동일 패턴)
//
//  ■ A44-Fix-1 ✅ [simDrivingCycle] 비-WLTP 커널 m_eff 미적용 — 🔴 HIGH
//     원인: A43-Fix-4가 simWLTP에만 적용됨, simDrivingCycle(JC08/CLTC/EPA/RDE 등)은 누락
//     수정 전: a = F / p.m (회전관성 미포함) → 비-WLTP 사이클 에너지 ~2~4% 오차
//     수정 후: m_eff_dc = effectiveMass(p) → 구동·회생 양쪽 a_vehicle 계산에 적용
//
//  ■ A44-Fix-2 ✅ [simDrivingCycle] 비-WLTP 커널 회생 드라이브 모드 미연동 — ⚠️ MEDIUM
//     원인: A43-Fix-5가 simWLTP에만 적용됨, simDrivingCycle 회생 구간은 고정 0.8 그대로
//     수정 전: brake_d = demand × 0.8 — ECO/SPORT JC08/CLTC 회생 차이 미반영
//     수정 후: driveModeRegenStrength(p) × aggressiveness 패턴 동일 적용
//
//  ■ A44-Fix-3 ✅ [simWLTP] E_aux_kWh 단일점 추정 → 루프 내 실적산 — ⚠️ MEDIUM
//     수정 전: hvacDetailedPower(T_amb, 0, soc_init, p) × t_total × 0.6 / 1000
//             → soc_init 고정(SOC 변화 무시), 속도 0 고정(저속/고속 HVAC 편차 무시),
//               0.6 인수(구동 비율 근사) → E2 그래프 E_aux 표시 부정확
//     수정 후: E_aux_drive_J 누산기 추가 → drive 루프에서 chain.P_aux×dt 직접 적산
//             E_aux_kWh = (E_aux_drive_J + E_aux_regen_J) / 3.6M → 실제 HVAC 에너지 정확 반영
//
//  ■ A44-Fix-4 ✅ [DC3 PHASE_COLORS] "ExtraHigh" ↔ "Extra-High" 불일치 — ⚠️ MEDIUM
//     수정 전: PHASE_COLORS = { ExtraHigh:"#ef4444" } vs simWLTP phaseSummary.phase = "Extra-High"
//             → PHASE_COLORS["Extra-High"] = undefined → DC3 Phase 테이블 Extra-High 행 색상 누락
//     수정 후: _base/"ExtraHigh" → "Extra-High" 통일. _phaseMap.WLTP / fallback / _c3HighPhase 동일 수정
// ────────────────────────────────────────────────────────────
// [A43] 구동 제어·동역학·에너지 연결 검토 보고서 6개 수정 반영 (2026-03-06):
//   출처: ev_drive_dynamics_energy_analysis.docx
//
//  ■ A43-Fix-1 ✅ [simLaunch] 토크 응답 필터 순서 수정 — 🔴 HIGH
//     수정 전: LPF → 슬루레이트 이중 클램프 → SPORT 모드 정상상태 ±15 Nm 진동
//     수정 후: 슬루레이트로 T_slew 생성 → LPF가 T_slew 추종 → 단조 수렴(±0 Nm)
//             T_slew_sl 상태 변수 추가. 5줄 수정
//
//  ■ A43-Fix-2 ✅ [simReaccel] TCS 슬립률 항상 0 버그 수정 — 🔴 HIGH
//     수정 전: slip_est = v > 1 ? 0 : 0 (항상 0) → TCS 완전 비활성화
//     수정 후: simLaunch와 동일한 omega_w/v_wheel 기반 슬립 계산 적용
//             SNOW 노면 고토크 구간 TCS 안전성 복원. 3줄 수정
//
//  ■ A43-Fix-3 ✅ [driveModeRegenStrength] OFF_ROAD fallback 1.0 → 0.6 — ⚠️ MEDIUM
//     수정 전: params.offroad_regen_strength ?? 1.0 (SPORT보다 높음)
//     수정 후: params.offroad_regen_strength ?? 0.6 (DEFAULT_PARAMS와 일치)
//
//  ■ A43-Fix-4 ✅ [simWLTP] 유효 질량(m_eff) 적용 — ⚠️ MEDIUM
//     수정 전: a = F / p.m — 회전관성 미포함 → WLTP 에너지 ~2~4% 오차
//     수정 후: effectiveMass(p) → m_eff_wltp 사용. 구동/회생 두 구간 모두 적용
//
//  ■ A43-Fix-5 ✅ [simWLTP] 회생 강도 드라이브 모드 연동 — ⚠️ MEDIUM
//     수정 전: wltp_regen_aggressiveness 고정 0.8 → 모드별 회생 차이 미반영
//     수정 후: driveModeRegenStrength(p) × aggressiveness → ECO/SPORT WLTP 전비 차이 실제 반영
//
//  ■ A43-Fix-6 ✅ [computeEnergyChain] HVAC live SOC 사용 — 📌 LOW
//     수정 전: params.soc_init || params.soc_current || 0.8 (고정값)
//     수정 후: soc (live SOC 파라미터 직접 전달) → SOC<20% 구간 HVAC 정밀도 향상
// ────────────────────────────────────────────────────────────
// [A36] 팝업 영문화 + 30개 그래프 팝업 추가 + 2단계 성능 최적화 (2026-03-06):
//   베이스: Advance35-BugFix
//
//  ■ A36-FIX-1 ✅ [GRAPH_INFO_DB prm.n] 파라미터 이름 한국어 단일값 → 이중언어 객체 변환
//     수정 전: { n:"차량 질량", ... } → 영어 모드에서도 한국어 표시
//     수정 후: { n:{ko:"차량 질량", en:"Vehicle Mass"}, ... } — 122개 전체 변환
//     렌더러: p.n → typeof p.n==="object" ? (L==="en" ? p.n.en : p.n.ko) : p.n (하위 호환)
//
//  ■ A36-FIX-2 ✅ [graphId 미적용 카드 30개 → 전체 적용 완료]
//     수정 전: 전체 85개 RLGraphCard 중 graphId 누락 30개 → ℹ 버튼 없음
//     수정 후: DC1~DC8D, S1~S4, JERK 서브카드 5개, Safety 9개, B_GLOSS, V_KPI 추가
//     GRAPH_INFO_DB 신규 30개 항목 추가 (목적/분석이유/물리파라미터 이중언어)
//
//  ■ A36-PERF ✅ [2단계 비동기 시뮬 — Core/Heavy 분리]
//     Phase-1 Core (~300ms): 도로부하·발진·재가속·코스팅·제동·정상효율·에너지소비
//       → runCoreSim() / runCoreComparison() → setSimResults(core) 즉시 반영
//     Phase-2 Heavy (setTimeout(0) 이후, ~700ms): WLTP·온도민감도·열스트레스·민감도분석
//       → runHeavySim() / runComparison() → setSimResults(full) 완성
//     로딩 표시: r._coreOnly=true 동안 Thermal·WLTP·Sensitivity 탭에 HeavyLoadingBadge 표시
//     setTimeout 50ms (초기) → 0ms (Heavy) 순서로 브라우저 렌더 프레임 보장
//
// [A35-BugFix] 싱글/듀얼 모터 설정 → 차량 동역학·전력 에너지 섹션 반영 (2026-03-06):
//   베이스: Advance35
//
//  ■ A35-BUG-M1 ✅ [rlPmaxAtV] motor_front_power_ratio — Dead parameter 해소
//     수정 전: motor_front_power_ratio 값이 UI 슬라이더에만 존재, 실제 계산 전혀 미사용
//     수정 후: rlPmaxAtV()에 듀얼 모터 전/후 출력 개별 상한 제약 추가
//             torque_split_front(요청 분배비) vs motor_front_power_ratio(출력 비율) 비교
//             → 병목 축 기준으로 P_lim_wheel을 재산정하여 R1·R4 그래프에 반영
//
//  ■ A35-BUG-M2 ✅ [computeEnergyChain / computeRegenChain] 듀얼 모터 손실 스케일링
//     수정 전: 싱글/듀얼 구분 없이 단일 모터·인버터 손실만 계산
//             → 듀얼 모터 시 인버터 2개, 모터 2개 손실 완전 누락 → E1~E4 에너지 과소 추정
//     수정 후: motor_config === 'dual' 또는 awd_enable=true 감지 시 물리적 스케일링 적용
//             · 동손(Cu): torque_split_front 기반 → (rf²+rr²) 배 (50:50시 0.5배)
//             · 철손(Fe)/기계손(Mech): 스테이터/베어링 2개 → 2배
//             · 인버터 전도손: 선형(45%)+이차(55%) 근사 분리 스케일링 + 보조전력 2배
//             · 스위칭손: I_rms 선형 → 1배 유지
//             회생 경로(computeRegenChain)에도 동일 스케일링 적용
//
//  ■ A35-BUG-M3 ✅ [e1Data useMemo] motor_config/awd_enable/torque_split_front 의존배열 추가
//     수정 전: [wdata, mc, P_tot, steadyEff] — motor_config 변경 시 e1Data 미갱신 가능성
//     수정 후: params.motor_config, params.awd_enable, params.torque_split_front 추가
//             → 모터 구성 변경 즉시 E1 누적 에너지 분해 그래프 갱신
//
//  ■ A35-BUG-M4 ✅ [L1 접지한계] 싱글 모터 구동축 하중 비율 반영
//     수정 전: F_trac_lim = μ × m × g × cosθ — AWD/싱글 구분 없이 전체 중량 사용
//             → 싱글 후륜 모터 시 비구동 전륜 포함한 과대 접지한계 표시
//     수정 후: DEFAULT_PARAMS에 rear_weight_ratio: 0.55 추가
//             · AWD(듀얼): traction_factor = 1.0 (4륜 구동, 전체 중량 기준)
//             · 싱글 후륜:  traction_factor = rear_weight_ratio (후륜 정적 하중 비율)
//             · 싱글 전륜:  traction_factor = 1 − rear_weight_ratio (전륜 정적 하중 비율)
//             차량 제원 탭에 rear_weight_ratio 슬라이더 추가 (싱글 모터 선택 시만 표시)
// ────────────────────────────────────────────────────────────
//   베이스: Advance31 (A31-Fix-1~4 개선 완료)
//
//  ■ 신규 기능: GRAPH_INFO_DB + GraphInfoPopup + RLGraphCard ℹ 버튼
//   · GRAPH_INFO_DB: 모든 그래프의 목적·분석이유·물리파라미터 정의
//     (R1~R4, L1~L3, RA1~RA3, C1~C2, E1~E4, DC1~DC2, T1~T4, B1~B4,
//      S1~S2, H1~H2, CS1, SF1, V1~V2, DC7~DC8, S5, MC, BMS, ESC, FOC, TIRE, BC)
//   · GraphInfoPopup: 3섹션 (목적/분석이유/물리파라미터) 모달 팝업
//     - 배경 오버레이 클릭 또는 ESC 키로 닫기
//     - 한국어/영어 lang 자동 연동
//   · RLGraphCard: graphId prop 추가 → ℹ 버튼 자동 표시
//     - graphId 없으면 기존과 완전 동일 동작 (하위 호환)
//   · 45개 RLGraphCard 인스턴스 전체에 graphId 적용 완료
// ────────────────────────────────────────────────────────────
// [Advance31] 그래프 검토 보고서 4개 개선 항목 반영 (2026-03-06):
//   베이스: Advance30 (BUG·WARN 전항목 검증 완료)
//   출처: EV_Sim_Graph_검토_보고서_Advance30.docx — 잔여 이슈 4건 해소
//
//  ■ A31-Fix-1 ✅ [T2] 열 발생원 분해 — steadyEff 속도별 LUT 보간 적용
//     수정 전: mid_chain 단일 비율(k_batt/k_inv...) 전 구간 균일 적용
//             → 저속(정지·가속)과 고속(크루즈) 손실 구조 차이 미반영
//     수정 후: r.steadyEff LUT 속도별 선형 보간 → 각 타임스텝 v_ref 기준 실제 손실 비율 적용
//             (E1 e1Data 개선과 동일 패턴 / Advance24-FIX-B 설계 의도 T2에 확장)
//             steadyEff 없으면 mid_chain 비율 fallback 유지
//             useMemo 의존배열에 steadyEff 추가
//
//  ■ A31-Fix-2 ✅ [B2] Ri vs 온도·SOC — soh_now 실 시뮬 값 연결
//     수정 전: soh_now = r.passFailReport ? 0.92 : 0.95 (고정 추정값)
//             → soh_enable=ON 상태에서도 실측 SOH 미반영
//     수정 후: soh_enable=ON AND r.launch100.soh_state.soh 존재 시 실 시뮬 값 우선 사용
//             OFF 또는 시뮬 미실행 시 기존 추정값(0.95) fallback
//             WARN-06 buildRadar 패턴과 동일하게 통일 (🔬실측/〜추정 배지 표시)
//             useMemo 의존배열에 params.soh_enable 추가
//
//  ■ A31-Fix-3 ✅ [B4] 에너지밀도 — pack_mass_kg 파라미터 추가 및 UI 명시
//     수정 전: E_density = battery_energy / (m × 0.001) — 차량 총 질량 기반 근사
//             → 팩 질량(pack_mass_kg) 파라미터 없어 Wh/kg 정확도 낮음
//     수정 후: DEFAULT_PARAMS에 pack_mass_kg: 400 추가 (기본값 배터리팩 질량 [kg])
//             E_density = battery_energy × 1000 / pack_mass_kg (Wh/kg 정확 계산)
//             B4 레이더 정규화 기준 패널에 "에너지밀도: pack_mass_kg 기반 (팩 질량)" 주석 추가
//             차량 정보 탭 spec 섹션에 pack_mass_kg 슬라이더 추가
//
//  ■ A31-Fix-4 ✅ [Drivability] filterTau — params.tau_torque_{mode} 기반 초기값 연동
//     수정 전: filterTau useState(0.1) — 고정 초기값, params 변경 시 미동기
//             → 드라이브 모드별 설계된 tau_torque 값과 분리
//     수정 후: filterTau 초기값 = params.tau_torque_{drive_mode} (현재 드라이브 모드 시상수)
//             "모드 기본값 복원" 버튼 추가 → 클릭 시 현재 모드의 tau로 리셋
//             슬라이더 힌트에 현재 드라이브 모드 및 설계값 표시
// ────────────────────────────────────────────────────────────
// [Advance30] 전체 이슈 완결 검증 (2026-03-06):
//   베이스: Advance29 (모든 BUG·WARN 수정 완료 확인)
//   EV_Sim_파라미터_연결_검토.docx 기준 — 즉시수정·단기개선·장기개선 전항목 반영 완료
//
//  ■ 즉시 수정 (BUG — 결과 오류) — 전항목 ✅ 완료
//   BUG-01 ✅: R2 useMemo 의존배열에 rlMassKg 추가 → 적재 변경 즉시 정속전비 갱신
//   BUG-02 ✅: R4/RA2 P_cont 0.70 하드코딩 → params.P_cont_ratio || 0.70 참조
//   BUG-03 ✅: L1 접지한계선 F_trac_lim = mu × (params.m + rlMassKg) × 9.81 × cosθ
//   BUG-04 ✅: C1 오버레이 rho=1.2 하드코딩 → params.rho || 1.2
//   BUG-05 ✅: C2 Euler 적분 rho=1.2 하드코딩 → params.rho || 1.2
//   BUG-06 ✅: sensLC_compare(p, thr) — simLaunch(p, 1.0, ...) → simLaunch(p, thr, ...)
//             CompareTabContent에 rlThrottle prop 전달 완료
//
//  ■ 단기 개선 (WARN — 정밀도) — 전항목 ✅ 완료
//   WARN-01 ✅: rlCalcVmax에 throttle_pct 인자 추가 → 부분 개도 P-based v_max 정확 반영
//   WARN-02 ✅: R3 냉각기 CdA +0.04 하드코딩 → params.CdA_cool_open || 0.04
//   WARN-03 ✅: RA1/L1 cold_derate 0.72 하드코딩 → params.cold_derate_factor || 0.72
//   WARN-04 ✅: steadyAt_cmp 고정 Crr → CrrDynamic(v, T_amb, p) 적용
//   WARN-05 ✅: Compare Detail PcA/PcB P_cont_ratio 기본값 0.65 → 0.70 통일
//
//  ■ 장기 개선 (기능 추가) — 전항목 ✅ 완료
//   WARN-06 ✅: buildRadar SOH — soh_enable=ON 시 실 시뮬 soh_state.soh 우선 사용
//   WARN-07 ✅: RA2 P_avail_thr / P_avail_combined / dP_thr 커브 추가
//
//  ■ 부가 개선 (Advance29 신규)
//   G3: Tornado 차트 subtitle/배지에 현재 개도 표시, KPI 패널 개도 항목 추가
//   Auto-run: 개도(rlThrottle) 변경 시 300ms 디바운스 후 runSim() 자동 실행
// ────────────────────────────────────────────────────────────
// [Advance29] 수정 상세 이력 (2026-03-06):
//   BUG-01: R2 useMemo 의존배열에 rlMassKg 추가 (적재 변경 즉시 반영)
//   BUG-02: R4/RA2 P_cont 0.70 하드코딩 → params.P_cont_ratio 참조
//   BUG-03: L1 접지한계선 F_trac_lim에 rlMassKg 반영
//   BUG-04: C1 오버레이 rho=1.2 하드코딩 → params.rho || 1.2
//   BUG-05: C2 Euler 적분 rho=1.2 하드코딩 → params.rho || 1.2
//   BUG-06+G3: sensLC_compare에 thr 인자 추가, CompareTabContent에 rlThrottle 전달
//   RA2: P_avail_thr / P_avail_combined / dP_thr 필드 추가, 개도 커브 렌더링
//   G3: Tornado 차트 subtitle/배지에 현재 개도 표시, KPI 패널 개도 항목 추가
//   Auto-run: 개도(rlThrottle) 변경 시 300ms 디바운스 후 runSim() 자동 실행
//   WARN-01: rlCalcVmax에 throttle_pct 인자 추가 → 부분 개도 P-based v_max 정확 반영
//   WARN-02: R3 냉각기 CdA +0.04 하드코딩 → params.CdA_cool_open || 0.04
//   WARN-03: RA1/L1 cold_derate 0.72 하드코딩 → params.cold_derate_factor || 0.72
//   WARN-04: steadyAt_cmp 고정 Crr → CrrDynamic(v, T_amb, p) 적용
//   WARN-05: Compare Detail PcA/PcB P_cont_ratio 기본값 0.65→0.70 통일 (R4·RA2와 일치)
//   WARN-06: buildRadar SOH — soh_enable=ON 시 실 시뮬 soh_state.soh 우선 사용,
//            OFF 시 Wang2014 경험적 추정 유지, 레이더 카드에 🔬실측/〜추정 배지 표시
// ── 정식 함수 목록 (v04 기준) ─────────────────────────────
// ──────────────────────────────────────────────────────────
//  Domain              Canonical (Use This)         Replaces / Notes
//  ─────────────────── ──────────────────────────── ────────────────────────────────────────
//  Battery OCV         batteryOCV_smart()           batteryOCV_LUTcompat(제거), batteryOCV(제거)
//  Battery Model       battery2RCModelWithSEI()     battery2RCModel(제거)
//  Driving Resistance  resistanceForcesV3()         variableCdA/crosswindAeroForce(내부통합)
//  HVAC / Aux Power    hvacDetailedPower()          heatPumpPower(제거), auxPowerModel(제거)
//  Kalman SOC          kalmanSOCUpdate()            [R-14] simLaunch/simReaccel 루프 연동 완료
//                      initKalmanByType()
//  Torque / AWD        torqueVectoring()            dualMotorAWDDistribution(제거)
//  Vertical Load       computeVerticalLoad_v4()     computeVerticalLoad(제거)
//  SOH / Arrhenius     updateSOH()                  arrheniusSOH_v3(인라인)
//  Thermal Step        thermalStep(+phi_flow)       [R-13] 냉각유량 방열 반영 신규 파라미터
//  BMS Cells           bmsStep()                    [R-15] simLaunch/simReaccel 루프 연동 완료
//  Global UI           RLKpiBadge, RLScenBtn,       tab-local 중복 컴포넌트(제거)
//                      RLHwPopup, RLTooltip,
//                      RLGraphCard, RLHwBtn
// ──────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance27 → Advance28)
//     제목: 공기밀도(ρ) 단일 진입점 — 헤더 대기환경 패널로 통합
// ──────────────────────────────────────────────────────────────
//  [A28-UI1] 차량 제원 설정 패널에서 공기밀도(ρ) PI 항목 제거
//            이유: 헤더 대기환경 패널(기온·고도 연동 자동 산출 + 직접 입력)과 중복
//            params.rho는 헤더에서만 조절 → 모든 물리식·그래프 즉시 반영 유지
//  [A28-UI2] 차량 제원 설정 힌트 박스에 공기밀도 안내 문구 추가
//            "공기밀도(ρ)는 헤더 대기 환경 패널에서 기온·고도 연동으로 조절합니다"
//  연결된 물리식 / 그래프 (params.rho 경유, 헤더 변경 시 즉시 갱신)
//    · resistanceForcesV3()  : F_aero = ½·ρ·CdA·v²  (모든 시뮬레이션 공통)
//    · rlBuildR1()           : R1 그래프 공기저항 분해 · v_cross 교차점
//    · rlBuildR2base()       : R2 정속 전비 곡선  (Fa ∝ ρ)
//    · rlBuildR3()           : R3 CdA·Crr 민감도 그래프
//    · RA2 useMemo           : 재가속 탭 P_req_base (ρ 직접 참조)
//    · simLaunch / simReaccel: L1·L2·L3 발진 & RA1 시계열
//    · simWLTP / simDrivingCycle: WLTP·사이클 주행 에너지 계산
//    · rhoCorrected()        : 기온·고도 입력 시 ρ 자동 산출 공식
//                               ρ = 1.225 × (288.15/T_K) × exp(−g·h/(287.05·T_K))
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance26 → Advance27)
//     제목: 헤더 전역 연동 — 공기밀도·맞바람·SOC·노면
// ──────────────────────────────────────────────────────────────
//  [A27-UI1] 헤더 대기환경 재배치
//            행1: 기온(°C) + 고도(m)
//            행2: 공기밀도(kg/m³, ρ → 라벨 "공기밀도") + 맞바람(m/s, 0~30, 빠른선택 0/3/5/10)
//  [A27-UI2] 헤더 차량조건 SOC 추가
//            행1: 차중(kg) — 기존 유지
//            행2: SOC 80% / SOC 60% / SOC 20% 토글 버튼
//  [A27-Fix1] 발진 탭 노면 배지 — dry일 때 완전 숨김 (조용한 연결)
//             비-dry(습윤·적설·빙판)일 때만 µ값과 함께 배지 표시
//  [A27-Fix2] 재가속 탭 RA1 SOC 버튼 삭제 → 헤더 SOC 배지로 교체
//             rlSocScenario prop 수신, socScenario = rlSocScenario
//  [A27-Fix3] 재가속 탭 RA2 버튼 전체 삭제
//             - 평지/3%등판 버튼 → 헤더 도로조건 구배에 연산 연결
//             - 맞바람 버튼 → 헤더 대기환경 맞바람(rlHeadwind) 연산 연결
//             - SOC 기준/80/60/20 버튼 → 헤더 SOC(rlSocScenario) 연산 연결
//  [A27-Fix4] RA2 데이터 연산 전면 교체
//             단일 P_req_base(헤더 구배+맞바람+rho 반영)
//             단일 P_avail_soc(헤더 SOC 반영)
//             useMemo 의존성: [params,rlMassKg,rlGrade,headwind_ms,socScenario]
//  [A27-Fix5] RA2 차트 Lines: 헤더 연동 단일 커브로 단순화
//             P_req_base 이름에 구배/맞바람 동적 표시
//             P_avail_soc: SOC≠80%일 때만 표시, 헤더 색상 연동
//  [A27-Fix6] RA2 노하우 KH3 SOC 배지 → P_avail_soc + 현재 SOC 레이블로 교체
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance25 → Advance26)
//     제목: 헤더 UI 2열 재배치 + 발진 탭 노면 조건 헤더 연동
// ──────────────────────────────────────────────────────────────
//  [A26-UI1] 헤더 드라이브 모드: 1열 5버튼 → 2열 배치
//            행1: ECO / NORMAL / SPORT
//            행2: SNOW / OFF_ROAD
//  [A26-UI2] 헤더 주행 사이클: Full-Spec 추가 + 4개씩 2열 배치
//            행1: WLTP / FTP-75 / JC08 / CLTC
//            행2: EPA Hwy / EPA City / RDE / Full-Spec
//            Full-Spec 선택 시 driving_cycle_mode="csv_full" 토글
//  [A26-UI3] 헤더 도로 조건: 단일 행 → 2열 배치
//            행1: 구배 숫자입력 + -5°/0°/+3°/+8° 빠른버튼
//            행2: 건조/습윤/적설/빙판 토글 버튼 (4종)
//  [A26-UI4] 헤더 빠른 설정: 가로 flex → 3개씩 2열 그리드
//  [A26-Fix1] 발진 탭 노면 조건 버튼(건조/습윤/빙설) 삭제
//             대신 헤더 road_condition 직접 반영
//             ROAD_MU_MAP: dry=1.0, wet=0.7, snow=0.4, ice=0.3
//             현재 노면을 µ값과 함께 배지로 표시 (헤더 도로조건과 동기)
//  [A26-Fix2] 발진 탭 scenarioActive 조건: muScenario→road_cond 교체
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance24 → Advance25)
//     제목: 주행환경-열관리 연동 7개 버그·설계 문제 전면 수정
// ──────────────────────────────────────────────────────────────
//  [A25-Fix1] simDrivingCycle: T_amb_override가 thermalStep에 미반영 수정
//             기존: const p = params  (원본 참조 → T_amb_override 무시)
//             수정: const p = { ...params, T_amb }  (simWLTP와 동일 패턴)
//  [A25-Fix2] simWLTP + simDrivingCycle: coolantFlowControl 미호출 → phi_flow=1.0 고정 수정
//             수정: cool_state_w / cool_state_dc 추가, 0.5s마다 coolantFlowControl 호출,
//                   결과 phi_flow를 thermalStep 8번째 인자로 전달
//  [A25-Fix3] updateVehiclePhysics: phi_flow 계산 후 thermalStep에 미전달 수정
//             수정: thermalStep 마지막 인자로 new_cool_state.phi_flow 전달
//  [A25-Fix4] simWLTP + simDrivingCycle: hvacDetailedPower에 고정 soc_init 대신 현재 soc 전달
//             수정: p.soc_init → soc (현재 SOC → 저SOC 보호 로직 정상 작동)
//  [A25-Fix5] simWLTP + simDrivingCycle: resistanceForcesV3 T_tire 인자 개선
//             기존: thState.T_cool → 물리적으로 무관한 냉각수온으로 Crr 계산
//             수정: T_tire_sim 간이 타이어 온도 모델 (구름 마찰열 + 대류 냉각)
//  [A25-Fix6] hvacDetailedPower: 단순 선형 COP → heatPumpCOP() 카르노 기반 모델로 교체
//             냉매종류·제상손실·등엔트로피효율 반영 → 저온 구간 HVAC 에너지 정밀도 향상
//  [A25-Fix7] simWLTP + simDrivingCycle: HVAC 전류 → 배터리 내부저항 발열 Q_b_acc 환류
//             Q_hvac_batt = (P_hvac/V_dc)² × R_int(soc, T_batt) → 혹한 자기가온 정확 반영
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance16 → Advance17)
//     제목: 구동 제어 설정 첫 화면 = 운전자 입력/신호 처리 + 제어 설정 → 물리식/그래프 완전 연동
// ──────────────────────────────────────────────────────────────
//  [A17-UI1] ControlModal 초기 subTab: "traction_lut" → "sig_input"
//            구동 제어 설정 첫 화면이 '운전자 입력 / 신호 처리'로 설정됨
//  [A17-Phys1] driveModeThrottle() — app_deadzone_pct 데드존 필터 반영
//              수식: p_eff = (pedal - dz) / (1 - dz)  [pedal ≤ dz → 0]
//              LUT 경로(app_pct) + 파워함수 경로(throttle_effective) 모두 p_eff 사용
//  [A17-Phys2] simWLTP() — look-ahead feedforward 반영
//              demand += wltp_lookahead_s × (v_ref_next - v_ref)
//              ROC 제한: |demand - prev_demand| ≤ app_roc_limit/100 (1s 단위)
//  [A17-Phys3] simWLTP() — BPP 데드존 + 감지 임계값 반영
//              bpp_eff = demand - max(bpp_deadzone_pct, brake_detect_threshold)/100
//  [A17-Phys4] simDrivingCycle() — look-ahead feedforward 반영
//              lookahead_ff = wltp_lookahead_s × (v_ref_next - v_ref)
//  [A17-Phys5] simDrivingCycle() — BPP 데드존 + 감지 임계값 반영
//  [A17-Param] DEFAULT_PARAMS: 신규 5개 파라미터 추가
//              wltp_lookahead_s:1.0 / app_deadzone_pct:2 / app_roc_limit:100
//              bpp_deadzone_pct:3 / brake_detect_threshold:5
//  [A17-UI2]  sig_input 탭: APP 데드존 미리보기 차트 추가 (LineChart: RAW vs 유효 페달%)
//  [A17-UI3]  sig_input 탭: 시뮬 연동 상태 배지(✅) 전 섹션 표시
//  [A17-UI4]  sig_input 탭: WLTP look-ahead 공식 업데이트 (lookahead_s·Δv_ahead 명시)
//  [A17-UI5]  drv_mode 탭: app_deadzone_pct 중복 슬라이더 제거 (sig_input으로 통합)
//  [A17-UI6]  sig_input 탭: wltp_tracking_kp UI 기본값 수정 (0.5 → 3.0, DEFAULT_PARAMS 일치)
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance15 → Advance16)
//     제목: 구동 제어 설정 전면 재배치 — 블럭도 흐름 일치 + 탭 구조 재설계
// ──────────────────────────────────────────────────────────────
//  [A16-Tab1] 주행 전략 그룹 재구성 (5탭)
//             ① drv_mode (新): 드라이브 모드 총괄 — 5모드 파라미터 + 원페달 통합
//             ② regen_map : 회생 맵 (기존 유지)
//             ③ traction_lut : 트랙션 토크 맵 (기존 유지)
//             ④ arbitration : Arbitration/T_req (기존 유지)
//             ⑤ awd_calib : AWD 분배 (기존 유지)
//             friction_brake → calib 그룹으로 이동
//  [A16-Tab2] 모터 제어 그룹 재구성 (5탭)
//             ① torque_ref (新): 토크 참조 생성 — APP데드존 + WLTP드라이버PI 통합
//             ② speed : 속도 PI (Outer Loop, 기존 유지 / WLTP섹션 torque_ref로 이동)
//             ③ fw_mtpa : MTPA/FW (기존, 아이콘·레이블 정비)
//             ④ current : 전류 PI (기존 유지)
//             ⑤ pwm : 인버터/PWM (기존, 레이블 정비)
//             ctrl_analysis → calib 그룹으로 이동
//  [A16-Tab3] BMS 그룹 레이블·아이콘 정비
//             bms_cell: "셀/데이터수집" / bms_volt: "보호·제어 로직" (🛡️)
//  [A16-Tab4] 진단/보정 그룹 확장
//             friction_brake(🛑 유압분배) + ctrl_analysis(🎛️ 제어루프분석) 추가
//             isBranch/isAnalysis 구분선 calib 그룹 내에서 동작
//  [A16-Blk]  블럭도 tabs[] 레퍼런스 업데이트
//             drive: mode_sel → drv_mode / motor: tref → torque_ref
// ──────────────────────────────────────────────────────────────
//     제목: 제어 관계도 20% 확대 + 주행전략/모터제어/BMS 블럭도 전면 재설계
// ──────────────────────────────────────────────────────────────
//  [A16-Diag0] 파워트레인 제어 관계도 20% 확대 — viewBox 유지, 렌더 크기 754×110
//  [A16-Diag1] 주행 전략 제어 흐름 블럭도 2행 구조로 재설계
//              메인: 운전자 입력→신호처리→드라이브모드→토크맵LUT→Arbitration→토크명령출력→MCU/ECU
//              보조: 기타입력→센서인터페이스→진단/보호↔PMIC/전원↔통신모듈
//              피드백: MCU/ECU→CAN→Arbitration / 센서→신호처리
//  [A16-Diag2] 모터 제어 흐름 블럭도 2행 구조로 재설계
//              메인: VCU입력→토크참조생성→속도PI→T_req→MTPA/FW→전류PI→인버터/PWM→모터구동
//              보조: 기타입력→센서인터페이스→진단/보호↔전원관리↔통신모듈
//              피드백: 속도(엔코더/홀) / 전류(Clarke/Park→전류PI) / 온도·전압(보호 로직)
//  [A16-Diag3] BMS 제어 흐름 블럭도 2행 구조로 재설계
//              메인: 센서입력→데이터수집→상태추정→제어로직→출력제어→배터리팩/외부
//              보조: 기타입력→통신인터페이스→진단/보호↔전원관리↔MCU/Failsafe
//              피드백: 팩→센서→수집(모니터링) / 상태→제어(밸런싱) / 보호→출력(즉시차단)
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance13 → Advance14)
//     제목: 간이 파워트레인 블럭도 삽입 + 모달 탭 UX 강화
// ──────────────────────────────────────────────────────────────
//  [A14-MiniDiag] 헤더 하단 인라인 패널 ~ Nav 탭 사이에 간이 블럭도 삽입
//    · MINI_SYSTEMS: Drive Strategy / Motor Control / BMS 3개 시스템 나란히 표시
//    · 각 블럭 클릭 → openCtrlAt(group, subTab) → ControlModal 해당 탭으로 직접 오픈
//    · 시스템 레이블 클릭 → 해당 그룹 첫 탭 오픈
//    · Drive: Arb 블럭 아래 하향 분기(friction_brake) 유지
//    · 시스템 간 신호 버스 행: T_req / Vdc / I_lim / I_motor / CAN
//    · 교차 연결(INTER): drive→motor(T_req), bms→motor(Vdc), bms→drive(I_lim dashed)
//  [A14-Props]  ControlModal에 initialGroup / initialSubTab prop 추가
//    · show 변경 시 useEffect로 지정 그룹/탭 자동 이동
//  [A14-State]  메인 앱에 ctrlInitGroup / ctrlInitSubTab 상태 추가
//    · openCtrlAt(group, subTab) 헬퍼 함수로 일괄 처리
//  [A14-TabBar] Level-2 서브탭 바 개선
//    · 흐름 순서 번호 표시 (1. 2. 3. ...)
//    · 분기 탭(friction_brake) / 분석 탭(ctrl_analysis) 앞에 구분선 삽입
//    · 분기/분석 탭은 색상으로 시각적 구별
//  [A14-L1Bar]  Level-1 그룹 탭 상단에 시스템 흐름 미니 표시
//    · 🎮 주행 전략 → ⚡ 모터 제어 → 🔋 BMS | 🔬 진단/보정
//    · 현재 선택 그룹 강조 (opacity + fontWeight)
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance12 → Advance13)
//     제목: 주행 전략 블럭도 구조 수정 — T_req 분기 노드 + 탭 순서 재조정
// ──────────────────────────────────────────────────────────────
//  [A13-Diag1] 블럭도 driveBlocks 재설계
//              · limit(⚠️) + arb(⚖️) 중복 블럭 → Arbitration 단일 블럭으로 통합
//              · out(T_req) 독립 블럭 제거 → Arbitration 블럭 outputLabel 배지로 표기
//              · friction_brake 직렬 체인 이탈 → Arbitration 하향 분기 블럭으로 변경
//              · mainBlocks: input→mode→lut→arb(▶T_req)→awd
//              · branchBlock: friction_brake (arb 아래 수직 연결)
//  [A13-Diag2] 경로 범례 3개: 트랙션 / 회생 / 분기(T_req→유압)
//  [A13-Diag3] Arbitration 블럭 sub-text: "BMS·온도·슬립 → T_req" (역할 명확화)
//  [A13-Tab1]  drive 탭 순서 재조정
//              메인 경로: regen_map → traction_lut → arbitration → awd_calib
//              분기 경로: friction_brake (마지막 위치, 메인 흐름과 분리)
//              arbitration 탭 레이블: "Arbitration / T_req" (T_req 산출 역할 명시)
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance11 → Advance12)
//     제목: 인버터 제어기&BMS 설정 전면 구조 개편 — 블럭도 흐름 정합 + 설정 보완
// ──────────────────────────────────────────────────────────────
//  [A12-Name1] 헤더 버튼 "제어기/BMS" → "구동 제어" / "Drive Control"
//  [A12-Name2] 모달 제목 "인버터 제어기 & BMS 설정" → "파워트레인 제어 파라미터" / "Powertrain Control Parameters"
//  [A12-Struct1] CTRL_GROUPS: brake 그룹 해체 — arbitration 탭을 drive 그룹으로 통합
//  [A12-Struct2] drive 그룹 탭 순서 블럭도 일치: regen_map→traction_lut→arbitration→friction_brake→awd_calib
//  [A12-Struct3] calib 그룹 label: "모델 보정" → "진단/보정" (TCS/ESC 제거 후 성격 명확화)
//  [A12-Drive1] traction_lut: inverter_delay_ms + current_loop_bw_hz 제거 (pwm/current 탭으로 이동)
//               APP 데드존 (app_deadzone_pct) 신규 추가 — 페달 무반응 영역 보정
//  [A12-Drive2] arbitration: TCS/ESC 슬립 제어 섹션 추가 (tire_lateral에서 이동)
//               tcs_enable/esc_enable 토글 + TCS슬립·토크저감·ESC 요레이트·제동·토크컷 파라미터
//  [A12-Motor1] pwm: 인버터 지연 & 데드타임 보상 섹션 신규 추가
//               inverter_delay_ms (traction_lut에서 이동) + current_loop_bw_hz (이동)
//               dead_time_comp_gain + dead_time_comp_i_thr 신규 파라미터
//  [A12-Motor2] speed: WLTP/FTP 드라이버 PI 섹션 신규 추가
//               wltp_tracking_kp + wltp_tracking_ki + wltp_lookahead_s 파라미터 노출
//  [A12-BMS1]   bms_volt: bms_balance_threshold 제거 (bms_cell로 이동)
//               밸런싱 분석 패널 제거 (bms_cell로 이동)
//               충전 프로파일 CC/CV 섹션 신규 추가: bms_cc_cv_soc · bms_cv_taper_rate · bms_chg_end_current · bms_dcfc_max_soc
//  [A12-BMS2]   bms_cell: 셀 밸런싱 설정 섹션 신규 추가
//               bms_balance_strategy 선택 (Passive/Active) + bms_balance_threshold 이동
//               bms_R_bal · bms_I_bal_active 파라미터 + 밸런싱 시뮬레이션 패널 이동
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance10 → Advance11)
//     제목: 블럭도 정합성 수정 — 주행전략·모터제어·BMS 8개 항목
// ──────────────────────────────────────────────────────────────
//  [A11-Drive1] 주행전략 블럭도: AWD 분배 블럭 추가 (T_req 뒤 마지막 위치, tabs:["awd_calib"])
//  [A11-Drive2] 주행전략 블럭도: Drive Mode → regen_map 전용 / LUT → traction_lut 전용 (탭 분리)
//               회생·트랙션 병렬 경로 안내 레이블 추가
//  [A11-Drive3] 주행전략 블럭도: friction_brake → limit 블럭에서 제거, Arbitration 블럭에 통합
//  [A11-Motor1] 모터제어 블럭도: 운전자입력·토크명령 dead block(tabs:[]) 제거
//  [A11-Motor2] 모터제어 블럭도: T_req sub-text "드라이브 모드+회생 맵" → "Speed PI 출력 토크 지령"
//               T_req tabs: ["speed","fw_mtpa"] → ["speed"] 단독
//               MTPA/FW tabs: ["fw_mtpa"] 단독 유지 (경계 명확화)
//               ctrl_analysis 블럭 신규 추가 (맨 뒤, tabs:["ctrl_analysis"])
//  [A11-Motor3] 모터제어 블럭도: Id 피드백 루프 추가 (모터 → 전류센서 → d축 PI)
//  [A11-BMS1]   BMS 블럭도: bms_volt 중복 매핑 수정
//               센서입력 tabs:[] / 데이터수집 tabs:["bms_cell"] / 보호·제어 tabs:["bms_volt"]
//  [A11-BMS2]   BMS 블럭도: A09 탭 순서(셀→SOC→칼만→전압보호)와 블럭도 매핑 일치
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance06 → Advance07)
//     제목: 트랙션/회생 모드별 분리 · Arbitration 탭 이동 · 회생 맵 구조 개편
// ──────────────────────────────────────────────────────────────
//  [A07-UI1] 트랙션 토크 맵: 프리셋 → 현재 선택 모드 전용 초기화 버튼으로 교체
//            페달 응답·토크 응답성 → 선택 모드 단일 카드로 변경 (모든 모드 동시 표시 제거)
//  [A07-UI2] 트랙션 토크 맵: 원페달 드라이빙 회생 레벨 섹션 삭제
//  [A07-UI3] 회생 맵 구조 개편 (모드별 탭 + 심플/LUT 통합)
//            · 모드 탭(ECO/NORMAL/SPORT/SNOW/OFF_ROAD) 추가 (selRegenMode 상태)
//            · 회생 강도 슬라이더: 값을 오른쪽 뱃지 → 왼쪽 레이블 인라인 표기로 이동
//            · 저속 페이드·최대 감속도·고속 블렌딩·고속 비율 → 모드별 패널로 통합
//            · 4-버튼 프리셋 → 모드별 회생 설정 초기화 버튼으로 교체
//            · 모드별 회생 설정 요약 패널 추가
//  [A07-UI4] Arbitration 탭: 모델 보정 왼쪽으로 이동 (brake 그룹 → BMS 다음)
//            라벨 한국어 표기: "조율" / 영어: "Arbitration"
// §  CHANGELOG  (Advance05 → Advance06)
//     제목: 트랙션 탭 모드 셀렉터 통합 · 원페달 이동 · 제동분배 이동 · Arbitration 검토
// ──────────────────────────────────────────────────────────────
//  [A06-UI1] 응답감도 + 토크응답성 → 모드 선택 카드 안에 2행 레이아웃으로 통합
//  [A06-UI2] traction_lut_enable 기본값 false→true
//  [A06-UI3] 원페달 활성화 스위치 → 차량 정보 차량 제원(spec) 탭으로 이전
//            원페달 회생 레벨 → 회생 맵 심플 모드 저속 페이드 왼쪽으로 이전
//            트랙션 탭 원페달 섹션은 레벨 슬라이더만 남기고 스위치 제거
//  [A06-UI4] 회생 맵 공통 regen_strength 패널: LUT 캘리브레이션 모드일 때 숨김
//            심플 모드에서만 표시하도록 !isLUT 조건 추가
//  [A06-UI5] friction_brake 탭 → 주행 전략 그룹으로 이동
//            제동 캘리브레이션 그룹에서 제거, brake 그룹 탭이 arbitration만 남음
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance04 → Advance05)
//     제목: 드라이브 모드 탭 통합 · LUT pkScale 자동산출 · 유효성검사
// ──────────────────────────────────────────────────────────────
//  [A05-UI1] 헤더 Grade/Road/T/Alt 중복 텍스트 삭제 (인라인 패널 중복)
//  [A05-UI2] 드라이브 모드 탭 삭제 → 트랙션 토크 맵 탭으로 통합
//           · 응답감도 + max_torque_ratio → LUT ON 시 greyed-out
//           · 원페달 드라이빙 → 트랙션 탭 (spec 탭 중복 제거)
//           · 토크 응답성 → 트랙션 탭
//  [A05-UI3] 회생 강도(regen_strength) → 회생 맵 탭 5모드 편집 패널로 이전
//  [BugFix-A05-01] getLUTPkScale() 헬퍼 추가
//           - pkScale 산출 5개소 (rlCalcVmax/rlCalcVmaxTorque/rlBuildR1/rlBuildR4/R0) 통일
//           - traction_lut_enable=true 시 max(LUT[APP=100%]) / T_max_motor 자동 산출
//           - false 시 기존 *_max_torque_ratio 사용 (폴백 유지)
//  [BugFix-A05-02] validateTractionLUT() 유효성 검사 함수 추가
//           - 5개 조건 검사: APP=0%행 0 / 최대셀>0 / 행단조감소 / 열단조증가 / T_max×1.5
//           - LUT 편집기에 실시간 경고/오류 배너 + 재설정 버튼
//           - pkScale 자동 산출값을 UI에 표시 (성능 그래프 반영 확인)
//  [BugFix-A05-03] rlBuildR1 thr_clip 클리핑 버그 수정
//           - 기존: Math.min(1.0, thr) → LUT 편집 시 성능 그래프 불일치
//           - 수정: Math.min(pkScale_r1 + 0.05, thr) → LUT 변경 즉시 반영
//  [BugFix-A05-04] simReaccel normal_regen_strength 하드코딩 → driveModeRegenStrength()
// ──────────────────────────────────────────────────────────────
// §  BUGFIX CHANGELOG  (Advance03 → Advance04)
//     제목: 트랙션LUT v_kmh 수정 · 회생맵 전시뮬 적용 · AWD LUT 조건 독립화
// ──────────────────────────────────────────────────────────────
//  [BugFix-A04-01] driveModeThrottle v_kmh 미전달 수정 (전체 6개소)
//           - simLaunch: mode_throttle_fixed & PID 루프 내 v*3.6 전달
//           - simReaccel: mode_throttle_fixed_ra & PID 루프 내 v*3.6 전달
//           - simDrivingCycle: demand 경로 v*3.6 전달
//           - simWLTP: raw throttle → driveModeThrottle(throttle_raw, p, v*3.6) 경유로 교체
//           → LUT 2D 보간이 항상 현재 차속 기준으로 작동하게 됨 (기존: v=0 고정)
//
//  [BugFix-A04-02] 회생 맵 LUT (regen_calib_enable) 전시뮬 적용
//           - simLaunch: is_regen 분기 → regen_calib_enable 시 regenMapDemand 우선 적용
//           - stepVehiclePhysics: 동일 패턴 적용 (simReaccel 경유)
//           - simDrivingCycle: 감속 분기 → regen_calib_enable 시 regenMapDemand 적용
//           - simWLTP: 감속 분기 → regen_calib_enable 시 regenMapDemand 적용
//           → regen_calib_enable=true 상태에서 코스팅/원페달 LUT 전시뮬에 반영
//
//  [BugFix-A04-03] AWD LUT 조건 torque_vectoring_enable 종속 해제
//           - simLaunch / simReaccel / stepVehiclePhysics 3개소
//           - 기존: (torque_vectoring_enable && awd_enable)
//           - 수정: ((awd_calib_enable || torque_vectoring_enable) && awd_enable)
//           → awd_calib_enable=true 단독으로도 torqueVectoring() LUT 진입 가능
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance02 → Advance03)
//     제목: AWD 분배 모드별 독립 LUT — 트랙션 토크맵 연동 편집기
// ──────────────────────────────────────────────────────────────
//  [A03-01] DEFAULT_PARAMS: 모드별 AWD 전륜 비율 LUT 5종 추가
//           - awd_front_lut_eco:     0:100~20:80 후륜 중심 (효율 우선, F=μN)
//           - awd_front_lut_normal:  40:60~50:50 균형 (yaw rate 안정)
//           - awd_front_lut_sport:   20:80~30:70 후륜 중심 (M=F×r 코너링)
//           - awd_front_lut_snow:    55:45~60:40 전륜 중심 (μ 낮은 노면 직진성)
//           - awd_front_lut_offroad: 50:50 + 저속 벡터링 (크로스 액슬 가상 락커)
//           - awd_mode_lut_enable: true — 드라이브 모드 전환 시 LUT 자동 교체
//           - awd_mode_override: 전 모드 null — LUT 추종 기본
//  [A03-02] torqueVectoring(): 모드별 LUT 자동 선택 로직
//           - awd_mode_lut_enable=true → modeLutKey[mode] 우선 → 없으면 awd_front_lut
//           - 고정 override(숫자) → LUT 무시
//  [A03-03] AWD 분배 탭 UI 전면 재설계
//           - 5모드 탭 (ECO/NORMAL/SPORT/SNOW/OFF_ROAD) 전환 → 모드별 LUT 독립 편집
//           - 트랙션 토크맵 참조 패널: 동일 모드 APP100% 행 N·m값 실시간 표시
//           - 테이블 셀 하단 소자: 동일 위치 트랙션 토크(N·m) 참조
//           - 셀 배경색: 전후 비율(파랑↔초록↔빨강) × 트랙션 강도(투명도)
//           - 선택 모드 곡선 차트 + 5모드 비교 차트(토크40% 기준선) 동시 표시
//           - 프리셋 복원(모드별) + 전체 초기화 버튼
//           - selAwdMode 상태: React 훅 규칙 준수 (컴포넌트 레벨 선언)
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance01 → Advance02)
//     제목: EXPERT→OFF_ROAD 변경 · 트랙션LUT N·m화 · 토크응답 · AWD 초기값 물리적 설정
// ──────────────────────────────────────────────────────────────
//  [A02-01] EXPERT 드라이브 모드 → OFF_ROAD (🗻) 전면 교체
//           - 모든 파라미터 키: expert_* → offroad_*
//           - 아이콘 🏁 → 🗻 / 초기값 재설정 (sens 0.75, ratio 1.0, regen 0.6)
//  [A02-02] 트랙션 토크맵 LUT: 배율[0~1.25] → 목표 토크 (N·m)
//           - T_max_motor=400N·m 기준 변환 (ECO 최대280, NORMAL 400, SPORT 460, SNOW 220, OFF_ROAD 400)
//           - driveModeThrottle() LUT 경로: T_req_Nm / T_max_motor → throttle ratio 반환
//           - 차트 Y축 "배율" → "N·m" 단위로 표시
//  [BugFix-A02-01] motorTorqueSpeed() throttle 클램프 수정
//           - 기존: Math.min(1, throttle) → SPORT(1.15)/OFF_ROAD(1.0) 고배율 완전 무효화
//           - 수정: Math.min(modeMaxRatio+0.05, throttle) → 모드별 최대 배율 실효화
//  [BugFix-A02-02] pkScale 함수(rlCalcVmax/rlBuildR1/rlBuildR4/R0) SNOW·OFF_ROAD 추가
//           - 기존: ECO/SPORT/NORMAL 3종만 → SNOW(0.55)/OFF_ROAD(1.0) 미반영 버그
//  [A02-03] 토크 응답성 파라미터 추가 (업계 표준 PMSM/SiC 인버터 기반)
//           - tau_torque_{eco/normal/sport/snow/offroad}: 1차 LPF 시상수 [s]
//           - torque_slew_rate_{eco/normal/sport/snow/offroad}: 슬루레이트 [N·m/s]
//           - inverter_delay_ms, current_loop_bw_hz: 인버터 지연·전류루프 BW
//           - simLaunch/simReaccel 물리 루프 실제 반영 (1차LPF + slew rate 클램프)
//           - 제어기 설정 > 드라이브 모드 탭에 토크 응답성 UI 패널 추가
//  [A02-04] AWD 모드별 초기값 물리적 근거 기반 설정
//           - ECO: 0.2 (후륜 80%, 효율 우선 — F=μN 후륜 집중)
//           - NORMAL: null + LUT 40:60~50:50 (yaw rate 안정화)
//           - SPORT: null + LUT 후륜 바이어스 30:70~20:80 (코너링 M=F×r 극대화)
//           - SNOW: 0.55 (앞륜 55%, 직진성 확보 — μ 낮은 노면 언더스티어 유도)
//           - OFF_ROAD: null (50:50 동적 + 토크벡터링 — 크로스 액슬 가상 락커)
//           - NORMAL awd_front_lut 재설계 (40:60→50:50 균형 범위)
//           - AWD UI에 모드별 물리적 설명 및 추천 배지 추가
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_28 → Advance21_29)  ← 원페달 제원 탭 통합 + EnvModal 레이아웃 개선
// ──────────────────────────────────────────────────────────────
//  [A29-01] 원페달 드라이빙 선택 기능을 VehicleInfoModal 차량 제원(spec) 탭에 추가:
//           - one_pedal_enable ON/OFF 버튼 + 상태 설명 텍스트
//           - regen_one_pedal_level 슬라이더 (0.1~1.0, step 0.05)
//           - 차량별 독립 설정 (editTarget A/B 분리)
//  [A29-02] 메인 UI 설정 버튼 행(차량정보/주행환경/제어기BMS/...) 바로 아래 인라인 환경 패널 추가:
//           - 도로 조건: 구배(°) 입력 + 노면 상태 셀렉트
//           - 대기 환경: 기온(°C) · 고도(m) · 공기밀도(ρ) 인라인 입력
//           - 빠른 설정: 6개 프리셋 토글 버튼 (현재 활성 프리셋 하이라이트)
//           - 모달 없이 메인 화면에서 바로 환경 조건 변경 가능
//           - EnvModal은 드라이빙 사이클/상세 설정용으로 유지
//  [A29-03] Tesla Model 3 LR AWD 프리셋 원페달 기본 ON 설정:
//           - one_pedal_enable: true (Tesla Model 3 실제 사양 반영)
//           - regen_one_pedal_level: 0.5 (Tesla 기본 50% 회생 강도)
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_34 → Advance21_35)
//     제목: 제어기 설정 초기 탭 변경 + 제어 루프 탭 목적 가이드 추가
// ──────────────────────────────────────────────────────────────
//  [A35-01] ControlModal 초기 탭 변경
//           · group: "motor" → "drive" (주행 전략 탭이 먼저 보임)
//           · subTab: "current" → "drive_mode"
//
//  [A35-02] 제어 루프 분석 탭 목적 설명 배너 전면 교체 (14개 탭 전부)
//           · 다중 루프 계층 → 분리비(≥5×) 만족 여부 확인
//           · 제어 합성(종합) → 전체 종합 → 점수 → 추천 Kp/Ki → 적용 안내
//           · Nyquist → PM·GM 개루프 안정성 여유 정량화
//           · 근궤적 → Kp 증가에 따른 폐루프 극점 이동
//           · 이산(Z-Domain) → 단위원 내부 극점 확인
//           · Anti-windup → 오버슈트·정착시간 비교
//           · 자동 PI 튜닝 → IMC/ZN/SIMC/Cohen-Coon 추천
//           · 게인 스케줄링 → 속도·부하·온도 구간별 최적 게인
//           · 3D 토크맵 → T(RPM, Iq) WebGL 서피스
//           · MRAC → MIT 규칙 적응 게인 시뮬
//           · H∞ → 혼합 감도 강인성 정량화
//           · MPC → 후퇴 지평 속도 추종 QP
//           · d↔q 교차커플링 → 분리 보상 필요 구간 확인
//           · 스키드패드 → 타이어 포화·언더스티어 구배
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_33 → Advance21_34)
//     제목: 제어 루프 — 2단 카테고리 네비게이션 + Control Synthesis 탭
// ──────────────────────────────────────────────────────────────
//  [A34-01] SUB_TABS → SUB_GROUPS 2단 계층 구조 (6개 카테고리)
//           · 루프 구조 / 안정성 분석 / 설계·튜닝 / 고급 제어 / 모터 특성 / 차량 동역학
//           · Level-1: 카테고리 탭바 (활성 카테고리 하이라이트)
//           · Level-2: 해당 카테고리 서브탭 (좌측 컬러 보더)
//           · handleSubGroupChange() 로 카테고리 전환 시 첫 서브탭 자동 포커스
//
//  [A34-02] computeControlSynthesis() 신규 함수
//           · 입력: cascade / nyquist / autotuning / antiwindup / zdomain / gainSched
//           · 판정 항목: 전류PM / 속도PM / 분리비 / Z안정성 / Anti-WU오버슈트 / GS최소PM
//           · 신호등(🟢🟡🔴) + 상세 설명 per 항목
//           · 추천 Kp_iq/Ki_iq/Kp_spd/Ki_spd (IMC×0.6 + SIMC×0.4 가중 평균, PM 보정)
//           · 종합 신뢰도 점수 0~100점
//
//  [A34-03] 'synthesis' 서브탭 렌더링 추가 (루프 구조 카테고리 내)
//           · 원형 종합 점수 + 적용 권장/주의/문제 레이블
//           · 설계 기준 체크리스트 카드 (6항목)
//           · 현재 설정 vs 추천값 Before/After 비교표 (Δ% 표시)
//           · 실제 적용 방법 안내 패널
//           · 5개 KPI 요약 카드 (하단)
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_32 → Advance21_33)
//     제목: 제어 루프 — 게인 스케줄링 탭 추가
// ──────────────────────────────────────────────────────────────
//  [A33-01] computeGainScheduling(params) 함수 신규 추가
//           · 스케줄링 축 3종: RPM / 토크(부하) / 모터 온도
//           · RPM 축 (6구간): Lq 자속 포화 근사 (0.72~1.00×)
//           · 토크 축 (5구간): 부하 증가 시 Rs↑, Lq↓ 모델링
//           · 온도 축 (7구간): Rs(T)=Rs₀·[1+α·(T-T_ref)], λpm(T) 변화 반영
//           · IMC 기반 Kp_i/Ki_i/Kp_s/Ki_s + BW/PM/분리비 산출
//
//  [A33-02] ControlLoopTabContent — 'gainschedule' 서브탭 추가
//           · gsAxis state (rpm | torque | temp) 축 전환
//           · gainSchedData useMemo 추가 [displayParams 의존]
//           · 3종 차트: Kp 비교 / Ki 비교 / BW·PM 추이
//           · 구간별 상세 테이블: 공칭 대비 Δ%, PM 색상, 분리비, OK/검토 배지
//           · 설계 가이드 패널 (Bumpless Transfer 요령 포함)
// ──────────────────────────────────────────────────────────────
// §  BUG FIX CHANGELOG  (Advance21_32 → Advance21_32 rev.1)
// ──────────────────────────────────────────────────────────────
//  [A32-BUG1] autotuning useMemo: tuneMethod 의존성 배열 누락
//             → tuneMethod 전환 시 차트 미갱신 → 의존성 추가
//  [A32-BUG2] computeNyquistData: wc_est=0 (Kp=0 or L=0) → NaN
//             → early return + w 시작값 Math.max(…,1e-6) 가드
//  [A32-BUG3] computeRootLocus speed: Math.acos 범위 초과 → NaN
//             → Math.max(-1, Math.min(1, …)) 클램프 추가
//  [A32-BUG4] simAntiWindup: tau = J/Kp → Kp=0 시 Infinity
//             → Math.max(Kp, 1e-6) 가드
//  [A32-BUG5] computeAutoTuning Cohen-Coon: r_cc = theta/tau_p → tau_p≈0 overflow
//             → Math.max(tau_p, 1e-9) 가드
//  [A32-BUG6] computeDQCoupling: Lq=Ld (SPM) 시 분모=0 → Infinity
//             → |Lq-Ld|<1e-9 이면 Id_op=0 (SPM MTPA: Id 불필요)
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_31 → Advance21_32)  ← ControlModal 2-Level 탭 구조
// ──────────────────────────────────────────────────────────────
//  [A32-01] ControlModal 서브탭 구조 리팩토링:
//           단일 13개 플랫 리스트 → 4개 그룹 + 하위 서브탭 2-Level 구조
//
//           그룹 1  🎮 주행 전략 (Driving Strategy)
//             · 드라이브 모드 / 회생 맵
//           그룹 2  ⚡ 모터 제어 (Motor Control)
//             · 전류 제어기 / 속도 제어기 / FW·MTPA / PWM·스위칭 / 제어 루프 분석
//           그룹 3  🔋 BMS
//             · 전압/전류 / SOC·온도 / 셀 구성 / 칼만 필터
//           그룹 4  🔬 모델 보정 (Model Calibration)
//             · 물리식 보정 / 타이어·횡력
//
//           변경 내역:
//           - useState("current") → useState("motor") + useState("current") 분리
//           - subTabs 단일 배열 → CTRL_GROUPS 계층 구조로 교체
//           - handleGroupChange(): 그룹 전환 시 첫 번째 서브탭 자동 이동
//           - UI: Level-1 탭(그룹) → Level-2 탭(세부) 2단 탭 바 렌더링
//           - Level-1 활성 그룹 색상으로 Level-2 좌측 보더 강조
//           - 콘텐츠 렌더링(subTab ===) 코드 변경 없음 — 호환성 유지
//
//  [A32-02] 전력·에너지 > 제어 루프 탭 제거 (A31에서 이미 삭제됨, A32 계승)
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_30 → Advance21_31)  ← 설정 패널 → 그래프 반영 강화
// ──────────────────────────────────────────────────────────────
//  [A31-FIX-1] Battery Health B1 SOH 추이 그래프:
//              T_amb 하드코딩(T25=298.15K, T45=318.15K) 제거
//              → params.T_amb 기반 Arrhenius k_cal_op / k_cyc_op 동적 산출
//              → 설정 기온 기준 SOH 실선 + 25°C 비교선(T_amb≠25시) 추가
//  [A31-FIX-2] Battery Health B3 Cycle Life 차트:
//              고정 temps=[25,35,45,55,60] → params.T_amb 중심 ±20°C 5점 동적 스윕
//              → 차트 내 현재 T_amb ReferenceLine(등황색 점선) 삽입
//  [A31-FIX-3] DOE evaluateMetric:
//              simLaunch grade=0 하드코딩 → params.grade_deg 동적 반영
//              (time_0_100, peak_temp_motor 두 케이스 동시 수정)
//  [A31-FIX-4] AdvancedFinalTabContent rlData Road Load 곡선:
//              params.rho_air → params.rho 우선 참조, 키 이름 불일치 해소
//  [A31-FIX-5] AdvancedFinalTabContent BT-B2 Ri vs 온도 차트:
//              현재 T_amb ReferenceLine(등황색 점선) 추가
//  [A31-FIX-6] DCTabContent WLTP 온도 민감도 차트:
//              고정 x=25 기준선 → x=params.T_amb 동적 기준선으로 교체
//              T_amb≠25 시 25°C 보조 기준선(회색 반투명) 병행 표시
//  [A31-FIX-7] rolloverSimulation 전복 임계 횡가속도:
//              a_y_crit = SSF×g → min(SSF×g×cos(grade), μ_road×g×cos(grade))
//              road_condition μ + grade_deg 동시 반영
//              4WS Rollover 탭 UI에 노면·구배 영향 배지 패널 추가
//  [A31-FIX-8] Sensitivity SV-S1 Tornado 차트:
//              drive_mode별 민감도 스케일 계수 적용 (ECO×1.20 / SPORT×0.88)
//              차트 상단 drive_mode 선택 배지 + 스케일 설명 추가
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_29 → Advance21_30)
// ──────────────────────────────────────────────────────────────
//  [A30-01] Functional Safety (HARA) 탭 전체 한/영 i18n 완성:
//           - FAULT_MODES 전체 20개: nameEn, categoryEn, mechEn, safety_goalEn, fsrEn, tsrEn 추가
//           - HARATabContent 헤더·버튼·네비게이션·배지 모두 lang 조건부
//           - Hazard List 테이블: 위험 명칭, 카테고리, 필터 레이블, 시뮬 버튼 영문화
//           - 상세 패널: 고장 메커니즘·안전 목표·발생 확률 영문화
//           - ASIL Matrix: 해저드 레이블, S-severity 레이블(Minor/Serious/Fatal) 영문화
//           - Safety Goal / FSR / FTTI / TSR / SS / Fault Injection / ASIL Re-check 전 섹션 영문화
//           - generateFSR_TSR: safety_goalEn, fsrEn, tsrEn, verification lang-neutral 처리
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_26 → Advance21_27)  ← FS 노하우 i18n + 중복 제거
// ──────────────────────────────────────────────────────────────
//  [A27-01] Functional Safety 노하우 패널 중복 물리식 제거:
//           - FS RLKnowHowPanel children에서 curKH.formula 명시 블록 삭제
//           - RLKnowHowPanel 내부(activeHW.formula)에서만 1회 렌더링
//  [A27-02] FS_KHs 전체 한/영 i18n 완성:
//           - tag, title, keyPoints, actions, improvement 모두 lang 조건부
//           - formula는 이미 lang 조건부 (유지)
//           - RLInlineKpi title/items 레이블도 한/영 분기
//           - 3개 KH: ASIL D Core / Thermal Runaway Risk / Simulation Validation
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (Advance21_25 → Advance21_26)  ← 모달 i18n 완성
// ──────────────────────────────────────────────────────────────
//  [A26-01] VehicleInfoModal i18n (이전 작업)
//  [A26-02] EnvModal / ControlModal / CSVModal 한/영 i18n 완성:
//           - useLang() + e(ko,en) 헬퍼 추가
//           - 헤더 영문 소제목 제거 (4개 모달 모두) — 중복 문자 해소
//           - EnvModal: 도로조건, 대기환경, 빠른설정, 사이클선택, 버튼 전체
//           - ControlModal: 13개 서브탭, 드라이브모드/회생/BMS/전류·속도 제어기
//           - CSVModal: 헤더, 데이터셋 레이블, 카운터, 다운로드 버튼, 힌트
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v01 → v02)
//  [R-01] 모든 "삭제 →" 주석 스텁 정리: 구형 함수 호출 경로 제거
//  [R-02] initKalmanSOC + kalmanUpdateSOC → kalmanSOCUpdate 바로 위로 이동 (논리적 그룹화)
//  [R-03] CSV parseFullSpecCycleCSV: 열 이름 기반 유연 파싱 + 상세 검증 메시지 추가
//  [R-04] Monte Carlo WebWorker 래퍼 추가 (simMonteCarlo_Worker blob URL 방식)
//  [R-05] Global UI 컴포넌트 섹션 헤더 명확화 — §UI 마커 추가
//  [R-06] computeEnergyChain 내 computeBatteryLosses → 인라인 Ri 모델 통합 확인
//  [R-07] simLaunch per-wheel TV 토크 로깅 완성 (T_FL/T_FR/T_RL/T_RR 데이터 포함)
//  [R-08] DEFAULT_PARAMS: v7meta 초기값 추가 (IRI, tire_pressure_actual 등)
// ──────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v02 → v03)
//  [R-09] auxPowerModel wrapper 제거: hvacDetailedPower 직접 호출로 통합 (5곳)
//  [R-10] Performance Radar 레이아웃: 항목 정보 → 그래프 오른쪽으로 이동 (VC-V1, F1)
//  [R-11] Drivability 섹션 위치 이동: Road Load 다음 → Regen Brake 다음으로
//  [R-12] i18n 완성: 분석 노하우 섹션 제목·항목·축 라벨 EN 대응 (C1/C2·RB1-3·RA1-2·L1/L2·HARA·B1-4·RoadEnv)
// ──────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v03 → v04)  ← 도메인 통합 / GAP 해소
//  [R-13] thermalStep 고도화: coolantFlowControl.phi_flow → UA_rad 가중치 반영
//          (냉각수 유량 증가 시 방열 성능 향상 피드백 완전 연동)
//  [R-14] simLaunch GAP-01 해소: kalmanSOCUpdate 루프 연동
//          (2RC V_terminal 기반 EKF/UKF SOC 추정 → regen·derate 피드백)
//  [R-15] simLaunch GAP-07 해소: bmsStep 루프 연동 (bms_detailed_enable 게이팅)
//          셀 전압 편차 → SOC 추정 보정, 밸런싱 에너지 손실 계상
//  [R-16] simReaccel 완전 패리티 달성:
//          kalmanSOCUpdate + coolantFlowControl + sei_state + bmsStep 추가
//          (simLaunch v04와 동일 물리 체인 보장)
//  [R-17] simLaunch / simReaccel 데이터 로그 확장:
//          kalman_soc, bms_max_dv, cool_phi_flow 필드 추가
//  [R-18] 구형 삭제 스텁 주석 완전 제거:
//          batteryOCV 구형 주석, battery2RCModel 구형 주석,
//          dualMotorAWDDistribution 구형 주석,
//          dynamicTorqueDistribution 구형 주석,
//          computeVerticalLoad 구형 주석, resistanceForces 구형 주석 제거
//  [R-19] init3DThermalModel undefined 참조 → init3DThermalState 로 수정
//          (simLaunch params.thermal_3d_enable 게이팅 블록 정상화)
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v04 → v05)  ← 렌더링 버그 수정 · 미연동 기능 연동
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v05 → v06)  ← 함수 통합 · 미연동 연동 · 불필요 삭제
//  [R-06-A] gearLossTemp ← gearLossTempEnhanced 통합 (단일 canonical 함수)
//           computeGearLosses 제거 → computeEnergyChain은 gearLossTemp 사용
//  [R-06-B] pacejkaLateralFull_v3 ← pacejkaLateralForce 통합
//           lateralDynamicsStep → pacejkaLateralFull_v3 사용
//  [R-06-C] updateTireTemperatureEnhanced → canonical, 구형 제거
//           simLaunch tire_RL 상태 실제 연동 (updateTireTemperatureEnhanced)
//  [R-06-D] suspensionStep → stepVehiclePhysics + simLaunch 연동
//           Fz_front/Fz_rear → lateralDynamicsStep 피드백 완성
//  [R-06-E] thermalDeratingFactor → simLaunch 토크 한계 적용
//  [R-06-F] deadtimeCompensation → simLaunch/simReaccel FOC PI 경로 연동
//  [R-06-G] liPlatingDetect → simLaunch/simReaccel 회생 구간 연동
//  [R-06-H] 미사용 함수 삭제 (13개):
//           combinedSlipCorrection, initLateralState, estimateMuHat,
//           motorEfficiency, computeTorqueFromIq, gradeProfile,
//           sobolSensitivityIndex, compareReaccelWithCSV, analyzeBMSBalance,
//           hybridSensorless, sensorlessControl, update3DThermalDistribution,
//           harmonicCurrentControl
//  [R-05-fix1] HARATabContent lang ReferenceError 해소:
//              const lang = useLang() 추가 → Functional Safety 탭 정상 렌더링
//  [R-05-fix2] 반복제어(RC) 토크리플 보상 simLaunch/simReaccel 루프 연동:
//              params.rc_ripple_enable(기본 false) 게이팅
//              FOC PI 경로에 repetitiveController + harmonicCompensation 삽입
//              rep_d_state / rep_q_state 루프 상태 유지
//  [R-05-fix3] simLaunch 회생 경로 HVAC P_aux 연동:
//              computeRegenChain P_aux=0 → hvacDetailedPower 별도 계산 후 P_elec에 합산
//              회생 중 에어컨/난방 부하가 배터리 SOC에 정확히 반영됨
// ──────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v10_BugFix → v12)  ← 가변CdA 연동 · 런치컨트롤 · 스키드패드 · 회생최적화 · 고급제어
// ──────────────────────────────────────────────────────────────────────────────
//  [R-12-01] 가변 CdA (variableCdA) simLaunch / simReaccel 완전 연동
//            - 루프 내 res.CdA_v3 → params.CdA_v3 갱신 (매 스텝 그릴셔터 반영)
//            - 데이터 로그: CdA_v3, shutter_open 필드 추가
//            - DEFAULT_PARAMS: grille_shutter_enable, CdA_closed, CdA_open, CdA_speed_coeff 초기값 명시
//  [R-12-02] Launch Control 알고리즘
//            - launchControlTorque(): 슬립률 기반 토크 램프 + 최적 μ 추적 + 반응시간 모델
//            - DEFAULT_PARAMS: launch_control_enable, lc_slip_target, lc_torque_ramp_rate
//            - simLaunch 루프 연동: launch_control_enable 게이팅
//  [R-12-03] 스키드 패드 정상 선회 시뮬 (simSkidPad)
//            - simSkidPad(): 정상 원운동 횡력 · 요 레이트 · 타이어 슬립각 · g-값 완전 계산
//            - Pacejka 비선형 횡력 연동 (pacejkaLateralFull_v3)
//            - SkidPadTabContent: 반지름/속도 스위프 그래프, 최대 횡G 예측, 언더스티어 맵
//  [R-12-04] 제동 에너지 회수 최적화 (regenEnergyOptimizer)
//            - regenEnergyOptimizer(): 속도·SOC·온도·슬립 기반 최적 회생 토크 동적 산출
//            - 마찰제동 vs 회생제동 분배 최적화 (에너지 최대 회수 목적함수)
//            - DEFAULT_PARAMS: regen_optimizer_enable, regen_opt_weight_energy, regen_opt_weight_comfort
//  [R-12-05] 위치 루프 독립 제어기 파라미터 (Cascade 개선)
//            - DEFAULT_PARAMS: Kp_pos, Ki_pos, Kd_pos 독립 추가
//            - computeCascadeAnalysis() 위치 루프 PID 완전 계산 (Ki_pos, Kd_pos 반영)
//            - 위치 루프 전달함수 3차 이상 정확 계산
//  [R-12-06] 적응 제어 (MRAC — Model Reference Adaptive Control)
//            - computeMRAC(): MIT 규칙 기반 적응 게인 Kp(t), Kr(t) 시뮬
//            - 기준 모델 응답 vs 플랜트 응답 오차 → 적응 법칙
//  [R-12-07] 강인 제어 (H∞ — H-infinity Robust Control)
//            - computeHinf(): H∞ 노름 계산 · 불확실성 한계 ΔP 대비 안정 여유
//            - μ-synthesis 근사 (S/T/KS 혼합 감도 가중치)
//  [R-12-08] 예측 제어 (MPC — Model Predictive Control)
//            - computeMPC(): 예측 지평 N 스텝 · 상태공간 최적화 (QP 근사)
//            - 제약 조건 (토크 포화 · dU 제한) 명시적 표현
//  [R-12-09] 전류 루프 교차 커플링 (d↔q) 해석 분리
//            - computeDQCoupling(): 역기전력 교차 커플링 항 Ld·ωe·Iq / Lq·ωe·Id 분리 계산
//            - 분리 보상 (Decoupling) 전·후 주파수 응답 비교
//            - 독립 탭 'coupling' 으로 ControlLoopTabContent 에 추가
// §  REFACTORING CHANGELOG  (v09 → v10)  ← 드라이버 모델 · 다단 변속 · 3D 열 · FMEA 자동화
// ──────────────────────────────────────────────────────────
//  [R-10-01] GAP-06 해소: PID 기반 실제 드라이버 모델 구현 (driverPIDController)
//            - simLaunch / simReaccel / simWLTP 전 시뮬에 driverPIDController 연동
//            - pedal_pos [0~1] PID 출력으로 교체: throttle=1.0 고정 방식 제거
//            - Jerk 제한(max_jerk_param) + anti-windup + 드라이브 모드 감도 반영
//            - DEFAULT_PARAMS: driver_pid_enable, Kp_drv, Ki_drv, Kd_drv, drv_max_jerk 추가
//  [R-10-02] GAP-10 해소: 다단 기어 변속 모델 (2-speed/AMT) 구현 (multiGearShift)
//            - getActiveGear(): SOC·속도·토크 기반 자동 변속 로직 (2단 AMT)
//            - gear_ratios[]: [단수1 감속비, 단수2 감속비] 배열 파라미터 추가
//            - 변속 히스테리시스 + 변속 충격(torque_dip) 모델링
//            - simLaunch / simReaccel / simWLTP 전 시뮬 연동
//            - DEFAULT_PARAMS: multi_gear_enable, gear_ratios, gear_shift_speed_kmh 추가
//  [R-10-03] GAP-05 완전화: 3D 열 분포 실시간 루프 연동
//            - update3DThermalStep(): 유한 차분법 셀-셀 열전도 + 대류 냉각 실시간 계산
//            - deratingFactor3D(): 3D 핫스팟 기반 토크 디레이팅 계수 산출
//            - simLaunch / simReaccel 루프 내 thermal_3d 상태 매 스텝 갱신
//            - hotspot 위치·온도·T_delta → 출력 제한 피드백 완전 연동
//  [R-10-04] 기능4: FMEA 자동화 + Fault Injection 연속 테스트 + SOTIF + SME + FIT
//            - FMEA_COMPONENT_FIT: 부품별 FIT 데이터 테이블 (20개 부품)
//            - computeFMEA(): Effect/Severity/Occurrence/Detection → RPN 자동 산출
//            - runFaultInjectionSeries(): FAULT_MODES 전체 자동 연속 테스트
//            - computeSOTIF(): ISO 21448 기반 센서 성능 한계·OEDR·알려진/미지 위험 분석
//            - computeSME(): 안전 메커니즘 효율(SME) 정량화 (진단 커버리지 × 감지율)
//            - FMEASafetyTabContent: 기능4 전용 UI 탭 (FMEA 테이블·RPN 차트·SOTIF·SME 대시보드)
// ██████████████████████████████████████████████████████████
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance18 → Advance18_01)
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
//  ═══════════════════════════════════════════════════════════════════
//  §  CHANGELOG  (Advance21_23 → Advance21_24)  ← Radar 버그수정 + 중복 물리식 제거
//  ═══════════════════════════════════════════════════════════════════
//  [A21-24-01] 🔴 차량 비교 노하우 물리식 중복 표시 제거
//              - 원인: CompareTabContent의 RLKnowHowPanel children에 formula 블록을
//                      별도로 삽입 → RLKnowHowPanel 내부 formula 렌더링과 이중 표시
//              - 수정: children 내 명시적 formula div 블록 제거
//                      (RLKnowHowPanel 자체가 activeHW.formula를 한 번 렌더링)
//
//  [A21-24-02] 🔴 Performance Radar — WLTP 전비 점수 오산 수정
//              [A] getWltpE fallback 계수 교정: 0.80 → 1.15
//                  구 코드: steadyEff[80].kWh_100km × 0.80 (WLTP를 정속 80보다 낮게 추정)
//                  수정   : WLTP 사이클은 가속·감속·저속 구간으로 정속 80 대비
//                           10~20% 소비 증가 → × 1.15 보정
//                  참조속도: 80 km/h → 70 km/h(WLTP 대표 속도 근접)로 변경
//              [B] 정규화 범위 재보정: span 22(8~30 kWh) → span 20(10~30 kWh)
//                  구 코드: normalize(30 - e, 0, 22) — 8 kWh가 만점이어서 실 EV가 낮은 점수
//                  수정   : normalize(30 - e, 0, 20) — 10 kWh 만점, 15~20 kWh EV → 50~75 pt
//
//  [A21-24-03] 🔴 Performance Radar — 종합 효율 점수 오산 수정
//              [A] getEtaOpt 로직 개선: 전체 최고값 → WLTP 대표구간(60~100km/h) 평균
//                  이유: 저부하 속도의 편향된 최고값 취득 방지
//              [B] 정규화 범위 재보정: 70~100% → 75~95%
//                  구 코드: normalize(eta, 70, 100) — 87%가 56 pt (실제 좋은 EV가 낮게 표시)
//                  수정   : normalize(eta, 75, 95) — 87% → 60 pt, 92% → 85 pt (합리적 분포)
//
//  ═══════════════════════════════════════════════════════════════════
//  §  CHANGELOG  (Advance21_19 → Advance21_20)  ← Bug Fix & Params Cleanup
//  ═══════════════════════════════════════════════════════════════════
//  [A21-20-01] BugFix — LaunchTabContent: paramsA 고정 → activeSimVehicle 전환
//              - 함수 시그니처: { r, paramsA, ... } → { r, params, ... }
//              - 내부 paramsA 참조 15곳 → params 치환
//              - 마운트: paramsA={paramsA} → params={p_launch} (activeSimVehicle 기반)
//              - 영향: µ·m·g 접지한계, 드라이브모드 배지, CdA 분석, I_max 표시가
//                      항상 M3(A) 기준으로 표시되던 버그 수정 → 선택된 차량 기준 표시
//
//  [A21-20-02] BugFix — 5개 탭 params={paramsA} 고정 → activeSimVehicle 전환
//              대상: BatteryHealthTabContent, EnhancedPhysicsTabContent,
//                    HARATabContent, FMEASafetyTabContent, DOETabContent
//              - 각 탭에 p_deg/p_enh/p_hara/p_fmea/p_doe 로컬 변수 + IIFE 패턴 통일
//              - I6(B) 선택 시 해당 차량 파라미터로 올바르게 렌더
//
//  [A21-20-03] Params Cleanup — TESLA_M3_PARAMS & IONIQ6_PARAMS 중복 키 34개 삭제
//              삭제 기준: DEFAULT_PARAMS와 완전 동일한 값 → 상속으로 충분
//              삭제된 키 (17개/프리셋 × 2):
//                Crr_v_coeff, E_on_off, K_aero, K_brake_mu, K_inv_cond,
//                K_sensor_noise, R_sw, T_amb, T_batt_derate_start, V_f_diode,
//                bms_t_charge_min, bms_v_cell_max, fw_enable, grade_deg,
//                mtpa_enable, regen_blend_low_speed, overmod_enable
//              동작 변화 없음 (DEFAULT_PARAMS {...} PRESET 스프레드 시 동일값 상속)
//
//  [A21-20-04] DEFAULT_PARAMS overmod_enable: true → false
//              - M3·I6 양쪽 모두 false로 선언 (SiC IGBT 기반 차량 표준)
//              - DEFAULT도 false로 통일하여 커스텀 차량 생성 시 동일 기본값 적용
//
// §  CHANGELOG  (Advance21_17 → Advance21_18)  ← 물리식 총점검 버그수정 (A21-18)
// ──────────────────────────────────────────────────────────────
//  [A21-18-01] 🔴 치명 computeEnergyChain: P_ac에 gear.P_loss 포함
//              에너지 흐름 Battery→Inverter→Motor→Gearbox→Wheel 완전 반영
//              배터리 소비 1-3% 과소 추정 해소 (고속 기어 손실 ~100-500W 반영)
//  [A21-18-02] 🔴 치명 stepVehiclePhysics: 회생제동 F_drv eta_dl ÷ 방향 교정
//              ×η (구동 방향) → ÷η (회생 방향) 수정 · 감속력 ~10% 상향 교정
//  [A21-18-03] 🟠 높음 backEMFObserver: dq 유도계수 교차항 수정
//              E_d_meas: Lq·dIq → Ld·dId / E_q_meas: Ld·dId → Lq·dIq
//              센서리스 속도/위치 추정 정확도 개선
//  [A21-18-04] 🟠 높음 computeMTPV: MTPV 이차방정식 a계수 교정
//              ωe²(Ld²+Lq²) → ωe²·Ld² (Iq=0 근사 MTPV 표준식)
//              고속 약자속 구간 전류 기준 및 최대 토크 정확도 개선
//  [A21-18-05] 🟡 중간 computeCurrentReferenceWithMTPV: MTPA 돌극 성분 반영
//              Id=0 고정 → Id=-(λpm-√(λpm²+8(Lq-Ld)²Iq²))/(4(Lq-Ld))
//              IPMSM 저속 최적 효율 운전점 ~2-8% Iq 절감
//  [A21-18-06] 🟢 낮음 regenFrictionBlend: dT_max 저크 제한 dead code 수정
//              dT_max 계산 후 T_regen 변화율 제한에 실제 적용 (마찰 보상 포함)
//  [A21-18-07] 🟢 낮음 computeKuPhysics: Ku = m*(l_f/Car-l_r/Caf)/L 정규화
//              L(휠베이스) 나눔으로 단위 s²/m 달성 · ku_auto_calc 고속 분모역전 방지
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
//  [A18_02-01] Road Load 탭 변수명 → 물리적 의미 표시 변환 (이중언어 대응)
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_16 → Advance21_17)  ← 전력 연산 버그 3종 수정
// ──────────────────────────────────────────────────────────────
//              ─ 원인 ─
//              기존: T_max_at_speed = Math.min(T_max, params.P_max_wheel / omega_motor)
//                    P_max_wheel [W] / omega_motor [rad/s] = 기준 혼합 (휠출력 / 모터각속도)
//                    motorTorqueSpeed(A21-15-01)은 P_max_wheel / eta_dl / omega 로 올바르게 처리
//                    → 고속 전력제한 구간에서 토크 상한 약 5%(=1/eta_dl) 과대 허용
//              ─ 수정 ─
//              T_max_at_speed = Math.min(T_max, params.P_max_wheel / (eta_dl * omega_motor))
//              ─ 영향 ─
//              motorTorqueSpeed / rlPmaxAtV / rlCalcVmaxTorque 와 기준 일관화
//              고속 전력제한 구간 토크 상한 약 5% 하향 교정
//
//  [A21-17-02] computeRegenChain: P_wheel_regen 물리 오류 및 P_gear_out dead code 제거 (전력연산 버그)
//              ─ 원인 ─
//              기존: P_wheel_regen = T_regen × gear_ratio × (omega_motor / gear_ratio)
//                                  = T_regen × omega_motor  ← gear_ratio 상쇄
//                    → 사실상 P_motor_mech 와 동일값 = 모터 샤프트 출력 (바퀴 출력 아님)
//                    회생 물리 방향: 바퀴 → 기어박스(η_dl 손실) → 모터
//                    올바른 P_wheel = P_motor_mech / eta_dl  (바퀴에서 더 많은 에너지 흡수)
//                    → eta_regen = P_batt_charge / P_wheel_regen 분모 과소 → eta_regen 약 5% 과대 표시
//              기존: P_gear_out = P_wheel_regen × eta_dl — 방향 역산 오류 + 이후 미사용 (dead code)
//              ─ 수정 ─
//              P_wheel_regen = T_regen × omega_motor / eta_dl  (실제 바퀴 회생 흡수 출력 [W])
//              P_gear_out 제거 (dead code)
//              P_motor_mech = T_regen × omega_motor 유지 (하위 손실 계산 기준 불변)
//              ─ 영향 ─
//              에너지·SOC 누산 영향 없음 / eta_regen 표시값 약 5% 하향 교정 (물리적으로 정확)
//
//  [A21-17-03] simDrivingCycle(WLTP): 회생·코스팅 구간 HVAC(P_aux) SOC 미차감 수정 (전력연산 버그)
//              ─ 원인 ─
//              구동(demand>0): computeEnergyChain 내부에서 P_aux 포함 → SOC 정확히 차감 ✓
//              회생(demand<-0.05): P_batt_charge SOC 충전만 있고, P_aux SOC 차감 없음
//              코스팅(demand≈0): E_aux_J 누산만 되고 SOC 차감 없음
//              → 회생·코스팅 비율 높은 사이클(WLTP Extra High 등)에서 SOC 낙관적 오산
//                 저온(-10°C, P_aux≈3 kW) 기준 코스팅·회생 비율 약 30% → SOC 약 1~2% 과대 추정
//              ─ 수정 ─
//              회생 경로: soc = socUpdate(soc, P_aux, dt, p) + E_aux_J += P_aux×dt 추가
//              코스팅 경로: soc = socUpdate(soc, P_aux, dt, p) 추가 (E_aux_J 누산 기존 유지)
//              ─ 영향 ─
//              HVAC 부하가 클수록(저온·고온) WLTP 사이클 종료 SOC 낮아짐 (물리적으로 정확)
//              주행거리 추정이 보수적으로 교정됨
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_15 → Advance21_16)  ← rlCalcVcross CrrDynamic 정합 + dead code 제거
// ──────────────────────────────────────────────────────────────
//  [A21-16-01] rlCalcVcross: 고정 Crr → CrrDynamic 이분탐색 교차속도 수치 풀이 (BugFix-1)
//              ─ 원인 ─
//              기존: vsq = (m × g × params.Crr) / (0.5 × rho × CdA) — 속도·온도 독립 Crr 고정 사용
//                    rlBuildR1 그래프는 CrrDynamic(v, T_amb) 사용 → v_cross 불일치
//                    Crr_v_coeff=0.0002 기준 100 km/h에서 Crr 약 15% 증가
//                    → v_cross 약 5~8 km/h 낮게 표시
//              ─ 수정 ─
//              이분탐색(30회, 정밀도 ~0.001 m/s)으로 F_aero(v) = F_roll(v, CrrDynamic) 수치 풀이
//              탐색 범위: 1~250 km/h / 교차점 없을 경우 고정 Crr fallback 보존
//              ─ 영향 ─
//              v_cross가 R1 그래프의 실제 F_aero=F_roll 교차점과 정확히 일치
//
//  [A21-16-01b] rlCalcVcross: 이분탐색 전제조건 양쪽 경계 검증 추가 (BugFix-1b) ← 리뷰에서 발견
//              ─ 원인 ─
//              v16-1 수정에서 diff(hi) < 0 케이스만 fallback 처리
//              diff(lo) >= 0 케이스(극단적 파라미터: 고CdA·저질량) 미처리
//              → 이분탐색 전제 diff(lo)<0 && diff(hi)>0 미보장 → 잘못된 값 수렴 가능
//              ─ 수정 ─
//              if (diff(lo) >= 0) return lo * 3.6 조기 반환 추가
//              → 양쪽 경계 검증 후 전제 보장된 상태에서만 이분탐색 진행
//
//  [A21-16-02] rlSteadyPower: eta_dl_ / eta_inv_ 미사용 변수(dead code) 제거 (BugFix-2)
//              ─ 원인 ─
//              기존: eta_dl_, eta_inv_ 지역 변수가 정의만 되고 반환값 및 이후 연산에 미사용
//                    P_wheel_kW = F_total × v / 1000 (올바름) 계산에 불필요하게 공존
//              ─ 수정 ─
//              eta_dl_, eta_inv_ 두 변수 선언 제거 → P_wheel_kW 계산은 기존과 동일하게 유지
//              ─ 영향 ─
//              계산 정확도 무변화 / 코드 명확성 향상 (dead code 제거)
//
//  [A21-16-03] rlCalcVcross 수식 툴팁 갱신: 구형 공식 → CrrDynamic 이분탐색 반영 (UI 일관성)
//              ─ 원인 ─
//              UI R1KnowHowPanel formula 문자열이 v_cross = √(m·g·Crr/(½·ρ·CdA)) 구형 표시
//              실제 계산(v16-1)과 UI 설명 불일치 → 사용자 혼란 가능
//              ─ 수정 ─
//              formula 문자열을 CrrDynamic(v,T) 이분탐색 방식으로 업데이트 (KO/EN 양쪽)
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_14 → Advance21_15)  ← 토크 연산 관련 버그 5종 수정
// ──────────────────────────────────────────────────────────────
//  [A21-15-01] motorTorqueSpeed: T_power_limit 기준 불일치 수정 (B1)
//              ─ 원인 ─
//              기존: T_power_limit = P_max_wheel / omega_m
//                    → 분자가 휠 출력[W], 분모가 모터 각속도[rad/s] → 모터 샤프트 토크가 아닌 혼합 기준
//                    → 올바른 모터 샤프트 토크 상한 = P_max_motor / omega_m = (P_max_wheel / eta_dl) / omega_m
//              ─ 수정 ─
//              T_power_limit = (P_max_wheel / eta_dl) / omega  (모터 샤프트 출력 기준)
//              ─ 영향 ─
//              저속(T_max_m binding): 무변화 / 고속 전력 제한 구간: 토크 상한 약 5%(1/eta_dl) 증가
//              → 고속 발진 가속 및 일정 출력 주행 토크가 실제 물리에 더 가까운 값으로 교정
//
//  [A21-15-02] motorTorqueSpeed: T_batt_lim 단위 오류 수정 (B2)
//              ─ 원인 ─
//              기존: T_batt_lim = (P_dis * 1000) / omega
//                    P_dis_max_T() 반환값: [W] (P_dis_ref = 250000 W) → ×1000 = 1000배 과대
//                    결과적으로 T_batt_lim이 항상 T_max_m, T_power_limit보다 커서 무효 상한
//              ─ 수정 ─
//              T_batt_lim = P_dis / omega  (* 1000 제거 → 이미 W 단위이므로 /omega = N·m)
//              ─ 영향 ─
//              배터리 온도 디레이팅이 낮을 때(저온·고온) T_batt_lim이 실제로 binding 됨
//              → 배터리 보호 디레이팅 토크 제한이 올바르게 동작
//
//  [A21-15-03] simLaunch/simReaccel: TV 삼항 데드코드 정리 (B3)
//              ─ 원인 ─
//              기존: tv_result = (TV && AWD) ? torqueVectoring(...) : torqueVectoring(...)
//                    양쪽이 완전히 동일한 호출 → TV 비활성 경로 없음
//              ─ 수정 ─
//              TV+AWD 모두 활성 시만 torqueVectoring 호출, 그 외엔 passthrough 결과 객체 직접 생성
//              → 불필요한 함수 호출 1회 제거, 코드 의도 명확화
//
//  [A21-15-04] simLaunch/simReaccel: AWD 시 T_mot = T_rear+T_front 합산으로 수정 (B4)
//              ─ 원인 ─
//              기존: T_mot = tv_result.T_rear || tv_result.T_front || fallback
//                    AWD 시 T_rear만 사용 → 전륜 토크 완전 누락
//                    computeEnergyChain(T_mot) → 전체 구동 토크 아닌 후륜 토크로 에너지 계산
//                    → AWD 50:50 분배 시 에너지/SOC 최대 50% 과소 추정
//              ─ 수정 ─
//              T_mot_total = (tv_result.T_front || 0) + (tv_result.T_rear || 0)
//              AWD 비활성 시 T_mot_after_esc 유지 (기존 동작 보존)
//              ─ 영향 ─
//              AWD 구동 시 실제 전체 구동 토크 기반으로 에너지·SOC 올바르게 계산
//
//  [A21-15-05] rlCalcVmaxTorque: P_max_motor → rlPmaxAtV 기반으로 교체 (B5)
//              ─ 원인 ─
//              기존: P_max_motor = P_max_wheel / eta_dl  (배터리 출력 제한 P_max_battery 미반영)
//                    → 배터리 바인딩 조건에서 T_motor_max 과대 계산 → v_max_torque 과대 추정
//              ─ 수정 ─
//              rlPmaxAtV(v, params) [A21-14-01 수정 버전, 휠 출력 기준, 배터리 제한 포함] 활용
//              T_motor_max = min(T_max_m, P_wheel_max/(omega_m×eta_dl)) × pkScale_vmt
//              → rlBuildR1, rlCalcVmax, rlBuildR4 와 동일한 파워 상한 기준으로 완전 정합
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_13 → Advance21_14)  ← rlPmaxAtV 출력 기준 일관화 + rlCalcVmax P_rd 정합 수정
// ──────────────────────────────────────────────────────────────
//  [A21-14-01] rlPmaxAtV: 반환값 기준 혼합 버그 수정 (저속=모터샤프트 / 고속=휠 → 항상 휠 출력으로 통일)
//              ─ 원인 ─
//              기존: T_pk = min(T_max_motor,  P_max_wheel/ω_m,  P_batt×η/ω_m)
//                    return T_pk × ω_m / 1000
//                → ① T_max_motor binding 시: T_max_motor × ω_m = 모터 샤프트 출력 [kW]
//                → ② P_max_wheel/ω_m binding 시: P_max_wheel = 휠 출력 [kW]  (기준 불일치)
//              ─ 수정 ─
//              세 제약을 모두 휠 출력 [W] 기준으로 변환 후 min() → 직접 반환
//                P_lim_Tmax  = T_max_motor × ω_m × η_dl   (모터토크 상한 → 휠 출력)
//                P_lim_wheel = P_max_wheel                  (휠 스펙 그대로)
//                P_lim_batt  = P_max_battery × η_inv × η_dl (배터리→휠)
//                return min(P_lim_Tmax, P_lim_wheel, P_lim_batt) / 1000   [kW, 항상 휠 기준]
//              ─ 영향 ─
//              • 저속 (T_max_motor binding): 기존 대비 × η_dl(≈0.95) 감소 → 더 정확한 휠 출력
//              • 고속 (P_max_wheel binding): 기존과 동일 (이미 휠 기준이었음)
//              • rlBuildR1 P_wheel_max_v / (ω_m × η_dl) = T_motor 역산: 수정 후에도 동일하게 유효
//  [A21-14-02] rlCalcVmax: P_rd 연산 오류 + 주석 오기재 수정
//              ─ 원인 ─
//              기존: P_rd = F_total × v / η_dl / 1000   (주석: "kW 휠 출력 기준" — 오기재)
//                    실제로 η_dl 역산 → 모터 샤프트 출력 기준
//                    반면 P_pk = rlPmaxAtV × pkScale → 수정 후 휠 출력 기준
//                    → 고속 영역에서 양쪽 기준 불일치 → v_max 약 (1/η_dl − 1) ≈ 5% 과소 추정
//              ─ 수정 ─
//              P_rd = F_total × v / 1000   [kW, 순수 휠 기계 출력] (η_dl 역산 제거)
//              비교: P_pk(휠 출력 상한) ≥ P_rd(휠 부하) → 물리적으로 올바른 정속 가능 조건
//              ─ 영향 ─
//              • P_rd가 η_dl(≈0.95) 만큼 감소 → P_pk ≥ P_rd 조건 더 여유롭게 달성
//              → v_max 약 2~5 km/h 증가 (실제 물리에 더 가까운 값)
//              • load_pct = P_road/P_motor 비율도 정확히 휠 기준으로 표시
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_12 → Advance21_13)  ← Drive Mode pkScale 일관성 수정 + R0 Y축 · R2/R3 fallback 정합성
// ──────────────────────────────────────────────────────────────
//  [A21-13-01] rlBuildR1: pkScale 적용 — SPORT/ECO 모드에서 T_drive_wheel 곡선 정확화 (P1)
//              - 기존: P_wheel_max_v = rlPmaxAtV(v, params) * 1000  (NORMAL 기준 고정)
//              - 수정: pkScale(drive_mode) 추출 후 P_wheel_max_v *= pkScale
//                      T_motor_max 상한도 T_max_motor * pkScale 반영
//              → rlCalcVmax / rlBuildR4 의 P_pk = rlPmaxAtV * pkScale 와 완전 일관성 확보
//              → R0 차트 T_drive_wheel 곡선이 드라이브 모드별 실제 피크 토크를 정확히 표시
//  [A21-13-02] rlCalcVmaxTorque: pkScale 적용 — drive_mode → v_max_torque 즉시 반영 (P2)
//              - 기존: T_motor_max = min(T_max_m, P_max_motor/omega) — drive_mode 무관 (NORMAL 고정)
//              - 수정: pkScale 추출 → T_motor_max *= pkScale
//              → SPORT 모드: v_max_torque 증가, ECO 모드: v_max_torque 감소 (v_max_power 비교 패널 정합)
//              → R0 상단 "⚖️ 방법 비교" Δv 값이 drive_mode 에 무관하게 크게 벌어지던 현상 해소
//  [A21-13-03] R0 차트 T_axis_max: pkScale 반영 — SPORT 모드 Y축 클리핑 방지 (P3)
//              - 기존: T_wheel_100 = T_max_motor × GR × η_dl  (pkScale 미반영)
//              - 수정: 현재 drive_mode pkScale 추출 → T_wheel_100 *= pkScale
//              → SPORT 1.15배 과부스트 시 T_drive_wheel 선이 차트 위로 잘리는 버그 수정
//  [A21-13-04] rlBuildR2base: v_max_chart 기본값 250 → 300 정합 (P4)
//              - DEFAULT_PARAMS.v_max_chart=300 기준과 불일치하는 fallback 수정
//  [A21-13-05] rlBuildR3: v_max_chart 기본값 250 → 300 정합 (P5)
//              - rlBuildR2base와 동일 fallback 수정 (R2/R3 차트 X축 범위 일관성)
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_11 → Advance21_12)  ← R0 그래프 300km/h 확장 + 파생 버그 수정
// ──────────────────────────────────────────────────────────────
//  [A21-12-01] R0 그래프 최고속도 300 km/h 확장
//              - rlBuildR1 루프: vk <= 240 → vk <= 300
//              - R0 차트 XAxis: domain/ticks 240 → 300
//              - R1 차트 XAxis: 하드코딩 240 → params.v_max_chart 동적 적용
//              - rlBuildR4 루프: vk <= 240 → vk <= 300
//              - DEFAULT_PARAMS.v_max_chart: 240 → 300
//              - TESLA_M3_PARAMS.v_max_chart: 240 → 300
//              (IONIQ6는 v_max_sw=185이므로 v_max_chart=185 유지)
//  [A21-12-02] rlBuildR1 내 T_motor_cmd: Math.min(1.15, thr) → Math.min(1.0, thr)
//              - [A21-11-03]과 동일한 스로틀 클리핑 버그 존재 → 동일하게 수정
//  [A21-12-03] rlBuildR1 내 T_motor_max 계산: rlPmaxAtV 기준으로 통일
//              - 기존: P_max_wheel / eta_dl / omega (배터리 제한 미반영)
//              - 수정: rlPmaxAtV(v, params) × 1000 / omega
//                → [A21-11-02] 수정된 rlPmaxAtV(휠출력 기준, 배터리 제한 포함)와 일관성 확보
//  [A21-12-04] rlBuildR4 load 계산 기준 통일
//              - 기존: r.P_batt_kW / P_pk × 100  (P_batt=배터리기준, P_pk=휠기준 → 불일치)
//              - 수정: r.P_wheel_kW / P_pk × 100  (양쪽 모두 휠 출력 기준)
//              - rlSteadyPower에 P_wheel_kW 반환 추가 (= P_batt × eta_dl × eta_inv)
//              - dP_ 계산도 동일 기준으로 수정
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_10 → Advance21_11)  ← R0 연산 버그 수정
// ──────────────────────────────────────────────────────────────
//  [A21-11-01] rlCalcVmax: P 비교 기준 불일치 수정
//              - P_rd = F_total·v / (eta_dl·eta_inv) / 1000  → 배터리 소비 kW (기존 유지)
//              - P_pk = rlPmaxAtV(v,params)                  → 이 함수는 모터 기계출력 kW
//              - 비교 기준 통일: P_pk를 배터리 기준으로 변환
//                P_pk_batt = P_pk / eta_motor_inv  (모터 기계출력 → 배터리 역산)
//              - 또는 P_rd를 모터 기계 출력 기준으로 변환:
//                P_rd_mech = F_total·v / eta_dl / 1000
//              → 채택: P_pk(기계출력) vs P_rd_mech(도로부하→모터출력 기준)으로 통일
//  [A21-11-02] rlPmaxAtV: 배터리 출력 제한 방향 수정
//              - 기존: P_max_battery * eta_motor_inv / omega  (배터리→기계 방향 ✓)
//              - 단, P_max_wheel이 휠 기준이므로 eta_dl 반영 필요
//              - 수정: P_max_battery * eta_motor_inv * eta_driveline / omega (휠 출력 기준)
//                → rlPmaxAtV는 휠 출력 kW로 통일 (P_max_wheel과 동일 단위)
//  [A21-11-03] rlCalcVmaxTorque: 스로틀 클리핑 수정
//              - 기존: T_motor_max * Math.min(1.15, thr)
//              - thr = driveModeThrottle(thr_raw, params) → 0~1.15 범위
//              - Math.min(1.15, thr)는 SPORT 1.15×를 허용 → T_max_motor 초과 의미 없음
//              - 수정: T_motor_max는 피크 토크 상한이므로 thr를 0~1.0으로 클리핑
//                T_drive_wheel = T_motor_max * Math.min(1.0, thr) * gear_ratio * eta_dl
//  [A21-11-04] rlBuildR1: grade_rad 이중 계산 쓰레기 코드 제거
//              - 라인 10116: gpct/100*PI/2 (잘못된 공식) 즉시 atan으로 덮어쓰는 코드 삭제
//  [A21-11-05] v_max 소프트웨어 제한 파라미터 추가
//              - DEFAULT_PARAMS / TESLA_M3_PARAMS: v_max_sw 추가
//              - rlCalcVmax / rlCalcVmaxTorque: v_max_sw 캡 적용
//              - Tesla M3 LR AWD: v_max_sw = 250 km/h (Highland 이전 기준)
//  [A21-11-06] TESLA_M3_PARAMS: P_max_wheel 수정
//              - 기존: 300000 W → Model 3 LR AWD 실제 시스템 출력 ~258 kW (휠 기준)
//              - 수정: 258000 W (모터 전기 출력 340kW × eta_dl 0.95 × eta_inv 0.91 ≈ 293kW,
//                       but 실제 스펙은 LR AWD 258kW 휠출력 기준)
//  [A21-11-07] IONIQ6_PARAMS: v_max_sw 누락 수정 (Tesla M3와 동일 버그)
//              - v_max_chart: 185 km/h 로 표시 상한은 맞았지만,
//                rlCalcVmax / rlCalcVmaxTorque 탐색 루프는 v_max_chart를 보지 않으므로
//                v_max_sw가 없으면 소프트웨어 제한이 실제로 적용되지 않음
//              - 추가: v_max_sw: 185  (현대 공식 소프트웨어 제한 185 km/h)
//              - P_max_wheel: 239000 W — 현대 공식 수치(239kW 시스템 출력)이므로 유지
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21 → Advance21_R0Graph)
// ──────────────────────────────────────────────────────────────
//  [A21-05] R0 그래프 신규 추가 — R1/R2 위에 전체 너비 배치
//           - 주제: 구동 토크(T_drive_wheel) vs 저항 환산 토크(T_resist_wheel) 속도별 비교
//           - 교차점 = 최대 속도 (토크 균형점) 시각화
//           - 우측 Y축: 주행저항 F_total [N] 동시 표시
//  [A21-06] 최대속도 박스 R0로 이전
//           - 기존 R1 내 전력기반 최대속도 박스 → R0 상단 KPI 영역으로 이동
//           - 기존 R1 내 토크기반 최대속도 박스 → R0 상단 KPI 영역으로 이동
//           - R1에서 두 박스 제거 → 시나리오 입력만 유지
//  [A21-07] Light 모드 가독성 개선
//           - 모든 하드코딩 배경색(#1e1b3a 등) → COLORS 시스템 연동
//           - 박스 배경: cardBg 사용, 테두리: accent5 계열 고대비
//           - 수치 텍스트: COLORS.text 기반 고대비 색상 적용
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_03 → Advance21_04)
// ──────────────────────────────────────────────────────────────
//  [A21-04-01] 적재량·구배 → 로드로드·발진·재가속·코스팅·회생제동 전 탭 반영
//           - runSim(): rlMassKg → params.m 합산, rlGrade → params.grade_deg 변환
//             → runFullSim(simP) 에 적재·구배 반영된 params 전달
//             → launch/reaccel/coast/braking/energy/thermal 등 전 시뮬 결과(r)에 즉시 반영
//           - useEffect: rlMassKg / rlGrade 변경 시 250ms 디바운스 후 runSim() 자동 재실행
//  [A21-04-02] 시나리오 UI — 전체 탭에서 항상 표시
//           - 기존: activeTab === "roadload" 조건 → 해당 탭만 표시
//           - 변경: ["roadload","launch","reaccel","coast","braking"] 탭 전체에서 표시
//           - 적재·구배 값이 0이 아닐 때 주황 테두리로 활성 상태 시각 표시
//  [A21-04-03] LaunchTabContent rlMassKg·rlGrade props 추가 (runSim 반영 메타 보존)
//  [A21-04-04] ReAccelTabContent ra2Data useMemo에 rlMassKg·rlGrade 반영
//           - mass = params.m + rlMassKg → F_base, F_grade3, F_wind5 전 항목 갱신
//           - _gr_base = sin(atan(rlGrade/100)) → 기준 구배 오프셋으로 추가
//  [A21-04-05] CoastTabContent c2Data useMemo에 rlMassKg·rlGrade 반영
//           - mass = params.m + rlMassKg → Euler 적분 관성 변화
//           - 시나리오 grade_pct에 _gOff(전역 구배) 오프셋 추가
//  [A21-04-06] RegenBrakeTabContent rb1Data useMemo에 rlMassKg·rlGrade 반영
//           - m = params.m + rlMassKg → P_req_full, P_req_mid, P_mu_lim 갱신
//           - _grade_rad = atan(rlGrade/100) → cos/sin 보정으로 F_req_full 재산출
//  [A21-04-07] DriveModeBadge 확장 → 적재/구배 정보 함께 표시
//           - rlMassKg > 0: ⚖ +N kg 주황 배지
//           - rlGrade ≠ 0: ▲/▼ N% 구배 녹색 배지
//           - 발진·재가속·코스팅·회생제동 탭 상단에 모드+시나리오 복합 표시
// §  CHANGELOG  (Advance21_02 → Advance21_03)
// ──────────────────────────────────────────────────────────────
//  [A21-03-01] ECO/NORMAL/SPORT → 발진·재가속·코스팅·회생제동 그래프 즉시 반영
//           - 드라이브 모드 버튼 onClick: 시뮬 탭(launch/reaccel/coast/braking 등)에서
//             runSim() 80ms 딜레이로 즉시 재실행 → r(시뮬 결과) 갱신
//           - useEffect: paramsA/B.drive_mode 감시 → 150ms 디바운스 후 runSim() 재실행
//             (버튼 외 코드 경로로 모드가 바뀌어도 안전하게 재실행 보장)
//  [A21-03-02] LaunchTabContent drive_mode 계수 즉시 반영
//           - dm_launch_k: ECO×0.70 / NORMAL×1.00 / SPORT×1.15
//           - l1Data.a_mode: 가속도 시나리오 오버레이에 모드 계수 복합 적용
//  [A21-03-03] ReAccelTabContent drive_mode 계수 즉시 반영
//           - clamp_k에 dm_ra_k 추가: ECO×0.70 / NORMAL×1.00 / SPORT×1.15
//           - T_act, v_kmh_adj, t_reach 등 전 KPI에 모드 반영
//  [A21-03-04] CoastTabContent drive_mode 회생 드래그 즉시 반영
//           - regen_drag_factor: ECO=eco_regen_strength / NORMAL=0.7 / SPORT=1.0
//           - c2Data Euler 적분에 F_regen_drag 추가 → 코스팅 거리·시간 모드별 변화
//           - ECO: 회생 드래그 강함 → 코스팅 거리 감소
//           - SPORT: 회생 드래그 최대 → 코스팅 시 빠른 감속
//  [A21-03-05] RegenBrakeTabContent drive_mode 회생 파워 즉시 반영
//           - dm_regen_scale: ECO×0.65(부드러운 감속) / NORMAL×1.00 / SPORT×1.10(강한 감속)
//           - rb1Data clamp_k에 dm_regen_scale 추가 → P_regen_eff 곡선 즉시 변화
//  [A21-03-06] DriveModeBadge 공통 컴포넌트 추가
//           - 발진·재가속·코스팅·회생제동 탭 상단에 현재 모드 배지 표시
//           - 드라이브 모드 효과(스로틀 배율, 회생 배율, η 보정) 인라인 표시
//           - "▸ 그래프 즉시 반영" 텍스트로 사용자에게 즉각 피드백 제공
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance21_01 → Advance21_02)
// ──────────────────────────────────────────────────────────────
//  [A21-02-01] ECO/NORMAL/SPORT 선택 → Road Load 그래프 즉시 반영
//           - rlBuildR1(): driveModeThrottle() 적용 → 구동 토크·최대속도 모드별 반영
//           - rlCalcVmaxTorque(): driveModeThrottle() 적용 → 토크 기반 최대속도 즉시 갱신
//           - rlCalcVmax(): drive_mode η 보정 + 피크파워 스케일 → 전력 기반 최대속도 반영
//           - rlSteadyPower(): drive_mode η 보정 + ECO P_aux 절약 → R2(정속전비) 즉시 반영
//           - rlBuildR4(): drive_mode 피크 파워 스케일 → R4(부하율) 즉시 반영
//  [A21-02-02] Road Load 컨트롤 바 — 드라이브 모드 배지 추가
//           - 현재 선택된 ECO/NORMAL/SPORT 배지를 컨트롤 바 우측에 표시
//           - η 보정값(+4%/±0%/-4%) · P_pk 스케일(70%/100%/115%) 인라인 표시
//           - 모드 변경 즉시 배지 색상·수치 반영 → 시각적 피드백 강화
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance20 → Advance21)
// ──────────────────────────────────────────────────────────────
//  [A21-01] rlCalcVmaxTorque(): 구동 토크 ↔ 주행저항 토크 균형점 기반 최대 속도 연산 추가
//           - 연산 원리: T_drive_wheel(v) = T_motor(v) × gear_ratio × η_dl (구동 토크)
//                        T_resist_wheel(v) = F_total(v, grade, mass) × r_wheel (저항 환산 토크)
//                        T_drive_wheel ≥ T_resist_wheel 를 만족하는 최대 v → v_max_torque
//           - 반환값: { v_max_torque, T_drive_at_vmax, T_resist_at_vmax, T_margin, torque_load_pct }
//  [A21-02] rlBuildR1(): T_resist_wheel (주행저항 환산 휠 토크 (N·m)) 필드 추가
//           - T_resist_wheel = F_total × r_wheel (구동력 균형 기준 저항 토크)
//           - 기존 T_drive_wheel 과 함께 R1 그래프에 동시 표시 → 교차점 = v_max (토크 기반)
//  [A21-03] R1 ComposedChart — T_resist_wheel 라인 추가 (빨간색 파선, yAxisId="T")
//           - T_drive_wheel 과 T_resist_wheel 의 교차점 → 토크 기반 최대 속도 시각화
//           - ReferenceLine: v_max_torque 위치에 토크 균형점 수직선 표시
//  [A21-04] R1 최대속도 정보 박스 — 토크 기반 v_max 패널 추가
//           - 전력 기반 v_max (P_motor = P_road 교차) 와 나란히 표시
//           - 토크 균형 여유(T_margin), 토크 부하율(torque_load_pct) KPI 포함
//           - 두 방법의 일치/불일치 여부로 파워트레인 여유 해석 가능
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance18_02 → Advance18_03)
// ──────────────────────────────────────────────────────────────
//  [A18_03-01] Launch/Re-Accel/Coast/Regen Brake 탭 변수명 → 물리적 의미 변환 (이중언어)
//              T_motor → 모터 토크 / Motor Torque
//              T_wheel → 휠 토크 / Wheel Torque
//              T_req → 요구 토크 / Requested Torque
//              T_act → 실제 토크 / Actual Torque
//              P_elec → 전기 입력 전력 / Electrical Input Power
//              η_motor → 모터 효율 / Motor Efficiency
//              F_net → 순 구동력 / Net Drive Force
//              P_regen,max → 최대 회생전력 / Max Regen Power
//              P_regen → 회생전력 / Regen Power
//              P_friction → 마찰제동전력 / Friction Braking Power
//              P_req → 필요 전력/요구 제동전력 / Required Power
//              P_peak → 순간 최대출력 / Peak Power
//              P_cont → 연속 허용출력 / Continuous Rated Power
//              P_avail → 가용 전력 / Available Power
//              µ·m·g → 접지력 한계 / Traction Limit
//              ΔP → 여유 전력 / Power Reserve
//              v_eq → 항력 균형 속도 / Drag Equilibrium Speed
//              a_th → 회생 한계 감속도 / Regen Threshold Deceleration
//              EMF → 역기전력 / Back-EMF
//              v_cross → 교차속도(공기저항=구름저항) / Crossover Speed
//              P_aero → 공기저항 전력 / Aero Drag Power
//              P_roll → 구름저항 전력 / Rolling Resist. Power
//              P_total → 총 주행저항 전력 / Total Road Load Power
//              F_aero → 공기저항력 / Aero Drag Force
//              F_roll → 구름저항력 / Rolling Resist. Force
//              v_opt → 최적 경제속도 / Optimal Eco Speed
//              P_aux → 보조부하 전력 / Aux Load Power
//              v_air → 유효 공기속도 / Effective Air Speed
//              P_cont → 연속 허용출력 / Continuous Rated Power
//              P_peak → 순간 최대출력 / Peak Power
// ──────────────────────────────────────────────────────────────
//  [A18_01-01] KPI 배지 레이블: "ΔP+適載@80" → "적재 ΔP @80" (한글화)
//  [A18_01-02] RLHwPopup: 팝업 방식 전면 폐기 → null 반환으로 무력화
//              (기존 수십 곳 호출부 수정 없이 일괄 적용)
//  [A18_01-03] RLHwBtn: 활성 시 안내문구 → "▼ 아래에 상세 표시 중"
//  [A18_01-04] RLKnowHowPanel: 선택된 노하우 인라인 상세 자동 렌더링
//              헤더(아이콘+제목+닫기) + KPI수치(children) + 공식 + 핵심포인트 + 실무액션
//              → R1~R4, 에너지, 론치, 배터리, WLTP 전 탭 일괄 적용
//  [A18_01-05] RLInlineKpi: 외곽 박스 제거, 패널 내부용 수치 카드 스타일로 변경
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance18 → Advance19)
// ──────────────────────────────────────────────────────────────
//  [A19-UI1] 토크맵 LUT: 페달 응답·토크 응답성 → 룩업 테이블 왼쪽 열 아래 배치
//  [A19-UI2] 중재/토크지령: 회생 맵 섹션 재배치 — ① LUT 캘리브 → ② 모드별 회생 강도 2행
//  [A19-UI3] 속도 PI 보드선도: 이득선도(좌) + 위상선도(우) 2열 배치
//  [A19-UI4] 진단/보정 — 회생 맵 캘리브 탭 완전 삭제 (중재 탭에 통합됨)
//  [A19-UI5] 제어 루프 분석 — 탭별 목적 가이드를 각 상세 탭 상단으로 이동
//            마스터 가이드 패널 제거, CurPurpose() 컴포넌트로 탭별 인라인 표시
//  [A19-UI6] 실시간 파라미터 스윕 — 전류/속도 루프 보드선도 동시 표시
//            ANIM_PARAM_DEFS에 loop/graph/color 메타데이터 추가
//            스윕 활성 시 전류 루프 보드선도 + 속도 루프 보드선도 2열 동시 표시
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance23 → Advance24)
//     제목: 주행 환경 ↔ 에너지 체인 4대 불일치 수정
// ──────────────────────────────────────────────────────────────
//  [A24-FIX-A] mid_chain 손실 비중 대표값 개선 (simWLTP + simDrivingCycle 공통)
//              수정 전: 단일 스냅샷(t≈900s 또는 data_out 중간 1개)
//                       → 해당 순간 부하만 반영, E1 손실 스택 왜곡
//              수정 후:
//                simWLTP     → Low(t=300s)/Medium(t=900s)/ExtraHigh(t=1500s) 3구간 chain 평균
//                simDrivingCycle → 사이클 길이 25%/50%/75% 지점 3구간 chain 평균
//                결과: 전 사이클 부하 구조를 대표하는 손실 비중 → E1 누적 손실 스택 정확도 향상
//
//  [A24-FIX-B] EnergyTabContent E1 e1Data 손실 분해 동적화
//              수정 전: mid_chain 단일 비율(k_batt/k_inv/k_cu..)을 전 구간 균일 적용
//                       → 정지·저속·고속 구간의 손실 구조 차이 미표현
//              수정 후: steadyEff 속도 LUT 선형 보간 → 각 타임스텝 v_sim에 맞는 실제 손실 비율
//                       steadyEff 없으면 mid_chain 비율로 fallback
//              의존성: useMemo deps에 steadyEff 추가
//
//  [A24-FIX-C] EnergyTabContent E3/E4 온도 시나리오 실제 물리 재계산
//              수정 전: 고정 배율(cold×1.35, hot×0.95 등) → rhoCorrected/CrrDynamic/Rs_T 미반영
//              수정 후: generateSteadyStateEfficiency(params with T_amb override) 재호출
//                E3: -10°C / 45°C 시나리오별 실제 F_aero(rho), F_roll(Crr), P_cu(Rs) 반영
//                E4: -10°C / 45°C 시나리오별 각 손실 항목 실제 재계산
//                SiC 스위칭 손실 보정(P_inv × 0.6)은 파라미터 미노출이므로 기존 방식 유지
//              의존성: useMemo deps에 params 추가
//
//  [A24-FIX-D] 대기 환경 인라인 패널 rho 자동 동기화
//              수정 전: T_amb/altitude 변경 시 rho 필드 불변
//                       → resistanceForcesV3에서 rhoCorrected()가 실제 rho 재계산하나 UI 불일치
//              수정 후: T_amb 또는 altitude 변경 onChange에서 rhoCorrected() 공식으로 rho 즉시 갱신
//                       rho 직접 입력은 사용자 의도 존중하여 자동 갱신 없음
//
//  [A24-FIX-E] useEffect 환경 감시 목록 누락 항목 추가
//              수정 전: IRI, tire_pressure_actual, crosswind_enable, v_wind, wind_angle_deg
//                       → EnvModal "✓ 적용 & 재실행" 버튼 없이는 자동 재시뮬 안됨
//              수정 후: 위 5개 파라미터(A/B 각각) 를 300ms 디바운스 useEffect deps에 추가
//                       → EnvModal 에서 슬라이더 조작만으로 자동 재시뮬
// ──────────────────────────────────────────────────────────────
//     제목: 주행 사이클 시뮬레이션 버그 수정 4종 — WLTP 외 사이클 정상 연동
// ──────────────────────────────────────────────────────────────
//  [A23-FIX-1] simDrivingCycle — _kp_eff/_ki_eff 블록 스코프 버그 수정
//              수정 전: inner for(si) 블록 안 const 선언 → outer loop에서 ReferenceError
//              수정 후: 루프 진입 전 호이스팅(drive_mode는 시뮬 중 불변이므로 안전)
//              영향: WLTP 제외 전 사이클(FTP75/EPA/JC08/CLTC/RDE) simDrivingCycle 정상화
//  [A23-FIX-2] genericPhaseTag — 6개 사이클 Phase 태깅 누락 추가
//              수정 전: EPA_HWY/EPA_CITY/JC08/RDE_URBAN/RURAL/MOTORWAY → 전부 'Main'
//              수정 후:
//                EPA_HWY     → 'Highway' (단일 고속 구간)
//                EPA_CITY    → 'City'    (단일 도심 구간)
//                JC08        → jc08PhaseTag(t): Low(<400s)/Medium(<800s)/High(≥800s)
//                RDE_URBAN   → 'Urban'   (profile.phase 임베드 백업)
//                RDE_RURAL   → 'Rural'
//                RDE_MOTORWAY→ 'Motorway'
//              영향: DC3 Phase별 전비/회생율 막대 정상 출력
//  [A23-FIX-3] generateEPA_CITY_Profile — FTP75 슬라이스 → UDDS 독립 keypoint 프로파일
//              수정 전: FTP75 앞 1369초 단순 슬라이스 + phase 태깅 없음
//              수정 후: UDDS 대표 keypoints 기반 선형보간 + phase:'City' 내장
//              특성 반영: 평균 31.5km/h, 최고 91.2km/h, 23회 정지
//  [A23-FIX-4] simDrivingCycle — 샘플링 해상도 개선 (ti%10 → ti%5)
//              수정 전: 10 profile-point(≈10s) 간격 → EPA_HWY 76포인트, JC08 120포인트
//              수정 후: 5 profile-point(≈5s) 간격  → EPA_HWY 153포인트, JC08 240포인트
//              영향: DC1/DC2/DC4 그래프 곡선 해상도 simWLTP와 동일 수준으로 개선
//  [A23-FIX-5] DCTabContent DC3 _phaseMap — EPA_HWY/CITY/JC08/RDE 단일Phase 오류 수정
//              수정 전: EPA_HWY["Highway","Main"], EPA_CITY["City","Main"] 등 'Main' 포함
//              수정 후: 실제 생성되는 Phase만 명시 (단일 Phase 사이클은 1개 항목)
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
//  [A18-01] 개도 입력 박스: onBlur → onChange 500ms 디바운스
//           (_r1thrTimer ref 추가, blur 시 즉시 확정)
//  [A18-02] R1 노하우 버튼 클릭 → 팝업 제거, 패널 내 인라인 표시
//           - R1KnowHowPanel 신규 컴포넌트: 버튼 토글 + 상세 박스 인라인
//           - 상세 박스: 공식(formula) + KPI 수치 + 핵심포인트 + 실무액션 통합
//           - handleR1: makeHwHandler/posSetter 제거 → 단순 toggle 함수
//           - RLHwPopup(R1) 제거 (R2~R4 팝업은 유지)
//           - 버튼 안내문구: 활성 시 "▼ 아래에 상세 표시 중" 표시
//  [A18-UI1] 토크맵 LUT: 룩업 테이블 + 차속별 목표 토크 곡선 2열 배치
//  [A18-UI2] Arbitration/T_req: BMS 충방전 한계(좌) + 온도 디레이트(우) 2열 배치
//            ROC/Transient → BMS 충방전 밑, TCS/ESC → 온도 디레이트 밑
//  [A18-UI3] Arbitration 탭명 한국어: "중재 / 토크 지령", 블럭도도 동일
//  [A18-UI4] BMS 출력 제어: 밸런싱 출력(좌) + 릴레이/콘택터(우) 2열 배치
//  [A18-UI5] 신호처리: BPP+원페달+WLTP PI 통합 → APP와 2열 배치
//  [A18-UI6] 전류 PI 보드 선도: 이득선도(좌) + 위상선도(우) 2열 배치
//  [A18-UI7] 회생 맵 캘리브 → 주행전략 Arbitration/T_req 탭에 통합
//            regen_map 탭 클릭 시 arbitration으로 자동 리다이렉트
// ──────────────────────────────────────────────────────────────
// §  CHANGELOG  (Advance16 → Advance17)  ← R1 타이어 구동토크 반영
// ──────────────────────────────────────────────────────────────
//  [A17-01] rlBuildR1(params, throttle_pct) — 개도 파라미터 추가
//           - throttle_pct: 0~100% 입력 (기본값 100%)
//           - T_motor_cmd = T_max_motor × thr × 파워한계 clamp
//           - T_drive_wheel = T_motor_cmd × gear_ratio × eta_driveline (타이어 구동토크 [Nm])
//           - T_motor_cmd 도 데이터 레코드에 포함 (툴팁 확장 가능)
//  [A17-02] RLTabContent — r1Throttle state 추가 (기본 100%)
//           - r1Data useMemo: rlBuildR1(params, r1Throttle) 로 업데이트
//  [A17-03] R1 그래프 UI — 개도 입력 박스 (시나리오 버튼 우측)
//           - 숫자 입력 필드, 0~100 범위, step=5, 보라색 스타일
//  [A17-04] R1 ComposedChart — 우측 Y축 (구동토크) + T_drive_wheel 라인 추가
//           - yAxisId="T" 우측 보조축: 구동토크 (N·m)
//           - 보라색(#a78bfa) 파선 라인, 범례에 개도 % 표시
//           - 기존 P_roll/P_aero/P_total 라인은 yAxisId="P" 로 명시
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v17 → v18)  ← Safety & Security 통합 재구성
// ──────────────────────────────────────────────────────────────
//  [R-18-01] NAV_CATEGORIES 'ha' 카테고리 재구성:
//            카테고리명 변경: "Functional Safety (ISO 26262)" → "Safety & Security"
//            (Functional Safety + Cybersecurity + SOTIF 3개 표준 통합 아우르는 상위 명칭)
//  [R-18-02] 3개 메인 섹션으로 단순화 (기존 9개 세부 섹션 → 내부 네비게이션으로 처리):
//            Section 1: id="hara"               — Functional Safety (ISO 26262)
//            Section 2: id="cybersecurity_tara"  — Cybersecurity (ISO/SAE 21434) (ISO/SAE 21434)
//            Section 3: id="sotif_iso21448"      — SOTIF Analysis (ISO 21448)
//  [R-18-03] 버그 수정: NAV_CATEGORIES 'ha' 세부 섹션 ID vs 메인 렌더러 ID 불일치 해소
//            (기존: sections=["hazard_list",...] → activeTab="hazard_list" → HARATabContent 렌더링 안됨)
//            (수정: sections=["hara","cybersecurity_tara","sotif_iso21448"] → 정상 렌더링)
//  [R-18-04] 신규 컴포넌트: CybersecurityTabContent (ISO/SAE 21434 TARA)
//            - Asset Identification / Threat Scenario / Impact Rating / Attack Feasibility / Risk Determination
//            - FMEASafetyTabContent의 TARA 섹션에서 독립 컴포넌트로 분리
//  [R-18-05] 신규 컴포넌트: SOTIFTabContent (ISO 21448)
//            - OEDR 성능 / Known Scenarios KPI / Unknown Risk 게이지 / 인증 상태
//            - FMEASafetyTabContent의 SOTIF 섹션에서 독립 컴포넌트로 분리
//  [R-18-06] FMEASafetyTabContent SECTIONS에서 "sotif", "tara" 제거
//            → 탭명 업데이트: "FMEA/SOTIF" → "FMEA/FIT/FTA"
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v16 → v17)  ← Functional Safety 계층 재구성
// ──────────────────────────────────────────────────────────────
//  [R-17-01] NAV_CATEGORIES 'ha' 섹션 재구성:
//            HARA & ASIL Concept / Functional Safety Concept /
//            Technical Safety Concept / Verification & Validation
//            4개 그룹 × 총 9개 세부 섹션으로 계층 확장
//  [R-17-02] AF_CATS 'ha' 섹션 동일 계층 구조 동기화
//  [R-17-03] HARATabContent 내 SECTIONS → FS_GROUPS 2단계 계층 구조로 교체
//            - 그룹 헤더(비클릭) + 세부 섹션 버튼 시각적 계층화
//            - EN / KO 이중 라벨 적용
//  [R-17-04] 신규 세부 섹션 콘텐츠 추가:
//            Safety Goal (SG) / FTTI / SS (Safe State) 전용 뷰
//  [R-17-05] 기존 섹션 ID 재매핑:
//            hazards→hazard_list, asil→asil_matrix,
//            fsr→fsr, sim→fault_injection, graph→asil_recheck
//            + tsr / ftti / ss / safety_goal 신규 추가
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v14 BugFix01 → v15)  ← 기능 안전 완전체
// ──────────────────────────────────────────────────────────────
//  [R-15-01] DFMEA↔PFMEA 완전 연계
//            - DFMEA_DESIGN_ITEMS: 설계 FMEA 마스터 데이터 (18개 설계 고장 모드)
//            - PFMEA_PROCESS_ITEMS: 공정 FMEA 마스터 데이터 (15개 공정 고장 모드)
//            - computeDFMEA(): 설계 고장 모드 → RPN 자동 산출 (설계 관점 S/O/D)
//            - computePFMEA(): 공정 고장 모드 → RPN 자동 산출 (공정 관점 S/O/D)
//            - linkDFMEA_PFMEA(): 설계 고장 ↔ 공정 원인 자동 매핑
//              (설계 고장이 어떤 공정 단계에서 유발되는지 양방향 연계)
//            - FMEASafetyTabContent에 'dfmea' 섹션 신규 추가
//  [R-15-02] FTA 결함 트리 자동 생성
//            - FTA_TOP_EVENTS: 최상위 위험 이벤트 마스터 (5개 Top Events)
//            - buildFaultTree(): FAULT_MODES → FTA 트리 자동 구성
//              (Top Event → Intermediate → Basic 이벤트 계층 자동 분류)
//            - computeFTA(): MCS(최소 절단 집합), 시스템 PFH 계산
//              AND 게이트: P = ∏P_i, OR 게이트: P = 1 - ∏(1-P_i)
//            - FTATreeRenderer: SVG 기반 인터랙티브 트리 렌더러
//              노드 확장/접기, 게이트 심볼, 확률 오버레이
//            - FMEASafetyTabContent에 'fta' 섹션 신규 추가
//  [R-15-03] ISO 26262 Part 4 TARA (시스템 수준) 완전 대응
//            - TARA_SYSTEM_ASSETS: 시스템 자산 목록 (10개 핵심 자산)
//            - TARA_DAMAGE_SCENARIOS: 손상 시나리오 데이터베이스
//            - computeTARA_Part4(): ISO 26262 Part 4 전체 TARA 워크플로우
//              ① 자산 식별 (Asset Identification)
//              ② 손상 시나리오 정의 (Damage Scenario Definition)
//              ③ 위협 분석 (Threat Analysis)
//              ④ 공격 경로 분석 (Attack Path Analysis)
//              ⑤ 리스크 값 산출 (Risk Value = Impact × Likelihood)
//              ⑥ 리스크 처리 결정 (Risk Treatment Decision)
//            - generateTARA_Report(): CSV 형식 TARA 보고서 자동 생성
//            - FMEASafetyTabContent에 'tara' 섹션 신규 추가
//            - FMEASafetyTabContent 헤더 v15 업데이트
// ──────────────────────────────────────────────────────────────
// §  REFACTORING CHANGELOG  (v15 → v16)  ← 분석/통계 완전체
// ──────────────────────────────────────────────────────────────
//  [R-16-01] Monte Carlo Web Worker 완전 마이그레이션 (UI 블로킹 완전 해소)
//            - simMonteCarloWorkerCode: Blob URL 기반 인라인 Web Worker 코드
//              (proxyRun / Box-Muller / 청크 루프 / 완전 통계 워커 내 처리)
//            - simMonteCarloAsyncWorker(): Worker 우선 실행 → 미지원시 setTimeout 폴백
//              postMessage(progress) → onProgress 실시간 반영
//              Worker 완료 후 terminate() + resolve() 보장
//            - runEnhancedMonteCarloWorker(): EnhancedPhysicsTab용 Worker 연동
//              LHS 샘플 생성 → Worker에 직렬화 전달 → Sobol Si 결과 수신
//            - DEFAULT_PARAMS: mc_use_worker(기본 true), mc_worker_chunk(기본 40) 추가
//  [R-16-02] Sobol 민감도 지수 완전 구현 (1차 Si + 총차수 STi)
//            - sobolSequence(n, k): Saltelli Sobol 준난수 시퀀스 생성 (van der Corput)
//            - computeSobolIndices(paramRanges, modelFn, N): Saltelli (2010) 추정량
//              A/B 매트릭스 + AB_i 교차 → Si = (f_B·(f_ABi−f_A)) / Var(Y)
//              STi = (f_A·(f_A−f_ABi)) / (2·Var(Y))  (Jansen 추정량)
//            - EnhancedPhysicsTab 'mc' 섹션에 Sobol Si/STi 추가:
//              바 차트 2중 (1차 / 총차수), 지배 파라미터 자동 강조
//  [R-16-03] ANOVA (분산분석) 구현
//            - computeOneWayANOVA(groups): 일원 ANOVA — SS_between/SS_within,
//              F-통계량, η² (에타제곱), p값 근사 (F CDF 근사 알고리즘)
//            - computeTwoWayANOVA(data, levels_A, levels_B): 이원 ANOVA
//              SS_A / SS_B / SS_AB(교호작용) / SS_Error 완전 분해
//              F_A / F_B / F_AB + p값 + η²_A / η²_B / η²_AB
//            - DOETabContent에 'anova' 탭 추가:
//              인자별 그룹 분산분석 / 교호작용 유의성 / 설명력 시각화
//  [R-16-04] Pareto 최적화 프론티어 구현
//            - isParetoOptimal(point, others): 단일 점 Pareto 우성 판정
//            - computeParetoFront(points, objectives): n목적함수 Pareto 집합 계산
//              objectives: [{key, minimize}] 배열 — 최소/최대 혼합 지원
//            - computeNSGA2Sort(population): NSGA-II 비지배 정렬 (front 1,2,3…)
//              + 혼잡도 거리(crowding distance) 계산
//            - DOETabContent에 'pareto' 탭 추가:
//              2D/3D Pareto 산점도, 비지배 해 강조, 트레이드오프 분석
//  [R-16-05] 제약 조건 최적화 구현
//            - CONSTRAINT_DEFS: 기본 제약 조건 정의 (ASIL 충족, 열 한계 등)
//            - penaltyObjective(x, objFn, constraints, mu): 패널티 함수법
//              등호/부등호 제약 모두 지원 (μ·Σmax(0,gᵢ)² + ν·Σhⱼ² 패널티 항)
//            - constrainedOptimize(objFn, bounds, constraints, options):
//              Augmented Lagrangian 방법 (패널티 계수 증가 + 라그랑지 승수 업데이트)
//              Nelder-Mead 심플렉스 내부 최적화 서브루틴 (자체 구현)
//            - DOETabContent에 'constrained' 탭 추가:
//              제약 조건 설정 UI / 최적해 탐색 진행 / Pareto+제약 통합 뷰
// ──────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────
// [Disp02] Launch 섹션 전면 재설계 (ev-sim_Disp02_Launch.jsx)
//  · L1 그래프: v(t) / a(t) / F_net / µ·m·g 한계선 오버레이
//  · L2 그래프: T_motor / T_wheel / P_elec / η_motor 오버레이
//  · L3 그래프: 열 상태 (배터리/인버터/모터/냉각수)
//  · L4: 에너지 체인 스냅샷
//  · 노면 조건 토글 (dry/wet/snow), 저온 배터리 토글, TCS 토글
//  · 노하우 클릭 시 그래프 강조(음영/굵기) + 상세 팝업
//  · KPI 배지: 0→60/0→100 Δt, F_trac_lim, η peak 등 즉시 갱신
// ============================================================
// ────────────────────────────────────────────────────────────
// NEW v5 Improvements — Loop Integration (Critical Fixes):
//  [v5-fix1] computeBatteryLosses → computeEnergyChain 통합 (일관된 Ri 모델)
//  [v5-fix2] 2RC Battery Model → simLaunch/simReaccel 실제 적분 연동
//  [v5-fix3] MTPV/FW computeCurrentReferenceWithMTPV → 루프 Iq*/Id* 연동
//  [v5-fix4] regenTorqueEnhanced → simLaunch throttle<0 회생 구간 실제 연동
//  [v5-fix5] lateralDynamicsStep + escIntervention → 루프 psi_dot 실제 적분
//  [v5-fix6] updateSOH + applySOHDegradation → P_max/Q_nom 피드백 연동
//  [v5-fix7] simReaccel 전면 개편: TV/TCS/2RC/MTPV/SOH/ESC simLaunch와 패리티
// NEW v4 Physics — AWD / Torque Vectoring (Document-Specified):
//  [TV-1] Ku 물리 기반 자동계산: Ku=m/(Cαf·Cαr)·(a·Cαf-b·Cαr)
//  [TV-2] ψ̇_ref 포화 한계: ψ̇_max = μ·g/v  (물리적 한계 클리핑)
//  [TV-3] λ_fr 적응형 전후배분: λ_fr=clip(λ0+kμ·Δμ+kUS·eψ, λmin, λmax)
//  [TV-4] 롤 강성(kr_f/kr_r) 기반 좌우 하중이동 완전체
//  [TV-5] TV 전후 가중치 wf/wr: ΔFx_f=(2wf·Mz)/t_f, ΔFx_r=(2wr·Mz)/t_r
//  [TV-6] 마찰원 균등 스케일링: s=min(Fz_i·μ/|Fx_req_i|) → 일관된 제약
//  [TV-7] simLaunch 루프 per-wheel 토크 완전 통합
// ────────────────────────────────────────────────────────────
// NEW v2 Physics (6 Upgrades from v1):
//  ① WLTP 완전 고도화: 속도추종PI·E_WLTP 적분·CO2배출·사이클분석
//  ② 2-DOF Bicycle 횡/요 다이나믹스: Pacejka 비선형 연동
//  ③ 열 펌프(HP) Carnot COP 고도화: 냉매/압축기 모델
//  ④ OCV-SOC Nernst 방정식 + 온도보정 LUT 이중모델
//  ⑤ Quarter-Car 서스펜션 + 수직 하중이동 → Pacejka Fz
//  ⑥ BMS 셀 밸런싱: 패시브/액티브 에너지·시간 계산
// ────────────────────────────────────────────────────────────
// NEW v3 Physics (7 Major Upgrades — Document-Specified):
//  [A] 측풍(Crosswind) 반영 공기저항: F_drag=(½ρCdA)(v_v+v_w·cosβ)²
//  [B] 자기 포화 L_d(i_d,i_q) 전압방정식 + Steinmetz 철손보정
//  [C] Arrhenius SOH dQ/dt=A·exp(-Ea/RT)·t^(n-1) + Li-Plating 감지
//  [D] Combined Slip μ_res=√(μx²+μy²)≤μ_max 완전체 Friction Ellipse
//  [E] Pacejka F_y 완전체 (E항 포함): Fy=D·sin[C·arctan{Bα-E(Bα-arctan(Bα))}]
//  [F] ESC/TCS 개입 로직: 요레이트 오차 기반 제동/토크컷 전략
//  [G] 그릴 셔터 / 가변 CdA 모델 + 속도 의존 항력계수
// ────────────────────────────────────────────────────────────
// 1D Longitudinal + 2-DOF Lateral Vehicle Dynamics
// IPMSM + FOC/MTPA/MTPV/FW + Full Energy Chain
// 2RC Battery + SOH + EKF/UKF Kalman SOC
// 4-Node Thermal + Heat Pump (Carnot COP)
// WLTP/FTP-75/EPA Cycle Simulation + Phase Analysis
// Regen Braking + ABS + Brake Thermal + Pacejka Tire
// Bode Plot (PI stability) | KO/EN Language Toggle
// Full-Spec Driving Cycle CSV Template & Analyzer
// ============================================================

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// §0  LANGUAGE / i18n SYSTEM
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { LangContext, useLang } from './contexts/LangContext.js';
import { ThemeContext }          from './contexts/ThemeContext.js';


// [Advance33] PopupContext: 그래프 정보 팝업을 DOM 최상위에서 렌더링
// position:fixed가 overflow:auto 조상에서 클리핑되는 문제 해결
const PopupContext = createContext(null);
const usePopup = () => useContext(PopupContext);

// [V25-TIP-SINGLE] 전역 툴팁 단일 오픈 컨텍스트
const RLKpiOpenContext = createContext(null);






// Thermal state update (4-node: batt, inv, motor, coolant)
// ── [R-13] thermalStep v04: coolantFlowControl.phi_flow → UA_rad 방열 가중치 반영 ──
// phi_flow 없을 시 기존 속도 의존 phi만 사용 (하위 호환성 유지)

// ============================================================
// OCV & Physics Engine
// ============================================================

// ============================================================
// PRIORITY 1 ENHANCEMENTS: 2RC Battery Model + SEI
// ============================================================
// PRIORITY 1 ENHANCEMENTS: MTPV (Maximum Torque Per Voltage)
// ============================================================


// ============================================================
// PRIORITY 1 ENHANCEMENTS: Pacejka Tire Model (Longitudinal)
// ============================================================


// ============================================================
// PRIORITY 1 ENHANCEMENTS: Dual Motor AWD (Basic)
// ============================================================
// ── AWD Torque Vectoring (torqueVectoring) → TV-1~TV-7 완전 구현 ──
// RWD/AWD 분기, 물리 기반 Ku, 롤강성 하중이동, 마찰원 스케일링 포함

// ╔═══════════════════════════════════════════════════════════╗
// ║         PRIORITY 2 ENHANCEMENTS (PHASE 2)                 ║
// ╚═══════════════════════════════════════════════════════════╝

// ============================================================
// PRIORITY 2.1: 센서리스 제어 (백-EMF 옵저버)
// ============================================================



// [R-06-H deleted] sensorlessControl// ============================================================
// PRIORITY 2.2: 동적 토크 분배 / 수직 하중 (computeVerticalLoad_v4)
// ── 롤강성 포함 4휠 수직하중 완전 모델 (v4) — torqueVectoring 에서 호출 ──
// ============================================================

// ============================================================
// PRIORITY 2.3: SOH (State of Health) 모델
// ============================================================




// ============================================================
// PRIORITY 2.4: 데드타임 보상
// ============================================================



// ╚═══════════════════════════════════════════════════════════╝
// END OF PRIORITY 2 ENHANCEMENTS
// ╚═══════════════════════════════════════════════════════════╝

// ╔═══════════════════════════════════════════════════════════╗
// ║         PRIORITY 3 ENHANCEMENTS (PHASE 3)                 ║
// ║         Advanced Control & Thermal Features               ║
// ╚═══════════════════════════════════════════════════════════╝

// ============================================================
// PRIORITY 3.1: 토크 벡터링 (좌우 독립 제어)
// ============================================================


// ============================================================
// v4.0 [TV-1] Ku (Understeer Gradient) — Physics-Based Auto Calculation
// Ku = (m / (C_af * C_ar)) * (a * C_af - b * C_ar)
// C_af, C_ar: cornering stiffness per axle [N/rad]
// a=l_f, b=l_r (CG to front/rear axle)
// ============================================================

// ============================================================
// v4.0 [TV-2] Yaw Rate Reference with Physical Saturation Limit
// psi_dot_ref = (v / (L + Ku*v^2)) * delta
// psi_dot_max = mu * g / v  (Kamm's Circle lateral limit)
// ============================================================

// ============================================================
// v4.0 [TV-4] Vertical Load with Roll Stiffness Distribution
// ΔFz_lat,front = m*ay*h_roll * (kr_f/(kr_f+kr_r)) / t_f
// ΔFz_lat,rear  = m*ay*h_roll * (kr_r/(kr_f+kr_r)) / t_r
// ============================================================

// ============================================================
// v4.0 [TV-3] Adaptive Front/Rear Torque Distribution
// lambda_fr = clip(lambda0 + k_mu*(mu_ref-mu_hat) + k_US*e_psi, min, max)
// ============================================================

// ============================================================
// v4.0 [TV-5] TV Yaw Moment split to front + rear
// ΔFx_f = 2*wf*Mz/t_f,  ΔFx_r = 2*wr*Mz/t_r
// wf + wr = 1 (normalized)
// ============================================================

// ============================================================
// v4.0 [TV-6] Per-wheel Friction Circle Scaling
// s = min_i(mu_i * Fz_i / |Fx_req_i|)  => scale all wheels equally
// Prevents any single wheel from exceeding the friction circle
// ============================================================

// ============================================================
// v4.0 [TV-OPT] Simple mu_hat Estimator (slip-based)
// mu_hat = F_total_est / (m * ax_est)  — passive estimation
// ============================================================
// [R-06-H deleted] estimateMuHat

// [v4 UPDATED] computeYawRateReference — now delegates to v4 physics
// Ku is auto-calculated from Cf/Cr/l_f/l_r when ku_auto_calc=true
// psi_dot saturated at mu*g/v physical limit when psi_dot_sat_enable=true
// ── computeYawRateReference 삭제 → computeYawRateRef_v4(...).psi_ref 직접 호출로 변경 ──

/* ══════════════════════════════════════════════════════════════════
 * torqueVectoring  ← dualMotorAWDDistribution + dynamicTorqueDistribution 완전 통합
 *   Fallback 경로: TV 비활성 → grip-based dynamic split → fixed split → RWD
 ════════════════════════════════════════════════════════════════════ */


// ── [V42-AWD-GRF] AWD 지면반력 시각화 계산 ─────────────────────────────────
/**
 * calcAWDGroundForceViz(params, v_kmh, torq_pct, ay_g)
 * 드라이브 조건 (차속·구동토크%·횡가속) → 4륜 토크·수직하중·마찰원 활용률 계산
 * - computeVerticalLoad_v4 연동 (동적 하중 이동)
 * - 모드별 AWD LUT bilinear2D 보간
 * - torque_vectoring_enable 시 tvYawMomentSplit 좌우 ΔT 반영
 */

/**
// ============================================================
// [R-10-02] GAP-10: 다단 기어 변속 모델 (2-speed / AMT)
// ============================================================
/**
 * initGearState(params)
 * 변속기 상태 초기화
 */

/**
 * getActiveGear(v_ms, T_demand, soc, gear_state, params, dt, t)
 * 속도·토크·SOC 기반 자동 변속 로직
 *   - 변속 히스테리시스 + 변속 충격(torque_dip) 모델링
 *   - 반환: { gear_ratio_eff, gear_state_new, torque_scale }
 */
// ============================================================





// ──────────────────────────────────────────────────────────────
// [G] 가변 CdA — 그릴 셔터 + 속도 의존 항력계수
//   CdA_v3 = CdA_base(shutter) × (1 + k_speed · v²)
// ──────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────
// [B] 자기 포화 L_d(i_d, i_q) / L_q(i_d, i_q) 전압방정식
//   L_d(i_d,i_q) = L_d0 / (1 + k1_d·|i_d| + k2_d·|i_q|)
//   V_q = R_s·i_q + L_q·(di_q/dt) + ω_e·(L_d·i_d + ψ_f)
//   [Steinmetz 철손] P_iron = k_h·f·B^n + k_e·(f·B)²
// ──────────────────────────────────────────────────────────────



// ──────────────────────────────────────────────────────────────
// [C] Arrhenius SOH + Li-Plating 감지
//   dQ_cap/dt = A · exp(-E_a / R·T) · t^(n-1)
//   φ_anode = V_cell - I·R_int → if < 0 → plating
// ──────────────────────────────────────────────────────────────
// ── arrheniusSOH_v3 삭제 → updateSOH 내부로 통합 완료 ──


// ──────────────────────────────────────────────────────────────
// [D/E] Combined Slip 완전체 + Pacejka F_y (E항 포함)
//   μ_res = √(μ_x² + μ_y²) ≤ μ_max  (Friction Ellipse)
//   F_y = D·sin[C·arctan{B·α − E·(B·α − arctan(B·α))}]
// ──────────────────────────────────────────────────────────────


// ──────────────────────────────────────────────────────────────
// [F] ESC / TCS 개입 로직
//   ESC: 요레이트 오차 → 개별 제동 토크 생성
//   TCS: 슬립비 초과 → 구동 토크 컷
// ──────────────────────────────────────────────────────────────



// ──────────────────────────────────────────────────────────────
// v3 UPGRADED resistanceForces (replaces original)
//   Integrates: crosswind + variable CdA + friction ellipse output
// ──────────────────────────────────────────────────────────────
/* ══════════════════════════════════════════════════════════════════
 * resistanceForcesV3  ← tirePressureCrr + roadRoughnessResistance 통합
 *   단일 진입점으로 모든 저항력(공기·구름·경사·IRI·공기압 보정) 산출
 ════════════════════════════════════════════════════════════════════ */

// ── resistanceForcesV3: 공기저항·구름저항·경사·IRI·측풍·가변CdA 완전 통합 v3 ──

// ══════════════════════════════════════════════════════════════════════════════
// [R-12-02] Launch Control 알고리즘
// ══════════════════════════════════════════════════════════════════════════════
/**
 * launchControlTorque(v, omega_m, mu_hat, soc, params, lc_state, dt)
 * 슬립률 기반 최적 토크 램프 — TCS + 런치 컨트롤 통합
 *   slip_ratio = (v_wheel - v_body) / v_body (구동 중)
 *   목표: slip_ratio → lc_slip_target (최적 μ 영역 유지)
 */

// ══════════════════════════════════════════════════════════════════════════════
// [R-12-03] 스키드 패드 정상 선회 시뮬
// ══════════════════════════════════════════════════════════════════════════════
/**
 * simSkidPad(params, R_pad_m)
 * 정상 원운동(Steady-State Circular Motion) 완전 분석
 *   - 횡력 요구량: Fy_req = m·v²/R
 *   - Pacejka 비선형 타이어 횡력으로 최대 속도/G 추정
 *   - 언더스티어 구배(Ku) vs 속도 맵
 */

// ══════════════════════════════════════════════════════════════════════════════
// [R-12-04] 제동 에너지 회수 최적화
// ══════════════════════════════════════════════════════════════════════════════
/**
 * regenEnergyOptimizer(omega_m, v, soc, T_batt, T_motor, params, brake_demand)
 * 동적 최적 회생 토크 산출
 *   목적함수: max E_regen = η_regen·T_regen·ω / (w_E·E_friction + w_J·ΔT²)
 *   제약: T_regen ≤ T_max_regen, SOC ≤ soc_max, T_batt ≤ T_limit
 */

// ══════════════════════════════════════════════════════════════════════════════
// [R-12-06] MRAC — Model Reference Adaptive Control
// ══════════════════════════════════════════════════════════════════════════════
/**
 * computeMRAC(params)
 * MIT 규칙 기반 적응 게인 시뮬레이션
 *   기준 모델: ẋ_m = A_m·x_m + B_m·r  (원하는 응답)
 *   플랜트:    ẋ   = A·x + B·(Kp(t)·r + Kr(t)·x)
 *   적응 법칙: Kṗ = -γ_p·e·r, Kṙ = -γ_r·e·x  (MIT rule)
 */

// ══════════════════════════════════════════════════════════════════════════════
// [R-12-07] H∞ 강인 제어
// ══════════════════════════════════════════════════════════════════════════════
/**
 * computeHinf(params)
 * H∞ 혼합 감도 문제:
 *   min ||[W1·S, W2·KS, W3·T]||∞ < γ
 *   S = 1/(1+PC) : 감도함수, T = PC/(1+PC) : 보완감도함수
 */

// ══════════════════════════════════════════════════════════════════════════════
// [R-12-08] MPC — Model Predictive Control
// ══════════════════════════════════════════════════════════════════════════════
/**
 * computeMPC(params)
 * 속도 추종 MPC: 예측 지평 N 스텝, 상태 x=[v], 입력 u=F_drive
 *   min Σ_{k=0}^{N} [q(v_ref-v_k)² + r·ΔU_k²]
 *   s.t. |ΔU_k| ≤ ΔU_max, |U_k| ≤ U_max
 */

// ══════════════════════════════════════════════════════════════════════════════
// [R-12-09] 전류 루프 d↔q 교차 커플링 해석
// ══════════════════════════════════════════════════════════════════════════════
/**
 * computeDQCoupling(params, omega_e_rad)
 * IPMSM dq 좌표계 전압 방정식 교차 커플링 항 분석
 *   V_d = Rs·Id + Ld·dId/dt − ωe·Lq·Iq   ← q→d 커플링: -ωe·Lq·Iq
 *   V_q = Rs·Iq + Lq·dIq/dt + ωe·Ld·Id + ωe·λpm ← d→q 커플링: +ωe·Ld·Id
 *
 * 분리 보상 후:
 *   V_d* = V_d_PI − ωe·Lq·Iq  (d축 보상 전압)
 *   V_q* = V_q_PI + ωe·Ld·Id + ωe·λpm
 */


// [R-06-H deleted] motorEfficiency


// [R-06-H deleted] computeTorqueFromIq// ============================================================
// Enhanced Regen Braking
// ============================================================

// Reverse energy chain for regen

// ── [R-06] computeBatteryLosses → computeEnergyChain 통합 확인 ────────────────
// computeEnergyChain 내부에서 R_int_T(soc, T_batt, params) 직접 사용 (Ri 모델 일관성 확보)
// computeBatteryLosses는 독립 호출(roadload 등)에서만 사용

// ╔══════════════════════════════════════════════════════════════════╗
// ║           v7 신규 물리 / 제어 모듈                                ║
// ╚══════════════════════════════════════════════════════════════════╝

// [NAV-Dedup] NAV_CATEGORIES 인라인 선언 제거 → constants/navCategories.js import로 대체
// (파일 상단 import { NAV_CATEGORIES, AF_CATS } from './constants/navCategories.js' 로 처리됨)
const AF_CATS = NAV_CATEGORIES; // 내부 하위 호환 별칭

// ──────────────────────────────────────────────────────────────────
// §  PARAM_INFO_DATA — 탭별 적합 파라미터 & 영향 분석 데이터 (이중언어)
// ──────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────
// [v7-2] 노면 거칠기 (IRI 기반 추가 저항)
// 출처: ISO 8608 / Nakajima (2006)
// ΔCrr_IRI = k_IRI_crr·IRI + k_IRI_vib·IRI·v
// ──────────────────────────────────────────────────────────────────

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-2] 동적 Crr — PSD 기반 + 풍향 완전 반영
// ══════════════════════════════════════════════════════════════════════════
// ── dynamicCrrWithWind 삭제 → resistanceForcesV3 (crosswindAeroForce + IRI 통합) 로 대체 ──

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-2] 경사 프로파일 θ(t) — 사인파/평탄/실측 지원
// ══════════════════════════════════════════════════════════════════════════
// [R-06-H deleted] gradeProfile// ══════════════════════════════════════════════════════════════════════════
// [enhanced-3] battery2RCModel SEI_loss 항목 추가
// ══════════════════════════════════════════════════════════════════════════
/* ══════════════════════════════════════════════════════════════════
 * battery2RCModelWithSEI  ← battery2RCModel 완전 통합 (v7 정밀 모델)
 *   2RC 등가회로 + SEI 저항 증가 + Li-plating 위험도 한 함수에서 처리
 *   OCV: batteryOCV_smart (LUT 70% + Nernst 30% + 온도 보정)
 ════════════════════════════════════════════════════════════════════ */

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-4] IPMSM dq 루프 수렴성 검사 + Bode 안정성
// ══════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-7] ESC / TCS — 4휠 토크 + Yaw-rate PI + Kalman μ̂
// ══════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-9] 에너지 체인 — 온도 의존 기어 손실 (확장)
// ══════════════════════════════════════════════════════════════════════════
// [R-06-A] 통합 기어 손실 함수 — gearLossTempEnhanced + gearLossTemp 통합
// params.gear_loss_temp_enable: false → 고정 eta 모드, true(기본) → 온도 의존 모드

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-10] HVAC 히트펌프 확장 모델
// ══════════════════════════════════════════════════════════════════════════
// ── heatPumpPowerEnhanced 삭제 → hvacDetailedPower로 완전 대체 ──

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-11] Pacejka 타이어 복합 슬립 — Dugoff + Friction Circle
// ══════════════════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════════════════════════
// [enhanced-12] AWD 토크 벡터링 — 슬립각 기반
// ══════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-13] SOC Kalman Filter
// ══════════════════════════════════════════════════════════════════════════
// ── socKalmanUpdate 삭제 → kalmanSOCUpdate (EKF/UKF/Linear 통합 디스패처) 로 대체 ──
// ══════════════════════════════════════════════════════════════════════════
// [enhanced-15] BMS 셀 밸런싱 + Fault Diagnosis
// ══════════════════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════════════════════════
// [enhanced-16] Monte Carlo / LHS / Sobol 민감도 분석
// ══════════════════════════════════════════════════════════════════════════

// [R-06-H deleted] sobolSensitivityIndex


// ══════════════════════════════════════════════════════════════════════════
// [R-16-01] Monte Carlo Web Worker 완전 마이그레이션
// ══════════════════════════════════════════════════════════════════════════

// [Refactor] Worker 코드 → workers/montecarlo.worker.js 로 분리

/**
 * simMonteCarloAsyncWorker(params, options)
 * Web Worker 우선 실행 → 미지원 환경은 setTimeout 폴백 보장
 */

// ══════════════════════════════════════════════════════════════════════════
// [R-16-02] Sobol 민감도 지수 완전 구현 (Saltelli 2010 추정량)
// ══════════════════════════════════════════════════════════════════════════

/**
 * vanDerCorput(n, base) — van der Corput 저차원 불일치 수열
 */

/**
 * sobolSequence(n, k)
 * k차원 준난수 Sobol 시퀀스 (van der Corput 기반 간소화 구현)
 * 반환: n×k Array (각 원소 ∈ [0,1])
 */

/**
 * computeSobolIndices(paramRanges, modelFn, N)
 * Saltelli (2010) 추정량 — 1차 Si + 총차수 STi
 *
 * Si  = (1/N) Σ f_B(xⱼ)·[f_ABᵢ(xⱼ) − f_A(xⱼ)] / Var(Y)   (Jansen Si)
 * STi = (1/2N) Σ [f_A(xⱼ) − f_ABᵢ(xⱼ)]² / Var(Y)          (Jansen STi)
 *
 * @param {Object}   paramRanges  { key: [lo, hi], ... }
 * @param {Function} modelFn      (paramObj) → scalar
 * @param {number}   N            기본 샘플 수 (기본 256)
 */

// ══════════════════════════════════════════════════════════════════════════
// [R-16-03] ANOVA (분산분석) 구현
// ══════════════════════════════════════════════════════════════════════════

/**
 * F분포 p값 근사 (Abramowitz & Stegun 공식 기반)
 * betaI(x, a, b) 불완전 베타함수 연속분수 근사
 */


/**
 * F분포 우측 꼬리 확률 P(F > f | df1, df2)
 */

/**
 * computeOneWayANOVA(groups)
 * 일원 분산분석
 * @param {number[][]} groups  그룹별 관측값 배열 (groups.length ≥ 2)
 * 반환: { SS_between, SS_within, SS_total, df_between, df_within,
 *         MS_between, MS_within, F, p_value, eta_sq, omega_sq,
 *         group_means, grand_mean, significant }
 */

/**
 * computeTwoWayANOVA(data, levels_A, levels_B)
 * 이원 분산분석 (균형 설계)
 * @param {Object[][]} data    data[i][j] = 셀 (a_i, b_j)의 관측값 배열
 * @param {string[]}   levels_A  인자 A 레벨 이름
 * @param {string[]}   levels_B  인자 B 레벨 이름
 */

/**
 * computeANOVAFromDOE(doeResult)
 * DOE 결과 → 자동 일원 ANOVA (각 인자별 그룹화)
 */

// ══════════════════════════════════════════════════════════════════════════
// [R-16-04] Pareto 최적화 프론티어
// ══════════════════════════════════════════════════════════════════════════

/**
 * isParetoOptimal(point, candidates, objectives)
 * @param  point       {[key]: value, ...}
 * @param  candidates  배열
 * @param  objectives  [{key, minimize}]
 */

/**
 * computeParetoFront(points, objectives)
 * objectives: [{key, minimize}, ...]
 * 반환: { pareto_front[], dominated[], all_with_rank[] }
 */

/**
 * computeNSGA2Sort(population, objectives)
 * NSGA-II 비지배 정렬 + 혼잡도 거리
 * @param  population  [{key: value, ...}] 배열
 * @param  objectives  [{key, minimize}]
 * 반환: population에 _rank, _crowding 필드 추가
 */

/**
 * sweepParetoSurface(params, factors, objectives, nSamples)
 * 인자 조합 스윕 → Pareto 프론티어 생성
 * objectives 예시: [{key:'range_km',minimize:false},{key:'energy_wh_km',minimize:true}]
 */

// ══════════════════════════════════════════════════════════════════════════
// [R-16-05] 제약 조건 최적화
// ══════════════════════════════════════════════════════════════════════════

/**
 * 기본 제약 조건 정의 (EV 파워트레인 최적화 기준)
 */
/**
 * penaltyObjective(x, objFn, evalFn, constraintDefs, params, mu)
 * Augmented Penalty: f(x) + μ·Σmax(0, gᵢ)²
 */

/**
 * nelderMead(fn, x0, options)
 * Nelder-Mead 심플렉스 최적화 (자체 구현)
 * @param  fn   목적함수 (Array → scalar)
 * @param  x0   초기점 Array
 * @param  options: { maxIter, tol, bounds }
 */

/**
 * constrainedOptimize(objFn, evalFn, factors, constraints, params, options)
 * Augmented Lagrangian 제약 조건 최적화
 * 내부 최적화: Nelder-Mead 심플렉스
 *
 * @param  objFn        ([factor values]) → scalar (최소화)
 * @param  evalFn       ([factor values]) → {range_km, time_0_100, ...}
 * @param  factors      [{name, param_key, low, high}, ...]
 * @param  constraints  CONSTRAINT_DEFS 배열 (active 필드 포함)
 * @param  params       기본 파라미터 객체
 * @param  options      { maxOuter, mu0, muScale, onProgress }
 */

// ══════════════════════════════════════════════════════════════════════════
// [enhanced-4] 4-노드 열 Closed-loop derating (thermalStep 결과 즉시 적용)
// ══════════════════════════════════════════════════════════════════════════

// ── roadRoughnessResistance 삭제 → resistanceForcesV3 내부로 통합 ──

// ──────────────────────────────────────────────────────────────────
// [v7-3] 타이어 공기압 의존 Crr
// ΔCrr = α·(p_nom - p_act)/p_nom + β·((p_nom - p_act)/p_nom)²
// ──────────────────────────────────────────────────────────────────
// ── tirePressureCrr 삭제 → resistanceForcesV3 내부로 통합 ──

// ──────────────────────────────────────────────────────────────────
// [v7-4] SEI 성장 + Li-plating 화학 모델
// SEI 성장: δ_SEI(t) = sqrt(k_SEI · t · exp(-Ea / RT))
// R_sei = R_sei_init + k_sei_R · δ_SEI  (단위저항에서 총저항으로)
// ──────────────────────────────────────────────────────────────────


// ──────────────────────────────────────────────────────────────────
// [v7-5] 비선형 regen / 마찰 블렌딩
// S-커브로 저속·저SOC에서 부드럽게 마찰제동으로 전환
// ──────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────
// [v7-6] 기어박스 온도 의존 손실 맵
// P_gear = P_viscosity(T) + P_bearing(ω) + P_mesh(T)
// ──────────────────────────────────────────────────────────────────
// [R-06-A] gearLossTemp 삭제 → gearLossTempEnhanced (통합 canonical 함수)로 대체

// 기어박스 열 상태 갱신

// ──────────────────────────────────────────────────────────────────
// [v7-7] HVAC 상세 모델 (히트펌프 COP 기반)
// COP_heat = COP_base + k·(T_amb - T_ref_heat) ; 저온에서 COP 하락
// ──────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────
// [v7-8] RLS 기반 μ 재귀 최소제곱 추정
// ──────────────────────────────────────────────────────────────────
// [v7-9] 냉각 유량 최적화 (PI 기반 유량 제어)
// 목표: T_cool → T_target, 조작: flow_rate (L/min)
// 부산물: 펌프 전력 계산
// ──────────────────────────────────────────────────────────────────


// ──────────────────────────────────────────────────────────────────
// [v7-10] Pass / Fail 자동 검증
// ──────────────────────────────────────────────────────────────────

// ╚══════════════════════════════════════════════════════════════════╝
// END OF v7 물리 / 제어 모듈
// ╚══════════════════════════════════════════════════════════════════╝



// [R-06-A] computeGearLosses 삭제 → gearLossTempEnhanced로 통합

// Unified forward energy chain (drive mode)

// ============================================================
// Simulation Kernels (Thermal-Coupled)
// ============================================================


// ════════════════════════════════════════════════════════════════════════════
// [PHASE 2] CORE PHYSICS ENGINE ─ 통합 물리 상태 업데이트 함수
// simLaunch / simCoast / simReaccel / simBraking 공통 물리 방정식 통합
// 시나리오 함수는 입력(throttle/brake_demand) 제어만 담당
// ════════════════════════════════════════════════════════════════════════════

/**
 * initVehiclePhysicsState(params, v_init_ms)
 * 모든 시나리오 공통 초기 물리 상태 생성
 */

/**
 * stepVehiclePhysics(state, inputs, params, dt, dt_th)
 * ─ 단일 타임스텝의 모든 물리 연산 수행
 *   inputs: { throttle, brake_demand, grade_rad, is_regen_only, env_override }
 * ─ 반환: 새로운 state (불변 방식, 원본 미수정)
 */




// ══════════════════════════════════════════════════════════════════════════
// [enhanced-6] simReaccel — 실차 CSV 자동 비교
// ══════════════════════════════════════════════════════════════════════════
// [R-06-H deleted] compareReaccelWithCSV



// 액티브 밸런싱 계산

// 통합 BMS 밸런싱 분석 (셀 전압 배열 입력)
// [R-06-H deleted] analyzeBMSBalance// ═══════════════════════════════════════════════════════
// PHASE 4: ABS, Brake Thermal, Fade, Sensor/Kalman Models
// ═══════════════════════════════════════════════════════

// ── ABS 슬립 제어 ──

// ── 브레이크 디스크 열 모델 ──

// ── 브레이크 페이드 계수 ──

// ── 센서 노이즈 모델 ──

// ── 칼만 필터 SOC 추정기 ──
// ── initKalmanSOC / kalmanUpdateSOC → §Kalman 섹션(_linearKFInit / _linearKFUpdate)으로 이동됨 ──
// ══════════ Braking with Regen + ABS + Brake Thermal + Sensor + NEW v3 Regen Map ══════════

// ════════════════════════════════════════════════════════════════════════════
// [PHASE 3] 유압 제동 지연 모델 (Hydraulic Brake Response Dynamics)
// 회생제동(τ=0.02s)과 유압제동(τ=0.2s)의 시상수 차이 모사
// 제동 블렌딩 시 실제 유압 압력 지연 반영 → 저크 분석에 연계
// ════════════════════════════════════════════════════════════════════════════


/**
 * brakeSystemDynamics(brakeState, p_brake_cmd, p_regen_cmd, params, dt)
 * 
 * - 유압 브레이크: 1차 지연 필터 τ_hyd = 0.2s (마스터 실린더 응답)
 * - 회생제동 모터: 1차 지연 필터 τ_regen = 0.02s (전류 제어 응답)
 * - 두 제동계의 협조제어 시 저크 발생 원인 모사
 * 
 * @param {Object} brakeState initBrakeSystemState() 반환값
 * @param {number} p_brake_cmd  총 제동 요청 [0-1]
 * @param {number} p_regen_cmd  회생제동 요청 [0-1] (≤ p_brake_cmd)
 * @param {Object} params       차량 파라미터
 * @param {number} dt           타임스텝 [s]
 * @returns {{ p_hyd_actual, p_regen_actual, F_hyd, F_regen, jerk_brake, blend_ratio }}
 */

// ════════════════════════════════════════════════════════════════════════════
// [PHASE 3] 캐빈 실내 열역학 모델 (Cabin Thermal Model + HVAC)
// 외기 온도, 태양 복사열, HVAC 제어, 창문 단열 통합
// ════════════════════════════════════════════════════════════════════════════


/**
 * cabinThermalModel(cabinState, env, hvacSetting, params, dt)
 * 
 * 실내 열역학 방정식:
 *   C_cabin * dT_cabin/dt = Q_solar + Q_ext_transfer - Q_hvac
 * 
 * @param {Object} cabinState    initCabinThermalState() 반환값
 * @param {Object} env           { temp_ext, is_sunny, solar_irr_Wm2, v_kmh }
 * @param {Object} hvacSetting   { target_temp, mode }  ('cool'|'heat'|'off')
 * @param {Object} params        차량 파라미터
 * @param {number} dt            타임스텝 [s]
 * @returns {{ T_cabin, q_hvac_pwr_kW, state }}
 */


// ── 긴급 제동 시나리오 (ABS 유무 비교, 페이드 반복 시험) ──

// ════════════════════════════════════════════════════════════════════════════
// [PHASE 4] 승차감(Drivability) 저크 분석 엔진
// Tip-in / Tip-out 토크 필터링에 따른 종방향 저크(m/s³) 계산
// 유압 제동 지연 연계 저크 분석
// ════════════════════════════════════════════════════════════════════════════

/**
 * computeDrivabilityJerk(data, dt)
 * 시뮬레이션 데이터 배열로부터 저크 시계열 및 통계 계산
 * @param {Array}  data  시뮬레이션 스텝 데이터 배열 (각 요소에 .a 필드 필요)
 * @param {number} dt    타임스텝 간격 [s]
 * @returns {{ jerk_series, jerk_max, jerk_rms, jerk_95pct, tip_in_events, tip_out_events }}
 */

/**
 * simDrivabilityTipIn(params, v_init_kmh, throttle_step, torque_filter_tau)
 * 팁인(급가속) 시나리오: 정해진 속도에서 throttle을 스텝 입력
 * 토크 필터 시상수(torque_filter_tau)에 따른 저크 변화 분석
 * 
 * @param {Object} params            차량 파라미터
 * @param {number} v_init_kmh        초기 속도 [km/h]
 * @param {number} throttle_step     스텝 throttle 값 [0-1] (예: 0 → 0.8)
 * @param {number} torque_filter_tau 토크 필터 시상수 [s] (0 = 무필터)
 * @returns {{ data, jerk_analysis }}
 */

/**
 * simDrivabilityTipOut(params, v_init_kmh, throttle_init, torque_filter_tau)
 * 팁아웃(급감속/코스팅 전환) 시나리오
 */

/**
 * simDrivabilityBraking(params, v_init_kmh, brake_demand, torque_filter_tau)
 * 유압 브레이크 지연 + 회생제동 협조에 따른 저크 시나리오
 */

/**
 * runDrivabilityAnalysis(params, options)
 * 전체 승차감 분석 통합 실행
 * @param {Object} params   차량 파라미터
 * @param {Object} options  { v_init_kmh, throttle_step, tau_filter_values, brake_demand }
 */


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// §BODE  PI 제어기 보드 선도 계산 (Bode Plot for PI loops)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
/**
 * Compute Bode data for a PI current controller (d or q axis)
 *
 * Plant (continuous):  G_plant(s) = 1/(L·s + R)
 * Controller (PI):     C(s) = Kp + Ki/s
 * Computation+PWM delay: e^(-s·1.5·Ts)  [1.5-샘플 지연 모델]
 * Open-loop:          L(s) = C(s)·G_plant(s)·e^(-s·1.5·Ts)
 * Closed-loop (CL):   T(s) = L/(1+L)
 *
 * ─── BUG FIX (v3.2) ───────────────────────────────────────
 * 이전 버전: f_max = 20 kHz → wc = Kp/L ≈ 26.5 kHz 가 범위 밖
 *            → 0 dB 교차점 미감지 → PM/BW = N/A
 * 수정:      f_max = 5×wc_est/2π 로 동적 결정 (최소 50 kHz)
 *            + 300 포인트로 해상도 향상
 */

/**
 * Speed loop Bode
 *
 * 이중-루프 구조:
 *   내부(전류) 루프 근사: Gi(s) = ωc_i / (s + ωc_i)  [1차 근사, ωc_i = Kp_id/Ld]
 *   기계적 플랜트:        Gm(s) = 1/(J_eq·s)
 *   속도 루프 제어기:     C_s(s) = Kp_spd + Ki_spd/s  (PI)
 *   개방루프:             L_s(s) = C_s · Gi · Gm
 *
 * ─── BUG FIX (v3.2) ───────────────────────────────────────
 * 이전 버전: f_min = 0.1 Hz → 0 dB 교차점 ≈ 0.48 Hz 가 범위 밖
 *            → PM = N/A, GM = ∞ 표기 오류
 * 수정:      f_min = 0.001 Hz 로 확장 (저주파 이중적분 이득 포착)
 *            + GM = ∞ 일 때 물리적 해석을 UI에 명확히 표시
 *            + 위상 -180° 저주파 접근 경고 로직 추가
 */


// ══════════════════════════════════════════════════════════════
// §CONTROL_LOOP  제어 루프 심화 분석 함수 모음  [v08 신규]
//  ① computeNyquistData    — Nyquist 선도 (연속/이산 선택)
//  ② computeRootLocus      — 근궤적 (PI + 1차 플랜트)
//  ③ computeZDomainData    — 이산 시간 Z-domain 분석
//  ④ simAntiWindup         — Anti-windup 비교 시뮬레이션
//  ⑤ computeAutoTuning     — Ziegler-Nichols / IMC 자동 게인 설계
//  ⑥ computeCascadeAnalysis— 전류·속도·위치 3중 루프 계층 분석
//  ⑦ computeGainScheduling — 구간별 최적 Kp/Ki 게인 스케줄링  [A33 신규]
// ══════════════════════════════════════════════════════════════

// ─── ① Nyquist 선도 ────────────────────────────────────────────

// ─── ② 근궤적 (Root Locus) ─────────────────────────────────────

// ─── ③ Z-domain 이산 시간 분석 ────────────────────────────────

// ─── ④ Anti-windup 비교 시뮬레이션 ────────────────────────────

// ─── ⑤ 자동 PI 게인 튜닝 (Ziegler-Nichols / IMC / SIMC) ────────

// ─── ⑥ 다중 루프 계층 (Cascade) 분석 ────────────────────────────

// ══════════════════════════════════════════════════════════════════════════════
// [A33] ⑦ computeGainScheduling — 구간별 최적 Kp/Ki 게인 스케줄링
// ══════════════════════════════════════════════════════════════════════════════
/**
 * 3가지 스케줄링 축(RPM / 토크 / 온도)에 대해 구간별로
 * IMC 기반 최적 Kp·Ki를 산출합니다.
 *
 * 전류 루프:  Kp = τ / (K·(λ+θ))   Ki = Kp / τ   (IMC)
 *             τ = Lq/Rs(T),  K = 1/Rs(T),  λ = 5·ctrl_dt
 * 속도 루프:  Kp = J_eq·ωci / N_ratio  Ki = Kp·ωcs
 *             J_eq 는 토크 구간에서 변하지 않으나
 *             모터 특성(Rs, Lq)이 온도/RPM 에 따라 변함
 *
 * 반환 구조:
 *   { rpm: [{rpm, Kp_i, Ki_i, Kp_s, Ki_s, tau_i, BW_i_hz, BW_s_hz, PM_i, PM_s}],
 *     torque: [...],
 *     temp: [...],
 *     nominal: {Kp_i, Ki_i, Kp_s, Ki_s} }
 */

// ══════════════════════════════════════════════════════════════════════════════
// [A34] computeControlSynthesis — 전체 제어 루프 분석 종합 및 추천 게인 산출
// ══════════════════════════════════════════════════════════════════════════════
/**
 * 모든 분석 서브탭 결과를 입력받아:
 *   1. 항목별 Pass/Warn/Fail 신호등 판정
 *   2. 추천 Kp_iq, Ki_iq, Kp_spd, Ki_spd 산출 (가중 평균)
 *   3. 종합 신뢰도 점수 (0~100)
 *
 * @param {object} params  - 현재 파라미터
 * @param {object} cascade - computeCascadeAnalysis 결과
 * @param {object} nyquist - computeNyquistData 결과 (loopType='current')
 * @param {object} autotuning - computeAutoTuning 결과
 * @param {object} antiwindup - simAntiWindup 결과
 * @param {object} zdomain  - computeZDomainData 결과
 * @param {object} gainSched- computeGainScheduling 결과
 */


// ─────────────────────────────────────────────────────────────
// Road Load 분석 전용 헬퍼 (R1~R4 그래프)
// ─────────────────────────────────────────────────────────────





// [A20-05] rlCalcVmax: 시나리오(구배, 적재) 조건에서 최대 정속 가능 속도 계산
// P_motor_max(v) ≥ P_road(v, grade, mass) 를 만족하는 최대 v 를 1 km/h 정밀도로 탐색
// [A21-11-01] 비교 기준 통일: 양쪽 모두 휠 출력(kW) 기준으로 비교
// [Advance29-WARN01] throttle_pct 인자 추가: 부분 개도 시 v_max(전력 기반) 정확히 반영
// [Fix-RL1] headwind_ms 인자 추가: 맞바람 조건에서 v_max 정확 반영

// [A21-01] rlCalcVmaxTorque: 구동 토크(T_drive_wheel) ↔ 주행저항 환산 휠 토크(T_resist_wheel) 균형점
// ──────────────────────────────────────────────────────────────────────────────
//  물리 원리:
//    구동 토크    T_drive_wheel(v) = T_motor(v) × gear_ratio × η_driveline  (N·m)
//    저항 환산 토크 T_resist_wheel(v) = F_total(v, grade, mass) × r_wheel     (N·m)
//
//  T_drive_wheel ≥ T_resist_wheel 를 만족하는 최대 v → v_max_torque [km/h]
//  이 속도가 타이어에서 실현 가능한 물리적 최대 속도 (토크 평형점, R0 기준)
// ──────────────────────────────────────────────────────────────────────────────
// [Fix-RL2] headwind_ms 인자 추가: 토크 기반 v_max도 맞바람 반영




// [Fix-RL3] user_grade_pct, user_v_wind 추가: 사용자 설정 구배·맞바람 시나리오 반영
// ─────────────────────────────────────────────────────────────


// ============================================================
// Analysis Functions
// ============================================================




// Thermal stress test: repeated WOT acceleration


// ============================================================
// UI Components
// ============================================================
// Vehicle Presets
// ============================================================





// ============================================================
// Extended Vehicle Presets — Kia / Hyundai / Tesla (Advance59)
// ─────────────────────────────────────────────────────────────
// 공통 메모
//   • 800V 차량: V_dc=680(피크 800), I_max_rms 낮춤
//   • 400V 차량: V_dc=400, I_max_rms 높음
//   • CdA = Cd × 전면적(A), Crr 공칭값
//   • l_f/l_r = 무게중심까지 거리 (wheel_base × 전방하중비)
// ─────────────────────────────────────────────────────────────

// ─── Tesla Model Y Long Range AWD (2024) ────────────────────
// 공식 제원: m=2003 kg, WLTP 533 km, 75 kWh, Cd=0.227, A≈2.59

// ─── Tesla Model S Plaid (2024) ──────────────────────────────
// 공식 제원: m=2162 kg, WLTP ~637 km, 95 kWh(usable), Cd=0.208, A≈2.36

// ─── Tesla Model X Long Range AWD (2024) ────────────────────
// 공식 제원: m=2487 kg, WLTP ~560 km, 95 kWh(usable), Cd=0.250, A≈2.85

// ─── Hyundai Ioniq 5 Long Range AWD (E-GMP 800V, 2023) ───────
// 공식 제원: m=2100 kg, WLTP 481 km, 77.4 kWh, Cd=0.288, A≈2.46

// ─── Hyundai Ioniq 5 Long Range RWD (E-GMP 800V, 2023) ───────
// 공식 제원: m=1985 kg, WLTP 507 km, 77.4 kWh, 후륜단독 160kW

// ─── Hyundai Ioniq 5 N (E-GMP 800V, 2024) ───────────────────
// 공식 제원: m=2235 kg, WLTP 448 km (추정), 84 kWh, 478kW(부스트 608kW)

// ─── Kia EV6 Long Range AWD (E-GMP 800V, 2023) ───────────────
// 공식 제원: m=2010 kg, WLTP 506 km, 77.4 kWh, 239kW/605Nm AWD

// ─── Kia EV6 GT (E-GMP 800V, 2023) ─────────────────────────
// 공식 제원: m=2155 kg, WLTP 424 km, 77.4 kWh, 430kW/740Nm AWD

// ─── Kia EV9 Long Range AWD (800V, 2024) ────────────────────
// 공식 제원: m=2597 kg, WLTP 505 km, 99.8 kWh, 283kW/600Nm AWD

// ─── Kia EV3 Long Range RWD (400V, 2024) ────────────────────
// 공식 제원: m=1685 kg, WLTP 605 km, 81.4 kWh, 150kW/310Nm RWD

// ─── Kia EV6 Long Range RWD (E-GMP 800V, 2023) ───────────────
// 공식 제원: m=1870 kg, WLTP 528 km, 77.4 kWh, 168kW/350Nm RWD

// ============================================================
// Comparison Simulation Runner
// ============================================================
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// §CSV_TEMPLATE  Full-Spec 드라이빙 사이클 CSV 분석기
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
/**
 * Template column spec (10 ms sampling):
 * Col 0: Time_s           [s]     - 시간 (0.01 s steps)
 * Col 1: APS_pct          (%)     - 가속 페달 (0–100)
 * Col 2: BPS_pct          (%)     - 브레이크 페달 (0–100)
 * Col 3: DriveMode        [-]     - ECO=0 / NORMAL=1 / SPORT=2
 * Col 4: SteerAngle_deg   [deg]   - 횡방향 조향각
 * Col 5: T_amb_C          (°C)    - 외기온
 * Col 6: RoadMu           [-]     - 노면 마찰계수
 * Col 7: Grade_deg        [deg]   - 구배 (+상향)
 *
 * Header row 0 (name): Time_s, APS_pct, BPS_pct, DriveMode, SteerAngle_deg, T_amb_C, RoadMu, Grade_deg
 * Header row 1 (unit): [s], (%), (%), [ECO=0/NOR=1/SPT=2], [deg], (°C), [-], [deg]
 * Row 2+: numeric data
 *
 * Initial conditions (separate section at bottom, prefixed with #):
 *   # init_soc_pct, 90
 *   # init_batt_temp_C, 25
 *   # init_speed_kmh, 0
 */

/** Generate CSV template content (string) */

/** Parse full-spec driving cycle CSV → structured object */

/**
 * Simulate parsed full-spec cycle using vehicle params
 * Uses Euler integration, 10 ms step assumed
 */

// ============================================================
// [A36-PERF] runCoreSim / runHeavySim — 2단계 우선순위 분리
//   Phase-1 Core  (~300ms): 도로부하·발진·재가속·코스팅·제동·정상효율·에너지소비 → 즉시 표시
//   Phase-2 Heavy (~700ms): WLTP·온도민감도·열스트레스·민감도분석 → 백그라운드 완성
// ============================================================





// [A36-PERF] Core-only comparison (fast, ~600ms for both vehicles)






// ============================================================
// [Disp11] Compare — Simulation Helpers
// ============================================================
// [Advance29-BUG06] thr 인자 추가: 현재 rlThrottle을 반영한 민감도 계산






// ============================================================
// [BugFix7] Error Boundary - 렌더 크래시 원인을 화면에 표시
// ============================================================
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[EV Sim] Render error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace', background: '#1a1a2e', color: '#ef4444', minHeight: '100vh' }}>
          <h2 style={{ color: '#f59e0b' }}>⚠️ EV Simulator - Render Error</h2>
          <p style={{ color: '#e2e8f0', marginBottom: 16 }}>렌더링 중 오류가 발생했습니다. 아래 오류 메시지를 확인하세요.</p>
          <pre style={{ background: '#0d0d1a', padding: 20, borderRadius: 8, color: '#ef4444', fontSize: 12, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
          </pre>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginTop: 20, padding: '8px 20px', background: '#f59e0b', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}


// ══════════════════════════════════════════════════════════════════════════════
// §  DOE / BAYESIAN OPTIMIZATION ENGINE  [Refactoring_06 신규]
// ══════════════════════════════════════════════════════════════════════════════
// [DOE-1]  2^k 완전 요인 설계 (Full Factorial)
// [DOE-2]  Gaussian Process 서로게이트 + EI 기반 Bayesian 최적화
// [DOE-3]  신뢰 구간 자동 계산 (t-분포 / Bootstrap)
// [DOE-4]  통계 보고서 CSV 자동 출력
// ══════════════════════════════════════════════════════════════════════════════

/* ── [DOE-1] 2^k 완전 요인 설계 ─────────────────────────────────────────── */



/* ── [DOE-2] Gaussian Process (RBF kernel) + Bayesian Opt ───────────────── */





/* ── [DOE-3] 신뢰 구간 계산 ─────────────────────────────────────────────── */


/* ── [DOE-4] CSV 보고서 생성 + 다운로드 ─────────────────────────────────── */

function downloadCSVReport(csv, filename) {
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// ============================================================
// RLGraphCard의 ℹ 버튼 클릭 시 표시되는 그래프 정보 팝업

function RLKhDetail({ hw, lang }) {
  if (!hw) return null;
  const kps = hw.keyPoints || hw.kps || [];
  const acts = hw.actions || hw.acts || [];
  if (kps.length === 0 && acts.length === 0) return null;
  return (
    <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:10 }}>
      {kps.length > 0 && (
        <div style={{ flex:1, minWidth:180 }}>
          <div style={{ fontSize:10, fontWeight:700, color:COLORS.textMuted, marginBottom:5 }}>
            {lang==="en"?"📌 Key Points":"📌 핵심 포인트"}
          </div>
          {kps.map(function(p, i) {
            return (
              <div key={i} style={{ fontSize:10, color:COLORS.text, marginBottom:4,
                paddingLeft:8, borderLeft:"2.5px solid "+(hw.color||"#6b7280"), lineHeight:1.55 }}>
                {p}
              </div>
            );
          })}
        </div>
      )}
      {acts.length > 0 && (
        <div style={{ flex:1, minWidth:180 }}>
          <div style={{ fontSize:10, fontWeight:700, color:COLORS.textMuted, marginBottom:5 }}>
            {lang==="en"?"🔧 Action Items":"🔧 실무 액션"}
          </div>
          {acts.map(function(a, i) {
            return (
              <div key={i} style={{ fontSize:10, color:COLORS.text, marginBottom:4,
                paddingLeft:8, borderLeft:"2.5px solid #22c55e", lineHeight:1.55 }}>
                {a}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 노하우 버튼 (Launch HwButton 동일) ──────────────────────────────




function generateCSV(data, columns, lang) {
  if (!data || data.length === 0) return "";
  const keys = columns || Object.keys(data[0]);
  // ─────────────────────────────────────────────────────────────────────────
  // [V33] Row 2: Physical description — full key review across all datasets
  //   Datasets covered:
  //     simLaunch / simReaccel   → t v_kmh v_ms a s F_drv F_aero F_roll F_grade
  //                                P_mech P_elec T_motor_torque omega_motor
  //                                T_batt T_inv T_motor_temp T_cool
  //                                derate_batt derate_inv derate_motor
  //                                F_rough delta_Crr_p T_gear eta_gear_v7
  //                                mu_rls regen_blend sei_R li_plating
  //                                pump_power coolant_flow drive_mode throttle_eff
  //                                foc_mode m_eff tcs_active tcs_T_cut
  //                                P_iron_steinmetz P_hys P_eddy
  //                                CdA_v3 shutter_open delta_F_wind crosswind_on
  //                                lc_active lc_slip lc_e_slip
  //                                awd_mode lambda_fr TV_active yaw_rate_ref yaw_error
  //                                Ku M_z_required friction_scale mu_hat
  //                                Fz_FL Fz_FR Fz_RL Fz_RR
  //                                T_FL T_FR T_RL T_RR T_front_total T_rear_total
  //                                fw_regime is_regen T_regen T_friction_brake P_regen
  //                                esc_active esc_M_z psi_dot beta ay_lat
  //                                V_terminal R0_batt soh_pct li_plating_events
  //                                kalman_soc bms_max_dv cool_phi_flow li_plating_risk
  //                                driver_pedal driver_pid_active gear_num gear_ratio_eff
  //                                gear_shifting torque_scale_gear
  //                                hotspot_T T_delta_3d derate_3d
  //                                + ...chain spread: P_wheel P_motor_mech P_ac P_dc
  //                                  P_batt_chem P_aux P_loss_batt P_loss_inv P_loss_motor
  //                                  P_loss_gear P_cu P_fe P_mech_loss P_aero P_roll P_grade
  //                                  eta_batt eta_inv eta_motor eta_gear eta_total
  //                                  Q_batt Q_inv Q_motor T_batt T_inv T_motor T_cool
  //                                  derate_batt derate_inv derate_motor
  //                                  Rs_eff lambda_eff Ri_eff rho_eff Crr_eff
  //     simCoast                 → t v_kmh s a F_aero F_roll F_grade F_total
  //                                F_dt_drag P_gear_drag P_mech_drag P_fe_coast P_dt_drag
  //                                soc rho_eff Crr_eff T_motor_temp
  //                                F_regen_coast regen_coast_active
  //     simBraking               → t v_kmh v_kmh_sensor a a_sensor s lambda abs_active
  //                                fade_factor T_disc F_regen F_friction F_brake F_aero F_roll
  //                                T_regen P_batt_charge eta_regen soc T_batt T_inv
  //                                T_motor_temp E_regen_cum
  //     simRoadLoad              → v_kmh F_drive_max eta_total_ss P_batt_ss kWh_100km
  //                                (dynamic: F_{gd}° P_{gd}° v_lim_{gd}deg)
  //     generateSteadyStateEff.  → v_kmh eta_batt eta_inv eta_motor eta_gear eta_total
  //                                P_loss_batt P_loss_inv P_cu P_fe P_mech_loss
  //                                P_loss_gear P_aux P_aero P_roll kWh_100km
  //     generateEnergyConsumption→ v_kmh kWh_100km range_km P_batt
  //     simThermalStress         → cycle t_global T_batt T_inv T_motor T_cool soc
  //                                launch_time derate_batt derate_inv derate_motor
  //     sensitivityAnalysis      → param key p_base dT_launch_hi dT_launch_lo
  //                                dT_coast_hi dT_coast_lo S_launch S_coast S_regen
  //                                E_launch_hi E_launch_lo eta_regen_hi eta_regen_lo
  //                                (results): param variation value launch_time coast_time
  //     simWLTP                  → t phase v_ref v_sim soc T_batt T_motor T_inv T_cool
  //                                d_cum E_cum_kWh E_net_cum_kWh E_regen_cum
  //                                kWh_100km_cum derate_batt derate_inv derate_motor
  //     simWLTPTempSens          → e_wltp range_km
  //     simFullSpecCycle         → t v_kmh aps bps a_ms2 soc_pct P_kW P_mech_kW P_aux_kW
  //                                F_drv F_brk F_air F_roll F_grade F_res grade T_amb
  //                                T_batt road_mu dist_m drive_mode steer_deg a_y F_scrub mu_lon
  //     compare_summary          → metric metricEn [vehA] [vehB] unit
  //     wltpTempSens             → e_wltp range_km
  // ─────────────────────────────────────────────────────────────────────────
  const koNameMap = {
    // ── 시간·거리·속도·가속도 ──
    t:                  "시간[s]",
    v_kmh:              "차속[km/h]",
    v_ms:               "차속[m/s]",
    v:                  "차속[m/s]",
    v_ref:              "목표차속[km/h]",
    v_sim:              "시뮬차속[km/h]",
    a:                  "가속도[m/s²]",
    a_ms2:              "가속도[m/s²]",
    a_sensor:           "가속도(센서측정)[m/s²]",
    s:                  "이동거리[m]",
    dist_m:             "이동거리[m]",
    dist_km:            "이동거리[km]",
    d_cum:              "누적주행거리[km]",
    v_kmh_sensor:       "차속(센서측정)[km/h]",
    v_kmh_ref:          "목표차속[km/h]",
    v_max_kmh:          "최고속도[km/h]",
    v_avg_kmh:          "평균속도[km/h]",
    // ── 배터리·SOC ──
    soc:                "배터리SOC[0-1]",
    soc_pct:            "배터리SOC[%]",
    dSOC:               "SOC변화량[-]",
    I_batt:             "배터리전류[A]",
    V_dc:               "DC링크전압[V]",
    Vdc:                "DC링크전압[V]",
    V_terminal:         "배터리단자전압[V]",
    R0_batt:            "배터리내부저항R0[Ω]",
    Ri_eff:             "유효내부저항Ri[Ω]",
    soh_pct:            "배터리SOH[%]",
    kalman_soc:         "칼만필터추정SOC[%]",
    bms_max_dv:         "BMS셀최대전압편차[mV]",
    sei_R:              "SEI피막저항[Ω]",
    li_plating:         "리튬석출카운트[-]",
    li_plating_events:  "리튬석출이벤트수[-]",
    li_plating_risk:    "리튬석출위험도[-]",
    cool_phi_flow:      "냉각수유량계수[-]",
    hotspot_T:          "3D배터리최고온도[°C]",
    T_delta_3d:         "3D배터리온도편차[°C]",
    derate_3d:          "3D온도디레이팅계수[-]",
    // ── 전력·에너지 ──
    P_kW:               "배터리전력[kW]",
    P_mech_kW:          "기계출력[kW]",
    P_aux_kW:           "보조부하전력[kW]",
    P_aux_W:            "보조부하전력[W]",
    P_batt_chem:        "배터리화학전력(방전기준)[kW]",
    P_batt_kW:          "배터리전력[kW]",
    P_batt_ss:          "정속배터리전력[kW]",
    P_batt:             "배터리전력[kW]",
    P_batt_charge:      "회생충전전력[kW]",
    P_wheel:            "휠출력[kW]",
    P_motor_mech:       "모터기계출력[kW]",
    P_ac:               "모터AC입력전력[kW]",
    P_dc:               "인버터DC입력전력[kW]",
    P_aux:              "보조부하전력[kW]",
    P_loss_batt:        "배터리Ri손실[kW]",
    P_loss_inv:         "인버터스위칭손실[kW]",
    P_loss_motor:       "모터전자기손실[kW]",
    P_loss_gear:        "기어마찰손실[kW]",
    P_cu:               "모터구리손(동손)[kW]",
    P_fe:               "모터철손[kW]",
    P_mech_loss:        "모터기계손실[kW]",
    P_aero:             "공기저항소비전력[kW]",
    P_roll:             "구름저항소비전력[kW]",
    P_grade:            "경사저항소비전력[kW]",
    P_elec:             "전기입력전력[kW]",
    P_mech:             "기계출력전력[kW]",
    P_regen:            "회생제동전력[kW]",
    P_iron_steinmetz:   "Steinmetz철손전력[W]",
    P_hys:              "히스테리시스손[W]",
    P_eddy:             "와전류손[W]",
    P_gear_drag:        "기어항력전력[kW]",
    P_mech_drag:        "기계항력전력[kW]",
    P_fe_coast:         "코스팅철손전력[kW]",
    P_dt_drag:          "구동계항력전력[kW]",
    pump_power:         "냉각펌프전력[W]",
    Q_batt:             "배터리발열량[W]",
    Q_inv:              "인버터발열량[W]",
    Q_motor:            "모터발열량[W]",
    E_cum_kWh:          "누적구동에너지[kWh]",
    E_net_cum_kWh:      "누적순소비에너지[kWh]",
    E_regen_cum:        "누적회생에너지[kWh]",
    E_batt_kWh:         "배터리에너지소비[kWh]",
    E_regen_kWh:        "회생에너지회수[kWh]",
    E_regen_Wh:         "회생에너지회수[Wh]",
    E_friction_Wh:      "마찰제동에너지소산[Wh]",
    kWh_100km:          "전비[kWh/100km]",
    kWh_100km_cum:      "누적전비[kWh/100km]",
    range_km:           "항속거리[km]",
    regen_pct:          "회생비율[%]",
    eta_regen:          "회생효율[%]",
    consumption_kWh100: "전비[kWh/100km]",
    e_wltp:             "WLTP인증전비[kWh/100km]",
    // ── 온도 ──
    T_batt:             "배터리온도[°C]",
    T_motor:            "모터권선온도[°C]",
    T_motor_temp:       "모터권선온도[°C]",
    T_inv:              "인버터온도[°C]",
    T_cool:             "냉각수온도[°C]",
    T_disc:             "브레이크디스크온도[°C]",
    T_amb:              "외기온도[°C]",
    T_gear:             "기어오일온도[°C]",
    t_global:           "누적경과시간[s]",
    // ── 힘 ──
    F_drv:              "구동력[N]",
    F_brk:              "제동력[N]",
    F_aero:             "공기저항력[N]",
    F_air:              "공기저항력[N]",
    F_roll:             "구름저항력[N]",
    F_grade:            "경사저항력[N]",
    F_res:              "총주행저항력[N]",
    F_total:            "총합력[N]",
    F_regen:            "회생제동력[N]",
    F_friction:         "마찰제동력[N]",
    F_brake:            "총제동력[N]",
    F_net:              "순합력[N]",
    F_brake_friction:   "마찰제동력[N]",
    F_regen_coast:      "코스팅회생제동력[N]",
    F_dt_drag:          "구동계항력[N]",
    F_rough:            "노면요철저항력[N]",
    F_drive_max:        "최대구동력[N]",
    F_scrub:            "타이어스크럽힘[N]",
    delta_F_wind:       "측풍부가공기저항력[N]",
    // ── 토크 ──
    T_motor_torque:     "모터토크[Nm]",
    T_motor_Nm:         "모터토크[Nm]",
    T_regen:            "회생제동토크[Nm]",
    T_regen_Nm:         "회생제동토크[Nm]",
    T_friction_brake:   "마찰제동토크[Nm]",
    T_wheel_Nm:         "휠토크[Nm]",
    T_FL:               "좌전휠토크[Nm]",
    T_FR:               "우전휠토크[Nm]",
    T_RL:               "좌후휠토크[Nm]",
    T_RR:               "우후휠토크[Nm]",
    T_front_total:      "전축합산토크[Nm]",
    T_rear_total:       "후축합산토크[Nm]",
    tcs_T_cut:          "TCS토크컷량[Nm]",
    esc_M_z:            "ESC요요모멘트[Nm]",
    M_z_required:       "요구요요모멘트[Nm]",
    // ── 회전·속도 ──
    omega_motor:        "모터각속도[rad/s]",
    omega_m:            "모터각속도[rad/s]",
    rpm:                "모터회전수[RPM]",
    // ── 전류·전압(d-q축) ──
    Id:                 "d축전류(자화전류)[A]",
    Iq:                 "q축전류(토크전류)[A]",
    Vd:                 "d축전압[V]",
    Vq:                 "q축전압[V]",
    // ── 효율 ──
    eta_batt:           "배터리효율[%]",
    eta_inv:            "인버터효율[%]",
    eta_motor:          "모터효율[%]",
    eta_gear:           "기어효율[%]",
    eta_total:          "파워트레인종합효율[%]",
    eta_total_ss:       "정속파워트레인효율[-]",
    eta_gear_v7:        "기어효율(동적)[−]",
    // ── 디레이팅 ──
    derate_batt:        "배터리디레이팅[%]",
    derate_inv:         "인버터디레이팅[%]",
    derate_motor:       "모터디레이팅[%]",
    // ── 제동·ABS·슬립 ──
    lambda:             "타이어슬립률[-]",
    abs_active:         "ABS개입여부[0/1]",
    fade_factor:        "브레이크페이드계수[-]",
    regen_coast_active: "코스팅회생활성[0/1]",
    abs_trigger:        "ABS작동[-]",
    slip_ratio:         "슬립률[-]",
    brake_fade:         "브레이크페이드[-]",
    // ── 노면·환경 ──
    grade:              "노면경사각[°]",
    grade_deg:          "노면경사각[°]",
    mu:                 "타이어노면마찰계수[-]",
    road_mu:            "노면마찰계수[-]",
    mu_hat:             "RLS추정마찰계수[-]",
    mu_rls:             "RLS추정마찰계수[-]",
    rho_eff:            "유효공기밀도[kg/m³]",
    Crr_eff:            "유효구름저항계수[-]",
    delta_Crr_p:        "타이어압력구름저항보정[×10³]",
    CdA_v3:             "유효공기저항면적CdA[m²]",
    // ── 페달 ──
    aps:                "가속페달개도[%]",
    bps:                "제동페달개도[%]",
    driver_pedal:       "드라이버페달명령[-]",
    throttle_eff:       "유효스로틀개도[-]",
    // ── 모드·상태 ──
    phase:              "주행구간[-]",
    drive_mode:         "드라이브모드[-]",
    fw_regime:          "FOC제어영역(MTPA/FW)[-]",
    foc_mode:           "FOC전류제어방식[-]",
    awd_mode:           "구동모드(RWD/AWD)[-]",
    is_regen:           "회생제동구간[0/1]",
    tcs_active:         "TCS작동여부[0/1]",
    esc_active:         "ESC작동여부[0/1]",
    driver_pid_active:  "PID드라이버활성[0/1]",
    gear_shifting:      "기어변속중[0/1]",
    shutter_open:       "에어셔터개방[0/1]",
    crosswind_on:       "측풍활성[0/1]",
    lc_active:          "런치컨트롤활성[0/1]",
    // ── 차량동역학 ──
    psi_dot:            "요레이트[rad/s]",
    beta:               "차체슬립각[rad]",
    ay_lat:             "횡방향가속도[m/s²]",
    a_y:                "횡방향가속도[m/s²]",
    steer_deg:          "조향각[°]",
    mu_lon:             "종방향마찰한계[-]",
    lambda_fr:          "전후축토크배분비[-]",
    TV_active:          "토크벡터링활성[0/1]",
    yaw_rate_ref:       "목표요레이트[rad/s]",
    yaw_error:          "요레이트오차[rad/s]",
    Ku:                 "언더스티어그래디언트[rad/(m/s²)]",
    friction_scale:     "마찰한계스케일[-]",
    Fz_FL:              "좌전타이어수직력[N]",
    Fz_FR:              "우전타이어수직력[N]",
    Fz_RL:              "좌후타이어수직력[N]",
    Fz_RR:              "우후타이어수직력[N]",
    // ✅ [FIX] 공기역학 다운포스 ↔ Fz 실시간 루프 — 신규 출력 필드
    F_down:             "공기역학다운포스[N]",
    dFz_aero_f:         "전륜다운포스수직력증가[N]",
    dFz_aero_r:         "후륜다운포스수직력증가[N]",
    // ── 기어·구동계 ──
    gear_num:           "현재기어단수[-]",
    gear_ratio_eff:     "유효기어비[-]",
    torque_scale_gear:  "기어단별토크스케일[-]",
    coolant_flow:       "냉각수유량[L/min]",
    regen_blend:        "회생-마찰제동혼합비[-]",
    m_eff:              "유효차량질량(회전관성포함)[kg]",
    Rs_eff:             "유효고정자저항Rs[Ω]",
    lambda_eff:         "유효쇄교자속λ[Wb]",
    lc_slip:            "런치컨트롤슬립[-]",
    lc_e_slip:          "런치컨트롤슬립오차[-]",
    // ── 민감도 분석 ──
    param:              "파라미터명",
    key:                "파라미터키",
    variation:          "변화율[%]",
    value:              "파라미터값",
    p_base:             "기준파라미터값",
    dT_launch_hi:       "발진시간변화(+)[s]",
    dT_launch_lo:       "발진시간변화(-)[s]",
    dT_coast_hi:        "코스트시간변화(+)[s]",
    dT_coast_lo:        "코스트시간변화(-)[s]",
    S_launch:           "발진시간무차원민감도[-]",
    S_coast:            "코스트시간무차원민감도[-]",
    S_regen:            "회생효율무차원민감도[-]",
    E_launch_hi:        "발진에너지(+)[Wh]",
    E_launch_lo:        "발진에너지(-)[Wh]",
    eta_regen_hi:       "회생효율(+)[%]",
    eta_regen_lo:       "회생효율(-)[%]",
    launch_time:        "발진0→100시간[s]",
    coast_time:         "코스트100→0시간[s]",
    launch_100_time_s:  "발진0→100시간[s]",
    coast_100_time_s:   "코스트100→0시간[s]",
    central_diff:       "민감도(중앙차분)[-]",
    delta_pct:          "파라미터변화율[%]",
    // ── 비교요약 ──
    metric:             "성능지표명",
    metricEn:           "성능지표명(영문)",
    unit:               "단위",
    unitLabel:          "단위",
    // ── 열해석 ──
    cycle:              "반복사이클번호[-]",
  };

  const enNameMap = {
    // ── Time · Distance · Speed · Acceleration ──
    t:                  "Time[s]",
    v_kmh:              "Vehicle Speed[km/h]",
    v_ms:               "Vehicle Speed[m/s]",
    v:                  "Vehicle Speed[m/s]",
    v_ref:              "Target Speed[km/h]",
    v_sim:              "Simulated Speed[km/h]",
    a:                  "Acceleration[m/s²]",
    a_ms2:              "Acceleration[m/s²]",
    a_sensor:           "Acceleration (sensor meas.)[m/s²]",
    s:                  "Travel Distance[m]",
    dist_m:             "Travel Distance[m]",
    dist_km:            "Travel Distance[km]",
    d_cum:              "Cumul. Travel Distance[km]",
    v_kmh_sensor:       "Vehicle Speed (sensor meas.)[km/h]",
    v_kmh_ref:          "Target Speed[km/h]",
    v_max_kmh:          "Max Speed[km/h]",
    v_avg_kmh:          "Average Speed[km/h]",
    // ── Battery · SOC ──
    soc:                "Battery SOC[0-1]",
    soc_pct:            "Battery SOC[%]",
    dSOC:               "SOC Change[-]",
    I_batt:             "Battery Current[A]",
    V_dc:               "DC Link Voltage[V]",
    Vdc:                "DC Link Voltage[V]",
    V_terminal:         "Battery Terminal Voltage[V]",
    R0_batt:            "Battery Internal Resistance R0[Ω]",
    Ri_eff:             "Effective Internal Resistance Ri[Ω]",
    soh_pct:            "Battery State of Health[%]",
    kalman_soc:         "Kalman Filter SOC Estimate[%]",
    bms_max_dv:         "BMS Max Cell Voltage Deviation[mV]",
    sei_R:              "SEI Film Resistance[Ω]",
    li_plating:         "Li Plating Count[-]",
    li_plating_events:  "Li Plating Event Count[-]",
    li_plating_risk:    "Li Plating Risk Level[-]",
    cool_phi_flow:      "Coolant Flow Factor[-]",
    hotspot_T:          "3D Battery Hotspot Temp.[°C]",
    T_delta_3d:         "3D Battery Temp. Gradient[°C]",
    derate_3d:          "3D Thermal Derate Factor[-]",
    // ── Power · Energy ──
    P_kW:               "Battery Power[kW]",
    P_mech_kW:          "Mechanical Output Power[kW]",
    P_aux_kW:           "Auxiliary Load Power[kW]",
    P_aux_W:            "Auxiliary Load Power[W]",
    P_batt_chem:        "Battery Chemical Power (discharge ref.)[kW]",
    P_batt_kW:          "Battery Power[kW]",
    P_batt_ss:          "Steady-State Battery Power[kW]",
    P_batt:             "Battery Power[kW]",
    P_batt_charge:      "Regen Charge Power[kW]",
    P_wheel:            "Wheel Output Power[kW]",
    P_motor_mech:       "Motor Mechanical Output Power[kW]",
    P_ac:               "Motor AC Input Power[kW]",
    P_dc:               "Inverter DC Input Power[kW]",
    P_aux:              "Auxiliary Load Power[kW]",
    P_loss_batt:        "Battery Ri Loss Power[kW]",
    P_loss_inv:         "Inverter Switching Loss[kW]",
    P_loss_motor:       "Motor Electromagnetic Loss[kW]",
    P_loss_gear:        "Gear Friction Loss[kW]",
    P_cu:               "Motor Copper Loss[kW]",
    P_fe:               "Motor Iron Loss[kW]",
    P_mech_loss:        "Motor Mechanical Loss[kW]",
    P_aero:             "Aerodynamic Drag Power[kW]",
    P_roll:             "Rolling Resistance Power[kW]",
    P_grade:            "Grade Resistance Power[kW]",
    P_elec:             "Electrical Input Power[kW]",
    P_mech:             "Mechanical Output Power[kW]",
    P_regen:            "Regenerative Braking Power[kW]",
    P_iron_steinmetz:   "Steinmetz Iron Loss Power[W]",
    P_hys:              "Hysteresis Loss[W]",
    P_eddy:             "Eddy Current Loss[W]",
    P_gear_drag:        "Gear Drag Power[kW]",
    P_mech_drag:        "Mechanical Drag Power[kW]",
    P_fe_coast:         "Coasting Iron Loss Power[kW]",
    P_dt_drag:          "Drivetrain Drag Power[kW]",
    pump_power:         "Cooling Pump Power[W]",
    Q_batt:             "Battery Heat Generation[W]",
    Q_inv:              "Inverter Heat Generation[W]",
    Q_motor:            "Motor Heat Generation[W]",
    E_cum_kWh:          "Cumul. Drive Energy[kWh]",
    E_net_cum_kWh:      "Cumul. Net Energy Consumption[kWh]",
    E_regen_cum:        "Cumul. Regen Energy Recovery[kWh]",
    E_batt_kWh:         "Battery Energy Consumption[kWh]",
    E_regen_kWh:        "Regen Energy Recovery[kWh]",
    E_regen_Wh:         "Regen Energy Recovery[Wh]",
    E_friction_Wh:      "Friction Braking Energy Dissipated[Wh]",
    kWh_100km:          "Energy Consumption[kWh/100km]",
    kWh_100km_cum:      "Cumul. Energy Consumption[kWh/100km]",
    range_km:           "Driving Range[km]",
    regen_pct:          "Regen Ratio[%]",
    eta_regen:          "Regen Efficiency[%]",
    consumption_kWh100: "Energy Consumption[kWh/100km]",
    e_wltp:             "WLTP Certified Energy Consumption[kWh/100km]",
    // ── Temperature ──
    T_batt:             "Battery Temperature[°C]",
    T_motor:            "Motor Winding Temperature[°C]",
    T_motor_temp:       "Motor Winding Temperature[°C]",
    T_inv:              "Inverter Temperature[°C]",
    T_cool:             "Coolant Temperature[°C]",
    T_disc:             "Brake Disc Temperature[°C]",
    T_amb:              "Ambient Temperature[°C]",
    T_gear:             "Gear Oil Temperature[°C]",
    t_global:           "Cumul. Elapsed Time[s]",
    // ── Force ──
    F_drv:              "Drive Force[N]",
    F_brk:              "Brake Force[N]",
    F_aero:             "Aerodynamic Drag Force[N]",
    F_air:              "Aerodynamic Drag Force[N]",
    F_roll:             "Rolling Resistance Force[N]",
    F_grade:            "Grade Resistance Force[N]",
    F_res:              "Total Road Load Force[N]",
    F_total:            "Total Force Sum[N]",
    F_regen:            "Regenerative Brake Force[N]",
    F_friction:         "Friction Brake Force[N]",
    F_brake:            "Total Brake Force[N]",
    F_net:              "Net Force[N]",
    F_brake_friction:   "Friction Brake Force[N]",
    F_regen_coast:      "Coasting Regen Brake Force[N]",
    F_dt_drag:          "Drivetrain Drag Force[N]",
    F_rough:            "Road Roughness Resistance Force[N]",
    F_drive_max:        "Max Drive Force[N]",
    F_scrub:            "Tyre Scrub Force[N]",
    delta_F_wind:       "Crosswind Additional Aero Force[N]",
    // ── Torque ──
    T_motor_torque:     "Motor Torque[Nm]",
    T_motor_Nm:         "Motor Torque[Nm]",
    T_regen:            "Regen Brake Torque[Nm]",
    T_regen_Nm:         "Regen Brake Torque[Nm]",
    T_friction_brake:   "Friction Brake Torque[Nm]",
    T_wheel_Nm:         "Wheel Torque[Nm]",
    T_FL:               "Front-Left Wheel Torque[Nm]",
    T_FR:               "Front-Right Wheel Torque[Nm]",
    T_RL:               "Rear-Left Wheel Torque[Nm]",
    T_RR:               "Rear-Right Wheel Torque[Nm]",
    T_front_total:      "Front Axle Total Torque[Nm]",
    T_rear_total:       "Rear Axle Total Torque[Nm]",
    tcs_T_cut:          "TCS Torque Cut Amount[Nm]",
    esc_M_z:            "ESC Yaw Moment[Nm]",
    M_z_required:       "Required Yaw Moment[Nm]",
    // ── Angular Speed ──
    omega_motor:        "Motor Angular Velocity[rad/s]",
    omega_m:            "Motor Angular Velocity[rad/s]",
    rpm:                "Motor Speed[RPM]",
    // ── d-q Axis Current & Voltage ──
    Id:                 "d-axis Current (Magnetizing)[A]",
    Iq:                 "q-axis Current (Torque-producing)[A]",
    Vd:                 "d-axis Voltage[V]",
    Vq:                 "q-axis Voltage[V]",
    // ── Efficiency ──
    eta_batt:           "Battery Efficiency[%]",
    eta_inv:            "Inverter Efficiency[%]",
    eta_motor:          "Motor Efficiency[%]",
    eta_gear:           "Gear Efficiency[%]",
    eta_total:          "Powertrain Total Efficiency[%]",
    eta_total_ss:       "Steady-State Powertrain Efficiency[-]",
    eta_gear_v7:        "Dynamic Gear Efficiency[-]",
    // ── Derate ──
    derate_batt:        "Battery Thermal Derate[%]",
    derate_inv:         "Inverter Thermal Derate[%]",
    derate_motor:       "Motor Thermal Derate[%]",
    // ── Braking · ABS · Slip ──
    lambda:             "Tyre Slip Ratio[-]",
    abs_active:         "ABS Intervention[0/1]",
    fade_factor:        "Brake Fade Factor[-]",
    regen_coast_active: "Coasting Regen Active[0/1]",
    abs_trigger:        "ABS Trigger[-]",
    slip_ratio:         "Slip Ratio[-]",
    brake_fade:         "Brake Fade[-]",
    // ── Road · Environment ──
    grade:              "Road Grade Angle[°]",
    grade_deg:          "Road Grade Angle[°]",
    mu:                 "Tyre-Road Friction Coefficient[-]",
    road_mu:            "Road Friction Coefficient[-]",
    mu_hat:             "RLS Estimated Friction Coefficient[-]",
    mu_rls:             "RLS Estimated Friction Coefficient[-]",
    rho_eff:            "Effective Air Density[kg/m³]",
    Crr_eff:            "Effective Rolling Resistance Coefficient[-]",
    delta_Crr_p:        "Tyre Pressure Rolling Resistance Correction[×10³]",
    CdA_v3:             "Effective Aero Drag Area CdA[m²]",
    // ── Pedal ──
    aps:                "Accelerator Pedal Position[%]",
    bps:                "Brake Pedal Position[%]",
    driver_pedal:       "Driver Pedal Command[-]",
    throttle_eff:       "Effective Throttle Opening[-]",
    // ── Mode · Status ──
    phase:              "Drive Phase[-]",
    drive_mode:         "Drive Mode[-]",
    fw_regime:          "FOC Control Region (MTPA/FW)[-]",
    foc_mode:           "FOC Current Control Method[-]",
    awd_mode:           "Drive Mode (RWD/AWD)[-]",
    is_regen:           "Regen Braking Phase[0/1]",
    tcs_active:         "TCS Active[0/1]",
    esc_active:         "ESC Active[0/1]",
    driver_pid_active:  "PID Driver Active[0/1]",
    gear_shifting:      "Gear Shifting[0/1]",
    shutter_open:       "Air Shutter Open[0/1]",
    crosswind_on:       "Crosswind Active[0/1]",
    lc_active:          "Launch Control Active[0/1]",
    // ── Vehicle Dynamics ──
    psi_dot:            "Yaw Rate[rad/s]",
    beta:               "Body Slip Angle[rad]",
    ay_lat:             "Lateral Acceleration[m/s²]",
    a_y:                "Lateral Acceleration[m/s²]",
    steer_deg:          "Steering Angle[°]",
    mu_lon:             "Longitudinal Friction Limit[-]",
    lambda_fr:          "Front-Rear Torque Split Ratio[-]",
    TV_active:          "Torque Vectoring Active[0/1]",
    yaw_rate_ref:       "Target Yaw Rate[rad/s]",
    yaw_error:          "Yaw Rate Error[rad/s]",
    Ku:                 "Understeer Gradient[rad/(m/s²)]",
    friction_scale:     "Friction Limit Scale[-]",
    Fz_FL:              "Front-Left Tyre Normal Load[N]",
    Fz_FR:              "Front-Right Tyre Normal Load[N]",
    Fz_RL:              "Rear-Left Tyre Normal Load[N]",
    Fz_RR:              "Rear-Right Tyre Normal Load[N]",
    // ✅ [FIX] Aero downforce ↔ Fz real-time loop — new output fields
    F_down:             "Aero Downforce[N]",
    dFz_aero_f:         "Front Axle Downforce Load Delta[N]",
    dFz_aero_r:         "Rear Axle Downforce Load Delta[N]",
    // ── Gear · Drivetrain ──
    gear_num:           "Current Gear Number[-]",
    gear_ratio_eff:     "Effective Gear Ratio[-]",
    torque_scale_gear:  "Gear Torque Scale Factor[-]",
    coolant_flow:       "Coolant Flow Rate[L/min]",
    regen_blend:        "Regen-Friction Brake Blend Ratio[-]",
    m_eff:              "Effective Vehicle Mass (incl. rotational inertia)[kg]",
    Rs_eff:             "Effective Stator Resistance Rs[Ω]",
    lambda_eff:         "Effective Flux Linkage λ[Wb]",
    lc_slip:            "Launch Control Slip[-]",
    lc_e_slip:          "Launch Control Slip Error[-]",
    // ── Sensitivity Analysis ──
    param:              "Parameter Name",
    key:                "Parameter Key",
    variation:          "Variation[%]",
    value:              "Parameter Value",
    p_base:             "Base Parameter Value",
    dT_launch_hi:       "Launch Time Change (+)[s]",
    dT_launch_lo:       "Launch Time Change (-)[s]",
    dT_coast_hi:        "Coast Time Change (+)[s]",
    dT_coast_lo:        "Coast Time Change (-)[s]",
    S_launch:           "Launch Time Dimensionless Sensitivity[-]",
    S_coast:            "Coast Time Dimensionless Sensitivity[-]",
    S_regen:            "Regen Efficiency Dimensionless Sensitivity[-]",
    E_launch_hi:        "Launch Energy (+)[Wh]",
    E_launch_lo:        "Launch Energy (-)[Wh]",
    eta_regen_hi:       "Regen Efficiency (+)[%]",
    eta_regen_lo:       "Regen Efficiency (-)[%]",
    launch_time:        "Launch 0→100 Time[s]",
    coast_time:         "Coast 100→0 Time[s]",
    launch_100_time_s:  "Launch 0→100 Time[s]",
    coast_100_time_s:   "Coast 100→0 Time[s]",
    central_diff:       "Sensitivity (Central Difference)[-]",
    delta_pct:          "Parameter Change Rate[%]",
    // ── Comparison Summary ──
    metric:             "Performance Metric Name",
    metricEn:           "Performance Metric Name (EN)",
    unit:               "Unit",
    unitLabel:          "Unit",
    // ── Thermal Stress ──
    cycle:              "Cycle Number[-]",
  };

  const nameMap = lang === "en" ? enNameMap : koNameMap;
  const header     = keys.join(",");
  const headerPhys = keys.map(k => nameMap[k] || k).join(",");
  const rows = data.map(d => keys.map(k => (d[k] !== undefined && d[k] !== null ? d[k] : "")).join(","));
  return header + "\n" + headerPhys + "\n" + rows.join("\n");
}

function downloadCSV(filename, csvContent) {
  const blob = new Blob(["" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getTabCategory(tabId) {
  for (const cat of NAV_CATEGORIES) {
    if (cat.sections.some(s => s.id === tabId)) return cat.id;
  }
  return NAV_CATEGORIES[0].id;
}


// ============================================================
// Main Component
// ============================================================
// ══════════ Braking Panel Component ══════════








function downloadHARAReport(csv, filename) {
  const blob = new Blob([""+csv], {type:"text/csv;charset=utf-8"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href=url; a.download=filename||"HARA_Report.csv"; a.click();
  URL.revokeObjectURL(url);
}





function injectGlobalLayoutStyles() {
  const id = "ev-sim-global-layout";
  if (document.getElementById(id)) return;
  const s = document.createElement("style");
  s.id = id;
  s.textContent = `
    html, body, #root { width: 100% !important; max-width: none !important; margin: 0; padding: 0; box-sizing: border-box; }
    * { box-sizing: border-box; }
  `;
  document.head.appendChild(s);
}
// [V35-FIX-PRINT] ─────────────────────────────────────────────────────────
// 인쇄/PDF 스타일 주입 (매 호출 시 재주입)
function injectPrintStyles() {
  const id = '__ev_print_style_v10__';
  const prev = document.getElementById(id);
  if (prev) prev.remove();
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @media print {
      * { -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important; }
      body { background: #fff !important; color: #000 !important;
             margin: 0 !important; padding: 0 !important; }
      button,
      [data-no-print],
      [style*="position: fixed"],
      [style*="position:fixed"] { display: none !important; }
      .recharts-wrapper, .recharts-surface {
        max-width: 100% !important; overflow: visible !important; }
      [data-print-section] { page-break-inside: avoid; break-inside: avoid; }
      [data-print-break]   { page-break-before: always; break-before: always; }
      h2, h3 { color: #1e293b !important; }
    }
  `;
  document.head.appendChild(s);
}

// [V35-FIX-PRINT] ─── 근본 원인 수정 ───────────────────────────────────────
// V34 버그: window.print()는 sandbox iframe에서 예외 없이 무음 실패(silent fail)
//           → try/catch 에서 예외가 안 발생 → printed=true 설정 → 모든 fallback 미실행
//
// V35 수정 전략:
//   ① iframe 여부 즉시 감지: window !== window.top
//   ② iframe 내부: window.print() 건너뛰고 beforeprint 이벤트 기반 판별 사용
//      → beforeprint 이벤트가 일정 시간 내 발생하면 성공으로 판단
//      → 미발생 시 다음 단계로 자동 전환
//   ③ 새 창(window.open) HTML 인쇄: iframe 환경에서 가장 신뢰성 높음
//   ④ 최종 fallback: alert(Ctrl+P 안내)
// [Verification_40-PRINT] 인쇄/PDF 수정
//   수정 전: window.open()을 setTimeout(350ms) 안에서 호출
//           → iframe 환경(Claude.ai 등)에서 브라우저가 사용자 제스처 컨텍스트를 350ms 후 소멸 처리
//           → window.open이 팝업 차단에 의해 null 반환 → fallbackAlert → 다이얼로그 무응답
//   수정 후: ① iframe 여부 즉시 감지 (동기)
//           ② iframe: window.open을 클릭 핸들러 안에서 즉시 호출 (사용자 제스처 보존)
//                     → 창이 열린 뒤 setTimeout(100ms) 내에서 HTML 콘텐츠 작성 (렌더링 보장)
//           ③ 비-iframe: 기존 window.print() 호출 (딜레이 100ms로 단축)
function handlePDFExport(vehicleName, tabName, lang) {
  injectPrintStyles();
  const locale   = lang === 'en' ? 'en-US' : 'ko-KR';
  const newTitle = `EV-Sim | ${vehicleName} | ${tabName} | ${new Date().toLocaleDateString(locale)}`;
  const prevTitle = document.title;
  document.title  = newTitle;
  const restoreTitle = () => setTimeout(() => { document.title = prevTitle; }, 1500);

  const fallbackAlert = () => {
    restoreTitle();
    alert(lang === 'en'
      ? 'Print dialog could not open automatically.\n\nPlease use your browser print shortcut:\n  Windows / Linux : Ctrl + P\n  macOS           : ⌘ + P'
      : '인쇄 대화상자를 자동으로 열 수 없습니다.\n\n브라우저 인쇄 단축키를 직접 사용해 주세요:\n  Windows / Linux : Ctrl + P\n  macOS           : ⌘ + P');
  };

  // ── 새 창에 HTML 콘텐츠 작성 후 인쇄 ───────────────────────────────
  const writeAndPrint = (pw) => {
    const styleBlocks = Array.from(document.querySelectorAll('style'))
      .map(el => `<style>${el.textContent}</style>`).join('\n');
    pw.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8">
      <title>${newTitle}</title>
      ${styleBlocks}
      <style>
        * { -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
        body { background:#fff !important; color:#000 !important; margin:12px; }
        button, [data-no-print] { display:none !important; }
        .recharts-wrapper { max-width:100% !important; }
      </style>
    </head><body>${document.body.innerHTML}</body></html>`);
    pw.document.close();
    pw.focus();
    setTimeout(() => {
      try { pw.print(); } catch (_) {}
      restoreTitle();
    }, 900);
  };

  // ① iframe 여부를 클릭 즉시 감지 (동기)
  const isIframe = (() => {
    try { return window.self !== window.top; } catch (_) { return true; }
  })();

  if (isIframe) {
    // ② iframe: window.open을 사용자 제스처 내에서 즉시 호출 → 팝업 차단 우회
    let pw = null;
    try { pw = window.open('', '_blank', 'width=1200,height=900,scrollbars=yes,resizable=yes'); } catch (_) {}
    if (!pw) { fallbackAlert(); return; }
    // 창은 열었지만 콘텐츠는 React 렌더 완료 후 작성 (100ms 이면 충분)
    setTimeout(() => { try { writeAndPrint(pw); } catch (_) { fallbackAlert(); } }, 100);
    return;
  }

  // ③ 비-iframe: beforeprint 이벤트로 window.print() 실행 여부 판별
  setTimeout(() => {
    let printFired = false;
    const onBeforePrint = () => { printFired = true; };
    window.addEventListener('beforeprint', onBeforePrint, { once: true });

    try { window.print(); } catch (_) {}

    setTimeout(() => {
      window.removeEventListener('beforeprint', onBeforePrint);
      if (printFired) {
        restoreTitle();
      } else {
        // window.print() 무음 실패 → 새 창으로 전환 (단, 여기서는 제스처 없어 팝업 차단 가능)
        let pw2 = null;
        try { pw2 = window.open('', '_blank', 'width=1200,height=900,scrollbars=yes,resizable=yes'); } catch (_) {}
        if (!pw2) { fallbackAlert(); return; }
        setTimeout(() => { try { writeAndPrint(pw2); } catch (_) { fallbackAlert(); } }, 100);
      }
    }, 200);
  }, 100);
}
// ─────────────────────────────────────────────────────────────────────────

// ══════════════════════════════════════════════════════════════
// §LIVE_ANIM  실시간 파라미터 슬라이더 애니메이션 정의  [v09 신규]
// ══════════════════════════════════════════════════════════════
const ANIM_PARAM_DEFS = [
  { key:'Kp_iq',  label:'Kp_iq (Iq 비례)',   min:0.5,  max:15,     step:0.15,  unit:'', loop:'current',  graph:'bode_current',  color:'#f87171' },
  { key:'Ki_iq',  label:'Ki_iq (Iq 적분)',   min:10,   max:500,    step:8,     unit:'', loop:'current',  graph:'bode_current',  color:'#fb923c' },
  { key:'Kp_spd', label:'Kp_spd (속도 비례)',min:20,   max:400,    step:8,     unit:'', loop:'speed',    graph:'bode_speed',    color:'#60a5fa' },
  { key:'Ki_spd', label:'Ki_spd (속도 적분)',min:500,  max:8000,   step:150,   unit:'', loop:'speed',    graph:'bode_speed',    color:'#34d399' },
  { key:'Kp_id',  label:'Kp_id (Id 비례)',   min:0.3,  max:10,     step:0.1,   unit:'', loop:'current',  graph:'bode_current',  color:'#a78bfa' },
];

// ══════════════════════════════════════════════════════════════════════════
// [v08/v09] ControlLoopTabContent — 제어 루프 심화 분석 탭
//   Sub-tabs: nyquist | rootlocus | zdomain | antiwindup | autotuning | cascade | torque3d
// ══════════════════════════════════════════════════════════════════════════

// ── DevHistoryPanel ──────────────────────────────────────────────────────────
function DevHistoryPanel({ lang }) {
  const T = (k, e) => lang !== "en" ? k : e;

  const VERSIONS = [
    {
      ver: "v2.2", date: "2026-04-24", color: "#8b5cf6",
      tags: [
        { cat:"UI",     text: T("차트열·라이트다크·언어·실행 버튼 높이 30px 통일","Toolbar control boxes unified to 30px height") },
        { cat:"UI",     text: T("주행사이클 드롭다운 레이블 단축 — Full-Spec CSV","Drive cycle dropdown label shortened — Full-Spec CSV") },
        { cat:"RENAME", text: T("ev-sim_v2_1.jsx → ev-sim_v2_2.jsx","ev-sim_v2_1.jsx → ev-sim_v2_2.jsx") },
      ],
    },
    {
      ver: "v2.1", date: "2026-04-20", color: "#0ea5e9",
      tags: [
        { cat:"FIX",  text: T("FIX-AERO-FZ: 공기역학 다운포스 ↔ 4코너 Fz 실시간 루프 연결","FIX-AERO-FZ: Aero downforce ↔ 4-corner Fz real-time loop") },
        { cat:"FEAT", text: T("차량 동역학 88→97% — 서스펜션 파레토 · NVH 2-DOF FRF","Vehicle dynamics 88→97% — Suspension Pareto · NVH 2-DOF FRF") },
        { cat:"FEAT", text: T("열관리 88→97% — 열저항·냉각용량·디레이팅 최적화기","Thermal 88→97% — Resistance/Cooling/Derate optimizers") },
        { cat:"FEAT", text: T("배터리: 셀 밸런싱 최적화기 6종 파레토","Battery: Cell Balancing Optimizer 6-strategy Pareto") },
        { cat:"FEAT", text: T("충전: 탄소집약도 충전 최적화기 (ISO 15118-20 §8.9)","Charging: Carbon-Intensity Optimizer (ISO 15118-20 §8.9)") },
        { cat:"FEAT", text: T("안전: ASIL 분해 최적화기 · 안전 커버리지 비용 최적화기","Safety: ASIL Decomp. Optimizer · Coverage-Cost Optimizer") },
      ],
    },
    {
      ver: "v2.0", date: "2026-03-07 ~ 2026-03-09", color: "#10b981",
      tags: [
        { cat:"ARCH", text: T("Phase 1~4: 상수/코어/스토어/컴포넌트 완전 모듈 분리","Phase 1–4: Full modularization — const/core/store/UI") },
        { cat:"FEAT", text: T("Gain Scheduling 3D 복합 보간 (V44)","Gain Scheduling 3D compound interpolation (V44)") },
        { cat:"FEAT", text: T("AWD 지면반력 실시간 시각화 · SOTIF ODD 256-셀 (V42)","AWD vertical load realtime viz · SOTIF ODD 256-cell (V42)") },
        { cat:"FEAT", text: T("DFMEA↔PFMEA 자동 연계 + 제어계획 (V43)","DFMEA↔PFMEA auto-link + Control Plan (V43)") },
        { cat:"FEAT", text: T("Three.js WebGL · PWA SW · RDE NOx/PM 배출 체인 (V41)","Three.js WebGL · PWA SW · RDE NOx/PM emission chain (V41)") },
        { cat:"FIX",  text: T("드라이브 모드 전체 그래프 반영 7건 (V19)","Drive mode full-graph fix 7 items (V19)") },
        { cat:"FIX",  text: T("배터리·열관리·에너지·비교·4WS 탭 전면 검증 (V9~V14)","Battery/Thermal/Energy/Compare/4WS full validation (V9–V14)") },
        { cat:"UI",   text: T("전 탭 KPI 배지 툴팁 전면 적용 (V23~V35)","All-tab KPI badge tooltip applied (V23–V35)") },
        { cat:"UI",   text: T("2단계 비동기 시뮬 Core/Heavy (A36)","2-stage async sim Core/Heavy (A36)") },
      ],
    },
    {
      ver: "v1.0", date: "2026-02 ~ 2026-03-06", color: "#f59e0b",
      tags: [
        { cat:"FEAT", text: T("정속·발진·재가속·코스팅·제동·에너지·열관리·비교 탭 완성","Steady/Launch/ReAccel/Coast/Brake/Energy/Thermal/Compare tabs") },
        { cat:"FEAT", text: T("파워트레인 제어 모달 전체 구축 (A07~A35)","Powertrain control modal built (A07–A35)") },
        { cat:"FEAT", text: T("헤더 전역 연동: 공기밀도·맞바람·SOC·노면 (A25~A29)","Header global link: air density/headwind/SOC/road (A25–A29)") },
        { cat:"FEAT", text: T("고급 제어: MRAC · H∞ · MPC · 런치컨트롤 (R-12)","Advanced control: MRAC · H∞ · MPC · Launch ctrl (R-12)") },
        { cat:"FEAT", text: T("HARA / FMEA / 사이버보안 / SOTIF 안전 탭 (R-10)","HARA / FMEA / Cybersecurity / SOTIF safety tabs (R-10)") },
        { cat:"REFACTOR", text: T("v01~v06 초기 모듈 통합 · dead code 제거","v01–v06 initial module merge · dead code removed") },
      ],
    },
  ];

  const CAT_COLOR = {
    "UI":       { bg:"#ede9fe", color:"#5b21b6" },
    "RENAME":   { bg:"#f1f5f9", color:"#475569" },
    "FIX":      { bg:"#fef2f2", color:"#b91c1c" },
    "ARCH":     { bg:"#fef9c3", color:"#92400e" },
    "FEAT":     { bg:"#e6f3de", color:"#3b6d11" },
    "REFACTOR": { bg:"#f0fdf4", color:"#166534" },
  };

  return (
    <div style={{ fontFamily:"inherit", height:"100%" }}>
      <div style={{ fontSize:14, fontWeight:800, color:"#8b5cf6", marginBottom:4 }}>
        {T("📋 개발 이력","📋 Development History")}
      </div>
      <div style={{ fontSize:11, color:COLORS.textMuted, marginBottom:14 }}>
        {T("버전별 주요 변경 이력 — 최신순","Key changes per version — newest first")}
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
        {VERSIONS.map((v, vi) => (
          <div key={v.ver} style={{ display:"flex", gap:0 }}>
            {/* 타임라인 선 */}
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", width:28, flexShrink:0 }}>
              <div style={{ width:12, height:12, borderRadius:"50%", background:v.color,
                border:`2px solid ${v.color}`, flexShrink:0, marginTop:2 }} />
              {vi < VERSIONS.length - 1 && (
                <div style={{ width:2, flex:1, background:COLORS.border, minHeight:16 }} />
              )}
            </div>

            {/* 카드 */}
            <div style={{ flex:1, marginLeft:8, marginBottom:vi < VERSIONS.length - 1 ? 14 : 0,
              background:COLORS.cardBg, border:`1px solid ${COLORS.border}`,
              borderLeft:`3px solid ${v.color}`, borderRadius:"0 8px 8px 0", padding:"10px 12px" }}>
              <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:8 }}>
                <span style={{ fontSize:13, fontWeight:800, color:v.color, fontFamily:mono }}>{v.ver}</span>
                <span style={{ fontSize:10, color:COLORS.textMuted }}>{v.date}</span>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                {v.tags.map((tag, ti) => {
                  const cs = CAT_COLOR[tag.cat] || { bg:"#f1f5f9", color:"#475569" };
                  return (
                    <div key={ti} style={{ display:"flex", gap:6, alignItems:"flex-start" }}>
                      <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:10,
                        background:cs.bg, color:cs.color, flexShrink:0, marginTop:1, fontFamily:mono }}>
                        {tag.cat}
                      </span>
                      <span style={{ fontSize:10, color:COLORS.text, lineHeight:1.5 }}>{tag.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DomainAuditPanel({ lang }) {
            const [auditTab, setAuditTab] = React.useState("overview");

            const domains = [
              {
                icon: "🔋", iconBg: "#e6f3de", title: lang === "en" ? "Battery Electrochemistry" : "배터리 전기화학",
                sub: "battery.js · 2,235줄", pct: 100, barColor: "#63aa22",
                items: [
                  { dot: "#63aa22", text: lang === "en" ? "2RC Model + SEI Growth" : "2RC 모델 + SEI 성장", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "EKF/UKF SOC Estimation" : "EKF/UKF SOC 추정", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "SOH · Rainflow · Arrhenius v3" : "SOH·Rainflow·Arrhenius v3", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "OCV LUT · Nernst · Hysteresis" : "OCV LUT·Nernst·hysteresis", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "CC/CV · DCFC · V2G Charging" : "CC/CV·DCFC·V2G 충전", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "3RC ECM (τ3≈300–600s diffusion)" : "3RC ECM (τ3≈300–600s 확산 임피던스)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "SPM Electrolyte Diffusion (η_conc)" : "SPM 전해질 확산 (η_conc·SOC_spm)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Cell Balancing Optimizer — 6-strategy Pareto (IEC 62660-1)" : "셀 밸런싱 최적화기 — 6전략 파레토 (IEC 62660-1)", badge: "impl" },
                ],
              },
              {
                icon: "⚡", iconBg: "#e6f1fb", title: lang === "en" ? "Motor / Inverter" : "모터 / 인버터",
                sub: "motor.js · motor_control.js", pct: 95, barColor: "#378add",
                items: [
                  { dot: "#63aa22", text: lang === "en" ? "FOC (Id/Iq PI) + FW MTPV + SVPWM/DPWM/6-step" : "FOC (Id/Iq PI) + 약계자 MTPV + SVPWM/DPWM/6-step", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Steinmetz Iron Loss v3 + PM Eddy + Temp. k_h" : "Steinmetz 철손 v3 + PM 와전류 + k_h 온도 보정", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Dead-time Comp. + Repetitive Ctrl + Torque Ripple" : "데드타임 보상 + 반복 제어기 + 토크 리플 6고조파", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "HFI / Back-EMF / MRAS Sensorless Observer" : "HFI / Back-EMF / MRAS 센서리스 관측기", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Cross-Saturation Inductance Ld/Lq(T)" : "교차 포화 인덕턴스 Ld/Lq(T) 온도 의존", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "MTPA→MTPV 2D Operating Point Map" : "MTPA→MTPV 2D 동작점 맵 (η 등고선)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Gear Ratio WLTP Efficiency Explorer" : "기어비 WLTP 효율 탐색기 (최적 GR 도출)", badge: "impl" },
                ],
              },
              {
                icon: "🌡️", iconBg: "#faeeda", title: lang === "en" ? "Thermal Management" : "열관리",
                sub: "thermal.js · 1,312줄", pct: 97, barColor: "#63aa22",
                items: [
                  { dot: "#63aa22", text: lang === "en" ? "4-Node Thermal + 3D Diffusion" : "4-노드 열 모델 + 3D 열 확산", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Heat Pump COP + Coolant Flow" : "히트펌프 COP + 냉각수 흐름", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Cabin HVAC + Waste Heat Recovery" : "캐빈 HVAC + 폐열 회수", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "PCM Phase-Change Cooling (Zalba 2003)" : "PCM 위상변화 냉각 (Zalba 2003)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Cell Internal Thermal: through/in-plane 2-node" : "셀 내부 열전도: through/in-plane 이방성 2-노드", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Thermal Resistance Pareto Optimizer" : "열저항 파레토 최적화기 (R_th_bc/mc/ic)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Cooling Capacity Optimizer (UA_rad · WLTP)" : "냉각 용량 최적화기 (UA_rad · WLTP 사이클)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Derate Threshold Sensitivity (T_derate vs SOH)" : "디레이팅 임계값 민감도 (T_derate vs SOH)", badge: "impl" },
                ],
              },
              {
                icon: "🚗", iconBg: "#fcebeb", title: lang === "en" ? "Vehicle Dynamics" : "차량 동역학",
                sub: "dynamics.js · vehicle.js · tire.js · 1,543줄", pct: 97, barColor: "#63aa22",
                items: [
                  { dot: "#63aa22", text: lang === "en" ? "4WS Suspension + Rollover" : "4WS 서스펜션 + 롤오버", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "AWD/TV Yaw Moment Control" : "AWD/TV 요 모멘트 제어", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Pacejka 2002 Full 89-coeff (Fx/Fy/Mz/Mx)" : "Pacejka 2002 전체 89계수 (Fx/Fy/Mz/Mx)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Thermal B·C·D Model — Sorniotti 2011" : "온도의존 B·C·D 완전 모델 (Sorniotti 2011)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Physics Road Crr: 12 surfaces + IRI/water/snow" : "물리 기반 노면 Crr: 12종 + IRI/수막/눈", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Aero Downforce → Fz Real-Time (ClA·v²)" : "공기역학 다운포스 ↔ Fz 실시간 루프 (ClA·v²)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Suspension Pareto Optimizer (Ride vs Handling)" : "서스펜션 파레토 최적화 (승차감 vs 조종성)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "NVH Vibration Spectrum + ISO 2631-1 Ride Index" : "NVH 진동 스펙트럼 + ISO 2631-1 승차감 지수", badge: "impl" },
                ],
              },
              {
                icon: "🛣️", iconBg: "#eeedfe", title: lang === "en" ? "Energy Chain / Road Load" : "에너지 체인 / 주행저항",
                sub: "vehicle.js · dynamics.js · simulation.js", pct: 93, barColor: "#534ab7",
                items: [
                  { dot: "#63aa22", text: lang === "en" ? "Road Load R1~R5 Decomposition" : "도로하중 R1~R5 분해", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "K-Factor Correction (K_aero, K_roll…)" : "K-인자 보정 (K_aero, K_roll, K_cu…)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "WLTP Cycle + MPC Energy Mgmt" : "WLTP 사이클 + MPC 에너지 관리", badge: "impl" },
                  // ✅ [FIX] 가변 CdA partial → impl 상향, 다운포스 루프 impl 추가
                  { dot: "#63aa22", text: lang === "en" ? "Variable CdA / Active Aero + Downforce Loop" : "가변 CdA / 능동 공력 + 다운포스 Fz 루프", badge: "impl" },
                ],
              },
              {
                icon: "🔌", iconBg: "#e1f5ee", title: lang === "en" ? "Charging Strategy / Smart Grid" : "충전 전략 / 스마트그리드",
                sub: "SmartGridService · OCPPService", pct: 88, barColor: "#1d9e75",
                items: [
                  { dot: "#63aa22", text: lang === "en" ? "TOU-based Charging Schedule" : "TOU 요금 기반 충전 스케줄", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "V2G Revenue Opt. + V2H/V2L" : "V2G 수익 최적화 + V2H/V2L", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Demand Response (DR) Evaluator — OpenADR 2.0b" : "수요 반응(DR) 24h 평가기 — OpenADR 2.0b", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Carbon-Intensity Charging Optimizer — ISO 15118-20 §8.9" : "탄소 집약도 충전 최적화기 — ISO 15118-20 §8.9", badge: "impl" },
                ],
              },
              {
                icon: "🛡️", iconBg: "#e6f1fb", title: lang === "en" ? "Functional Safety / Cybersecurity" : "기능안전 / 사이버보안",
                sub: "fmea.js · gsn.js · tara.js", pct: 83, barColor: "#378add",
                items: [
                  { dot: "#63aa22", text: lang === "en" ? "HARA → ASIL + FMEA/FTA" : "HARA → ASIL 결정 + FMEA/FTA", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "TARA (STRIDE + CVSS)" : "TARA (STRIDE + CVSS)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "ASIL Decomposition Optimizer — Cost·Coverage Pareto" : "ASIL 분해 최적화기 — 비용·커버리지 파레토 (fmea.js)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Safety Coverage vs Cost Optimizer — CM Portfolio Pareto" : "안전 커버리지 vs 비용 최적화기 — 대응책 포트폴리오 파레토", badge: "impl" },
                ],
              },
              {
                icon: "🔁", iconBg: "#faeeda", title: lang === "en" ? "Control Loop" : "제어 루프",
                sub: "motor_control.js · ControlLoopService", pct: 95, barColor: "#EF9F27",
                items: [
                  { dot: "#63aa22", text: lang === "en" ? "Bode Stability · Phase Margin · Nyquist · Root Locus" : "Bode 안정성 · PM · Nyquist · 근궤적", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "IMC · SIMC · Cohen-Coon · ZN Auto-Tuning" : "IMC · SIMC · Cohen-Coon · ZN 자동 튜닝", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "H∞ Mixed Sensitivity (Speed + Current Loop)" : "H∞ 혼합 감도 (속도+전류 루프)", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Gain Scheduling RPM/Torque/Temp + 3D Grid" : "게인 스케줄링 RPM/토크/온도 + 3D 격자", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Drive Mode Gain Map Optimizer (5 modes)" : "드라이브 모드 게인 맵 최적화기 5모드", badge: "impl" },
                  { dot: "#63aa22", text: lang === "en" ? "Multi-Loop Simultaneous Optimizer (PM≥45°)" : "멀티루프 동시 최적화 (PM ≥ 45° 제약)", badge: "impl" },
                ],
              },
            ];

            const coverageMatrix = {
              cols: lang === "en"
                ? ["Sensitivity","Pass/Fail","DOE/Bayes","Overview","Param Matrix","Design Reco"]
                : ["민감도 분석","합격·불합격","DOE·베이즈","통합 분석","파라미터 매트릭스","설계 권고"],
              rows: [
                { label: lang === "en" ? "🔋 Battery" : "🔋 배터리 전기화학",     cells: ["full","full","full","full","full","full"] },
                { label: lang === "en" ? "⚡ Motor" : "⚡ 모터 / 인버터",          cells: ["full","full","full","full","full","full"] },
                { label: lang === "en" ? "🌡️ Thermal" : "🌡️ 열관리",              cells: ["full","full","full","full","full","full"] },
                { label: lang === "en" ? "🚗 Dynamics" : "🚗 차량 동역학",         cells: ["full","full","full","full","part","full"] },
                { label: lang === "en" ? "🛣️ Energy Chain" : "🛣️ 에너지 체인",     cells: ["full","full","full","full","full","full"] },
                { label: lang === "en" ? "🔌 Charging" : "🔌 충전 전략",           cells: ["full","full","part","full","full","full"] },
                { label: lang === "en" ? "🛡️ Safety" : "🛡️ 기능안전·보안",        cells: ["part","part","part","full","full","full"] },
                { label: lang === "en" ? "🔁 Control" : "🔁 제어 루프",            cells: ["full","full","full","full","full","full"] },
              ],
            };


            const mappingRows = [
              { name: lang === "en" ? "Cell Balancing Optimizer" : "셀 밸런싱 최적화기",            bg: "#e6f3de", mod: "battery.js cellBalancingOptimizer",             svc: "—",                tab: lang === "en" ? "Battery Health" : "Battery Health", diff: lang === "en" ? "Done" : "완료", stars: "★★★★★" },
              { name: lang === "en" ? "Gear Ratio Efficiency Explorer" : "기어비 효율 최적화기",     bg: "#e6f3de", mod: "motor.js gearRatioEfficiencySearch",   svc: "DOEService",       tab: lang === "en" ? "Energy Chain" : "Energy Chain", diff: lang === "en" ? "Done" : "완료", stars: "★★★★★" },
              { name: lang === "en" ? "MTPA→MTPV Op. Point Map" : "MTPA→MTPV 동작점 맵",           bg: "#e6f3de", mod: "motor.js computeMotorOperatingPointMap", svc: "—",                tab: lang === "en" ? "Control Loop" : "Control Loop", diff: lang === "en" ? "Done" : "완료", stars: "★★★★☆" },              { name: lang === "en" ? "Cooling Capacity Optimizer" : "냉각 용량 최적화기", bg: "#e6f3de", mod: "thermal.js coolingCapacityOptimizer", svc: "—", tab: lang === "en" ? "Thermal Mgmt" : "Thermal Mgmt", diff: lang === "en" ? "Done" : "완료", stars: "★★★★★" },
              { name: lang === "en" ? "Derate Threshold Sensitivity" : "디레이팅 임계값 민감도",      bg: "#e6f3de", mod: "thermal.js / battery.js 2,185L", svc: "—",               tab: lang === "en" ? "Battery Health" : "Battery Health", diff: lang === "en" ? "Low" : "낮음", stars: "★★★★☆" },
              { name: lang === "en" ? "Suspension Pareto Opt." : "서스펜션 파레토 최적화", bg: "#e6f3de", mod: "dynamics.js suspensionParetoOptimizer", svc: "—", tab: lang === "en" ? "Suspension" : "Suspension", diff: lang === "en" ? "Done" : "완료", stars: "★★★★★" },
              { name: lang === "en" ? "TV Gain Auto-Tuning" : "TV 게인 자동 튜닝",      bg: "#e6f3de", mod: "dynamics.js adaptiveLambdaFr+vdControlStep", svc: "—", tab: lang === "en" ? "AWD/TV" : "AWD/TV", diff: lang === "en" ? "Done" : "완료", stars: "★★★☆☆" },
              { name: lang === "en" ? "NVH Vibration Spectrum" : "NVH 진동 스펙트럼",  bg: "#e6f3de", mod: "dynamics.js nvhVibrationSpectrum", svc: "—", tab: lang === "en" ? "Suspension" : "Suspension", diff: lang === "en" ? "Done" : "완료", stars: "★★★☆☆" },
              { name: lang === "en" ? "Carbon Intensity Chg. Optimizer" : "탄소 집약도 충전 최적화기", bg: "#e6f3de", mod: "SmartGridService carbonIntensityChargingOptimizer", svc: "SmartGrid", tab: lang === "en" ? "Charging" : "Charging", diff: lang === "en" ? "Done" : "완료", stars: "★★★★★" },
              { name: lang === "en" ? "DR Evaluator — OpenADR 2.0b" : "DR 수요반응 평가기 — OpenADR",  bg: "#e6f3de", mod: "SmartGridService demandResponseEvaluator",      svc: "SmartGrid", tab: lang === "en" ? "Charging" : "Charging", diff: lang === "en" ? "Done" : "완료", stars: "★★★★☆" },
              { name: lang === "en" ? "ASIL Decomposition Optimizer" : "ASIL 분해 최적화기",           bg: "#e6f3de", mod: "fmea.js asilDecompositionOptimizer",             svc: "FMEASafetyService", tab: lang === "en" ? "HW Safety" : "HW Safety", diff: lang === "en" ? "Done" : "완료", stars: "★★★★★" },
              { name: lang === "en" ? "Safety Coverage-Cost Optimizer" : "안전 커버리지-비용 최적화기", bg: "#e6f3de", mod: "fmea.js safetyCoverageCostOptimizer",            svc: "FMEASafetyService", tab: lang === "en" ? "HW Safety" : "HW Safety", diff: lang === "en" ? "Done" : "완료", stars: "★★★★★" },
              { name: lang === "en" ? "Multi-Loop Gain Optimizer" : "멀티루프 게인 최적화",          bg: "#e6f3de", mod: "motor_control.js multiLoopGainOptimizer",       svc: "ControlLoopSvc",   tab: lang === "en" ? "Control Loop" : "Control Loop", diff: lang === "en" ? "Done" : "완료", stars: "★★★★★" },
              { name: lang === "en" ? "Drive Mode Gain Map" : "드라이브 모드 게인 맵",               bg: "#e6f3de", mod: "motor_control.js optimizeDriveModeGainMap",    svc: "ControlLoopSvc",   tab: lang === "en" ? "Control Loop" : "Control Loop", diff: lang === "en" ? "Done" : "완료", stars: "★★★☆☆" },
              { name: lang === "en" ? "Domain-wide Pass/Fail Ext." : "전체 도메인 Pass/Fail 확장",   bg: "#e6f3de", mod: "simulation.js",               svc: "—",                tab: lang === "en" ? "Pass/Fail" : "Pass/Fail 탭", diff: lang === "en" ? "Low" : "낮음", stars: "★★★★★" },
            ];

            const BADGE = { impl: { bg: "#e6f3de", color: "#3b6d11", label: lang === "en" ? "Impl." : "구현" }, partial: { bg: "#faeeda", color: "#854f0b", label: lang === "en" ? "Partial" : "부분" }, gap: { bg: "#fcebeb", color: "#a32d2d", label: lang === "en" ? "Gap" : "미반영" } };
            const P_TAG = { high: { bg: "#fcebeb", color: "#a32d2d", label: lang === "en" ? "P1 High" : "P1 높음" }, mid: { bg: "#faeeda", color: "#854f0b", label: lang === "en" ? "P2 Med" : "P2 중간" }, low: { bg: "#e6f3de", color: "#3b6d11", label: lang === "en" ? "P3 Low" : "P3 낮음" }, done: { bg: "#e6f3de", color: "#3b6d11", label: lang === "en" ? "✅ Done" : "✅ 완료" } };
            const CELL_STYLE = { full: { background: "#e6f3de", color: "#3b6d11", fontWeight: 600 }, part: { background: "#faeeda", color: "#854f0b", fontWeight: 600 }, none: { color: COLORS.textMuted } };
            const CELL_LABEL = { full: "Full", part: "Partial", none: "—" };
            const MAP_ROW_BG = { "#e6f3de": { border: "0.5px solid #63aa22", label: lang === "en" ? "Direct reuse" : "직접 재사용" }, "#faeeda": { border: "0.5px solid #EF9F27", label: lang === "en" ? "Func + extra calc" : "함수 + 추가 연산" }, "#fcebeb": { border: "0.5px solid #a32d2d", label: lang === "en" ? "New impl. needed" : "신규 구현 필요" } };

            const auditTabs = [
              { id: "overview",  label: lang === "en" ? "Domain Status" : "도메인 현황" },
              { id: "coverage",  label: lang === "en" ? "Coverage Matrix" : "최적화 반영 현황" },
              { id: "mapping",   label: lang === "en" ? "Feature Mapping" : "기능 매핑 매트릭스" },
            ];

            const cardStyle = { background: COLORS.cardBg, border: `0.5px solid ${COLORS.border}`, borderRadius: 10, padding: "14px 16px" };
            const tagStyle = (bg, color) => ({ display: "inline-block", fontSize: 10, padding: "1px 7px", borderRadius: 10, fontWeight: 600, background: bg, color });

            return (
              <div style={{ fontFamily: "inherit" }}>
                {/* 헤더 */}
                <div style={{ fontSize: 14, fontWeight: 800, color: "#8b5cf6", marginBottom: 4 }}>
                  {lang === "en" ? "🗂️ Domain Completeness Audit" : "🗂️ 도메인 완성도 감사"}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>
                  {lang === "en"
                    ? "Review how thoroughly each EV physics domain is reflected across the Optimization & Design section."
                    : "최적화·설계 섹션 전반에 걸쳐 각 EV 물리 도메인이 얼마나 반영되어 있는지 정량 평가합니다."}
                </div>
                {/* 요약 통계 */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
                  {[
                    { num: "8",  numColor: "#3b6d11", label: lang === "en" ? "Physics Domains" : "물리 도메인" },
                    { num: "65", numColor: "#3b6d11", label: lang === "en" ? "Implemented Fns." : "구현 함수" },
                    { num: "0",  numColor: "#3b6d11", label: lang === "en" ? "Partial Remaining" : "부분 잔여 항목" },
                    { num: "0",  numColor: "#3b6d11", label: lang === "en" ? "Open Gaps" : "미구현 갭" },
                  ].map((s, i) => (
                    <div key={i} style={{ background: COLORS.bg, border: `0.5px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 8px", textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: s.numColor }}>{s.num}</div>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* 내부 탭 버튼 */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
                  {auditTabs.map(t => (
                    <button key={t.id} onClick={() => setAuditTab(t.id)}
                      style={{ padding: "5px 14px", fontSize: 11, border: `0.5px solid ${auditTab === t.id ? "#8b5cf6" : COLORS.border}`,
                        borderRadius: 6, background: auditTab === t.id ? "#8b5cf6" : COLORS.cardBg,
                        color: auditTab === t.id ? "#fff" : COLORS.textMuted, cursor: "pointer", fontWeight: auditTab === t.id ? 600 : 400, transition: "all 0.15s" }}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ── 탭 1: 도메인 현황 ── */}
                {auditTab === "overview" && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: 12 }}>
                    {domains.map((d, di) => (
                      <div key={di} style={cardStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: d.iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>{d.icon}</div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{d.title}</div>
                            <div style={{ fontSize: 10, color: COLORS.textMuted }}>{d.sub}</div>
                          </div>
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textMuted, marginBottom: 3 }}>
                            <span>{lang === "en" ? "Coverage" : "최적화 반영률"}</span><span>{d.pct}%</span>
                          </div>
                          <div style={{ height: 5, borderRadius: 3, background: COLORS.bg, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${d.pct}%`, background: d.barColor, borderRadius: 3, transition: "width 0.4s" }} />
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {d.items.map((it, ii) => (
                            <div key={ii} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 10, color: COLORS.textMuted, padding: "3px 0", borderTop: ii > 0 ? `0.5px solid ${COLORS.border}` : "none" }}>
                              <div style={{ width: 6, height: 6, borderRadius: "50%", background: it.dot, flexShrink: 0, marginTop: 3 }} />
                              <span style={{ flex: 1 }}>{it.text}</span>
                              <span style={{ ...tagStyle(BADGE[it.badge].bg, BADGE[it.badge].color), marginLeft: "auto", flexShrink: 0 }}>{BADGE[it.badge].label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── 탭 2: 최적화 반영 현황 매트릭스 ── */}
                {auditTab === "coverage" && (
                  <div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10 }}>
                      {lang === "en"
                        ? "Quantitative evaluation of how each domain is reflected in the 6 Optimization & Design tabs."
                        : "현재 최적화·설계 섹션 6개 탭에서 각 도메인이 얼마나 반영되어 있는지 정량 평가합니다."}
                    </div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12, fontSize: 10, color: COLORS.textMuted, alignItems: "center" }}>
                      {[{ bg: "#e6f3de", border: "0.5px solid #63aa22", label: lang === "en" ? "Full" : "완전 반영 (Full)" },
                        { bg: "#faeeda", border: "0.5px solid #EF9F27", label: lang === "en" ? "Partial" : "부분 반영 (Partial)" },
                        { bg: COLORS.cardBg, border: `0.5px solid ${COLORS.border}`, label: lang === "en" ? "None (—)" : "미반영 (—)" }].map((leg, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <div style={{ width: 12, height: 12, borderRadius: 2, background: leg.bg, border: leg.border }} />
                          <span>{leg.label}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ borderCollapse: "collapse", fontSize: 10, minWidth: 580 }}>
                        <thead>
                          <tr>
                            <th style={{ padding: "6px 10px", background: COLORS.cardBg, color: COLORS.textMuted, border: `0.5px solid ${COLORS.border}`, textAlign: "left", fontWeight: 600, minWidth: 140, whiteSpace: "nowrap" }}>
                              {lang === "en" ? "Domain" : "도메인"}
                            </th>
                            {coverageMatrix.cols.map(c => (
                              <th key={c} style={{ padding: "6px 8px", background: COLORS.cardBg, color: COLORS.textMuted, border: `0.5px solid ${COLORS.border}`, textAlign: "center", fontWeight: 600, whiteSpace: "nowrap", minWidth: 70, fontSize: 9 }}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {coverageMatrix.rows.map((row, ri) => (
                            <tr key={ri}>
                              <td style={{ padding: "5px 10px", border: `0.5px solid ${COLORS.border}`, fontWeight: 600, color: COLORS.text, background: COLORS.cardBg, whiteSpace: "nowrap", fontSize: 10 }}>{row.label}</td>
                              {row.cells.map((cell, ci) => (
                                <td key={ci} style={{ padding: "5px 8px", border: `0.5px solid ${COLORS.border}`, textAlign: "center", fontSize: 10, ...CELL_STYLE[cell] }}>{CELL_LABEL[cell]}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ marginTop: 12, background: COLORS.cardBg, borderRadius: 6, padding: "8px 12px", fontSize: 10, color: COLORS.textMuted }}>
                      {lang === "en"
                        ? <span>v2.1 기준: <strong style={{ color: COLORS.text }}>Battery, Motor, Thermal, Dynamics, Road Load, Control Loop, Charging, Safety/Security</strong> all substantially covered.</span>
                        : <span>v2.1 기준: <strong style={{ color: COLORS.text }}>배터리, 모터, 열관리, 차량동역학, 도로부하, 제어루프, 충전전략, 기능안전</strong> 전 도메인 실질 반영 완료.</span>}
                    </div>
                  </div>
                )}

                {/* ── 탭 3: 기능 매핑 매트릭스 ── */}
                {auditTab === "mapping" && (
                  <div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10 }}>
                      {lang === "en"
                        ? "Feature items to include in the 'Domain Completeness' tab and their linkage to existing modules."
                        : "\"도메인 완성도\" 탭에 포함할 기능 항목과 기존 탭·모듈의 연계 관계를 정리합니다."}
                    </div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12, fontSize: 10, color: COLORS.textMuted, alignItems: "center" }}>
                      {Object.entries(MAP_ROW_BG).map(([bg, v]) => (
                        <div key={bg} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <div style={{ width: 12, height: 12, borderRadius: 2, background: bg, border: v.border }} />
                          <span>{v.label}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ borderCollapse: "collapse", fontSize: 10, minWidth: 620 }}>
                        <thead>
                          <tr>
                            {[lang === "en" ? "Feature Item" : "도메인 기능 항목",
                              lang === "en" ? "Core Module" : "연계 core 모듈",
                              lang === "en" ? "Service" : "연계 서비스",
                              lang === "en" ? "Linked Tab" : "연계 탭",
                              lang === "en" ? "Difficulty" : "구현 난이도",
                              lang === "en" ? "Impact" : "완성도 기여"].map(h => (
                              <th key={h} style={{ padding: "6px 8px", background: COLORS.cardBg, color: COLORS.textMuted, border: `0.5px solid ${COLORS.border}`, textAlign: h === (lang === "en" ? "Feature Item" : "도메인 기능 항목") ? "left" : "center", fontWeight: 600, fontSize: 9, whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {mappingRows.map((row, ri) => {
                            const diffColor = row.diff === "High" || row.diff === "높음" ? "#a32d2d" : row.diff === "Med" || row.diff === "중간" ? "#854f0b" : "#3b6d11";
                            return (
                              <tr key={ri} style={{ background: ri % 2 === 0 ? "transparent" : COLORS.cardBg }}>
                                <td style={{ padding: "5px 10px", border: `0.5px solid ${COLORS.border}`, fontWeight: 600, color: COLORS.text, background: row.bg, fontSize: 10, whiteSpace: "nowrap" }}>{row.name}</td>
                                <td style={{ padding: "5px 8px", border: `0.5px solid ${COLORS.border}`, textAlign: "center", color: COLORS.textMuted, fontSize: 9 }}>{row.mod}</td>
                                <td style={{ padding: "5px 8px", border: `0.5px solid ${COLORS.border}`, textAlign: "center", color: COLORS.textMuted, fontSize: 9 }}>{row.svc}</td>
                                <td style={{ padding: "5px 8px", border: `0.5px solid ${COLORS.border}`, textAlign: "center", color: COLORS.textMuted, fontSize: 9 }}>{row.tab}</td>
                                <td style={{ padding: "5px 8px", border: `0.5px solid ${COLORS.border}`, textAlign: "center", color: diffColor, fontWeight: 600, fontSize: 9 }}>{row.diff}</td>
                                <td style={{ padding: "5px 8px", border: `0.5px solid ${COLORS.border}`, textAlign: "center", color: "#f59e0b", fontSize: 11 }}>{row.stars}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
}

function EVSimulatorInner() {
  var axRE1 = useAxesStore('MAIN_RE1', { xMin:0, xMax:180, yMin:0, yMax:700 });
  var axRE2 = useAxesStore('MAIN_RE2', { xMin:0, xMax:35,  yMin:0, yMax:70, y2Min:0, y2Max:12 });
  var axRE3 = useAxesStore('MAIN_RE3', { xMin:0, xMax:160, yMin:0, yMax:800 });
  var axRE4 = useAxesStore('MAIN_RE4', { xMin:0, xMax:90,  yMin:0, yMax:600 });
  var __re1KhId = useState(null); var re1KhId = __re1KhId[0]; var setRe1KhId = __re1KhId[1];
  var __re2KhId = useState(null); var re2KhId = __re2KhId[0]; var setRe2KhId = __re2KhId[1];
  var __re3KhId = useState(null); var re3KhId = __re3KhId[0]; var setRe3KhId = __re3KhId[1];
  var __re4KhId = useState(null); var re4KhId = __re4KhId[0]; var setRe4KhId = __re4KhId[1];
  var handleRe1 = function(e,id){ setRe1KhId(function(p){return p===id?null:id;}); };
  var handleRe2 = function(e,id){ setRe2KhId(function(p){return p===id?null:id;}); };
  var handleRe3 = function(e,id){ setRe3KhId(function(p){return p===id?null:id;}); };
  var handleRe4 = function(e,id){ setRe4KhId(function(p){return p===id?null:id;}); };
  // 전역 레이아웃 CSS 주입 (실행창 전체 너비 활용)

  // ============================================================
  // [Phase 3] 스토어 초기화 및 상태 구독
  // ============================================================
  const _pStore = React.useMemo(() => createParamsStore({
    DEFAULT_PARAMS, VEHICLE_PRESETS, TESLA_M3_PARAMS, IONIQ6_PARAMS, PRESET_GAIN_VERSION,
  }), []);
  const _rStore = React.useMemo(() => createSimResultStore(), []);
  const _uStore = React.useMemo(() => createUIStore(), []);

  // 파라미터 스토어
  const [_pSnap, _setPSnap] = useState(() => _pStore.getState());
  React.useEffect(() => _pStore.subscribe(s => _setPSnap({ ...s })), [_pStore]);
  const paramsA          = _pSnap.paramsA;
  const paramsB          = _pSnap.paramsB;
  const vehicleAKey      = _pSnap.vehicleAKey;
  const vehicleBKey      = _pSnap.vehicleBKey;
  const rlMassKg         = _pSnap.rlMassKg;
  const rlGrade          = _pSnap.rlGrade;
  const rlThrottle       = _pSnap.rlThrottle;
  const launchThrottle   = _pSnap.launchThrottle;
  const reaccelThrottle  = _pSnap.reaccelThrottle;
  const rlSocScenario    = _pSnap.rlSocScenario;
  const rlHeadwind       = _pSnap.rlHeadwind;
  const reaccelV0        = _pSnap.reaccelV0;
  const reaccelV1        = _pSnap.reaccelV1;
  const setParamsA       = (u) => _pStore.setParamsA(u);
  const setParamsB       = (u) => _pStore.setParamsB(u);
  const setVehicleAKey   = (k) => _pStore.setVehicleAKey(k);
  const setVehicleBKey   = (k) => _pStore.setVehicleBKey(k);
  const setRlMassKg      = (v) => _pStore.setRlMassKg(v);
  const setRlGrade       = (v) => _pStore.setRlGrade(v);
  const setRlThrottle    = (v) => _pStore.setRlThrottle(v);
  const setLaunchThrottle  = (v) => _pStore.setLaunchThrottle(v);
  const setReaccelThrottle = (v) => _pStore.setReaccelThrottle(v);
  const setRlSocScenario = (v) => _pStore.setRlSocScenario(v);
  const setRlHeadwind    = (v) => _pStore.setRlHeadwind(v);
  const setReaccelV0     = (v) => _pStore.setReaccelV0(v);
  const setReaccelV1     = (v) => _pStore.setReaccelV1(v);

  // 시뮬 결과 스토어
  const [_rSnap, _setRSnap] = useState(() => _rStore.getState());
  React.useEffect(() => _rStore.subscribe(s => _setRSnap({ ...s })), [_rStore]);
  const simResults          = _rSnap.simResults;
  const compResults         = _rSnap.compResults;
  const cycleSim            = _rSnap.cycleSim;
  const isRunning           = _rSnap.isRunning;
  const simError            = _rSnap.simError;
  const activeSimVehicle    = _rSnap.activeSimVehicle;
  const setSimResults       = (r) => _rStore.setSimResults(r);
  const setCompResults      = (r) => _rStore.setCompResults(r);
  const setCycleSim         = (r) => _rStore.setCycleSim(r);
  const setIsRunning        = (v) => _rStore.setIsRunning(v);
  const setSimError         = (m) => _rStore.setSimError(m);
  const setActiveSimVehicle = (v) => _rStore.setActiveSimVehicle(v);

  // UI 스토어
  const [_uSnap, _setUSnap] = useState(() => _uStore.getState());
  React.useEffect(() => _uStore.subscribe(s => _setUSnap({ ...s })), [_uStore]);
  const lang                 = _uSnap.lang;
  const isDark               = _uSnap.isDark;
  const activeTab            = _uSnap.activeTab;
  const activeCat            = _uSnap.activeCat;
  const graphInfoPopup       = _uSnap.graphInfoPopup;
  const showVehicleModal     = _uSnap.showVehicleModal;
  const showEnvModal         = _uSnap.showEnvModal;
  const showControlModal     = _uSnap.showControlModal;
  const ctrlInitGroup        = _uSnap.ctrlInitGroup;
  const ctrlInitSubTab       = _uSnap.ctrlInitSubTab;
  const showCSVModal         = _uSnap.showCSVModal;
  const showDataExportPopup  = _uSnap.showDataExportPopup;
  const showVehiclePresetModal = _uSnap.showVehiclePresetModal;
  const setLang               = (v) => _uStore.setLang(v);
  const setIsDark             = (v) => _uStore.setIsDark(v);
  const setActiveTab          = (v) => _uStore.setActiveTab(v);
  const setActiveCat          = (v) => _uStore.setActiveCat(v);
  const setGraphInfoPopup     = (v) => v ? _uStore.openGraphInfo(v?.info, v?.title) : _uStore.closeGraphInfo();
  const setShowVehicleModal   = (v) => v ? _uStore.openVehicleModal()  : _uStore.closeVehicleModal();
  const setShowEnvModal       = (v) => v ? _uStore.openEnvModal()      : _uStore.closeEnvModal();
  const setShowControlModal   = (v) => v ? _uStore.openControlModal()  : _uStore.closeControlModal();
  const setCtrlInitGroup      = (v) => { _uStore.getState().ctrlInitGroup  = v; };
  const setCtrlInitSubTab     = (v) => { _uStore.getState().ctrlInitSubTab = v; };
  const setShowCSVModal       = (v) => v ? _uStore.openCSVModal()      : _uStore.closeCSVModal();
  const setShowDataExportPopup= (v) => v ? _uStore.openDataExportPopup() : _uStore.closeDataExportPopup();
  const setShowVehiclePresetModal = (v) => v ? _uStore.openVehiclePresetModal() : _uStore.closeVehiclePresetModal();
  Object.assign(COLORS, isDark ? COLORS_DARK : COLORS_LIGHT);
  const t = makeT(lang, I18N);
  // [Perf-1] 정적 NS는 모듈 스코프에서 1회 처리됨.
  // 렌더마다 갱신이 필요한 동적 값(COLORS·lang·React훅 등)만 유지.
  const _g = globalThis;
  _g.COLORS = COLORS;
  _g.__EV_LANG__ = lang;
  _g.makeT = (lng) => makeT(lng, I18N);
  // React hooks & context refs (컴포넌트 분리 파일에서 globalThis로 접근하는 항목)
  _g.React = React;
  _g.useState = useState; _g.useCallback = useCallback; _g.useMemo = useMemo;
  _g.useRef = useRef; _g.useEffect = useEffect;
  _g.createContext = createContext; _g.useContext = useContext;
  _g.LangContext = LangContext; _g.useLang = useLang;
  _g.PopupContext = PopupContext; _g.usePopup = usePopup;
  _g.RLKpiOpenContext = RLKpiOpenContext;
  _g.modalOverlay = modalOverlay; _g.modalBox = modalBox;

  React.useEffect(() => { injectGlobalLayoutStyles(); }, []);
  // [Perf-2] 첫 마운트 후 physics NS를 백그라운드 dynamic import → globalThis 브리지 설정
  React.useEffect(() => { _initPhysicsBridge(); }, []);
  React.useEffect(() => {
    const catId = getTabCategory(activeTab);
    setActiveCat(catId);
  }, [activeTab]);
  // [Advance33] 그래프 정보 팝업 전역 상태 (PopupContext 통해 배포)
  // ── Road Load 전역 시나리오 파라미터 ──────────────────────────────
  // ECO/NORMAL/SPORT 옆에 표시되고 RLTabContent 전체에 공유
  const _rlMassTimer  = React.useRef(null);
  const _rlGradeTimer = React.useRef(null);
  // [Fix-Grade-Neg] 헤더 구배 입력 raw string state — 음수 입력 허용
  // onChange에서 parseFloat("-") = NaN → controlled input이 되돌아가는 문제 해결
  const [gradeRaw, setGradeRaw] = React.useState(String(paramsA.grade_deg ?? 0));
  React.useEffect(() => {
    // 외부 변경(프리셋 적용 등) 시 gradeRaw 동기화
    setGradeRaw(String(paramsA.grade_deg ?? 0));
  }, [paramsA.grade_deg]);
  const _rlSocTimer   = React.useRef(null);
  const _rlThrTimer   = React.useRef(null);  // [BugFix7] 시뮬 에러 표시
  // [Fix-Tamb-Neg] T_amb 헤더 입력 raw string state — 음수(영하) 입력 허용
  const [tambRaw, setTambRaw] = React.useState(String(paramsA.T_amb ?? 25));
  React.useEffect(() => {
    setTambRaw(String(paramsA.T_amb ?? 25));
  }, [paramsA.T_amb]);

  // [A14] 간이 블럭도 클릭 핸들러 — 그룹+첫탭 지정 후 모달 오픈
  const openCtrlAt = (group, subTab) => {
    setCtrlInitGroup(group);
    setCtrlInitSubTab(subTab);
    setShowControlModal(true);
  };
  const dataExportBtnRef = React.useRef(null);

  // ── 레이아웃 CSV 저장/불러오기 ──────────────────────────────
  const layoutFileInputRef = React.useRef(null);
  const [openParamPanel, setOpenParamPanel]     = React.useState(false);
  const [layoutNotif, setLayoutNotif]           = React.useState(null);  // { ok, message }
  const [showFolderModal, setShowFolderModal]   = React.useState(false);
  const [showSettingsPopup, setShowSettingsPopup] = React.useState(false);
  // ── 차트 열 레이아웃 (1열 / 2열 / 3열) ──────────────────────────
  const [colLayout, setColLayout] = React.useState(() => {
    try { localStorage.removeItem('ev-sim-col-layout'); } catch { /* ignore */ } return 1;
  });
  // colStr: colLayout → grid 값 직접 계산 (CSS 변수 race condition 방지)
  const colStr = colLayout === 1 ? '1fr' : colLayout === 3 ? '1fr 1fr 1fr' : colLayout === 4 ? '1fr 1fr 1fr 1fr' : '1fr 1fr';
  // colLayout 변경 시: CSS 변수 동기화 + Recharts ResponsiveContainer 강제 재측정
  // requestAnimationFrame: CSS 그리드가 브라우저에 페인트된 직후 resize 이벤트를 보내
  // 모든 ResponsiveContainer(RoadLoad·Launch 등)가 새 column 너비를 측정하도록 함
  React.useEffect(() => {
    document.documentElement.style.setProperty('--chart-cols', colStr);
    // double-rAF: 첫 번째 rAF에서 CSS 그리드가 브라우저에 paint된 후,
    // 두 번째 rAF에서 resize 이벤트를 보내 Recharts가 새 크기를 정확히 측정
    let id1, id2;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    });
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2); };
  }, [colStr]);
  const applyColLayout = React.useCallback((n) => {
    setColLayout(n);
    try { localStorage.setItem('ev-sim-col-layout', String(n)); } catch { /* ignore */ }
  }, []);
  const settingsBtnRef = React.useRef(null);
  // Full-Spec CSV 업로드 — 설정 드롭다운에서 직접 처리
  const fullSpecFileInputRef = React.useRef(null);
  const [fullSpecParsed, setFullSpecParsed] = React.useState(null);
  const [fullSpecError, setFullSpecError] = React.useState(null);
  const [csvInitSoc, setCsvInitSoc] = React.useState(90);
  const [csvInitTemp, setCsvInitTemp] = React.useState(25);
  const [csvInitSpd, setCsvInitSpd] = React.useState(0);

  const handleFullSpecUpload = React.useCallback((ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = parseFullSpecCycleCSV(e.target.result);
        if (result.ok) {
          setFullSpecParsed(result);
          setFullSpecError(null);
          if (result.initConds.soc_pct != null)    setCsvInitSoc(result.initConds.soc_pct);
          if (result.initConds.batt_temp_C != null) setCsvInitTemp(result.initConds.batt_temp_C);
          if (result.initConds.speed_kmh != null)   setCsvInitSpd(result.initConds.speed_kmh);
          setParamsA(p => ({ ...p, driving_cycle_data_full: result.rows, driving_cycle_name: file.name, driving_cycle_mode: "csv_full",
            cycle_type: "csv_full", 외기온: Math.round(result.meta.t_amb_avg), mu: +result.meta.avg_mu.toFixed(3) }));
          setParamsB(p => ({ ...p, driving_cycle_data_full: result.rows, driving_cycle_name: file.name, driving_cycle_mode: "csv_full",
            cycle_type: "csv_full", 외기온: Math.round(result.meta.t_amb_avg), mu: +result.meta.avg_mu.toFixed(3) }));
          setTimeout(() => runSim(), 80);
        } else {
          setFullSpecError(result.error || "파싱 오류");
        }
      } catch (err) { setFullSpecError("파일 읽기 오류: " + err.message); }
    };
    reader.readAsText(file);
  }, [setParamsA, setParamsB]);

  const handleTemplateDownload = React.useCallback(() => {
    const content = generateCycleCSVTemplate();
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "driving_cycle_template.csv";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }, []);
  // 폴더 이름 표시용 상태 (폴더 변경 시 리렌더 트리거)
  const [saveFolderName, setSaveFolderName] = React.useState('');
  const [loadFolderName, setLoadFolderName] = React.useState('');
  const _axesStore = getAxesStore();

  // 앱 초기화 시 IndexedDB에서 폴더 핸들 복원
  React.useEffect(() => {
    loadFolderHandles().then(() => {
      setSaveFolderName(getSaveFolder().name || '');
      setLoadFolderName(getLoadFolder().name || '');
    });
  }, []);

  const showNotif = React.useCallback((ok, message) => {
    setLayoutNotif({ ok, message });
    setTimeout(() => setLayoutNotif(null), 3500);
  }, []);

  // 폴더 변경 콜백 — LayoutFolderModal에서 호출
  const handleFolderChanged = React.useCallback(() => {
    setSaveFolderName(getSaveFolder().name || '');
    setLoadFolderName(getLoadFolder().name || '');
  }, []);

  // 저장
  const handleLayoutExport = React.useCallback(async () => {
    try {
      const { filename, folder } = await exportLayout(_uStore, _axesStore, _pStore);
      const dest = folder
        ? (lang === 'ko' ? `📁 ${folder} / ${filename}` : `📁 ${folder} / ${filename}`)
        : filename;
      showNotif(true, lang === 'ko' ? `💾 저장됨: ${dest}` : `💾 Saved: ${dest}`);
    } catch(err) {
      showNotif(false, `저장 오류: ${err.message}`);
    }
  }, [_uStore, _axesStore, _pStore, lang, showNotif]);

  // 불러오기 (File System Access API 지원 시 showOpenFilePicker 사용)
  const handleLayoutImportClick = React.useCallback(() => {
    if (FS_API_SUPPORTED) {
      // FSAPI 방식: importLayout에 file=null 전달 → 내부에서 showOpenFilePicker 호출
      importLayout(null, _uStore, _axesStore, _pStore, (result) => {
        if (result.message !== '취소됨') showNotif(result.ok, result.message);
      });
    } else {
      // fallback: 기존 <input type="file"> 방식
      layoutFileInputRef.current?.click();
    }
  }, [_uStore, _axesStore, _pStore, showNotif]);

  const handleLayoutImportFile = React.useCallback((ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    importLayout(file, _uStore, _axesStore, _pStore, (result) => {
      showNotif(result.ok, result.message);
    });
    ev.target.value = '';
  }, [_uStore, _axesStore, _pStore, showNotif]);
  // ← NEW: Full-spec cycle sim result

  // [VEH-FIX-D] 차량 프리셋 변경 시 환경·주행사이클 설정 보존
  //   수정 전: grade_deg/road_condition/T_amb/altitude/rho만 보존
  //            → cycle_type·driving_cycle_mode·mu·wind_speed_ms 가 VEHICLE_PRESETS 기본값(WLTP)으로 덮어씌워짐
  //            → 사용자가 FTP75/JC08 등으로 바꾼 후 차량 변경하면 WLTP로 리셋되는 버그
  //   수정 후: 대기환경(grade/road/T_amb/altitude/rho/mu/wind) + 주행사이클(cycle_type/driving_cycle_mode) 모두 보존

  // [A47] 차량 비교 선택 변경 시 자동 재실행
  // vehicleAKey/vehicleBKey 변경 → setParamsA/B 적용 후 runSimRef 갱신 대기(350ms) → 자동 실행
  const _vehicleTimerA = React.useRef(null);
  const _vehicleTimerB = React.useRef(null);
  useEffect(() => {
    if (_vehicleTimerA.current) clearTimeout(_vehicleTimerA.current);
    _vehicleTimerA.current = setTimeout(() => { runSimRef.current(); }, 350);
    return () => { if (_vehicleTimerA.current) clearTimeout(_vehicleTimerA.current); };
  }, [vehicleAKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (_vehicleTimerB.current) clearTimeout(_vehicleTimerB.current);
    _vehicleTimerB.current = setTimeout(() => { runSimRef.current(); }, 350);
    return () => { if (_vehicleTimerB.current) clearTimeout(_vehicleTimerB.current); };
  }, [vehicleBKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // [A36-PERF] _buildSimParams: params 조합 헬퍼 (중복 제거)
  const _buildSimParams = useCallback((currentP, _T_amb_cur, _batt_temp_sync, grade_deg_from_pct, _wind_ms) => {
    // rlSocScenario: 숫자(%) 직접 입력 방식으로 변경 (80 → 0.80)
    const _soc_init_override = (typeof rlSocScenario === 'number')
      ? rlSocScenario / 100
      : ({ high: 0.80, mid: 0.60, low: 0.20 }[rlSocScenario] ?? (currentP.soc_init || 0.9));
    return {
      ...currentP,
      m:             (currentP.m || 1850) + (rlMassKg || 0),
      grade_deg:     rlGrade !== 0 ? grade_deg_from_pct : (currentP.grade_deg || 0),
      wind_speed_ms: _wind_ms > 0 ? _wind_ms : (currentP.wind_speed_ms || 0),
      // [A41-BUG1-FIX] resistanceForcesV3는 params.v_wind를 읽음 → headwind를 정면풍(0°)으로 crosswind 경로에 동기화
      v_wind:          _wind_ms > 0 ? _wind_ms        : (currentP.v_wind || 0),
      wind_angle_deg:  _wind_ms > 0 ? 0               : (currentP.wind_angle_deg ?? 90),
      crosswind_enable: _wind_ms > 0 ? true            : (currentP.crosswind_enable || false),
      batt_temp:     _batt_temp_sync,
      gear_T_oil:    _T_amb_cur,
      T_brake_init:  _T_amb_cur,
      awd_enable:    (currentP.motor_config||"dual") === "dual" ? (currentP.awd_enable !== false) : false,
      _rl_mass_kg:  rlMassKg || 0,
      _rl_grade_pct: rlGrade || 0,
      soc_init:      _soc_init_override, // [A41-BUG5-FIX] SOC 시나리오 → 실제 시뮬 반영
    };
  }, [rlMassKg, rlGrade, rlHeadwind, rlSocScenario]); // [A41-BUG5-FIX] rlSocScenario 추가

  // [Perf-3] runSim async — _initPhysicsBridge() 완료 후 시뮬 실행
  // bridge가 이미 준비됐으면 await은 즉시 반환 (0ms overhead)
  const runSim = useCallback(() => {
    setIsRunning(true);
    setSimError(null);

    _initPhysicsBridge().then(() => {
      const sim = _simNS;
      // ── Phase 1: Core (~300ms) — 즉시 표시 ──
      setTimeout(() => {
        try {
          const currentP = activeSimVehicle === "A" ? paramsA : paramsB;
          const grade_deg_from_pct = rlGrade !== 0 ? Math.atan(rlGrade / 100) * 180 / Math.PI : 0;
          const _wind_ms  = rlHeadwind || 0;
          // [P2-B] || → ?? : T_amb=0 (0°C) 엣지케이스에서 0||25=25 오폭 방지
          const _T_amb_cur = currentP.T_amb ?? 25;
          const _T_amb_A   = paramsA.T_amb ?? 25;
          const _T_amb_B   = paramsB.T_amb ?? 25;

          const simP  = _buildSimParams(currentP, _T_amb_cur, Math.max(_T_amb_cur, currentP.batt_temp || _T_amb_cur), grade_deg_from_pct, _wind_ms);
          const simPA = _buildSimParams(paramsA,  _T_amb_A,  Math.max(_T_amb_A, paramsA.batt_temp || _T_amb_A), grade_deg_from_pct, _wind_ms);
          const simPB = _buildSimParams(paramsB,  _T_amb_B,  Math.max(_T_amb_B, paramsB.batt_temp || _T_amb_B), grade_deg_from_pct, _wind_ms);

          console.log('⚡ [Phase-1 Core] Running...', simP.shortName || 'Vehicle');
          const coreR    = sim.runCoreSim(simP,  reaccelV0, reaccelV1, launchThrottle / 100, reaccelThrottle / 100);
          const coreComp = sim.runCoreComparison(simPA, simPB, reaccelV0, reaccelV1, launchThrottle / 100, reaccelThrottle / 100);

          // Core 결과 즉시 반영 → 발진·재가속·도로부하·에너지 탭 바로 표시
          setSimResults({ ...coreR, drive_mode: simP.drive_mode || 'NORMAL', one_pedal: simP.one_pedal_enable || false, cycle_type: simP.cycle_type || 'WLTP' });
          setCompResults(coreComp);
          console.log('✅ [Phase-1 Core] Done');

          // ── Phase 2: Heavy (~700ms) — 브라우저 프레임 양보 후 실행 ──
          setTimeout(() => {
            try {
              console.log('🔥 [Phase-2 Heavy] Running WLTP/Thermal/Sensitivity...');
              const heavyR    = sim.runHeavySim(simP,  reaccelV0, reaccelV1, launchThrottle / 100, coreR);
              // [ENV-FIX-05] DUP-3 중복 연산 제거
              const isSameAsA = activeSimVehicle === "A";
              const heavyA = isSameAsA ? heavyR : sim.runHeavySim(simPA, reaccelV0, reaccelV1, launchThrottle / 100, coreComp.a);
              const heavyB = !isSameAsA ? heavyR : sim.runHeavySim(simPB, reaccelV0, reaccelV1, launchThrottle / 100, coreComp.b);
              const heavyComp = sim._buildCompMerged(heavyA, heavyB, simPA, simPB);
              setSimResults(heavyR);
              setCompResults(heavyComp);
              setIsRunning(false);
              console.log('✅ [Phase-2 Heavy] Done');
            } catch (err) {
              console.error('❌ [Phase-2 Heavy] Error:', err);
              setSimError(err.message || String(err));
              setIsRunning(false);
            }
          }, 0);

        } catch (error) {
          console.error('❌ [Phase-1 Core] Error:', error);
          setSimError(error.message || String(error));
          setIsRunning(false);
        }
      }, 50);
    }).catch(err => {
      console.error('❌ [Bridge] Load error:', err);
      setSimError('Physics engine failed to load: ' + (err.message || String(err)));
      setIsRunning(false);
    });
  }, [paramsA, paramsB, activeSimVehicle, reaccelV0, reaccelV1, rlMassKg, rlGrade, launchThrottle, reaccelThrottle, rlHeadwind, rlSocScenario, _buildSimParams]); // [A48] launchThrottle/reaccelThrottle 분리

  // [A21-DC7] runSimRef: 드라이빙 모드 effect의 stale-closure 방지용 최신 runSim 참조
  const runSimRef = React.useRef(runSim);
  useEffect(() => { runSimRef.current = runSim; }, [runSim]);

  // [Init-Lazy] 첫 실행 시 현재 탭(활성 탭)의 연산만 우선 처리
  // - SIM_TABS이 아닌 탭(roadload 등 useMemo 기반)으로 시작 시 → runSim 지연
  // - SIM_TABS로 시작 시 → 즉시 runSim 실행
  // - 이후 SIM_TABS 탭을 처음 방문할 때 한 번 runSim 실행 (중복 방지)
  const simInitialized = React.useRef(false);
  const SIM_TABS_SET = React.useMemo(() => new Set(["launch","reaccel","coast","braking","energy","thermal","driveability","wltp","sensitivity"]), []);

  useEffect(() => {
    if (SIM_TABS_SET.has(activeTab)) {
      // 첫 탭이 시뮬 필요 탭 → 즉시 실행
      const timer = setTimeout(() => { runSim(); simInitialized.current = true; }, 300);
      return () => clearTimeout(timer);
    }
    // 첫 탭이 useMemo 기반(roadload 등) → 시뮬 지연 (첫 방문 시 트리거)
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 탭 전환 시: 아직 시뮬 미실행 상태에서 SIM_TABS 첫 방문 → 자동 초기화
  useEffect(() => {
    if (!simInitialized.current && SIM_TABS_SET.has(activeTab)) {
      simInitialized.current = true;
      setTimeout(() => { runSimRef.current(); }, 100);
    }
  }, [activeTab, SIM_TABS_SET]); // eslint-disable-line react-hooks/exhaustive-deps

  // [A21-03] ECO/NORMAL/SPORT 변경 시 시뮬 결과(launch/reaccel/coast/regen) 자동 재계산
  // drive_mode가 바뀌면 runFullSim의 driveModeThrottle/driveModeRegenStrength가 달라지므로
  // 150ms 디바운스 후 runSim() 재실행 → 모든 시뮬 탭에 즉시 반영
  // [BUG-3 Fix] 마운트 시 skip → 초기 runSim(300ms)과 중복 방지
  const _driveModeTimer   = React.useRef(null);
  const _driveModeMounted = React.useRef(false);
  useEffect(() => {
    if (!_driveModeMounted.current) { _driveModeMounted.current = true; return; }
    // [A21-DC7] 탭 제한 제거 → 모든 탭에서 drive_mode 변경 시 시뮬 재실행
    if (_driveModeTimer.current) clearTimeout(_driveModeTimer.current);
    _driveModeTimer.current = setTimeout(() => { runSimRef.current(); }, 150);
    return () => { if (_driveModeTimer.current) clearTimeout(_driveModeTimer.current); };
  }, [paramsA.drive_mode, paramsB.drive_mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // [VEH-FIX-A] activeSimVehicle A↔B 전환 시 runSim 자동 재실행
  //   수정 전: setActiveSimVehicle만 호출 → r은 이전 차량 기준 유지
  //            → params(B차량)와 r(A차량 결과)가 불일치 → KPI/곡선 오표시
  //   수정 후: 전환 후 200ms 디바운스 → runSim 재실행 → r을 새 activeSimVehicle 기준으로 갱신
  //   [BUG-2 Fix] 초기 마운트 시 불필요 중복 실행 방지: _activeVehMounted ref로 최초 1회 skip
  const _activeVehTimer   = React.useRef(null);
  const _activeVehMounted = React.useRef(false);
  useEffect(() => {
    if (!_activeVehMounted.current) { _activeVehMounted.current = true; return; } // 마운트 시 skip
    if (_activeVehTimer.current) clearTimeout(_activeVehTimer.current);
    _activeVehTimer.current = setTimeout(() => { runSimRef.current(); }, 200);
    return () => { if (_activeVehTimer.current) clearTimeout(_activeVehTimer.current); };
  }, [activeSimVehicle]); // eslint-disable-line react-hooks/exhaustive-deps

  // [A21-04] 적재량·구배 변경 시 전체 탭(Road Load + 시뮬) 자동 재계산
  // rlMassKg/rlGrade → runSim()의 simP에 반영되므로 변경 즉시 디바운스 후 재실행
  // [BUG-3 Fix] 초기 마운트 시(rlMassKg=0, rlGrade=0) 불필요 중복 실행 방지
  const _scenarioTimer   = React.useRef(null);
  const _scenarioMounted = React.useRef(false);
  useEffect(() => {
    if (!_scenarioMounted.current) { _scenarioMounted.current = true; return; } // 마운트 시 skip
    if (_scenarioTimer.current) clearTimeout(_scenarioTimer.current);
    _scenarioTimer.current = setTimeout(() => { runSim(); }, 250);
    return () => { if (_scenarioTimer.current) clearTimeout(_scenarioTimer.current); };
  }, [rlMassKg, rlGrade]); // eslint-disable-line react-hooks/exhaustive-deps

  // [A41-BUG5-FIX] SOC 시나리오 변경 시 runSim 자동 재실행
  // 기존: SOC 버튼 = UI 오버레이 변경만, 실제 시뮬(launch/reaccel/coast/regen) 갱신 없음
  // 수정: rlSocScenario 변경 → 200ms 디바운스 후 runSim() 재실행
  // [BUG-3 Fix] 마운트 시 skip
  const _socTimer   = React.useRef(null);
  const _socMounted = React.useRef(false);
  useEffect(() => {
    if (!_socMounted.current) { _socMounted.current = true; return; }
    if (_socTimer.current) clearTimeout(_socTimer.current);
    _socTimer.current = setTimeout(() => { runSimRef.current(); }, 200);
    return () => { if (_socTimer.current) clearTimeout(_socTimer.current); };
  }, [rlSocScenario]); // eslint-disable-line react-hooks/exhaustive-deps

  // [A48] 개도 탭별 분리: launchThrottle/reaccelThrottle 변경 시 시뮬 자동 재실행
  // [BUG-3 Fix] 마운트 시 skip
  const _launchThrTimer    = React.useRef(null);
  const _launchThrMounted  = React.useRef(false);
  const _reaccelThrTimer   = React.useRef(null);
  const _reaccelThrMounted = React.useRef(false);
  useEffect(() => {
    if (!_launchThrMounted.current) { _launchThrMounted.current = true; return; }
    if (_launchThrTimer.current) clearTimeout(_launchThrTimer.current);
    _launchThrTimer.current = setTimeout(() => { runSimRef.current(); }, 300);
    return () => { if (_launchThrTimer.current) clearTimeout(_launchThrTimer.current); };
  }, [launchThrottle]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!_reaccelThrMounted.current) { _reaccelThrMounted.current = true; return; }
    if (_reaccelThrTimer.current) clearTimeout(_reaccelThrTimer.current);
    _reaccelThrTimer.current = setTimeout(() => { runSimRef.current(); }, 300);
    return () => { if (_reaccelThrTimer.current) clearTimeout(_reaccelThrTimer.current); };
  }, [reaccelThrottle]); // eslint-disable-line react-hooks/exhaustive-deps

  // [A21-ENV] 주행 환경 파라미터 변경 시 시뮬 자동 재실행
  // road_condition / T_amb / altitude / rho / grade_deg / cycle_type 변경 → 300ms 디바운스
  // [A24-FIX-E] 누락 항목 추가: IRI, tire_pressure_actual, crosswind_enable, v_wind, wind_angle_deg
  //   수정 전: EnvModal 상세 파라미터(IRI·공기압·측풍)는 "✓ 적용 & 재실행" 버튼 클릭 필요
  //   수정 후: 위 파라미터도 300ms 디바운스 자동 재실행 목록에 포함
  // [BUG-3 Fix] 마운트 시 skip → 초기 runSim(300ms)과 중복 방지
  const _envTimer   = React.useRef(null);
  const _envMounted = React.useRef(false);
  useEffect(() => {
    if (!_envMounted.current) { _envMounted.current = true; return; }
    if (_envTimer.current) clearTimeout(_envTimer.current);
    _envTimer.current = setTimeout(() => { runSimRef.current(); }, 300);
    return () => { if (_envTimer.current) clearTimeout(_envTimer.current); };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    paramsA.road_condition, paramsA.T_amb, paramsA.altitude, paramsA.rho,
    paramsA.grade_deg,      paramsA.cycle_type, paramsA.wind_speed_ms, paramsA.mu,
    paramsA.cycle_fullspec, paramsA.driving_cycle_mode,
    paramsA.IRI,            paramsA.road_roughness_enable,
    paramsA.tire_pressure_actual,
    paramsA.crosswind_enable, paramsA.v_wind, paramsA.wind_angle_deg,
    paramsB.road_condition, paramsB.T_amb, paramsB.altitude, paramsB.rho,
    paramsB.grade_deg,      paramsB.cycle_type, paramsB.wind_speed_ms, paramsB.mu,
    paramsB.cycle_fullspec, paramsB.driving_cycle_mode,
    paramsB.IRI,            paramsB.road_roughness_enable,
    paramsB.tire_pressure_actual,
    paramsB.crosswind_enable, paramsB.v_wind, paramsB.wind_angle_deg,
    rlHeadwind,  // [A33-R2] 맞바람 변경 시 시뮬 재실행
  ]);

  const r = simResults;
  const c = compResults;
  const cA = paramsA;
  const cB = paramsB;

  // [A36-PERF] Heavy 탭 로딩 표시기 — _coreOnly=true 동안 무거운 탭에 오버레이 표시
  const isHeavyLoading = !!(r && r._coreOnly);
  const HeavyLoadingBadge = () => isHeavyLoading ? (
    <div style={{
      display:"inline-flex", alignItems:"center", gap:6,
      background:"#f59e0b22", border:"1px solid #f59e0b66",
      borderRadius:6, padding:"3px 10px", fontSize:10,
      color:"#f59e0b", fontFamily:"'JetBrains Mono',monospace", marginBottom:8
    }}>
      <span style={{display:"inline-block",marginRight:2}}>⟳</span>
      {lang==="en" ? "WLTP / Thermal / Sensitivity computing…" : "WLTP · 온도 · 민감도 계산 중…"}
    </div>
  ) : null;

  return (
    <ThemeContext.Provider value={isDark}>
    <LangContext.Provider value={lang}>
    <PopupContext.Provider value={setGraphInfoPopup}>
    <RLKpiOpenContext.Provider value={React.useState(null)}>
    {/* [Advance33] 그래프 정보 팝업 — DOM 최상위 렌더링으로 overflow 클리핑 완전 방지 */}
    {graphInfoPopup && (
      <GraphInfoPopup
        info={graphInfoPopup.info}
        title={graphInfoPopup.title}
        lang={graphInfoPopup.lang || lang}
        onClose={() => setGraphInfoPopup(null)}
      />
    )}
    <div style={{ background: COLORS.bg, minHeight: "100vh", color: COLORS.text,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      width: "100%", boxSizing: "border-box",
      /* 실행 창 전체 너비 활용 */
      overflowX: "auto" }}>

      {/* Modals */}
      <VehicleInfoModal
        show={showVehicleModal} onClose={() => setShowVehicleModal(false)}
        paramsA={paramsA} paramsB={paramsB}
        setParamsA={setParamsA} setParamsB={setParamsB}
        presetsA={vehicleAKey} presetsB={vehicleBKey}
        onApply={runSim}
        onOpenPresetManager={() => setShowVehiclePresetModal(true)} />
      <EnvModal
        show={showEnvModal} onClose={() => setShowEnvModal(false)}
        paramsA={paramsA} paramsB={paramsB}
        setParamsA={setParamsA} setParamsB={setParamsB}
        onApply={runSim}
      />
      <ControlModal
        show={showControlModal} onClose={() => setShowControlModal(false)}
        onApply={runSim}
        paramsA={paramsA} paramsB={paramsB}
        setParamsA={setParamsA} setParamsB={setParamsB}
        initialGroup={ctrlInitGroup} initialSubTab={ctrlInitSubTab}
      />
      <CSVModal
        show={showCSVModal} onClose={() => setShowCSVModal(false)}
        compResults={compResults} paramsA={paramsA} paramsB={paramsB}
        reaccelV0={reaccelV0} reaccelV1={reaccelV1}
      />
      <LayoutFolderModal
        show={showFolderModal} onClose={() => setShowFolderModal(false)}
        onChanged={handleFolderChanged}
      />
      <VehiclePresetModal
        show={showVehiclePresetModal} onClose={() => setShowVehiclePresetModal(false)}
        paramsA={paramsA} paramsB={paramsB}
        vehicleAKey={vehicleAKey} vehicleBKey={vehicleBKey}
        setParamsA={setParamsA} setParamsB={setParamsB}
        setVehicleAKey={setVehicleAKey} setVehicleBKey={setVehicleBKey}
        VEHICLE_PRESETS={VEHICLE_PRESETS}
        onApply={runSim}
      />

      {/* ═══════════ Header ═══════════ */}
      <div style={{ background: `linear-gradient(135deg, ${COLORS.panel} 0%, ${COLORS.bg} 100%)`,
        borderBottom: `1px solid ${COLORS.border}`, padding: "10px 16px" }}>
        {/* Top row: logo + vehicle selectors + action buttons */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.accent4})`,
              width: 32, height: 32, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 900, color: "#000" }}>EV</div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{t("app_title")}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.accent,
                  background: `${COLORS.accent}18`, border: `1px solid ${COLORS.accent}60`,
                  borderRadius: 4, padding: "1px 6px", fontFamily: mono, letterSpacing: "0.03em" }}>v2.2</span>
                <span style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono, letterSpacing: "0.01em" }}>sunjaepark83@gmail.com</span>
              </div>
              <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono }}>{t("app_sub")}</div>
            </div>
          </div>

          {/* Action buttons — 차트열 → 라이트/다크 → 언어 토글 → 실행 버튼 순서 */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {/* ── 차트 열 레이아웃 전환 버튼 ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: mono, whiteSpace: "nowrap" }}>
                {lang === "ko" ? "차트 열" : "Cols"}
              </span>
              <div style={{ display: "flex", gap: 2, background: COLORS.panel, borderRadius: 6, padding: 3, border: `1px solid ${COLORS.border}`, height: 30, boxSizing: 'border-box', alignItems: 'center' }}>
                {[1, 2, 3, 4].map(n => {
                  const isActive = colLayout === n;
                  return (
                    <button key={n}
                      title={lang === "ko" ? `${n}열` : `${n} Col`}
                      onClick={() => applyColLayout(n)}
                      style={{
                        padding: "3px 8px", borderRadius: 4, border: "none", cursor: "pointer",
                        fontSize: 11, fontWeight: 700,
                        transition: "all 0.15s",
                        background: isActive ? COLORS.accent : "transparent",
                        color: isActive ? "#fff" : COLORS.textDim,
                        boxShadow: isActive ? `0 0 0 2px ${COLORS.accent}80` : "none",
                      }}>
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Theme Toggle */}
            <div style={{ display: "flex", gap: 3, background: COLORS.panel, borderRadius: 6, padding: 3, border: `1px solid ${COLORS.border}`, height: 30, boxSizing: 'border-box', alignItems: 'center' }}>
              {[{ v: false, label: "☀️", ko: "라이트", en: "Light" }, { v: true, label: "🌙", ko: "다크", en: "Dark" }].map(th => (
                <button key={th.en} onClick={() => setIsDark(th.v)}
                  title={lang === "ko" ? th.ko + " 테마" : th.en + " theme"}
                  style={{ padding: "3px 10px", borderRadius: 4, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
                    background: isDark === th.v ? (th.v ? '#1e293b' : '#fef9c3') : "transparent",
                    color: isDark === th.v ? (th.v ? '#e2e8f0' : '#92400e') : COLORS.textDim,
                    boxShadow: isDark === th.v ? `0 0 0 2px ${th.v ? '#334155' : '#fbbf24'}` : 'none',
                    transition: "all 0.2s" }}>
                  {th.label} <span style={{ fontSize:9, fontWeight:400 }}>{lang === "ko" ? th.ko : th.en}</span>
                </button>
              ))}
            </div>
            {/* Language Toggle */}
            <div style={{ display: "flex", gap: 3, background: COLORS.panel, borderRadius: 6, padding: 3, border: `1px solid ${COLORS.border}`, height: 30, boxSizing: 'border-box', alignItems: 'center' }}>
              {[
                { id: "ko", label: lang === "ko" ? "🇰🇷 한국어" : "🇰🇷 Korean" },
                { id: "en", label: lang === "ko" ? "🇺🇸 영어" : "🇺🇸 English" }
              ].map(l => (
                <button key={l.id} onClick={() => setLang(l.id)}
                  style={{ padding: "3px 10px", borderRadius: 4, border: "none", cursor: "pointer", fontSize: 10, fontWeight: 700,
                    background: lang === l.id ? COLORS.accent : "transparent",
                    color: lang === l.id ? "#fff" : COLORS.textDim, transition: "all 0.15s" }}>
                  {l.label}
                </button>
              ))}
            </div>
            {/* ── 레이아웃 알림 토스트 (위치 유지) ── */}
            {layoutNotif && (
              <div style={{
                position: "fixed", bottom: 24, right: 24, zIndex: 9999,
                padding: "10px 18px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: layoutNotif.ok ? '#16a34a' : '#dc2626',
                color: '#fff', boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                border: `1px solid ${layoutNotif.ok ? '#22c55e' : '#ef4444'}`,
              }}>
                {layoutNotif.message}
              </div>
            )}
            {/* 숨겨진 file input (FSAPI 미지원 fallback용) */}
            <input
              ref={layoutFileInputRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleLayoutImportFile}
            />
            {/* Run Button */}
            <button onClick={runSim} disabled={isRunning} style={{
              background: `linear-gradient(135deg, ${COLORS.accent}, #d97706)`,
              border: "none", color: "#000", padding: "0 18px", borderRadius: 5,
              cursor: "pointer", fontWeight: 700, fontSize: 12, opacity: isRunning ? 0.6 : 1,
              height: 30, boxSizing: 'border-box' }}>
              {isRunning ? t("simulating") : t("run_btn")}
            </button>
          </div>
        </div>

        {/* Bottom row: config buttons + single-vehicle selector */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, flexWrap: "wrap", gap: 6 }}>
          {/* Config buttons — 순서: 차량 정보 > 제어기/BMS > 드라이빙 사이클 > 데이터 출력 */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={() => setShowVehicleModal(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 6, cursor: "pointer",
                background: `linear-gradient(135deg, ${COLORS.accent2}22, ${COLORS.panel})`,
                border: `1px solid ${COLORS.accent2}40`, color: COLORS.text, fontSize: 12, fontWeight: 600,
                transition: "all 0.2s" }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = COLORS.accent2}
              onMouseOut={(e) => e.currentTarget.style.borderColor = `${COLORS.accent2}40`}>
              <span style={{ fontSize: 16 }}>🚗</span>
              <span>{t("vehicle_info")}</span>
            </button>
            <button onClick={() => setShowControlModal(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 6, cursor: "pointer",
                background: `linear-gradient(135deg, ${COLORS.accent5}33, ${COLORS.panel})`,
                border: `1px solid ${COLORS.accent5}40`, color: COLORS.text, fontSize: 12, fontWeight: 600,
                transition: "all 0.2s" }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = COLORS.accent5}
              onMouseOut={(e) => e.currentTarget.style.borderColor = `${COLORS.accent5}40`}>
              <span style={{ fontSize: 16 }}>🎛️</span>
              <span>{t("ctrl_bms")}</span>
            </button>
            {/* ⚙️ 설정 — 차량 프리셋 · 데이터 출력 · 레이아웃 통합 드롭다운 */}
            <div style={{ position: "relative" }}>
              <button
                ref={settingsBtnRef}
                onClick={() => setShowSettingsPopup(v => !v)}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 6, cursor: "pointer",
                  background: showSettingsPopup
                    ? `linear-gradient(135deg, #a78bfa44, ${COLORS.panel})`
                    : `linear-gradient(135deg, #a78bfa22, ${COLORS.panel})`,
                  border: `1px solid ${showSettingsPopup ? "#a78bfa" : "#a78bfa40"}`,
                  color: COLORS.text, fontSize: 12, fontWeight: 600, transition: "all 0.2s" }}>
                <span style={{ fontSize: 16 }}>⚙️</span>
                <span>{lang === "ko" ? "설정" : "Settings"}</span>
                <span style={{ fontSize: 9, color: COLORS.textMuted, marginLeft: 2 }}>{showSettingsPopup ? "▲" : "▼"}</span>
              </button>
              {showSettingsPopup && (
                <>
                  <div
                    style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1998 }}
                    onClick={() => setShowSettingsPopup(false)}
                  />
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute", top: "calc(100% + 6px)", left: 0,
                      background: COLORS.panel, border: `1px solid ${COLORS.border}`,
                      borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
                      zIndex: 1999, minWidth: 240, overflow: "hidden"
                    }}>
                    <div style={{ padding: "8px 14px 7px", fontSize: 10, fontWeight: 700,
                      color: "#a78bfa", borderBottom: `1px solid ${COLORS.border}`,
                      background: "#a78bfa10", letterSpacing: "0.05em" }}>
                      ⚙️ {lang === "ko" ? "설정" : "Settings"}
                    </div>
                    {/* ── 차량 프리셋 ── */}
                    <div style={{ padding: "5px 14px 3px", fontSize: 9, fontWeight: 700,
                      color: COLORS.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      {lang === "ko" ? "차량 프리셋" : "Vehicle Preset"}
                    </div>
                    <button
                      onClick={() => { setShowSettingsPopup(false); setShowVehiclePresetModal(true); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "8px 14px", background: "transparent",
                        border: "none", borderBottom: `1px solid ${COLORS.border}`,
                        color: COLORS.text, fontSize: 12, fontWeight: 600,
                        cursor: "pointer", textAlign: "left", transition: "background 0.15s" }}
                      onMouseOver={(e) => e.currentTarget.style.background = "#8b5cf622"}
                      onMouseOut={(e)  => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 17 }}>🗂️</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{lang === "ko" ? "프리셋 관리" : "Manage Presets"}</div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {lang === "ko" ? "차량 프리셋 CSV 저장 / 불러오기" : "Save / load vehicle preset CSV"}
                        </div>
                      </div>
                    </button>
                    {/* ── 주행 사이클 ── */}
                    <div style={{ padding: "5px 14px 3px", fontSize: 9, fontWeight: 700,
                      color: COLORS.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      {lang === "ko" ? "주행 사이클" : "Driving Cycle"}
                    </div>
                    {/* 사이클 템플릿 CSV 다운로드 */}
                    <button
                      onClick={() => { setShowSettingsPopup(false); handleTemplateDownload(); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "8px 14px", background: "transparent",
                        border: "none", borderBottom: `1px solid ${COLORS.border}20`,
                        color: COLORS.text, fontSize: 12, fontWeight: 600,
                        cursor: "pointer", textAlign: "left", transition: "background 0.15s" }}
                      onMouseOver={(e) => e.currentTarget.style.background = "#7c3aed22"}
                      onMouseOut={(e)  => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 17 }}>📋</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{lang === "ko" ? "사이클 템플릿 다운로드" : "Download Cycle Template"}</div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {lang === "ko" ? "Full-Spec CSV 입력 양식 (8열 · 10ms)" : "Full-Spec CSV input template (8 cols · 10ms)"}
                        </div>
                      </div>
                    </button>
                    {/* Full-Spec CSV 업로드 */}
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "8px 14px", background: "transparent",
                        borderBottom: `1px solid ${COLORS.border}`,
                        color: COLORS.text, fontSize: 12, fontWeight: 600,
                        cursor: "pointer", textAlign: "left", transition: "background 0.15s",
                        boxSizing: "border-box" }}
                      onMouseOver={(e) => e.currentTarget.style.background = "#ec489922"}
                      onMouseOut={(e)  => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 17 }}>📂</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: fullSpecParsed?.ok ? "#ec4899" : COLORS.text }}>
                          {lang === "ko" ? "Full-Spec CSV 업로드" : "Upload Full-Spec CSV"}
                          {fullSpecParsed?.ok && (
                            <span style={{ marginLeft: 6, fontSize: 9, color: "#ec4899", fontFamily: mono }}>
                              ✓ {(paramsA.driving_cycle_name || "").slice(0, 14)}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {fullSpecError
                            ? <span style={{ color: "#ef4444" }}>❌ {fullSpecError}</span>
                            : lang === "ko" ? "업로드 후 자동 시뮬 실행" : "Auto-run simulation after upload"}
                        </div>
                      </div>
                      <input
                        ref={fullSpecFileInputRef}
                        type="file" accept=".csv,.tsv,.txt"
                        style={{ display: "none" }}
                        onChange={(ev) => { setShowSettingsPopup(false); handleFullSpecUpload(ev); }}
                      />
                    </label>
                    {/* Full-Spec 업로드 성공 시 메타 요약 */}
                    {fullSpecParsed?.ok && (
                      <div style={{ padding: "6px 14px 8px", background: "#ec489910",
                        borderBottom: `1px solid ${COLORS.border}`,
                        fontSize: 9, color: COLORS.textMuted, fontFamily: mono, lineHeight: 1.7 }}>
                        <span style={{ color: "#ec4899", fontWeight: 700 }}>
                          {fullSpecParsed.meta.n.toLocaleString()}{lang === "ko" ? "행" : " rows"}
                        </span>
                        {" · "}⏱ {fullSpecParsed.meta.t_total_s}s
                        {" · "}🌡 {fullSpecParsed.meta.t_amb_avg}°C
                        {" · "}🛣 μ {fullSpecParsed.meta.avg_mu}
                        {fullSpecParsed.errors.length > 0 && (
                          <span style={{ color: "#f59e0b" }}> · ⚠ {fullSpecParsed.errors.length}{lang === "ko" ? "행 경고" : " warnings"}</span>
                        )}
                      </div>
                    )}
                    {/* ── 데이터 출력 ── */}
                    <div style={{ padding: "5px 14px 3px", fontSize: 9, fontWeight: 700,
                      color: COLORS.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      {lang === "ko" ? "데이터 출력" : "Data Export"}
                    </div>
                    <button
                      onClick={() => { setShowSettingsPopup(false); setShowCSVModal(true); }}
                      disabled={!compResults}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "8px 14px", background: "transparent",
                        border: "none", borderBottom: `1px solid ${COLORS.border}20`,
                        color: COLORS.text, fontSize: 12, fontWeight: 600,
                        cursor: compResults ? "pointer" : "not-allowed",
                        textAlign: "left", transition: "background 0.15s", opacity: compResults ? 1 : 0.45 }}
                      onMouseOver={(e) => compResults && (e.currentTarget.style.background = `${COLORS.accent3}22`)}
                      onMouseOut={(e)  => (e.currentTarget.style.background = "transparent")}>
                      <span style={{ fontSize: 17 }}>📥</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{lang === "ko" ? "CSV 파일" : "CSV File"}</div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {lang === "ko" ? "시뮬 결과를 Excel로 내보내기" : "Export simulation data to Excel"}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowSettingsPopup(false); handlePDFExport((activeSimVehicle==='A'?paramsA:paramsB).shortName||'EV', activeTab, lang); }}
                      disabled={!compResults}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "8px 14px", background: "transparent",
                        border: "none", borderBottom: `1px solid ${COLORS.border}`,
                        color: COLORS.text, fontSize: 12, fontWeight: 600,
                        cursor: compResults ? "pointer" : "not-allowed",
                        textAlign: "left", transition: "background 0.15s", opacity: compResults ? 1 : 0.45 }}
                      onMouseOver={(e) => compResults && (e.currentTarget.style.background = "#dc262622")}
                      onMouseOut={(e)  => (e.currentTarget.style.background = "transparent")}>
                      <span style={{ fontSize: 17 }}>🖨️</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{lang === "ko" ? "인쇄 / PDF" : "Print / PDF"}</div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {lang === "ko" ? "현재 화면을 인쇄하거나 PDF 저장" : "Print or save current view as PDF"}
                        </div>
                      </div>
                    </button>
                    {/* ── 레이아웃 ── */}
                    <div style={{ padding: "5px 14px 3px", fontSize: 9, fontWeight: 700,
                      color: COLORS.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      {lang === "ko" ? "레이아웃" : "Layout"}
                    </div>
                    <button
                      onClick={() => { setShowSettingsPopup(false); handleLayoutExport(); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "8px 14px", background: "transparent",
                        border: "none", borderBottom: `1px solid ${COLORS.border}20`,
                        color: COLORS.text, fontSize: 12, fontWeight: 600,
                        cursor: "pointer", textAlign: "left", transition: "background 0.15s" }}
                      onMouseOver={(e) => e.currentTarget.style.background = "#16a34a22"}
                      onMouseOut={(e)  => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 17 }}>💾</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: saveFolderName ? '#4ade80' : COLORS.text }}>
                          {lang === "ko" ? "레이아웃 저장" : "Save Layout"}
                          {saveFolderName && <span style={{ marginLeft: 5, fontSize: 9, fontFamily: "'JetBrains Mono',monospace" }}>📁{saveFolderName.length > 12 ? saveFolderName.slice(0,12)+'…' : saveFolderName}</span>}
                        </div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {lang === "ko" ? "현재 레이아웃을 CSV로 저장" : "Save current layout as CSV"}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowSettingsPopup(false); handleLayoutImportClick(); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "8px 14px", background: "transparent",
                        border: "none", borderBottom: `1px solid ${COLORS.border}20`,
                        color: COLORS.text, fontSize: 12, fontWeight: 600,
                        cursor: "pointer", textAlign: "left", transition: "background 0.15s" }}
                      onMouseOver={(e) => e.currentTarget.style.background = "#2563eb22"}
                      onMouseOut={(e)  => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 17 }}>📂</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: loadFolderName ? '#60a5fa' : COLORS.text }}>
                          {lang === "ko" ? "레이아웃 불러오기" : "Load Layout"}
                          {loadFolderName && <span style={{ marginLeft: 5, fontSize: 9, fontFamily: "'JetBrains Mono',monospace" }}>📁{loadFolderName.length > 12 ? loadFolderName.slice(0,12)+'…' : loadFolderName}</span>}
                        </div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {lang === "ko" ? "저장된 레이아웃 CSV 불러오기" : "Load saved layout CSV"}
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowSettingsPopup(false); setShowFolderModal(true); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "8px 14px", background: "transparent", border: "none",
                        color: COLORS.text, fontSize: 12, fontWeight: 600,
                        cursor: "pointer", textAlign: "left", transition: "background 0.15s" }}
                      onMouseOver={(e) => e.currentTarget.style.background = "#7c3aed22"}
                      onMouseOut={(e)  => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 17 }}>📁</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: (saveFolderName||loadFolderName) ? '#a78bfa' : COLORS.text }}>
                          {lang === "ko" ? "폴더 설정" : "Folder Settings"}
                        </div>
                        <div style={{ fontSize: 9, color: COLORS.textMuted, marginTop: 1 }}>
                          {lang === "ko" ? "저장·불러오기 폴더 지정" : "Configure save / load folders"}
                        </div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 개별 탭 차량 선택 — 라이트/다크 스타일 토글 */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: mono }}>{t("individual_tab")}</span>
            <div style={{ display: "flex", gap: 3, background: COLORS.panel, borderRadius: 6, padding: 3, border: `1px solid ${COLORS.border}` }}>
              {["A", "B"].map(v => {
                const vColor = v === "A" ? COLORS.vehA : COLORS.vehB;
                const isActive = activeSimVehicle === v;
                return (
                  <button key={v} onClick={() => setActiveSimVehicle(v)}
                    style={{ padding: "3px 10px", borderRadius: 4, border: "none", cursor: "pointer",
                      fontSize: 11, fontWeight: 700, transition: "all 0.15s",
                      background: isActive ? vColor : "transparent",
                      color: isActive ? "#000" : COLORS.textDim,
                      boxShadow: isActive ? `0 0 0 2px ${vColor}80` : "none" }}>
                    {v === "A" ? paramsA.shortName : paramsB.shortName}
                  </button>
                );
              })}
            </div>
          </div>



        </div>

        {/* [A29-02] 인라인 패널 — 전체 1행, 섹션 제목+컨텐츠 수직 중앙 정렬 */}
        <div style={{
          marginTop: 8, padding: "7px 14px",
          background: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: 8,
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px 0",
        }}>

          {/* ── 공통 섹션 셀 스타일 헬퍼 ── */}
          {(() => {
            const SEP = () => (
              <div style={{ width: 1, height: 28, background: COLORS.border, margin: "0 10px", flexShrink: 0 }} />
            );

            const LABEL_STYLE = (color) => ({
              fontSize: 9, fontWeight: 700, color, fontFamily: mono,
              whiteSpace: "nowrap", marginRight: 5, userSelect: "none",
            });

            const INPUT_BOX = (color) => ({
              display: "flex", alignItems: "center", gap: 2,
              borderRadius: 4, border: `1px solid ${color}40`, padding: "2px 5px",
              background: "transparent",
            });

            const INPUT_STYLE = (color, width) => ({
              width, fontSize: 11, background: "transparent", border: "none",
              color, textAlign: "center", outline: "none",
              fontWeight: 700, fontFamily: mono,
            });

            const UNIT = (color, txt) => (
              <span style={{ fontSize: 9, color, whiteSpace: "nowrap" }}>{txt}</span>
            );

            const DROPDOWN = (value, onChange, items, activeCol) => (
              <div style={{ position: "relative", flexShrink: 0 }}>
                <select value={value} onChange={onChange}
                  style={{
                    padding: "3px 22px 3px 8px", borderRadius: 5,
                    fontSize: 11, fontWeight: 700, fontFamily: mono,
                    background: COLORS.bg, border: `1px solid ${activeCol}`,
                    color: activeCol, cursor: "pointer", outline: "none",
                    appearance: "none", WebkitAppearance: "none",
                  }}>
                  {items.map(it => (
                    <option key={it.id ?? it.v ?? it.idx} value={it.id ?? it.v ?? String(it.idx)}
                      disabled={!!it.disabled}
                      style={{ color: it.disabled ? COLORS.textMuted : COLORS.text, background: COLORS.panel }}>
                      {it.label}
                    </option>
                  ))}
                </select>
                <span style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                  fontSize: 8, color: activeCol, pointerEvents: "none" }}>▼</span>
              </div>
            );

            // ─── 1. 드라이브 모드 ───
            const MODES = [
              { id: "ECO",      label: "🍃 ECO",      col: "#22c55e" },
              { id: "NORMAL",   label: "⚡ NORMAL",   col: COLORS.accent },
              { id: "SPORT",    label: "🔥 SPORT",    col: "#ef4444" },
              { id: "SNOW",     label: "❄️ SNOW",     col: "#38bdf8" },
              { id: "OFF_ROAD", label: "🗻 OFF-ROAD", col: "#a855f7" },
            ];
            const activeMode = (activeSimVehicle === "A" ? paramsA : paramsB).drive_mode || "NORMAL";
            const activeModeCol = (MODES.find(m => m.id === activeMode) || MODES[1]).col;

            // ─── 2. 주행 사이클 ───
            const fullSpecLoaded = !!(paramsA.driving_cycle_data_full?.length > 0 ||
                                      paramsB.driving_cycle_data_full?.length > 0 ||
                                      fullSpecParsed?.ok);
            const CYCLES = [
              { id: "WLTP",         label: "🌍 WLTP",          col: "#3b82f6" },
              { id: "FTP75",        label: "🇺🇸 FTP-75",        col: "#22c55e" },
              { id: "EPA_HWY",      label: "🇺🇸 EPA Hwy",       col: "#10b981" },
              { id: "EPA_CITY",     label: "🇺🇸 EPA City",      col: "#059669" },
              { id: "JC08",         label: "🇯🇵 JC08",          col: "#f59e0b" },
              { id: "CLTC",         label: "🇨🇳 CLTC",          col: "#ef4444" },
              { id: "RDE_FULL",     label: "🛣️ RDE Full",       col: "#8b5cf6" },
              { id: "RDE_URBAN",    label: "🏙️ RDE Urban",      col: "#a78bfa" },
              { id: "RDE_RURAL",    label: "🌿 RDE Rural",      col: "#6ee7b7" },
              { id: "RDE_MOTORWAY", label: "🚀 RDE Motorway",   col: "#f97316" },
              { id: "csv_full",
                label: fullSpecLoaded
                  ? "📂 Full-Spec CSV ✓"
                  : "📂 Full-Spec CSV",
                col: "#ec4899",
                disabled: !fullSpecLoaded },
            ];
            const refP = activeSimVehicle === "A" ? paramsA : paramsB;
            const activeCsvMode = refP.driving_cycle_mode || "default";
            const activeCycleId = activeCsvMode === "csv_full" ? "csv_full" : (refP.cycle_type || "WLTP");
            const activeCycleCol = (CYCLES.find(c => c.id === activeCycleId) || CYCLES[0]).col;

            // ─── 3. 차량 조건 ───
            const massActive = rlMassKg > 0;
            const socVal = typeof rlSocScenario === 'number' ? rlSocScenario : 80;

            // ─── 4. 도로 조건 ───
            const ROADS = [
              { v: "dry",  label: lang === "ko" ? "🟢 건조" : "🟢 Dry",  col: "#16a34a" },
              { v: "wet",  label: lang === "ko" ? "🔵 습윤" : "🔵 Wet",  col: "#2563eb" },
              { v: "snow", label: lang === "ko" ? "⚪ 적설" : "⚪ Snow", col: "#64748b" },
              { v: "ice",  label: lang === "ko" ? "🧊 빙판" : "🧊 Ice",  col: "#0ea5e9" },
            ];
            const activeRoad = ROADS.find(r => r.v === (paramsA.road_condition || "dry")) || ROADS[0];

            // ─── 5. 빠른 설정 ───
            const _rho = (T, alt) => +(1.225 * (288.15 / (T + 273.15)) * Math.exp(-9.81 * alt / (287.05 * (T + 273.15)))).toFixed(3);
            const PRESETS = [
              { idx: 0, label: lang === "ko" ? "─ 선택 ─"  : "─ Select ─", col: COLORS.textDim, vals: null },
              { idx: 1, label: lang === "ko" ? "🛣️ 표준"   : "🛣️ STD",    col: "#16a34a", vals: { T_amb: 25,  grade_deg: 0, road_condition: "dry",  altitude: 0,   mu: 1.0,  rho: _rho(25,  0)   } },
              { idx: 2, label: lang === "ko" ? "☀️ 여름"   : "☀️ Summer", col: "#f59e0b", vals: { T_amb: 35,  grade_deg: 0, road_condition: "dry",  altitude: 50,  mu: 1.0,  rho: _rho(35,  50)  } },
              { idx: 3, label: lang === "ko" ? "❄️ 겨울"   : "❄️ Winter", col: "#38bdf8", vals: { T_amb: -10, grade_deg: 0, road_condition: "snow", altitude: 50,  mu: 0.4,  rho: _rho(-10, 50)  } },
              { idx: 4, label: lang === "ko" ? "🏔️ 산악"   : "🏔️ Mtn",   col: "#a855f7", vals: { T_amb: 20,  grade_deg: 8, road_condition: "dry",  altitude: 800, mu: 0.9,  rho: _rho(20,  800) } },
              { idx: 5, label: lang === "ko" ? "🌧️ 우천"   : "🌧️ Rain",  col: "#2563eb", vals: { T_amb: 15,  grade_deg: 0, road_condition: "wet",  altitude: 100, mu: 0.7,  rho: _rho(15,  100) } },
              { idx: 6, label: lang === "ko" ? "🧊 빙판"   : "🧊 Ice",   col: "#0ea5e9", vals: { T_amb: -20, grade_deg: 2, road_condition: "ice",  altitude: 200, mu: 0.15, rho: _rho(-20, 200) } },
            ];
            const activePresetIdx = PRESETS.findIndex((p, i) => i > 0 && p.vals &&
              paramsA.T_amb === p.vals.T_amb && paramsA.grade_deg === p.vals.grade_deg &&
              paramsA.road_condition === p.vals.road_condition);
            const activePreset = activePresetIdx > 0 ? PRESETS[activePresetIdx] : PRESETS[0];

            return (
              <>
                {/* 1. 드라이브 모드 */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span style={LABEL_STYLE(COLORS.accent2)}>🎮 {lang === "ko" ? "드라이브 모드" : "Drive Mode"}</span>
                  {DROPDOWN(
                    activeMode,
                    ev => {
                      const mode = ev.target.value;
                      setParamsA(p => ({ ...p, drive_mode: mode }));
                      setParamsB(p => ({ ...p, drive_mode: mode }));
                      const SIM_TABS = ["launch","reaccel","coast","braking","energy","thermal","driveability","wltp","sensitivity"];
                      if (SIM_TABS.includes(activeTab)) setTimeout(() => runSim(), 80);
                    },
                    MODES,
                    activeModeCol
                  )}
                </div>

                <SEP />

                {/* 2. 주행 사이클 */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span style={LABEL_STYLE("#3b82f6")}>🛣️ {lang === "ko" ? "주행 사이클" : "Cycle"}</span>
                  {DROPDOWN(
                    activeCycleId,
                    ev => {
                      const id = ev.target.value;
                      if (id === "csv_full") {
                        if (!fullSpecLoaded) {
                          // CSV 미업로드 → 선택 차단하고 설정 팝업으로 안내
                          ev.target.value = activeCycleId;
                          setShowSettingsPopup(true);
                          return;
                        }
                        setParamsA(p => ({ ...p, driving_cycle_mode: "csv_full" }));
                        setParamsB(p => ({ ...p, driving_cycle_mode: "csv_full" }));
                      } else {
                        setParamsA(p => ({ ...p, cycle_type: id, driving_cycle_mode: "default" }));
                        setParamsB(p => ({ ...p, cycle_type: id, driving_cycle_mode: "default" }));
                      }
                      setTimeout(() => runSim(), 80);
                    },
                    CYCLES,
                    activeCycleCol
                  )}
                </div>

                <SEP />

                {/* 3. 차량 조건 */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                  <span style={LABEL_STYLE("#f97316")}>⚖ {lang === "ko" ? "차량 조건" : "Load"}</span>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 5,
                    padding: "2px 7px", borderRadius: 5, background: COLORS.bg,
                    border: `1px solid ${(massActive || socVal !== 100) ? "#f97316" : COLORS.border}`,
                  }}>
                    <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: mono, whiteSpace: "nowrap" }}>
                      {lang === "ko" ? "차중 +" : "Mass +"}
                    </span>
                    <div style={INPUT_BOX(massActive ? "#f97316" : COLORS.border)}>
                      <input type="text" inputMode="numeric"
                        defaultValue={rlMassKg} key={"m-" + rlMassKg}
                        onChange={ev => {
                          const raw = ev.target.value;
                          if (_rlMassTimer.current) clearTimeout(_rlMassTimer.current);
                          _rlMassTimer.current = setTimeout(() => {
                            const n = parseFloat(raw);
                            if (!isNaN(n)) setRlMassKg(Math.max(0, Math.min(2000, Math.round(n))));
                          }, 400);
                        }}
                        onBlur={ev => {
                          if (_rlMassTimer.current) { clearTimeout(_rlMassTimer.current); _rlMassTimer.current = null; }
                          const n = parseFloat(ev.target.value);
                          if (!isNaN(n)) setRlMassKg(Math.max(0, Math.min(2000, Math.round(n))));
                        }}
                        style={INPUT_STYLE(massActive ? "#f97316" : COLORS.text, 38)} />
                    </div>
                    {UNIT("#f97316", "kg")}
                    <div style={{ width: 1, height: 14, background: COLORS.border }} />
                    <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: mono }}>SOC</span>
                    <div style={INPUT_BOX(socVal !== 100 ? "#22c55e" : COLORS.border)}>
                      <input type="text" inputMode="numeric"
                        defaultValue={socVal} key={"soc-" + socVal}
                        onChange={ev => {
                          const raw = ev.target.value;
                          if (_rlSocTimer.current) clearTimeout(_rlSocTimer.current);
                          _rlSocTimer.current = setTimeout(() => {
                            const n = parseFloat(raw);
                            if (!isNaN(n)) setRlSocScenario(Math.max(5, Math.min(100, Math.round(n))));
                          }, 400);
                        }}
                        onBlur={ev => {
                          if (_rlSocTimer.current) { clearTimeout(_rlSocTimer.current); _rlSocTimer.current = null; }
                          const n = parseFloat(ev.target.value);
                          if (!isNaN(n)) setRlSocScenario(Math.max(5, Math.min(100, Math.round(n))));
                        }}
                        style={INPUT_STYLE(socVal !== 100 ? "#22c55e" : COLORS.text, 42)} />
                    </div>
                    {UNIT("#22c55e", "%")}
                  </div>
                </div>

                <SEP />

                {/* 4. 도로 조건 */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                  <span style={LABEL_STYLE(COLORS.accent)}>🛣️ {lang === "ko" ? "도로 조건" : "Road"}</span>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 5,
                    padding: "2px 7px", borderRadius: 5, background: COLORS.bg,
                    border: `1px solid ${paramsA.grade_deg !== 0 ? COLORS.accent : COLORS.border}`,
                  }}>
                    <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: mono }}>{lang === "ko" ? "구배" : "Grade"}</span>
                    <div style={INPUT_BOX(paramsA.grade_deg !== 0 ? COLORS.accent : COLORS.border)}>
                      <input type="text" inputMode="numeric"
                        value={gradeRaw}
                        onChange={ev => {
                          const raw = ev.target.value;
                          setGradeRaw(raw); // 항상 raw 문자열 반영 (중간 입력 "-", "-3." 등 허용)
                          const v = parseFloat(raw);
                          if (!isNaN(v)) {
                            const pct = +(Math.tan(v * Math.PI / 180) * 100).toFixed(2);
                            setParamsA(p => ({ ...p, grade_deg: v }));
                            setParamsB(p => ({ ...p, grade_deg: v }));
                            setRlGrade(pct);
                          }
                        }}
                        onBlur={ev => {
                          const v = parseFloat(ev.target.value);
                          if (!isNaN(v)) setTimeout(() => runSim(), 80);
                        }}
                        style={INPUT_STYLE(paramsA.grade_deg !== 0 ? COLORS.accent : COLORS.text, 28)} />
                    </div>
                    {UNIT(COLORS.accent, "°")}
                    <div style={{ width: 1, height: 14, background: COLORS.border }} />
                    <span style={{ fontSize: 9, color: COLORS.textDim, fontFamily: mono, whiteSpace: "nowrap" }}>{lang === "ko" ? "노면" : "Road"}</span>
                    {DROPDOWN(
                      paramsA.road_condition || "dry",
                      ev => {
                        setParamsA(p => ({ ...p, road_condition: ev.target.value }));
                        setParamsB(p => ({ ...p, road_condition: ev.target.value }));
                        setTimeout(() => runSim(), 80);
                      },
                      ROADS.map(r => ({ ...r, id: r.v })),
                      activeRoad.col
                    )}
                  </div>
                </div>

                <SEP />

                {/* 5. 대기 환경 */}
                <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                  <span style={LABEL_STYLE(COLORS.thermalOrange)}>🌡️ {lang === "ko" ? "대기 환경" : "Atmo"}</span>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 5,
                    padding: "2px 7px", borderRadius: 5, background: COLORS.bg,
                    border: `1px solid ${COLORS.border}`,
                  }}>
                    {[
                      { key: "T_amb", lbl: lang === "ko" ? "기온" : "Temp",      unit: "°C",    col: COLORS.thermalOrange, w: 30, min: -30, max: 50,   step: 5     },
                      { key: "rho",   lbl: lang === "ko" ? "공기밀도" : "Air ρ", unit: "kg/m³", col: COLORS.accent2,       w: 44, min: 0.5, max: 1.5,  step: 0.001 },
                    ].map(({ key, lbl, unit, col, w, min, max, step }, i) => (
                      <React.Fragment key={key}>
                        {i > 0 && <div style={{ width: 1, height: 12, background: COLORS.border + "60" }} />}
                        <span style={{ fontSize: 9, color: COLORS.textDim, fontFamily: mono, whiteSpace: "nowrap" }}>{lbl}</span>
                        <div style={INPUT_BOX(col)}>
                          <input type="text" inputMode="numeric"
                            value={key === "T_amb" ? tambRaw : paramsA[key]}
                            onChange={ev => {
                              const raw = ev.target.value;
                              if (key === "T_amb") setTambRaw(raw); // 중간 입력 허용 ("-", "-1" 등)
                              const v = parseFloat(raw);
                              if (!isNaN(v)) {
                                if (key === "T_amb") {
                                  const nT   = v;
                                  const nAlt = paramsA.altitude || 0;
                                  const rhoN = +(1.225 * (288.15 / (nT + 273.15)) * Math.exp(-9.81 * nAlt / (287.05 * (nT + 273.15)))).toFixed(3);
                                  setParamsA(p => ({ ...p, T_amb: v, rho: rhoN }));
                                  setParamsB(p => ({ ...p, T_amb: v, rho: rhoN }));
                                } else {
                                  setParamsA(p => ({ ...p, [key]: v }));
                                  setParamsB(p => ({ ...p, [key]: v }));
                                }
                              }
                            }}
                            onBlur={ev => {
                              const v = parseFloat(ev.target.value);
                              if (!isNaN(v)) setTimeout(() => runSim(), 80);
                            }}
                            style={INPUT_STYLE(col, w)} />
                        </div>
                        {unit && UNIT(col, unit)}
                      </React.Fragment>
                    ))}
                    <div style={{ width: 1, height: 12, background: COLORS.border + "60" }} />
                    <span style={{ fontSize: 9, color: "#06b6d4", fontFamily: mono, whiteSpace: "nowrap" }}>{lang === "ko" ? "맞바람" : "Headwind"}</span>
                    <div style={INPUT_BOX(rlHeadwind > 0 ? "#06b6d4" : COLORS.border)}>
                      <input type="text" inputMode="numeric"
                        value={rlHeadwind}
                        onChange={ev => {
                          const v = parseFloat(ev.target.value);
                          if (!isNaN(v)) setRlHeadwind(Math.max(0, Math.min(30, v)));
                        }}
                        onBlur={() => setTimeout(() => runSim(), 80)}
                        style={INPUT_STYLE(rlHeadwind > 0 ? "#06b6d4" : COLORS.text, 28)} />
                    </div>
                    {UNIT("#06b6d4", "m/s")}
                  </div>
                </div>

                <SEP />

                {/* 6. 빠른 설정 */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span style={LABEL_STYLE(COLORS.accent2)}>⚡ {lang === "ko" ? "빠른 설정" : "Presets"}</span>
                  {DROPDOWN(
                    String(activePresetIdx > 0 ? activePresetIdx : 0),
                    ev => {
                      const idx = parseInt(ev.target.value);
                      if (idx > 0 && PRESETS[idx].vals) {
                        const v = PRESETS[idx].vals;
                        setParamsA(p => ({ ...p, ...v }));
                        setParamsB(p => ({ ...p, ...v }));
                        if (v.grade_deg !== undefined) {
                          setRlGrade(+(Math.tan(v.grade_deg * Math.PI / 180) * 100).toFixed(2));
                          setGradeRaw(String(v.grade_deg)); // gradeRaw 입력창도 동기화
                        }
                        if (v.T_amb !== undefined) setTambRaw(String(v.T_amb)); // tambRaw 동기화
                        setTimeout(() => runSim(), 80);
                      }
                    },
                    PRESETS,
                    activePreset.col
                  )}
                </div>
              </>
            );
          })()}
        </div>

      </div>

      {/* ═══════════ 2-Level Nav: Category > Section ═══════════ */}
      <div style={{ background: COLORS.panel, borderBottom: `1px solid ${COLORS.border}` }}>
        {/* Level 1: Categories */}
        <div style={{ display: "flex", gap: 2, padding: "6px 12px 0", overflowX: "auto", borderBottom: `1px solid ${COLORS.border}40` }}>
          {NAV_CATEGORIES.map((cat) => {
            const isActive = activeCat === cat.id;
            return (
              <button key={cat.id}
                onClick={() => {
                  setActiveCat(cat.id);
                  // 해당 카테고리의 첫 번째 섹션으로 자동 이동
                  if (cat.sections.length > 0) setActiveTab(cat.sections[0].id);
                }}
                style={{
                  background: isActive ? cat.color : "transparent",
                  color: isActive ? "#fff" : COLORS.textMuted,
                  border: "none",
                  borderBottom: isActive ? `3px solid ${cat.color}` : "3px solid transparent",
                  padding: "7px 14px 8px",
                  borderRadius: "6px 6px 0 0",
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: isActive ? 800 : 400,
                  whiteSpace: "nowrap",
                  transition: "all 0.15s",
                }}>
                {lang === "ko" ? (cat.labelKo || cat.label) : cat.label}
              </button>
            );
          })}
        </div>
        {/* Level 2: Sections for active category */}
        {(() => {
          const curCat = NAV_CATEGORIES.find(c => c.id === activeCat);
          if (!curCat) return null;
          return (
            <div style={{ display: "flex", gap: 2, padding: "4px 20px", overflowX: "auto" }}>
              {curCat.sections.map((sec) => {
                const isActive = activeTab === sec.id;
                return (
                  <button key={sec.id}
                    onClick={() => { setActiveTab(sec.id); setOpenParamPanel(false); }}
                    style={{
                      background: isActive ? `${curCat.color}20` : "transparent",
                      color: isActive ? curCat.color : COLORS.textMuted,
                      border: `1px solid ${isActive ? curCat.color + "60" : "transparent"}`,
                      padding: "4px 12px",
                      borderRadius: 5,
                      cursor: "pointer",
                      fontSize: 10,
                      fontWeight: isActive ? 700 : 400,
                      whiteSpace: "nowrap",
                      transition: "all 0.15s",
                    }}>
                    {sec.icon} {lang === "ko" ? (sec.labelKo || sec.label) : sec.label}
                  </button>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* ═══════════ Content ═══════════ */}
      <div style={{ padding: "clamp(8px,2vw,14px)", overflowY: "auto", minHeight: "calc(100vh - 140px)", width: "100%", boxSizing: "border-box" }}>

        {/* ── 적합 파라미터 / 영향 박스 ── */}
        {(() => {
          const curCat = NAV_CATEGORIES.find(c => c.id === activeCat);
          const pInfo = PARAM_INFO_DATA[activeTab];
          if (!pInfo) return null;
          const catColor = curCat ? curCat.color : "#8b5cf6";
          const isEn = lang === "en";
          const panelTitle = isEn ? (pInfo.titleEn || pInfo.title) : pInfo.title;
          return (
            <div style={{ marginBottom: 10 }}>
              <button
                onClick={() => setOpenParamPanel(v => !v)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: openParamPanel ? `${catColor}20` : COLORS.cardBg,
                  border: `1.5px solid ${openParamPanel ? catColor : COLORS.border}`,
                  borderRadius: 8, padding: "7px 16px", cursor: "pointer",
                  color: openParamPanel ? catColor : COLORS.text,
                  fontWeight: 700, fontSize: 12, transition: "all 0.15s",
                  width: "100%", justifyContent: "space-between",
                }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15 }}>🎛️</span>
                  <span>{isEn ? "Suitable Parameters / Influence" : "적합 파라미터 / 영향"}</span>
                  <span style={{ fontSize: 10, fontWeight: 400, color: COLORS.textMuted, marginLeft: 4 }}>
                    {panelTitle}
                  </span>
                </span>
                <span style={{ fontSize: 14, color: catColor }}>{openParamPanel ? "▲" : "▼"}</span>
              </button>
              {openParamPanel && (
                <div style={{
                  background: COLORS.panel, border: `1.5px solid ${catColor}40`,
                  borderTop: "none", borderRadius: "0 0 8px 8px",
                  padding: "12px 16px", display: "flex", flexDirection: "column", gap: 14,
                }}>
                  {/* 적합 파라미터 테이블 */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: catColor, marginBottom: 8 }}>
                      📐 {isEn ? "Suitable Parameters" : "적합 파라미터"}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <thead>
                          <tr style={{ background: COLORS.cardBg }}>
                            {(isEn
                              ? ["Parameter", "Symbol", "Unit", "Default", "Physical Meaning"]
                              : ["파라미터명", "심볼", "단위", "기본값/범위", "설명 및 물리적 의미"]
                            ).map(h => (
                              <th key={h} style={{ padding: "5px 10px", textAlign: "left", color: COLORS.textMuted,
                                borderBottom: `1px solid ${COLORS.border}`, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pInfo.params.map((p, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : COLORS.cardBg,
                              borderBottom: `1px solid ${COLORS.border}20` }}>
                              <td style={{ padding: "6px 10px", color: COLORS.text, fontWeight: 600, whiteSpace: "nowrap" }}>
                                {isEn ? (p.nameEn || p.name) : p.name}
                              </td>
                              <td style={{ padding: "6px 10px", color: catColor, fontFamily: "'JetBrains Mono','Fira Code',monospace", fontWeight: 700, whiteSpace: "nowrap" }}>{p.sym}</td>
                              <td style={{ padding: "6px 10px", color: COLORS.textMuted, whiteSpace: "nowrap" }}>{p.unit}</td>
                              <td style={{ padding: "6px 10px", color: COLORS.accent, fontFamily: "'JetBrains Mono','Fira Code',monospace", fontWeight: 600, whiteSpace: "nowrap" }}>{p.val}</td>
                              <td style={{ padding: "6px 10px", color: COLORS.textMuted, lineHeight: 1.5, maxWidth: 320 }}>
                                {isEn ? (p.descEn || p.desc) : p.desc}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* 영향 분석 */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: catColor, marginBottom: 8 }}>
                      ⚡ {isEn ? "Cross-Section Influence" : "타 섹션에 미치는 영향"}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {pInfo.effects.map((e, i) => (
                        <div key={i} style={{
                          border: `1px solid ${COLORS.border}`,
                          borderRadius: 6, overflow: "hidden",
                        }}>
                          <div style={{ background: `${catColor}15`, padding: "5px 12px",
                            borderBottom: `1px solid ${catColor}30`,
                            fontSize: 11, fontWeight: 700, color: catColor,
                            fontFamily: "'JetBrains Mono','Fira Code',monospace" }}>
                            {isEn ? (e.paramEn || e.param) : e.param}
                          </div>
                          <div style={{ display: "flex", gap: 0 }}>
                            <div style={{ flex: 1, padding: "6px 12px",
                              borderRight: `1px solid ${COLORS.border}` }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: "#22c55e", marginBottom: 3 }}>
                                ✅ {isEn ? "Positive" : "긍정 영향"}
                              </div>
                              <div style={{ fontSize: 10, color: COLORS.text, lineHeight: 1.5 }}>
                                {isEn ? (e.posEn || e.pos) : e.pos}
                              </div>
                            </div>
                            <div style={{ flex: 1, padding: "6px 12px" }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: "#f59e0b", marginBottom: 3 }}>
                                ⚠️ {isEn ? "Trade-off" : "부정 영향 / 트레이드오프"}
                              </div>
                              <div style={{ fontSize: 10, color: COLORS.text, lineHeight: 1.5 }}>
                                {isEn ? (e.negEn || e.neg) : e.neg}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {!c && SIM_TABS_SET.has(activeTab) ? (
          <div style={{ textAlign: "center", paddingTop: 60 }}>
            {simError ? (
              <div style={{ background: "#1a0a0a", border: "2px solid #ef4444", borderRadius: 8, padding: 20, margin: "0 auto", maxWidth: 600, textAlign: "left" }}>
                <div style={{ color: "#ef4444", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>❌ 시뮬레이션 오류 (Simulation Error)</div>
                <pre style={{ color: "#fca5a5", fontSize: 11, whiteSpace: "pre-wrap", fontFamily: mono }}>{simError}</pre>
                <button onClick={runSim} style={{ marginTop: 12, padding: "6px 16px", background: "#ef4444", border: "none", borderRadius: 4, color: "#fff", cursor: "pointer", fontSize: 12 }}>재시도 (Retry)</button>
              </div>
            ) : (
              <div style={{ color: COLORS.textMuted }}>{lang === "ko" ? "▶ 실행 버튼을 눌러 시작하세요" : <span>Click <b>▶ Run</b> to start</span>}</div>
            )}
          </div>
        ) : (
          <ColLayoutContext.Provider value={colLayout}>
          <React.Suspense fallback={<div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 13 }}>{lang === "en" ? "Loading…" : "로딩 중…"}</div>}>
                {activeTab === "compare" && c && (
                  <CompareTabContent key={colLayout} c={c} paramsA={paramsA} paramsB={paramsB}
                    vehicleAKey={vehicleAKey} vehicleBKey={vehicleBKey}
                    setVehicleAKey={setVehicleAKey} setVehicleBKey={setVehicleBKey}
                    rlThrottle={launchThrottle}
                    reaccelV0={reaccelV0} reaccelV1={reaccelV1} />
                )}

                {activeTab === "roadload" && r && (() => {
                  const p_rl = activeSimVehicle === "A" ? paramsA : paramsB;
                  return <RLTabContent key={colLayout} params={p_rl}
                    rlMassKg={rlMassKg} rlGrade={rlGrade}
                    rlThrottle={rlThrottle} setRlThrottle={setRlThrottle}
                    _rlThrTimer={_rlThrTimer} rlHeadwind={rlHeadwind} />; // [A41-BUG2-FIX]
                })()}
                {/* ═══════════════════ DRIVEABILITY TAB ═══════════════════ */}
                {activeTab === "driveability" && (() => {
                  const p = activeSimVehicle === "A" ? paramsA : paramsB;
                  return <DrivabilityTabContent key={colLayout} r={r} params={p} />;
                })()}
                {/* ═══════════════════ LAUNCH TAB v2 ═══════════════════ */}
                {activeTab === "launch" && r && (() => {
                  const p_launch = activeSimVehicle === "A" ? paramsA : paramsB;
                  return (
                    <LaunchTabContent
                      key={colLayout}
                      r={r}
                      params={p_launch}
                      colLayout={colLayout}
                      rlMassKg={rlMassKg}
                      rlGrade={rlGrade}
                      rlHeadwind={rlHeadwind}
                      launchThrottle={launchThrottle}
                      setLaunchThrottle={setLaunchThrottle}
                      rlSocScenario={rlSocScenario}
                    />
                  );
                })()}
                {/* ═══════════════════ RE-ACCEL TAB ═══════════════════ */}
                {activeTab === "reaccel" && r && (() => {
                  const p_ra = activeSimVehicle === "A" ? paramsA : paramsB;
                  return (
                    <ReAccelTabContent
                      key={colLayout}
                      r={r}
                      params={p_ra}
                      reaccelV0={reaccelV0}
                      reaccelV1={reaccelV1}
                      setReaccelV0={setReaccelV0}
                      setReaccelV1={setReaccelV1}
                      rlMassKg={rlMassKg}
                      rlGrade={rlGrade}
                      reaccelThrottle={reaccelThrottle}
                      setReaccelThrottle={setReaccelThrottle}
                      rlSocScenario={rlSocScenario}
                      rlHeadwind={rlHeadwind}
                    />
                  );
                })()}

                {/* ═══════════════════ COAST TAB ═══════════════════ */}
                {activeTab === "coast" && r && (() => {
                  const p_coast = activeSimVehicle === "A" ? paramsA : paramsB;
                  return <CoastTabContent key={colLayout} r={r} params={p_coast}
                    rlMassKg={rlMassKg} rlGrade={rlGrade}
                    rlSocScenario={rlSocScenario} rlHeadwind={rlHeadwind} />; // [A41-BUG3-FIX]
                })()}
                                {/* ═══════════════════ REGEN BRAKE TAB ═══════════════════ */}
                {activeTab === "braking" && r && (() => {
                  const p_rb = activeSimVehicle === "A" ? paramsA : paramsB;
                  return <RegenBrakeTabContent key={colLayout} r={r} params={p_rb}
                    rlMassKg={rlMassKg} rlGrade={rlGrade}
                    rlSocScenario={rlSocScenario} rlHeadwind={rlHeadwind} />; {/* [Fix-RB-Wind] rlHeadwind 추가 */}
                })()}
                                {/* ═══════════════════ ENERGY TAB ═══════════════════ */}
                {activeTab === "energy" && r && (() => {
                  const p_energy = activeSimVehicle === "A" ? paramsA : paramsB;
                  return <EnergyTabContent key={colLayout} r={r} params={p_energy} />;
                })()}
                {activeTab === "thermal" && r && (() => {
                  const p_thermal = activeSimVehicle === "A" ? paramsA : paramsB;
                  return <><HeavyLoadingBadge /><ThermalTabContent key={colLayout} r={r} params={p_thermal} /></>;
                })()}
                {activeTab === "wltp" && r && (() => {
                  const p_dc = activeSimVehicle === "A" ? paramsA : paramsB;
                  return <><HeavyLoadingBadge /><DCTabContent key={colLayout} r={r} params={p_dc} /></>;
                })()}

                {activeTab === "sensitivity" && r && (() => {
                  const p_sens = activeSimVehicle === "A" ? paramsA : paramsB;
                  return <><HeavyLoadingBadge /><SensitivityTabContent key={colLayout} r={r} params={p_sens} /></>;
                })()}
          {activeTab === "passfail" && r && (() => {
            const pf = r.passFailReport;
            if (!pf) return <div style={{ color: COLORS.textMuted, padding: 20 }}>{lang==="en" ? "Pass/Fail data unavailable (re-run simulation)" : "Pass/Fail 데이터 없음 (시뮬레이션 재실행 필요)"}</div>;
            const statusColor = { "✅ PASS": "#22c55e", "⚠️ WARN": "#f59e0b", "❌ FAIL": "#ef4444" };
            const bgColor = pf.overall === "✅ PASS" ? "#052e16" : pf.overall === "⚠️ WARN" ? "#1c1203" : "#2d0a0a";

            // ── 카테고리 정의 (표시 순서)
            const catDefs = lang === "en"
              ? [
                  { key: "성능",   label: "🏎 Performance",    color: "#f59e0b" },
                  { key: "에너지", label: "⚡ Energy / Efficiency", color: "#22c55e" },
                  { key: "열관리", label: "🌡 Thermal",         color: "#f97316" },
                  { key: "제동",   label: "🛑 Braking",         color: "#60a5fa" },
                  { key: "민감도", label: "📊 Sensitivity",     color: "#a78bfa" },
                  { key: "기타",   label: "📋 Other",            color: "#6b7280" },
                ]
              : [
                  { key: "성능",   label: "🏎 성능",            color: "#f59e0b" },
                  { key: "에너지", label: "⚡ 에너지 / 효율",   color: "#22c55e" },
                  { key: "열관리", label: "🌡 열관리",           color: "#f97316" },
                  { key: "제동",   label: "🛑 제동",             color: "#60a5fa" },
                  { key: "민감도", label: "📊 민감도",           color: "#a78bfa" },
                  { key: "기타",   label: "📋 기타",             color: "#6b7280" },
                ];

            // ── 게이지 바: 측정값이 min~max 내 어느 위치인지 계산
            function gaugePos(val, min, max) {
              if (val === '–' || typeof val !== 'number') return null;
              // 상한만 있는 경우: 0 ~ max*1.2 범위
              // 하한만 있는 경우: min*0.5 ~ min*2 범위
              const lo = min !== null ? min * 0.8 : 0;
              const hi = max !== null ? max * 1.2 : (min !== null ? min * 2 : val * 1.5);
              const span = hi - lo || 1;
              const pct = Math.max(0, Math.min(100, ((val - lo) / span) * 100));
              const okLoPct = min !== null ? Math.max(0, ((min - lo) / span) * 100) : 0;
              const okHiPct = max !== null ? Math.min(100, ((max - lo) / span) * 100) : 100;
              return { pct, okLoPct, okHiPct };
            }

            const failItems = (pf.items || []).filter(i => i.status !== "✅ PASS");

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
                  {lang==="en" ? "✅ Pass / Fail Auto Validation Report" : "✅ Pass / Fail 자동 검증 리포트"}
                </div>

                {/* 종합 결과 배너 */}
                <div style={{ background: bgColor, border: `2px solid ${statusColor[pf.overall] || '#888'}`,
                  borderRadius: 10, padding: "12px 18px", display: "flex",
                  alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <span style={{ fontSize: 18, fontWeight: 800, color: statusColor[pf.overall] || COLORS.text }}>
                      {pf.overall} {lang==="en" ? "— Overall Result" : "— 종합 결과"}
                    </span>
                    <span style={{ marginLeft: 16, fontSize: 11, color: COLORS.textMuted }}>
                      {pf.vehicle} | {new Date(pf.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 16 }}>
                    {[["PASS", "#22c55e", pf.summary?.pass || 0], ["WARN", "#f59e0b", pf.summary?.warn || 0], ["FAIL", "#ef4444", pf.summary?.fail || 0]].map(([label, col, cnt]) => (
                      <div key={label} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: col }}>{cnt}</div>
                        <div style={{ fontSize: 10, color: COLORS.textMuted }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── 단일 테이블 (colgroup으로 열 너비 고정 → 카테고리 간 열 정렬 일치) ── */}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: 36 }} />    {/* 판정 */}
                      <col style={{ width: "18%" }} />  {/* 항목 */}
                      <col style={{ width: "11%" }} />  {/* 측정값 */}
                      <col style={{ width: "14%" }} />  {/* 기준 범위 */}
                      <col style={{ width: "16%" }} />  {/* 위치 게이지 */}
                      <col />                            {/* 개선 방법 (나머지) */}
                    </colgroup>
                    {/* 공통 헤더 (한 번만) */}
                    <thead>
                      <tr style={{ background: COLORS.cardBg }}>
                        {(lang==="en"
                          ? ["St","Item","Measured","Threshold","Range Gauge","Action"]
                          : ["판정","항목","측정값","기준 범위","위치","개선 방법"]
                        ).map(h => (
                          <th key={h} style={{ padding: "6px 8px", textAlign: "left",
                            color: COLORS.textMuted, borderBottom: `2px solid ${COLORS.border}`,
                            fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {catDefs.flatMap(cd => {
                        const catItems = (pf.items || []).filter(i => i.cat === cd.key);
                        if (!catItems.length) return [];
                        return [
                          // 카테고리 구분 행
                          <tr key={`cat-${cd.key}`}>
                            <td colSpan={6} style={{
                              padding: "8px 10px 4px",
                              borderTop: `1px solid ${COLORS.border}`,
                              borderLeft: `3px solid ${cd.color}`,
                              background: cd.color + "12",
                              fontSize: 11, fontWeight: 800, color: cd.color,
                            }}>
                              {cd.label}
                            </td>
                          </tr>,
                          // 항목 행들
                          ...catItems.map((item, idx) => {
                            const g = gaugePos(item.val, item.min, item.max);
                            const sColor = statusColor[item.status] || COLORS.textMuted;
                            const threshText = item.min != null && item.max != null
                              ? `${item.min} ~ ${item.max}`
                              : item.min != null ? `≥ ${item.min}`
                              : item.max != null ? `≤ ${item.max}` : '–';
                            return (
                              <tr key={`${cd.key}-${idx}`} style={{
                                background: idx%2===0 ? "transparent" : COLORS.cardBg,
                                borderBottom: `1px solid ${COLORS.border}18`,
                              }}>
                                {/* 판정 */}
                                <td style={{ padding: "7px 8px" }}>
                                  <span style={{ fontWeight: 700, color: sColor, fontSize: 13 }}>
                                    {item.status?.split(' ')[0]}
                                  </span>
                                </td>
                                {/* 항목명 */}
                                <td style={{ padding: "7px 8px", color: COLORS.text, fontWeight: 500,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                  title={lang==="en" ? (item.labelEn||item.label) : item.label}>
                                  {lang==="en" ? (item.labelEn||item.label) : item.label}
                                </td>
                                {/* 측정값 */}
                                <td style={{ padding: "7px 8px", fontFamily: mono, fontWeight: 700,
                                  color: sColor, whiteSpace: "nowrap", overflow: "hidden" }}>
                                  {item.val != null ? item.val : "–"}
                                  {item.unit ? <span style={{ fontSize: 9, color: COLORS.textMuted, marginLeft: 2 }}>{item.unit}</span> : null}
                                </td>
                                {/* 기준 범위 */}
                                <td style={{ padding: "7px 8px", color: "#60a5fa", fontFamily: mono,
                                  fontSize: 10, whiteSpace: "nowrap", overflow: "hidden" }}>
                                  {threshText}
                                  {item.unit ? <span style={{ color: COLORS.textMuted, marginLeft: 2 }}>{item.unit}</span> : null}
                                </td>
                                {/* 게이지 바 */}
                                <td style={{ padding: "7px 8px" }}>
                                  {g ? (
                                    <div style={{ position: "relative", height: 8, background: "#1f2937",
                                      borderRadius: 4, overflow: "hidden" }}>
                                      <div style={{ position: "absolute", left: `${g.okLoPct}%`,
                                        width: `${Math.max(0, g.okHiPct - g.okLoPct)}%`, height: "100%",
                                        background: "#22c55e30" }} />
                                      <div style={{ position: "absolute", left: `${g.pct}%`,
                                        transform: "translateX(-50%)", width: 3, height: "100%",
                                        background: sColor, borderRadius: 2 }} />
                                    </div>
                                  ) : <span style={{ color: COLORS.textMuted, fontSize: 10 }}>–</span>}
                                </td>
                                {/* 개선 방법 */}
                                <td style={{ padding: "7px 8px", fontSize: 10,
                                  color: item.status === "✅ PASS" ? COLORS.textMuted : "#fbbf24",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                  title={item.status === "✅ PASS" ? "" : (lang==="en" ? item.hintEn : item.hint)}>
                                  {item.status === "✅ PASS"
                                    ? (lang==="en" ? "✓ Normal" : "✓ 정상")
                                    : (lang==="en" ? item.hintEn : item.hint) || "–"}
                                </td>
                              </tr>
                            );
                          }),
                        ];
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── FAIL / WARN 항목 집중 개선 가이드 ── */}
                {failItems.length > 0 && (
                  <div style={{ background: "#1c1203", border: "1px solid #92400e",
                    borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b", marginBottom: 10 }}>
                      {lang==="en" ? "🔧 Improvement Checklist (FAIL / WARN)" : "🔧 개선 체크리스트 (FAIL / WARN 항목)"}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                      {failItems.map((item, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start",
                          background: COLORS.bg, borderRadius: 6, padding: "8px 12px",
                          border: `1px solid ${statusColor[item.status]}30` }}>
                          <span style={{ color: statusColor[item.status], fontSize: 13, flexShrink: 0 }}>
                            {item.status?.split(' ')[0]}
                          </span>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text }}>
                              {lang==="en" ? (item.labelEn||item.label) : item.label}
                              <span style={{ fontFamily: mono, color: statusColor[item.status], marginLeft: 8 }}>
                                {item.val}{item.unit ? ' '+item.unit : ''}
                              </span>
                              <span style={{ color: COLORS.textMuted, fontSize: 10, marginLeft: 6 }}>
                                ({lang==="en" ? "limit" : "기준"}: {item.min!==null && item.min!==undefined ? `≥${item.min}` : ''}{item.min!==null && item.min!==undefined && item.max!==null && item.max!==undefined ? ' ~ ' : ''}{item.max!==null && item.max!==undefined ? `≤${item.max}` : ''} {item.unit})
                              </span>
                            </div>
                            <div style={{ fontSize: 10, color: "#fbbf24", marginTop: 2 }}>
                              → {lang==="en" ? (item.hintEn||"Review simulation parameters") : (item.hint||"시뮬레이션 파라미터 검토")}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 기준값 수정 안내 */}
                <div style={{ padding: "10px 14px", background: COLORS.cardBg,
                  borderRadius: 8, fontSize: 10, color: COLORS.textMuted }}>
                  <strong style={{ color: COLORS.text }}>{lang==="en"?"Threshold Edit:":"기준값 수정:"}</strong>
                  &nbsp;{lang==="en"?"Parameter panel → items with pf_ prefix (e.g. pf_launch100_max, pf_wltp_max_kWh)":"파라미터 패널 → pf_launch100_max, pf_wltp_max_kWh 등 pf_ 접두사 항목에서 조정 가능"}
                </div>
              </div>
            );
          })()}

          {/* ═══════════ [Disp12] Battery Health 패널 ═══════════ */}
          {activeTab === "degradation" && (() => {
            const p_deg = activeSimVehicle === "A" ? paramsA : paramsB;
            return <BatteryHealthTabContent key={colLayout} r={r} params={p_deg} />;
          })()}

          {/* ═══════════ [enhanced] Enhanced Physics 패널 ═══════════ */}
          {activeTab === "enhanced" && (() => {
            const p_enh = activeSimVehicle === "A" ? paramsA : paramsB;
            return <EnhancedPhysicsTabContent key={colLayout} r={r} params={p_enh} />;
          })()}

          {/* ═══════════ [control_loop] Control Loop Analysis 패널 ═══════════ */}
          {activeTab === "control_loop" && (() => {
            const p_cl = activeSimVehicle === "A" ? paramsA : paramsB;
            const setP_cl = activeSimVehicle === "A" ? setParamsA : setParamsB;
            const onParamChange_cl = (key, val) => setP_cl(p => ({ ...p, [key]: val }));
            return <ControlLoopTabContent key={colLayout} params={p_cl} onParamChange={onParamChange_cl} />;
          })()}

          {/* ═══════════ [FS] Functional Safety HARA 패널 ═══════════ */}
          {/* ═══════════ [R-18] Safety & Security — Section 1: Functional Safety ═══════════ */}
          {activeTab === "hara" && (() => {
            const p_hara = activeSimVehicle === "A" ? paramsA : paramsB;
            return <HARATabContent key={colLayout} r={r} params={p_hara} />;
          })()}

          {activeTab === "technical_safety_concept" && (() => {
            const p_tsc = activeSimVehicle === "A" ? paramsA : paramsB;
            return <TechnicalSafetyConceptTabContent key={colLayout} r={r} params={p_tsc} />;
          })()}

          {/* ═══════════ [R-18] Safety & Security — Section 2: Cybersecurity (ISO/SAE 21434) ═══════════ */}
          {/* [VEH-FIX-C] activeSimVehicle 반영 — 기존 paramsA 하드코딩 수정 */}
          {activeTab === "cybersecurity_tara" && (
            <CybersecurityTabContent key={colLayout} r={r} params={activeSimVehicle === 'A' ? paramsA : paramsB} />
          )}

          {/* ═══════════ [R-18] Safety & Security — Section 3: SOTIF Analysis ═══════════ */}
          {/* [VEH-FIX-C] activeSimVehicle 반영 */}
          {activeTab === "sotif_iso21448" && (
            <SOTIFTabContent key={colLayout} r={r} params={activeSimVehicle === 'A' ? paramsA : paramsB} />
          )}

          {/* ═══════════ [R-10-04] FMEA / SME / FIT / FTA 패널 (TARA·SOTIF → Safety & Security 탭으로 이동) ═══════════ */}
          {activeTab === "fmea_safety" && (() => {
            const p_fmea = activeSimVehicle === "A" ? paramsA : paramsB;
            return <FMEASafetyTabContent key={colLayout} r={r} params={p_fmea} />;
          })()}

          {/* ═══════════ [Part 5] HW 안전 설계 (ISO 26262 Part 5) ═══════════ */}
          {activeTab === "hw_safety" && (() => {
            const p_hw = activeSimVehicle === "A" ? paramsA : paramsB;
            return <HWSafetyTabContent key={colLayout} r={r} params={p_hw} />;
          })()}

          {/* ═══════════ [Part 6] SW 안전 설계 (ISO 26262 Part 6) ═══════════ */}
          {activeTab === "sw_safety" && (() => {
            const p_sw = activeSimVehicle === "A" ? paramsA : paramsB;
            return <SWSafetyTabContent key={colLayout} r={r} params={p_sw} />;
          })()}

          {/* ═══════════ [V-Model ⑥] 추적성 매트릭스 & 안전 케이스 ═══════════ */}
          {activeTab === "traceability_case" && (() => {
            const p_tr = activeSimVehicle === "A" ? paramsA : paramsB;
            return <TraceabilityTabContent key={colLayout} r={r} params={p_tr} />;
          })()}

          {activeTab === "safety_verification" && (() => {
            const p_sv = activeSimVehicle === "A" ? paramsA : paramsB;
            return <SafetyVerificationTabContent key={colLayout} r={r} params={p_sv} />;
          })()}

          {/* ═══════════ [DOE] DOE & Bayesian 최적화 패널 ═══════════ */}
          {activeTab === "doe" && (() => {
            const p_doe = activeSimVehicle === "A" ? paramsA : paramsB;
            return <DOETabContent key={colLayout} params={p_doe} />;
          })()}

          {/* ═══════════ [R-Advance2] Analysis Overview — AdvancedFinalTabContent 연동 ═══════════ */}
          {activeTab === "analysis_overview" && (
            <AdvancedFinalTabContent key={colLayout} r={r} params={activeSimVehicle==='A' ? paramsA : paramsB} compResults={compResults} />
          )}


          {/* ═══════════ [v13] 4WS Suspension / Rollover 패널 ═══════════ */}
          {activeTab === "suspension4w" && (
            <SuspensionRolloverTabContent key={colLayout} activeParams={activeSimVehicle === 'A' ? paramsA : paramsB} />
          )}

          {/* ═══════════ [Domain-3] Gearbox 패널 ═══════════ */}
          {activeTab === "gearbox" && (() => {
            const p = activeSimVehicle === 'A' ? paramsA : paramsB;
            return <GearboxTabContent key={colLayout} params={p} />;
          })()}

          {/* ═══════════ [Domain-7] AWD Torque Vectoring 패널 ═══════════ */}
          {activeTab === "awd_tv" && (() => {
            const p = activeSimVehicle === 'A' ? paramsA : paramsB;
            return <AWDTorqueVectoringTabContent key={colLayout} params={p} />;
          })()}

          {/* ═══════════ [Domain-1] Charging Simulation 패널 ═══════════ */}
          {activeTab === "charging" && (() => {
            const p = activeSimVehicle === 'A' ? paramsA : paramsB;
            return <ChargingTabContent key={colLayout} params={p} />;
          })()}

          {/* ═══════════ [Domain-4] Predictive Energy Management 패널 ═══════════ */}
          {activeTab === "energy_mgmt" && (() => {
            const p = activeSimVehicle === 'A' ? paramsA : paramsB;
            return <EnergyMgmtTabContent key={colLayout} params={p} />;
          })()}

          {/* ═══════════ [v13] Road & Env 패널 ═══════════ */}
          {activeTab === "roadenv" && (() => {
            // ── curP: r 없이도 항상 사용 가능 ──
            const curP = activeSimVehicle === 'A' ? paramsA : paramsB;

            // ── cwXxx: curP 기반 정적 추출 (r 불필요) ──
            const cwEnabled   = curP.crosswind_enable || false;
            const cwVWind     = curP.v_wind || 0;
            const cwAngle     = curP.wind_angle_deg ?? 90;
            const cwLatFactor = curP.crosswind_lat_factor ?? 0.3;

            // [RE3] 노면 조건별 F_roll vs 속도 계산 (정적 computed, 시뮬 불필요)
            const re3Data = (() => {
              const _m    = curP.m    || 1800;
              const _Crr0 = curP.Crr  || 0.012;
              const _CrrV = curP.Crr_v_coeff  || 0.0001;
              const _CrrT = curP.Crr_T_coeff  || 0.003;
              const _T    = curP.T_amb ?? 25;
              // [Fix-RE3-pressure] 공기압 보정 항 추가 — 실제 시뮬 Crr_eff와 일관성 확보
              //   수정 전: Crr_base = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT)  (공기압 무시)
              //   수정 후: Crr_eff = Crr_base + ΔCrr_pressure (α_p·dp + β_p·dp²)
              const _p_nom   = curP.tire_pressure_nominal || 2.4;
              const _p_act   = curP.tire_pressure_actual  || 2.4;
              const _dp      = (_p_nom - _p_act) / _p_nom;
              const _alpha_p = curP.tire_pressure_crr_alpha || 0.008;
              const _beta_p  = curP.tire_pressure_crr_beta  || 0.0003;
              const _dCrr_p  = Math.max(0, _alpha_p * _dp + _beta_p * _dp * _dp);
              const roadK = { dry:1.0, wet:1.15, snow:2.50, ice:1.80 };
              const rows = [];
              for (let v_kmh = 0; v_kmh <= 160; v_kmh += 5) {
                const v_ms = v_kmh / 3.6;
                const dT = _T - 25;
                const Crr_eff = _Crr0 * (1 + _CrrV * v_ms * v_ms) * (1 + _CrrT * dT) + _dCrr_p;
                const row = { v_kmh };
                for (const [cond, k] of Object.entries(roadK)) {
                  row[cond] = parseFloat((_m * 9.81 * Crr_eff * k).toFixed(1));
                }
                rows.push(row);
              }
              return rows;
            })();

            // [RE4] 측풍 공기저항 vs 풍향각 계산 (v=100 km/h 기준, 정적 computed)
            const re4Data = (() => {
              // [Fix-RE4-rho] curP.rho(고정값) → rhoCorrected(T_amb, altitude) 로 교체
              const _rho = rhoCorrected(curP.T_amb ?? 25, curP.altitude || 0);
              const _CdA = curP.CdA_v3 || curP.CdA || 0.65;
              const _Ka  = curP.K_aero || 1.0;
              const v_ms = 100 / 3.6;
              // [Fix-RE4-windSpeeds] 현재 cwVWind를 목록에 동적 삽입
              //   수정 전: [0,5,10,15] 하드코딩 → cwVWind∉{0,5,10,15}이면 해당 선 없음
              //   수정 후: 중복 제거 후 정렬 → 현재 설정 풍속도 항상 선으로 표시
              const windSpeeds = [...new Set([0, 5, 10, 15, cwVWind > 0 ? Math.round(cwVWind) : null].filter(v => v !== null))].sort((a,b)=>a-b);
              const rows = [];
              for (let angle_deg = 0; angle_deg <= 90; angle_deg += 5) {
                const beta = angle_deg * Math.PI / 180;
                const row = { angle_deg };
                for (const v_w of windSpeeds) {
                  const v_long = v_ms + v_w * Math.cos(beta);
                  const v_lat  = v_w * Math.sin(beta);
                  const v_eff2 = v_long * v_long + cwLatFactor * v_lat * v_lat;
                  const F_cw   = 0.5 * _rho * _CdA * v_eff2 * _Ka;
                  const F_base = 0.5 * _rho * _CdA * v_ms * v_ms * _Ka;
                  row[`w${v_w}`] = parseFloat((F_cw - F_base).toFixed(1));
                }
                rows.push(row);
              }
              return { rows, windSpeeds };
            })();
            const re4Rows       = re4Data.rows;
            const re4WindSpeeds = re4Data.windSpeeds;

            // ── r 의존 변수: null-safe (r 없으면 빈 값, 차트/KPI는 조건부 렌더) ──
            const launch = r?.launch100;
            const v7meta = r?.v7_meta || {};
            const lData  = launch?.data || [];
            const roughEnabled = v7meta.road_roughness_enable || false;

            // [Fix-RE-02] RE1용 저항력 vs 속도 데이터: launch300 전체 범위 사용 (r 없으면 빈 배열)
            const re1Data = r ? (() => {
              const raw = (r.launch300?.data || lData).slice()
                .sort((a, b) => (a.v_kmh || 0) - (b.v_kmh || 0));
              const buckets = new Map();
              raw.forEach(d => {
                const key = Math.round((d.v_kmh || 0) / 2) * 2;
                if (!buckets.has(key)) buckets.set(key, {
                  ...d,
                  F_rough: d.F_rough ?? 0,
                });
              });
              return Array.from(buckets.values());
            })() : [];
            const re1MaxV  = re1Data.length > 0 ? Math.ceil(re1Data[re1Data.length - 1].v_kmh / 20) * 20 : 160;
            const re1Ticks = Array.from({ length: Math.floor(re1MaxV / 20) + 1 }, (_, i) => i * 20);

            // [P1-Fix] RE1 KPI ref100 소스를 re1Data로 통일
            const ref100 = re1Data.length > 0
              ? re1Data.reduce((best, d) => Math.abs((d.v_kmh||0) - 100) < Math.abs((best.v_kmh||0) - 100) ? d : best, re1Data[0])
              : null;
            const kpi_F_aero  = ref100?.F_aero  != null ? ref100.F_aero.toFixed(0)  : '—';
            const kpi_F_roll  = ref100?.F_roll  != null ? ref100.F_roll.toFixed(0)  : '—';
            const kpi_F_rough = ref100?.F_rough != null ? ref100.F_rough.toFixed(0) : '—';
            const kpi_v_ref   = ref100?.v_kmh   != null ? ref100.v_kmh.toFixed(1)   : '100';

            // [Fix-RE-vCross] RE1 교차속도 v_cross: re1Data에서 보간 산출
            const v_cross_actual = (() => {
              for (let i = 1; i < re1Data.length; i++) {
                const prev = re1Data[i - 1], curr = re1Data[i];
                const prevDiff = (prev.F_aero || 0) - (prev.F_roll || 0);
                const currDiff = (curr.F_aero || 0) - (curr.F_roll || 0);
                if (prevDiff <= 0 && currDiff >= 0) {
                  const t = (-prevDiff) / (currDiff - prevDiff);
                  return (prev.v_kmh || 0) + t * ((curr.v_kmh || 0) - (prev.v_kmh || 0));
                }
              }
              return null;
            })();
            return (
              <div>
                {/* 시뮬 미실행 안내 배너 */}
                {!r && (
                  <div style={{ padding:"10px 14px", borderRadius:8, marginBottom:14,
                    background: COLORS.panel, border:`1px solid ${COLORS.border}`,
                    color: COLORS.textMuted, fontSize:12 }}>
                    {lang==="en"
                      ? "⚡ Run simulation to display RE1 (Resistance Breakdown), RE2 (Gear Oil Temperature), and full KPI badges."
                      : "⚡ 시뮬레이션을 실행하면 RE1(저항력 분해), RE2(기어오일 온도), 전체 KPI 배지가 표시됩니다."}
                  </div>
                )}
                {/* KPI 배지 — ref100 유무에 따라 wrap 전환:
                    [UI-P3-Fix] ref100 없을 때(배지 6개) flexWrap:wrap → 스크롤바 공간 불필요 예약 방지
                                ref100 있을 때(배지 10개) flexWrap:nowrap + overflowX:auto 유지 */}
                <div style={{ display:"flex", flexWrap: ref100 ? "nowrap" : "wrap", gap:8, marginBottom:12,
                              overflowX: ref100 ? "auto" : "visible", paddingBottom:4,
                              scrollbarWidth: ref100 ? "thin" : undefined }}>
                  {/* 설정 KPI 6개 */}
                  {[
                    [lang==="en"?"IRI":"IRI", `${v7meta.IRI || 2.5}`, "m/km", "#60a5fa",
                      { title: lang==="en" ? "IRI (International Roughness Index)" : "IRI — 노면 거칠기 지수",
                        what:  lang==="en" ? "Quantifies road surface roughness. Higher IRI = rougher surface = increased rolling resistance." : "노면 거칠기를 수치화한 지수. 높을수록 거친 노면 → 구름저항 증가.",
                        why:   lang==="en" ? "IRI directly affects Crr via ΔCrr_IRI model. Rough roads significantly reduce efficiency." : "IRI가 클수록 ΔCrr_IRI가 증가 → 전비 저하. 불량 노면 진단의 핵심 지표.",
                        formula: "ΔCrr_IRI = k_IRI_crr·IRI + k_IRI_vib·IRI·v  (v: 차속 [m/s])\n  k_IRI_crr: 정적 계수, k_IRI_vib: 속도 의존 진동 계수\n  IRI 2.0→ 고속도로, 3.0→ 일반, 6.0→ 불량",
                        threshold: lang==="en" ? "Highway≈2.0 / Normal≈3.0 / Poor road≈6.0" : "고속도로≈2.0 / 일반도로≈3.0 / 불량노면≈6.0" }],
                    [lang==="en"?"Tire Pressure":"타이어 공기압", `${v7meta.tire_pressure || 2.4}`, "bar", "#22c55e",
                      { title: lang==="en" ? "Tire Inflation Pressure" : "타이어 공기압",
                        what:  lang==="en" ? "Current tire inflation pressure setting." : "현재 설정된 타이어 공기압.",
                        why:   lang==="en" ? "Low pressure → tyre flexes more → Crr increases. Correct pressure is critical for efficiency." : "공기압 저하 → 타이어 변형 증가 → Crr 증가 → 전비 저하. 적정 공기압 유지가 효율의 기본.",
                        threshold: lang==="en" ? "Typical 2.3~2.8 bar. Underinflation increases Crr ~2%/0.2 bar" : "일반 승용 2.3~2.8 bar. 0.2 bar 저압마다 Crr 약 2% 증가 추정" }],
                    [lang==="en"?"HVAC":"HVAC", v7meta.hvac_mode || "auto", "", "#f59e0b",
                      { title: lang==="en" ? "HVAC Mode" : "HVAC 모드",
                        what:  lang==="en" ? "Heating/Cooling system operating mode." : "냉난방 시스템 운전 모드.",
                        why:   lang==="en" ? "HVAC is the largest auxiliary load (1~5 kW). Mode affects optimal eco speed and range significantly." : "HVAC는 가장 큰 보조부하(1~5 kW). 모드가 최적 경제속도와 항속거리에 큰 영향.",
                        threshold: lang==="en" ? "auto: T-based | heat: winter | cool: summer | off: no HVAC" : "auto: 온도 기반 자동 | heat: 난방 | cool: 냉방 | off: 비활성" }],
                    [lang==="en"?"Gear Temp Model":"기어 온도 모델", v7meta.gear_loss_temp_enable ? "ON" : "OFF", "", "#a78bfa",
                      { title: lang==="en" ? "Gear Temperature Loss Model" : "기어 온도 손실 모델",
                        what:  lang==="en" ? "Enables temperature-dependent gear oil viscosity loss model." : "온도에 따른 기어오일 점도 손실 모델 활성 여부.",
                        why:   lang==="en" ? "Cold gear oil has higher viscosity → higher mechanical loss. Important for cold-start range accuracy." : "저온에서 기어오일 점도 급증 → 기계 손실 증가. 한랭 시동 전비 예측 정확도에 중요.",
                        threshold: lang==="en" ? "ON: low-temp viscosity included | OFF: isothermal loss (always 25°C)" : "ON: 저온 점도손실 포함 | OFF: 등온 손실(25°C 고정)" }],
                    [lang==="en"?"Regen Blend":"회생 블렌딩", v7meta.regen_blend_enable ? "ON" : "OFF", "", "#f472b6",
                      { title: lang==="en" ? "Non-linear Regen Blending" : "비선형 회생 블렌딩",
                        what:  lang==="en" ? "Enables S-curve interpolation between friction and regen braking at low speeds." : "저속 구간에서 마찰제동↔회생제동 S-커브 전환 모델 활성 여부.",
                        why:   lang==="en" ? "Smooth blending reduces jerk and disc brake wear. Important for NVH and brake pad longevity." : "부드러운 전환으로 저크 감소 · 디스크 패드 마모 최소화. NVH 및 브레이크 내구성에 중요.",
                        threshold: lang==="en" ? "ON: S-curve blend | OFF: linear blend (less realistic)" : "ON: S-커브 전환 | OFF: 선형 전환 (현실 미반영)" }],
                    [lang==="en"?"RLS μ":"RLS μ", v7meta.rls_mu_enable ? "ON" : "OFF", "", "#34d399",
                      { title: lang==="en" ? "RLS μ Friction Estimation" : "RLS μ 실시간 마찰계수 추정",
                        what:  lang==="en" ? "Recursive Least Squares friction coefficient estimator for real-time μ adaptation." : "RLS 알고리즘으로 실시간 마찰계수 μ를 추정·적응하는 모델.",
                        why:   lang==="en" ? "Adaptive μ enables accurate ABS/regen limits on wet/icy roads without preset lookup tables." : "적응형 μ 추정 → 빗길·빙판 ABS/회생 한계를 사전 LUT 없이 정확히 반영.",
                        threshold: lang==="en" ? "ON: adaptive | OFF: fixed μ (dry=1.0)" : "ON: 실시간 추정 | OFF: 고정 μ (건조=1.0)" }],
                    /* [Fix-RE-CW] 측풍 설정 KPI — 항상 표시, 활성 시 풍속·풍향각 추가 */
                    [lang==="en"?"Crosswind":"측풍", cwEnabled ? "ON" : "OFF", "", cwEnabled ? "#38bdf8" : COLORS.textMuted,
                      { title: lang==="en" ? "Crosswind Model" : "측풍 모델",
                        what:  lang==="en" ? "Enables lateral wind drag effect on aerodynamic resistance." : "횡방향 바람이 공기저항에 미치는 영향을 모델링.",
                        why:   lang==="en" ? "Crosswind increases effective airspeed → F_aero rises beyond pure headwind model." : "측풍 → 유효 대기속도 증가 → F_aero 상승. 고속도로 측풍 노출 전비 예측에 중요.",
                        formula: `v_eff = √(v_long² + λ·v_lat²)  λ(lat_factor)=${cwLatFactor.toFixed(2)}`,
                        threshold: lang==="en" ? "ON: crosswind F_aero included | OFF: headwind only" : "ON: 측풍 공기저항 반영 | OFF: 정면풍만" }],
                    ...(cwEnabled ? [
                      [lang==="en"?"Wind Speed":"풍속", cwVWind.toFixed(1), "m/s", "#38bdf8",
                        { title: lang==="en" ? "Wind Speed (v_wind)" : "풍속 (v_wind)",
                          what:  lang==="en" ? "Absolute wind speed for crosswind model." : "측풍 모델 입력 절대 풍속.",
                          formula: `v_eff_long = v_vehicle + v_wind·cos(β)  β=${cwAngle.toFixed(0)}°` }],
                      [lang==="en"?"Wind Angle":"풍향각", cwAngle.toFixed(0), "°", "#38bdf8",
                        { title: lang==="en" ? "Wind Angle (wind_angle_deg)" : "풍향각 (wind_angle_deg)",
                          what:  lang==="en" ? "0°=headwind, 90°=pure crosswind." : "0°=정면풍, 90°=완전 측풍.",
                          formula: `crosswind_lat_factor λ = ${cwLatFactor.toFixed(2)}` }],
                    ] : []),
                    /* [Fix-RE-vCross] 교차속도 KPI — re1Data 보간 산출, 시뮬 후 표시 */
                    ...(v_cross_actual != null ? [
                      [lang==="en"?"v×(Aero=Roll)":"교차속도", v_cross_actual.toFixed(1), "km/h", "#f59e0b",
                        { title: lang==="en" ? "Crossover Speed: F_aero = F_roll" : "교차 속도: 공기저항 = 구름저항",
                          what:  lang==="en" ? "Speed where aero drag equals rolling resistance. Below: Crr dominant. Above: CdA dominant." : "공기저항 = 구름저항이 되는 속도. 이하: 구름저항 지배, 이상: 공기저항 지배.",
                          why:   lang==="en" ? "Design priority boundary — invest in Crr below v_cross, CdA above." : "v_cross 이하 → Crr 개선 우선, 이상 → CdA 개선 우선.",
                          formula: "F_aero(v×) = F_roll(v×)  → bisection interpolated from re1Data chart" }],
                    ] : []),
                    /* 정량 저항력 KPI 4개 — ref100 있을 때만 */
                    ...(ref100 ? [
                      [lang==="en"?`Aero Drag @${kpi_v_ref}`:`공기저항 @${kpi_v_ref}`, kpi_F_aero, "N", COLORS.accent,
                        { title: lang==="en" ? "Aerodynamic Drag Force at ~100 km/h" : "~100 km/h에서의 공기저항력",
                          what:  lang==="en" ? "Actual simulated aerodynamic drag force near 100 km/h." : "100 km/h 근방 시뮬레이션 실측 공기저항력.",
                          why:   lang==="en" ? "Key input for understanding aero dominance at highway speed. Compare with F_roll to assess loss split." : "고속도로 속도에서 공기저항 지배 여부 판단의 핵심. F_roll과 비교해 손실 구조 파악.",
                          formula: (() => {
                            const _Ka = (activeSimVehicle==='A'?paramsA:paramsB).K_aero;
                            const _kaStr = (_Ka != null && _Ka !== 1.0) ? ` · K_aero(=${_Ka.toFixed(3)})` : '';
                            return `F_aero = ½ρ·CdA·v²${_kaStr}  (v≈${(kpi_v_ref/3.6).toFixed(1)} m/s)` +
                              (_kaStr ? `\n  ※ K_aero≠1.0: 실측 보정 계수 적용 중 → KPI 숫자는 보정값 반영` : '');
                          })() }],
                      [lang==="en"?`Rolling Res @${kpi_v_ref}`:`구름저항 @${kpi_v_ref}`, kpi_F_roll, "N", COLORS.accent2,
                        { title: lang==="en" ? "Rolling Resistance Force at ~100 km/h" : "~100 km/h에서의 구름저항력",
                          what:  lang==="en" ? "Actual simulated rolling resistance force near 100 km/h, including temperature effect." : "100 km/h 근방 온도·속도 의존 Crr 반영 시뮬레이션 실측 구름저항력.",
                          why:   lang==="en" ? "Shows tyre and road surface contribution. Flatter vs speed than aero → dominant at low speed." : "타이어·노면 기여를 직관적으로 확인. 속도에 비교적 완만 → 저속에서 지배.",
                          formula: "F_roll = m·g·Crr_eff(v,T,p)\n  Crr_eff = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT) + α_p·dp + β_p·dp²" }],
                      [lang==="en"?`Roughness @${kpi_v_ref}`:`거칠기저항 @${kpi_v_ref}`, roughEnabled ? kpi_F_rough : "OFF", roughEnabled ? "N" : "", roughEnabled ? '#f59e0b' : COLORS.textMuted,
                        { title: lang==="en" ? "IRI Roughness Resistance Force at ~100 km/h" : "~100 km/h IRI 거칠기 저항력",
                          what:  lang==="en" ? "Extra rolling resistance from road roughness (IRI model). Only active when road_roughness_enable=ON." : "노면 거칠기(IRI)로 인한 추가 구름저항력. road_roughness_enable=ON일 때만 활성.",
                          formula: "F_rough = ΔCrr_IRI·m·g\n  ΔCrr_IRI = k_IRI_crr·IRI + k_IRI_vib·IRI·v  (v: 차속 [m/s])",
                          threshold: lang==="en" ? "Enable via road_roughness_enable in V7 params" : "V7 파라미터에서 road_roughness_enable 활성화 필요" }],
                      [lang==="en"?"Aero/Roll":"공기/구름비",
                        ref100?.F_aero != null && ref100?.F_roll != null && ref100.F_roll > 0
                          ? (ref100.F_aero / ref100.F_roll).toFixed(2) : "—", "×", '#a78bfa',
                        { title: lang==="en" ? "Aero / Roll Force Ratio at ~100 km/h" : "공기저항 / 구름저항 비 (~100 km/h)",
                          what:  lang==="en" ? "F_aero / F_roll at current speed. > 1 means aero drag dominates." : "현재 속도에서 공기저항 / 구름저항 비. 1 초과면 공기저항 지배.",
                          why:   lang==="en" ? "Directly tells you which force to prioritize for efficiency improvement at this speed." : "이 속도에서 CdA 개선 vs Crr 개선 중 어느 쪽이 더 효과적인지 즉시 판단 가능.",
                          threshold: lang==="en" ? "> 1.5: aero dominant → CdA improvement is top priority" : "> 1.5: 공기저항 지배 → CdA 개선 우선" }],
                    ] : []),
                  ].map(item => (
                    <RLKpiBadge key={item[0]} label={item[0]} value={item[1]} unit={item[2]} color={item[3]} tooltip={item[4] || null} />
                  ))}
                </div>
                {/* IRI 저항 + 공기저항 비교 그래프 + RE3/RE4 통합 그리드 */}
                <div key={colLayout} style={{display:"grid",gridTemplateColumns:colStr,gap:14}}>
                {lData.length > 0 && (<>
                    <RLGraphCard
                      title={lang==="en"?"RE1 | Resistance Breakdown — IRI Roughness vs Aero/Rolling":"RE1 | 저항력 분해 — IRI 거칠기 vs 공기·구름저항"}
                      subtitle=""
                      graphId="RE1"
                      axisPanel={<AxesPanel axX={axRE1.axX} setAxX={axRE1.setAxX} axY={axRE1.axY} setAxY={axRE1.setAxY} xUnit="km/h" yUnit="N" hideY={axRE1.hideY} setHideY={axRE1.setHideY} />}>
                    {/* RE1 헤더 배지 */}
                    <div style={{display:"flex",justifyContent:"flex-end",marginBottom:4,flexWrap:"wrap",gap:4}}>
                      {[
                        {l:(activeSimVehicle==="A"?paramsA:paramsB).m+"kg",c:"#f97316",d:true},
                        {l:(lang==="ko"?({dry:"🟢건조",wet:"🔵습윤",snow:"⚪적설",ice:"🧊빙판"}):({dry:"🟢Dry",wet:"🔵Wet",snow:"⚪Snow",ice:"🧊Ice"}))[(activeSimVehicle==="A"?paramsA:paramsB).road_condition||"dry"],c:({dry:"#16a34a",wet:"#2563eb",snow:"#64748b",ice:"#0ea5e9"})[(activeSimVehicle==="A"?paramsA:paramsB).road_condition||"dry"],d:true},
                        {l:(activeSimVehicle==="A"?paramsA:paramsB).T_amb+"°C",c:"#fb923c",d:true},
                        {l:"μ "+((activeSimVehicle==="A"?paramsA:paramsB).mu??1.0).toFixed(2),c:"#a78bfa",d:true},
                        {l:"ρ "+((activeSimVehicle==="A"?paramsA:paramsB).rho??1.225).toFixed(3),c:"#60a5fa",d:true},
                        rlGrade!==0 && {l:(rlGrade>0?"▲":"▼")+Math.abs(rlGrade).toFixed(1)+"%",c:"#f59e0b",d:false},
                      ].filter(Boolean).map((b,i)=>(<span key={i} style={{fontSize:9,fontWeight:b.d?700:500,padding:"2px 5px",borderRadius:4,whiteSpace:"nowrap",background:b.c+(b.d?"22":"11"),color:b.c,border:"1px solid "+b.c+(b.d?"55":"28"),opacity:b.d?1:0.6}}>{b.l}</span>))}
                    </div>
                    {/* [Fix-RE-02] re1Data: launch300 전체 속도 범위, v_kmh 정렬·중복 제거 적용
                        [Fix-RE-03] domain/ticks: 실제 데이터 최대속도 기반 동적 계산
                        [Fix-RE-04] F_rough Line: road_roughness_enable=false 시 숨김 */}
                    <CB width="100%" height={220}>
                      <ComposedChart data={re1Data}
                        margin={{ top:5, right:10, left:0, bottom:20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                        <XAxis dataKey="v_kmh" stroke={COLORS.textMuted} tick={{ fontSize: 9, fill:COLORS.textMuted }}
                          type="number"
                          ticks={re1Ticks}
                          domain={axRE1.domX}
                          tickFormatter={function(v){return v;}}
                          label={{ value: lang==="en"?"Speed (km/h)":"속도 (km/h)", position:"insideBottom", offset:0, fontSize:10, fill:COLORS.textMuted }} />
                        <YAxis hide={axRE1.hideY} stroke={COLORS.textMuted} tick={{ fontSize: 9 }}
                          domain={axRE1.domY}
                        label={{ value: lang==="en"?"Force (N)":"저항력 (N)", angle:-90, position:"insideLeft", dx:10, fontSize:10, fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                        <Tooltip content={<CT />} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Line type="monotone" dataKey="F_aero" stroke={COLORS.accent}  strokeWidth={1.5} dot={false} name={lang==="en"?"Aero Drag (N)":"공기저항 (N)"} />
                        <Line type="monotone" dataKey="F_roll" stroke={COLORS.accent2} strokeWidth={1.5} dot={false} name={lang==="en"?"Rolling Resistance (N)":"구름저항 (N)"} />
                        {roughEnabled && (
                          <Line type="monotone" dataKey="F_rough" stroke="#f59e0b" strokeWidth={1.5} dot={false} name={lang==="en"?"IRI Roughness (N)":"IRI 거칠기 (N)"} />
                        )}
                        {/* [Fix-RE1-Fgrade] rlGrade≠0일 때 구배저항 라인 표시 — 저항력 분해 완성 */}
                        {rlGrade !== 0 && (
                          <Line type="monotone" dataKey="F_grade" stroke="#f472b6" strokeWidth={1.5} dot={false} strokeDasharray="5 2" name={lang==="en"?`Grade Resist. (${rlGrade>0?"▲":"▼"}${Math.abs(rlGrade).toFixed(1)}%)`:(`구배저항 (${rlGrade>0?"▲":"▼"}${Math.abs(rlGrade).toFixed(1)}%)`)} />
                        )}
                        {/* [Fix-RE-vCross] 교차속도 ReferenceLine — KH에서만 언급되고 차트에 없던 v_cross 시각화 */}
                        {v_cross_actual != null && (
                          <ReferenceLine
                            x={parseFloat(v_cross_actual.toFixed(1))}
                            stroke="#f59e0b"
                            strokeDasharray="5 3"
                            strokeWidth={1.5}
                            label={{ value: `v×=${v_cross_actual.toFixed(0)}`, position:"insideTopLeft", fill:"#f59e0b", fontSize:9, fontWeight:700 }}
                          />
                        )}
                      </ComposedChart>
                    </CB>
                    {(function() {
                      var RE1_KH = [
                        { id:1, icon:"🌍", color:"#f59e0b",
                          tag: lang==="en" ? "IRI Impact on Crr → Efficiency Loss Quantification" : "IRI가 Crr에 미치는 영향 → 전비 손실 정량화",
                          title: lang==="en" ? "Road Roughness → Rolling Resistance Correction (ΔCrr_IRI)" : "노면 거칠기 → 구름저항 보정 메커니즘 (ΔCrr_IRI)",
                          formula: lang==="en"
                            ? "ΔCrr_IRI = k_IRI_crr · IRI + k_IRI_vib · IRI · v  [dimensionless]\n  (k_IRI_crr: static IRI coefficient · k_IRI_vib: speed-dependent vibration term)\nF_rough = ΔCrr_IRI · m · g  [N]\nEfficiency loss ΔWh/km = F_rough · 1000 / 3600 / η_total  [Wh/km per km/h]"
                            : "ΔCrr_IRI = k_IRI_crr · IRI + k_IRI_vib · IRI · v  [무차원]\n  (k_IRI_crr: 정적 IRI 계수 · k_IRI_vib: 속도 의존 진동 항)\nF_rough = ΔCrr_IRI · m · g  [N]\n전비 손실 ΔWh/km = F_rough · 1000 / 3600 / η_total  [Wh/km per km/h]",
                          keyPoints: lang==="en" ? [
                            "IRI=" + (v7meta.IRI||2.5) + " m/km — Highway≈2.0, Normal≈3.0, Poor≈6.0 → F_rough @100=" + (roughEnabled?kpi_F_rough:"OFF") + " N",
                            "F_rough has speed-dependent term (IRI·v) → penalty grows faster than F_roll at highway speed",
                            "Poor road (IRI=6.0) can add 3–5% extra rolling resistance → ~10–15 Wh/km efficiency loss",
                          ] : [
                            "IRI=" + (v7meta.IRI||2.5) + " m/km — 고속도로≈2.0, 일반≈3.0, 불량≈6.0 → F_rough @100=" + (roughEnabled?kpi_F_rough:"OFF") + " N",
                            "F_rough는 속도 의존 항(IRI·v) 포함 → 고속에서 F_roll보다 빠르게 증가",
                            "불량 노면(IRI=6.0): 구름저항 3~5% 추가 → 전비 약 10~15 Wh/km 손실",
                          ],
                          actions: lang==="en" ? [
                            "Enable road_roughness_enable to see F_rough line on chart — compare IRI=2.5 vs 6.0",
                            "Real-route IRI mapping: weight IRI distribution to get accurate fleet energy prediction",
                            "Low-profile tyre vs high-profile: IRI sensitivity differs — high sidewall absorbs more road energy",
                          ] : [
                            "road_roughness_enable 활성 → 차트에서 F_rough 라인 확인 — IRI=2.5 vs 6.0 비교",
                            "실 노선 IRI 분포 가중 평균 → 정확한 차량 군집 전비 예측",
                            "저프로파일 vs 고프로파일 타이어: IRI 민감도 다름 — 높은 사이드월이 노면 에너지 더 흡수",
                          ],
                        },
                        { id:2, icon:"📊", color:"#60a5fa",
                          tag: lang==="en" ? "F_aero vs F_roll Crossover: Design Priority Shift Point" : "F_aero vs F_roll 교차점: 설계 개선 우선순위 전환점",
                          title: lang==="en" ? "Crossover Speed Analysis: When Aero Dominates Over Rolling Resistance" : "교차 속도 분석: 공기저항이 구름저항을 초과하는 지점",
                          formula: lang==="en"
                            ? (() => { const _Ka=(activeSimVehicle==='A'?paramsA:paramsB).K_aero; const _ks=(_Ka!=null&&_Ka!==1.0)?` · K_aero(=${_Ka.toFixed(3)})`:''; return `F_aero(v) = ½ · ρ · CdA · v²${_ks}  [N]  (quadratic: doubles every +41% speed)\nF_roll(v) = m · g · Crr(v,T)  [N]  (quasi-linear in v)\nCrossover v_cross: F_aero = F_roll  [km/h]\n  → solved via bisection on CrrDynamic(v,T_amb)·road_k  (≈ √(m·g·Crr/(½·ρ·CdA)) as approx)\nROI ratio @ 100 km/h: `+(ref100&&ref100.F_roll>0?(ref100.F_aero/ref100.F_roll).toFixed(2):"—")+"× → "+(ref100&&ref100.F_roll>0&&ref100.F_aero/ref100.F_roll>1.5?"CdA priority":"Crr priority")+(_ks?"\n  ※ K_aero≠1.0: aero calibration factor applied":""); })()
                            : (() => { const _Ka=(activeSimVehicle==='A'?paramsA:paramsB).K_aero; const _ks=(_Ka!=null&&_Ka!==1.0)?` · K_aero(=${_Ka.toFixed(3)})`:''; return `F_aero(v) = ½ · ρ · CdA · v²${_ks}  [N]  (2차: 속도 41% 증가마다 2배)\nF_roll(v) = m · g · Crr(v,T)  [N]  (v에 대해 준선형)\n교차 속도 v_cross: F_aero = F_roll  [km/h]\n  → CrrDynamic(v,T_amb)·road_k 이분탐색 수치 풀이  (근사식: √(m·g·Crr/(½·ρ·CdA)))\n@100 km/h ROI 비율: `+(ref100&&ref100.F_roll>0?(ref100.F_aero/ref100.F_roll).toFixed(2):"—")+"× → "+(ref100&&ref100.F_roll>0&&ref100.F_aero/ref100.F_roll>1.5?"CdA 우선":"Crr 우선")+(_ks?"\n  ※ K_aero≠1.0: 실측 공력 보정계수 적용 중":""); })(),
                          keyPoints: lang==="en" ? [
                            "@" + kpi_v_ref + " km/h — F_aero=" + kpi_F_aero + "N, F_roll=" + kpi_F_roll + "N, ratio=" + (ref100&&ref100.F_roll>0?(ref100.F_aero/ref100.F_roll).toFixed(2):"—") + "×",
                            "Below crossover: invest in Crr (tyre compound, inflation) — above crossover: invest in CdA (aero, grille)",
                            "EV with flat battery floor: h_cg low → narrow frontal area feasible → CdA improvement ROI high",
                          ] : [
                            "@" + kpi_v_ref + " km/h — F_aero=" + kpi_F_aero + "N, F_roll=" + kpi_F_roll + "N, 비율=" + (ref100&&ref100.F_roll>0?(ref100.F_aero/ref100.F_roll).toFixed(2):"—") + "×",
                            "교차 속도 이하: Crr 투자(타이어 컴파운드·공기압) — 이상: CdA 투자(공력·그릴 셔터)",
                            "EV 플랫 배터리: h_cg 낮음 → 좁은 전면 면적 가능 → CdA 개선 ROI 높음",
                          ],
                          actions: lang==="en" ? [
                            "Aero/Roll > 1.5: grille shutter + undertray are top priority improvements",
                            "Crr 1% reduction vs CdA 1% reduction: compare Wh/km delta at typical drive speed",
                            "IRI correction: at high IRI, F_rough adds to effective Crr — confirm F_rough included in crossover analysis",
                          ] : [
                            "공기/구름비 > 1.5: 그릴 셔터 + 언더트레이가 최우선 개선 항목",
                            "Crr 1% 저감 vs CdA 1% 저감: 대표 주행 속도에서 Wh/km 변화량 비교",
                            "IRI 보정: 고IRI에서 F_rough가 유효 Crr에 추가됨 → 교차 속도 분석에 F_rough 포함 확인",
                          ],
                        },
                        { id:3, icon:"💨", color:"#a78bfa",
                          tag: lang==="en" ? "Tire Pressure & Temperature → Crr Sensitivity" : "타이어 공기압·온도 → Crr 민감도",
                          title: lang==="en" ? "Crr Correction Model: Speed, Temperature & Pressure Effects on Rolling Loss" : "Crr 보정 모델: 속도·온도·공기압이 구름저항에 미치는 영향",
                          formula: lang==="en"
                            ? "Crr_eff(v,T,p) = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT) + ΔCrr_pressure  [dimensionless]\n  (Crr₀: base rolling resistance · Crr_v: speed coeff. · Crr_T: temperature coeff.)\nΔCrr_pressure = α_p·dp + β_p·dp²  (2nd-order pressure correction)\n  dp = (p_nom−p_act)/p_nom  [normalised pressure deviation]\n  α_p≈0.008 · β_p≈0.0003  (low pressure → dp>0 → ΔCrr_pressure>0 → Crr ↑)\nCurrent IRI=" + (v7meta.IRI||2.5) + " m/km · Pressure=" + (v7meta.tire_pressure||2.4) + " bar"
                            : "Crr_eff(v,T,p) = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT) + ΔCrr_pressure  [무차원]\n  (Crr₀: 기본 구름저항 · Crr_v: 속도 계수 · Crr_T: 온도 계수)\nΔCrr_pressure = α_p·dp + β_p·dp²  (2차 공기압 보정)\n  dp = (p_nom−p_act)/p_nom  [정규화 압력 편차]\n  α_p≈0.008 · β_p≈0.0003  (저압 → dp>0 → ΔCrr_pressure>0 → Crr ↑)\n현재 IRI=" + (v7meta.IRI||2.5) + " m/km · 공기압=" + (v7meta.tire_pressure||2.4) + " bar",
                          keyPoints: lang==="en" ? [
                            "Cold tyre (< 20°C): Crr can be 10–20% higher than hot tyre — significant cold-start efficiency penalty",
                            "Under-inflation by 0.4 bar: F_roll increases ~4% — always verify tyre pressure for accurate simulation",
                            "Speed term (Crr_v·v²): at 120 km/h the speed correction can add 15–25% to Crr₀",
                          ] : [
                            "저온 타이어(< 20°C): Crr이 열간 대비 10~20% 높음 — 한랭 시동 전비 저하의 주요 원인",
                            "0.4 bar 저압: F_roll 약 4% 증가 — 정확한 시뮬을 위해 항상 공기압 검증",
                            "속도 항(Crr_v·v²): 120 km/h에서 속도 보정이 Crr₀의 15~25% 추가 가능",
                          ],
                          actions: lang==="en" ? [
                            "Vary IRI 2.0→6.0 and observe F_rough slope on chart: slope = k_IRI_vib·m·g",
                            "Low temperature scenario: T_amb=−20°C → Crr_T correction → F_roll increase → compare with warm run",
                            "Tyre pressure target: optimal 2.5–2.8 bar for EV (heavier than ICE → slightly higher pressure)",
                          ] : [
                            "IRI 2.0→6.0 변화 시 F_rough 기울기 관찰: 기울기 = k_IRI_vib·m·g",
                            "저온 시나리오: T_amb=−20°C → Crr_T 보정 → F_roll 증가 → 열간 대비 비교",
                            "타이어 공기압 목표: EV는 ICE보다 무거워 2.5~2.8 bar 적정 (약간 높게 설정)",
                          ],
                        },
                      ];
                      return (
                        <RLKnowHowPanel
                          sectionTitle={lang==="en"?"💡 Analysis Know-How — RE1: Resistance Breakdown":"💡 분석 노하우 — RE1: 저항력 분해"}
                          khs={RE1_KH} activeId={re1KhId} onHwClick={handleRe1} lang={lang}>
                          {re1KhId===1 && (
                            <RLInlineKpi color="#f59e0b"
                              title={lang==="en"?"🌍 IRI Roughness Force Assessment":"🌍 IRI 거칠기 저항력 평가"}
                              hint={lang==="en"?"F_rough = ΔCrr_IRI·m·g — enable road_roughness for active measurement":"F_rough = ΔCrr_IRI·m·g — road_roughness 활성해야 측정값 표시"}
                              items={[
                                ["IRI", v7meta.IRI||2.5, "m/km"],
                                [lang==="en"?"F_rough @100":"F_rough @100", roughEnabled?kpi_F_rough:"OFF", roughEnabled?"N":""],
                                [lang==="en"?"F_aero @100":"F_aero @100", kpi_F_aero, "N"],
                                [lang==="en"?"F_roll @100":"F_roll @100", kpi_F_roll, "N"],
                              ]} />
                          )}
                          {re1KhId===2 && (
                            <RLInlineKpi color="#60a5fa"
                              title={lang==="en"?"📊 Aero vs Roll Force Ratio":"📊 공기/구름저항 비율"}
                              hint={lang==="en"?"> 1.5× → CdA improvement is top ROI priority":"> 1.5× → CdA 개선이 최우선 ROI 항목"}
                              items={[
                                [lang==="en"?"F_aero @100":"F_aero @100", kpi_F_aero, "N"],
                                [lang==="en"?"F_roll @100":"F_roll @100", kpi_F_roll, "N"],
                                [lang==="en"?"Ratio":"비율", ref100&&ref100.F_roll>0?(ref100.F_aero/ref100.F_roll).toFixed(2):"—", "×"],
                                [lang==="en"?"Priority":"우선순위", ref100&&ref100.F_roll>0&&ref100.F_aero/ref100.F_roll>1.5?(lang==="en"?"CdA":"CdA"):(lang==="en"?"Crr":"Crr"), ""],
                              ]} />
                          )}
                          {re1KhId===3 && (
                            <RLInlineKpi color="#a78bfa"
                              title={lang==="en"?"💨 Crr Correction Parameters":"💨 Crr 보정 파라미터"}
                              hint={lang==="en"?"Crr_eff = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT) + α_p·dp + β_p·dp²":"Crr_eff = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT) + α_p·dp + β_p·dp²"}
                              items={(() => {
                                const _p_nom   = curP.tire_pressure_nominal || 2.4;
                                const _p_act   = curP.tire_pressure_actual  || 2.4;
                                const _dp      = (_p_nom - _p_act) / _p_nom;
                                const _alpha_p = curP.tire_pressure_crr_alpha || 0.008;
                                const _beta_p  = curP.tire_pressure_crr_beta  || 0.0003;
                                const _dCrr_p  = Math.max(0, _alpha_p * _dp + _beta_p * _dp * _dp);
                                return [
                                  [lang==="en"?"Tire Pressure":"공기압", (v7meta.tire_pressure||_p_act).toFixed(2), "bar"],
                                  ["IRI", v7meta.IRI||2.5, "m/km"],
                                  [lang==="en"?"ΔCrr_pressure":"ΔCrr_압력보정", _dCrr_p.toFixed(5), ""],
                                  [lang==="en"?"Roughness":"거칠기 활성", roughEnabled?"ON":"OFF", ""],
                                ];
                              })()} />
                          )}
                        </RLKnowHowPanel>
                      );
                    })()}
                    </RLGraphCard>
                    <RLGraphCard
                      title={lang==="en"?"RE2 | Gear Oil Temperature & Coolant Flow (Launch Sim)":"RE2 | 기어오일 온도 & 냉각 유량 (발진 시뮬)"}
                      subtitle=""
                      graphId="RE2"
                      axisPanel={<AxesPanel axX={axRE2.axX} setAxX={axRE2.setAxX} axY={axRE2.axY} setAxY={axRE2.setAxY} xUnit="s" yUnit="°C"
                          hideY={axRE2.hideY} setHideY={axRE2.setHideY}
                          axY2={axRE2.axY2} setAxY2={axRE2.setAxY2} yUnit2="L/min"
                          hideY2={axRE2.hideY2} setHideY2={axRE2.setHideY2} />}>
                    {/* RE2 헤더 배지 */}
                    <div style={{display:"flex",justifyContent:"flex-end",marginBottom:4,flexWrap:"wrap",gap:4}}>
                      {[
                        {l:(activeSimVehicle==="A"?paramsA:paramsB).T_amb+"°C",c:"#fb923c",d:true},
                        {l:((activeSimVehicle==="A"?paramsA:paramsB).drive_mode||"NORMAL").toUpperCase(),c:({ECO:"#22c55e",NORMAL:"#94a3b8",SPORT:"#ef4444",SNOW:"#38bdf8",OFF_ROAD:"#f97316"})[((activeSimVehicle==="A"?paramsA:paramsB).drive_mode||"NORMAL").toUpperCase()]||"#94a3b8",d:false},
                        {l:(activeSimVehicle==="A"?paramsA:paramsB).m+"kg",c:"#f97316",d:false},
                        {l:(lang==="ko"?({dry:"🟢건조",wet:"🔵습윤",snow:"⚪적설",ice:"🧊빙판"}):({dry:"🟢Dry",wet:"🔵Wet",snow:"⚪Snow",ice:"🧊Ice"}))[(activeSimVehicle==="A"?paramsA:paramsB).road_condition||"dry"],c:({dry:"#16a34a",wet:"#2563eb",snow:"#64748b",ice:"#0ea5e9"})[(activeSimVehicle==="A"?paramsA:paramsB).road_condition||"dry"],d:false},
                      ].filter(Boolean).map((b,i)=>(<span key={i} style={{fontSize:9,fontWeight:b.d?700:500,padding:"2px 5px",borderRadius:4,whiteSpace:"nowrap",background:b.c+(b.d?"22":"11"),color:b.c,border:"1px solid "+b.c+(b.d?"55":"28"),opacity:b.d?1:0.6}}>{b.l}</span>))}
                    </div>
                    {/* [UI-P2-Fix] RE2 다운샘플: 짧은 런(데이터 부족) 시 차트 sparse 방지
                        수정 전: i % 3 === 0 고정 → 데이터 300점 미만이면 100점 이하로 감소
                        수정 후: 목표 300점 기준 동적 step 산출 → 최소 데이터 보장 */}
                    <CB width="100%" height={220}>
                      <LineChart data={(() => { const step = Math.max(1, Math.floor(lData.length / 300)); return lData.filter((_, i) => i % step === 0); })()}
                        margin={{ top:5, right:10, left:0, bottom:20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                        <XAxis dataKey="t" stroke={COLORS.textMuted} tick={{ fontSize: 9 }}
                          type="number" domain={axRE2.domX}
                          label={{ value: lang==="en"?"Time (s)":"시간 (s)", position:"insideBottom", offset:0, fontSize:9, fill:COLORS.textMuted }} />
                        <YAxis yAxisId="temp" hide={axRE2.hideY} stroke={COLORS.textMuted} tick={{ fontSize: 9 }}
                          domain={axRE2.domY}
                        label={{ value: lang==="en" ? "Temperature (°C)" : "온도 (°C)", angle:-90, position:"insideLeft", dx:10, fontSize:10, fill:COLORS.text , style:{textAnchor:"middle"}}}/>
                        <YAxis yAxisId="flow" orientation="right" hide={axRE2.hideY2} width={54} stroke={COLORS.textMuted} tick={{ fontSize: 9 }}
                          domain={axRE2.domY2}
                        label={{ value: lang==="en" ? "Flow Rate (L/min)" : "유량 (L/min)", angle:90, position:"insideRight", dx:2, fontSize:10, fill:COLORS.textMuted , style:{textAnchor:"middle"}}}/>
                        <Tooltip content={<CT />} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        {/* [A68-04-Fix-RE1] T_gear=좌측Y(temp) → 조건 없이 항상 표시, coolant_flow=우측Y(flow) → hideY2 연동 */}
                        <Line yAxisId="temp" type="monotone" dataKey="T_gear" stroke="#f97316" strokeWidth={1.5} dot={false} name={lang==="en"?"Gear Oil Temp (°C)":"기어오일 온도 (°C)"} />
                        {!axRE2.hideY2 && <Line yAxisId="flow" type="monotone" dataKey="coolant_flow" stroke="#38bdf8" strokeWidth={1.5} dot={false} name={lang==="en"?"Coolant Flow (L/min)":"냉각유량 (L/min)"} />}
                        {/* [Fix-RE2-RefLines] 외기온 기준선 + 60°C 워밍업 목표선 */}
                        {!axRE2.hideY && <ReferenceLine yAxisId="temp" y={curP.T_amb ?? 25} stroke="#fb923c" strokeDasharray="3 2" strokeWidth={1}
                          label={{value:`T_amb=${curP.T_amb ?? 25}°C`,position:"insideTopRight",fill:"#fb923c",fontSize:8}} />}
                        {!axRE2.hideY && <ReferenceLine yAxisId="temp" y={60} stroke="#22c55e" strokeDasharray="4 2" strokeWidth={1}
                          label={{value:lang==="en"?"Warmup target 60°C":"워밍업 목표 60°C",position:"insideBottomRight",fill:"#22c55e",fontSize:8}} />}
                      </LineChart>
                    </CB>
                    {(function() {
                      var tGearPeak = lData.length>0 ? Math.max(...lData.map(function(d){return d.T_gear||0;})) : 0;
                      var tGearFinal = lData.length>0 ? (lData[lData.length-1].T_gear||0) : 0;
                      var flowPeak = lData.length>0 ? Math.max(...lData.map(function(d){return d.coolant_flow||0;})) : 0;
                      var tLaunch = lData.length>0 ? (lData[lData.length-1].t||0) : 0;
                      var RE2_KH = [
                        { id:1, icon:"🌡️", color:"#f97316",
                          tag: lang==="en" ? "Cold Start Viscosity Loss → Gear Efficiency Penalty" : "저온 기동 점도 손실 → 기어 효율 페널티",
                          title: lang==="en" ? "T_gear Rise Curve: Cold Viscosity Penalty & Warm-Up Target" : "T_gear 상승 곡선: 저온 점도 손실 & 온도 수렴 목표",
                          formula: lang==="en"
                            ? "η_gear(T) = η_gear_hot − k_visc · (T_ref − T_gear)  [−]\n  (k_visc: viscosity loss coefficient per °C · T_ref: reference temperature ≈ 80°C)\nP_loss_gear(T) = T_motor · ω_motor · (1 − η_gear(T))  [W]\nWarm-up target: T_gear > 60°C for <5% viscosity penalty"
                            : "η_gear(T) = η_gear_hot − k_visc · (T_ref − T_gear)  [−]\n  (k_visc: 온도당 점도 손실 계수 · T_ref: 기준 온도 ≈ 80°C)\nP_loss_gear(T) = T_motor · ω_motor · (1 − η_gear(T))  [W]\n워밍업 목표: T_gear > 60°C에서 점도 페널티 < 5%",
                          keyPoints: lang==="en" ? [
                            "Peak T_gear = " + tGearPeak.toFixed(1) + "°C · Final T_gear = " + tGearFinal.toFixed(1) + "°C @ " + tLaunch.toFixed(0) + "s",
                            "Cold oil (T_gear < 20°C): viscosity 5–10× higher than hot oil → mechanical loss doubles",
                            "Gear temp loss model OFF → all gear loss calculated at 25°C (underestimates cold-start loss by 15–30%)",
                          ] : [
                            "최대 T_gear = " + tGearPeak.toFixed(1) + "°C · 최종 T_gear = " + tGearFinal.toFixed(1) + "°C @ " + tLaunch.toFixed(0) + "s",
                            "저온 오일(T_gear < 20°C): 점도 열간 대비 5~10배 높음 → 기계 손실 2배",
                            "기어 온도 손실 모델 OFF → 모든 기어 손실 25°C 기준(한랭 기동 손실 15~30% 과소 추정)",
                          ],
                          actions: lang==="en" ? [
                            "Enable gear_loss_temp_enable: compare launch efficiency with cold vs warm gear oil",
                            "Target: T_gear > 60°C within 2–5 km of cold start to exit viscosity-dominated loss regime",
                            "Oil thermal conductivity path: motor heat → sump → oil temp rise → efficiency recovery",
                          ] : [
                            "gear_loss_temp_enable 활성 → 저온 vs 열간 기어오일 발진 효율 비교",
                            "목표: 한랭 기동 2~5 km 이내 T_gear > 60°C 도달 → 점도 지배 손실 구간 이탈",
                            "오일 열전달 경로: 모터 열 → 오일 팬 → 오일 온도 상승 → 효율 회복",
                          ],
                        },
                        { id:2, icon:"💧", color:"#38bdf8",
                          tag: lang==="en" ? "Coolant Flow Rate → Thermal Convergence Speed" : "냉각수 유량 → 열 수렴 속도",
                          title: lang==="en" ? "Cooling System Response: Flow Rate & Temperature Steady-State" : "냉각 시스템 응답: 유량과 온도 정상 상태 수렴",
                          formula: lang==="en"
                            ? "Q_cool = coolant_flow · ρ_fluid · Cp_fluid · (T_in − T_out)  [W]\n  (ρ_fluid≈1060 kg/m³ for 50% glycol · Cp≈3500 J/kg·K)\ndT_gear/dt = (P_loss − Q_cool) / (m_oil · Cp_oil)  [°C/s]\nSteady state: T_gear_ss = T_amb + P_loss_avg / (U·A_cooler)  [°C]"
                            : "Q_cool = coolant_flow · ρ_fluid · Cp_fluid · (T_in − T_out)  [W]\n  (ρ_fluid≈1060 kg/m³ for 50% 글리콜 · Cp≈3500 J/kg·K)\ndT_gear/dt = (P_loss − Q_cool) / (m_oil · Cp_oil)  [°C/s]\n정상 상태: T_gear_ss = T_amb + P_loss_avg / (U·A_cooler)  [°C]",
                          keyPoints: lang==="en" ? [
                            "Peak coolant_flow = " + flowPeak.toFixed(1) + " L/min · High flow → faster heat rejection → lower T_gear_ss",
                            "Low flow (< 3 L/min): insufficient convection → T_gear overshoots → viscosity thinning (opposite risk at high T)",
                            "Thermal time constant τ = m_oil·Cp_oil / (U·A): reduce τ → faster warm-up AND faster cooling response",
                          ] : [
                            "최대 냉각유량 = " + flowPeak.toFixed(1) + " L/min · 유량↑ → 방열 빠름 → T_gear 정상 상태↓",
                            "저유량(< 3 L/min): 대류 부족 → T_gear 오버슈트 → 고온 점도 저하 (다른 방향의 위험)",
                            "열 시정수 τ = m_oil·Cp_oil / (U·A): τ 감소 → 워밍업 빠름 AND 냉각 응답 빠름",
                          ],
                          actions: lang==="en" ? [
                            "Overlay T_gear and coolant_flow: check if peak flow coincides with peak temperature — desired response",
                            "Target steady-state T_gear: 70–90°C for optimal viscosity (low loss without oxidation risk)",
                            "Variable-speed coolant pump: increase flow at high load, reduce at light cruise for parasitic pump loss savings",
                          ] : [
                            "T_gear와 coolant_flow 오버레이: 피크 유량이 최대 온도와 일치하는지 확인 — 정상 냉각 응답",
                            "목표 정상 상태 T_gear: 70~90°C (최적 점도 — 낮은 손실 + 산화 위험 없음)",
                            "가변 속도 냉각 펌프: 고부하 시 유량↑, 경부하 순항 시 유량↓ → 펌프 기생 손실 절감",
                          ],
                        },
                        { id:3, icon:"⚡", color:"#22c55e",
                          tag: lang==="en" ? "Thermal Management Strategy → Cold-Start Range Recovery" : "열관리 전략 → 한랭 기동 항속거리 회복",
                          title: lang==="en" ? "Integrated Thermal Strategy: Gear + Motor + Battery Co-Heating" : "통합 열관리 전략: 기어·모터·배터리 협조 예열",
                          formula: lang==="en"
                            ? "Range penalty (cold) = ΔWh_cold × (1/η_bat_cold − 1/η_bat_hot)  [Wh/km]\nHeat pump COP ≈ 2.5–3.5: Q_thermal = COP × P_hp  [W]\n  (Heat pump priority: battery > cabin > motor/gear → efficiency-first heating order)\nBreak-even distance = ΔE_preheat / ΔWh_km_recovery  [km]"
                            : "항속거리 페널티(저온) = ΔWh_cold × (1/η_bat_cold − 1/η_bat_hot)  [Wh/km]\n히트펌프 COP ≈ 2.5~3.5: Q_thermal = COP × P_hp  [W]\n  (히트펌프 우선순위: 배터리 > 실내 > 모터/기어 → 효율 우선 예열 순서)\n손익분기 거리 = ΔE_preheat / ΔWh_km_recovery  [km]",
                          keyPoints: lang==="en" ? [
                            "T_gear warm-up: gear efficiency recovers " + (v7meta.gear_loss_temp_enable?"(model active — check T_gear rise rate)":"(model OFF — enable gear_loss_temp_enable)"),
                            "Co-heating: motor copper loss → gear oil warming → avoids separate gear heater hardware",
                            "Cold battery discharge penalty (−20°C): internal resistance 2–3× → effective capacity 20–30% lower",
                          ] : [
                            "T_gear 워밍업: 기어 효율 회복 " + (v7meta.gear_loss_temp_enable?"(모델 활성 — T_gear 상승 속도 확인)":"(모델 OFF — gear_loss_temp_enable 활성 필요)"),
                            "협조 예열: 모터 구리손 → 기어오일 온도 상승 → 별도 기어 히터 없이 예열 가능",
                            "저온 배터리 방전 페널티(−20°C): 내부 저항 2~3배 → 유효 용량 20~30% 감소",
                          ],
                          actions: lang==="en" ? [
                            "Enable gear_loss_temp_enable + run cold (T_amb=−20°C) vs hot (T_amb=25°C) → quantify range delta",
                            "Pre-conditioning strategy: heat gear oil via no-load motor spin before departure → amortise preheat energy",
                            "Thermal simulation validation: T_gear convergence time vs real dyno data → calibrate k_visc coefficient",
                          ] : [
                            "gear_loss_temp_enable 활성 + 저온(T_amb=−20°C) vs 열간(25°C) 비교 → 항속거리 차이 정량화",
                            "예조온 전략: 출발 전 무부하 모터 회전으로 기어오일 예열 → 예열 에너지 손익분기 분석",
                            "열 시뮬레이션 검증: T_gear 수렴 시간 vs 실제 다이나모 데이터 → k_visc 계수 교정",
                          ],
                        },
                      ];
                      return (
                        <RLKnowHowPanel
                          sectionTitle={lang==="en"?"💡 Analysis Know-How — RE2: Gear Oil Temperature & Cooling":"💡 분석 노하우 — RE2: 기어오일 온도 & 냉각 유량"}
                          khs={RE2_KH} activeId={re2KhId} onHwClick={handleRe2} lang={lang}>
                          {re2KhId===1 && (
                            <RLInlineKpi color="#f97316"
                              title={lang==="en"?"🌡️ Gear Oil Temperature Curve":"🌡️ 기어오일 온도 상승 분석"}
                              hint={lang==="en"?"Target: T_gear > 60°C within 2–5 km — viscosity loss minimised":"목표: 2~5 km 이내 T_gear > 60°C 도달 — 점도 손실 최소화"}
                              items={[
                                [lang==="en"?"T_gear Peak":"T_gear 최대", tGearPeak.toFixed(1), "°C"],
                                [lang==="en"?"T_gear Final":"T_gear 최종", tGearFinal.toFixed(1), "°C"],
                                [lang==="en"?"T_model Active":"온도 모델", v7meta.gear_loss_temp_enable?(lang==="en"?"ON":"ON"):(lang==="en"?"OFF":"OFF"), ""],
                              ]} />
                          )}
                          {re2KhId===2 && (
                            <RLInlineKpi color="#38bdf8"
                              title={lang==="en"?"💧 Coolant Flow Rate Analysis":"💧 냉각수 유량 분석"}
                              hint={lang==="en"?"Steady-state T_gear target: 70–90°C for optimal gear oil viscosity":"정상 상태 T_gear 목표: 최적 기어오일 점도 70~90°C"}
                              items={[
                                [lang==="en"?"Peak Flow":"최대 유량", flowPeak.toFixed(1), "L/min"],
                                [lang==="en"?"Sim Duration":"시뮬 시간", tLaunch.toFixed(0), "s"],
                                [lang==="en"?"T_gear Final":"T_gear 최종", tGearFinal.toFixed(1), "°C"],
                              ]} />
                          )}
                          {re2KhId===3 && (
                            <RLInlineKpi color="#22c55e"
                              title={lang==="en"?"⚡ Thermal Strategy KPIs":"⚡ 열관리 전략 KPI"}
                              hint={lang==="en"?"Heat pump COP 2.5–3.5: most efficient cold-start heating source":"히트펌프 COP 2.5~3.5: 한랭 기동 가장 효율적인 예열 열원"}
                              items={[
                                [lang==="en"?"Gear Temp Model":"기어 온도 모델", v7meta.gear_loss_temp_enable?(lang==="en"?"ON":"ON"):(lang==="en"?"OFF":"OFF"), ""],
                                [lang==="en"?"T_gear Peak":"T_gear 최대", tGearPeak.toFixed(1), "°C"],
                                [lang==="en"?"Peak Flow":"최대 유량", flowPeak.toFixed(1), "L/min"],
                              ]} />
                          )}
                        </RLKnowHowPanel>
                      );
                    })()}
                    </RLGraphCard>

                </>)}
                {/* ── RE3/RE4: 정적 계산 — 시뮬 결과 불필요, 항상 표시 ── */}
                    {/* ── RE3: 노면 조건별 F_roll vs 속도 ── */}
                    <RLGraphCard
                      title={lang==="en"?"RE3 | Road Condition F_roll Comparison (dry/wet/snow/ice)":"RE3 | 노면 조건별 구름저항 비교 (건조/습윤/적설/빙판)"}
                      subtitle=""
                      graphId="RE3"
                      axisPanel={<AxesPanel axX={axRE3.axX} setAxX={axRE3.setAxX} axY={axRE3.axY} setAxY={axRE3.setAxY} xUnit="km/h" yUnit="N" hideY={axRE3.hideY} setHideY={axRE3.setHideY} />}>
                      {/* RE3 헤더 배지 */}
                      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:4,flexWrap:"wrap",gap:4}}>
                        {[
                          {l:curP.m+"kg",c:"#f97316"},
                          {l:"Crr₀="+((curP.Crr||0.012).toFixed(4)),c:"#22c55e"},
                          {l:curP.T_amb+"°C",c:"#fb923c"},
                        ].map((b,i)=>(
                          <span key={i} style={{fontSize:9,fontWeight:700,padding:"2px 5px",borderRadius:4,whiteSpace:"nowrap",background:b.c+"22",color:b.c,border:"1px solid "+b.c+"55"}}>{b.l}</span>
                        ))}
                      </div>
                      <CB width="100%" height={220}>
                        <ComposedChart data={re3Data} margin={{top:5,right:10,left:0,bottom:20}}>
                          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                          <XAxis dataKey="v_kmh" stroke={COLORS.textMuted} tick={{fontSize:9}} type="number"
                            domain={axRE3.domX}
                            ticks={[0,20,40,60,80,100,120,140,160]}
                            label={{value:lang==="en"?"Speed (km/h)":"속도 (km/h)",position:"insideBottom",offset:0,fontSize:10,fill:COLORS.textMuted}} />
                          <YAxis hide={axRE3.hideY} stroke={COLORS.textMuted} tick={{fontSize:9}} domain={axRE3.domY}
                            label={{value:lang==="en"?"F_roll (N)":"구름저항 (N)",angle:-90,position:"insideLeft",dx:10,fontSize:10,fill:COLORS.text,style:{textAnchor:"middle"}}} />
                          <Tooltip content={<CT />} />
                          <Legend wrapperStyle={{fontSize:10}} />
                          {/* [Fix-RE3-highlight] 현재 노면 조건 라인을 굵게+실선으로 강조, 나머지는 가늘게·점선 */}
                          {(()=>{
                            const cond = curP.road_condition || "dry";
                            const cfg = {
                              dry:  { stroke:"#16a34a", base:1.5, dash:undefined },
                              wet:  { stroke:"#2563eb", base:1.5, dash:"6 2" },
                              snow: { stroke:"#94a3b8", base:1.5, dash:"3 3" },
                              ice:  { stroke:"#0ea5e9", base:1.5, dash:"4 2" },
                            };
                            const condLabel = {
                              dry:  lang==="en"?"Dry (×1.00) ★":"건조 (×1.00) ★",
                              wet:  lang==="en"?"Wet (×1.15) ★":"습윤 (×1.15) ★",
                              snow: lang==="en"?"Snow (×2.50) ★":"적설 (×2.50) ★",
                              ice:  lang==="en"?"Ice (×1.80) ★":"빙판 (×1.80) ★",
                            };
                            const normalLabel = {
                              dry:  lang==="en"?"Dry (×1.00)":"건조 (×1.00)",
                              wet:  lang==="en"?"Wet (×1.15)":"습윤 (×1.15)",
                              snow: lang==="en"?"Snow (×2.50)":"적설 (×2.50)",
                              ice:  lang==="en"?"Ice (×1.80)":"빙판 (×1.80)",
                            };
                            return Object.entries(cfg).map(([key, c]) => {
                              const isCurrent = key === cond;
                              return (
                                <Line key={key} type="monotone" dataKey={key}
                                  stroke={c.stroke}
                                  strokeWidth={isCurrent ? 2.5 : c.base}
                                  strokeDasharray={isCurrent ? undefined : c.dash}
                                  dot={false}
                                  opacity={isCurrent ? 1 : 0.45}
                                  name={isCurrent ? condLabel[key] : normalLabel[key]} />
                              );
                            });
                          })()}
                        </ComposedChart>
                      </CB>
                      {(function(){
                        var RE3_KH = [
                          { id:1, icon:"🛣️", color:"#22c55e",
                            tag: lang==="en" ? "Road Condition Crr Multiplier → F_roll Scaling" : "노면 조건 Crr 배율 → F_roll 스케일",
                            title: lang==="en" ? "F_roll by Road Condition: Dry / Wet / Snow / Ice" : "노면 조건별 구름저항: 건조·습윤·적설·빙판",
                            formula: lang==="en"
                              ? "F_roll(cond) = m·g·Crr(v,T)·road_k\n  road_k: dry=1.00, wet=1.15, snow=2.50, ice=1.80\n  Crr(v,T) = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT)"
                              : "F_roll(cond) = m·g·Crr(v,T)·road_k\n  road_k: 건조=1.00, 습윤=1.15, 적설=2.50, 빙판=1.80\n  Crr(v,T) = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT)",
                            keyPoints: lang==="en" ? [
                              "Snow: road_k=2.50 → F_roll 2.5× dry → range loss ~30% even at 60 km/h",
                              "Ice: road_k=1.80 (lower than snow — compacted ice is smoother than loose snow)",
                              "Wet: road_k=1.15 — moderate penalty; aquaplaning μ drop separate from Crr model",
                            ] : [
                              "적설: road_k=2.50 → F_roll 2.5배 → 60 km/h에서도 항속거리 약 30% 감소",
                              "빙판: road_k=1.80 (다진 빙판이 적설보다 매끄러워 Crr은 낮음)",
                              "습윤: road_k=1.15 — 중간 페널티; 수막 현상 μ 저하는 별도 모델",
                            ],
                            actions: lang==="en" ? [
                              "Compare dry vs snow at 80 km/h: gap = F_roll difference → direct range impact",
                              "Winter tyre: Crr₀ ~10% higher than summer → compound effect with road_k=2.5",
                              "Adjust road_condition param and re-run to see F_roll and v_cross shift",
                            ] : [
                              "건조 vs 적설 80 km/h 비교: 간격 = F_roll 차이 → 직접적 항속거리 영향",
                              "겨울 타이어: Crr₀ 여름 대비 약 10% 높음 → road_k=2.5와 복합 효과",
                              "road_condition 변경 후 재시뮬 → F_roll·v_cross 이동 확인",
                            ],
                          },
                          { id:2, icon:"🌡️", color:"#fb923c",
                            tag: lang==="en" ? "Temperature × Road Condition Combined Crr Effect" : "온도·노면 조건 복합 Crr 효과",
                            title: lang==="en" ? "Cold + Slippery: Crr Compounding at Low Temperature" : "저온 + 미끄운 노면: 저온에서의 Crr 복합 증가",
                            formula: lang==="en"
                              ? "Crr(v,T,cond) = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT)·road_k\n  Cold tyre (T<10°C): Crr_T correction adds 10–20% on top of road_k\n  Combined penalty at −20°C snow: up to ×3.0× dry warm"
                              : "Crr(v,T,cond) = Crr₀·(1+Crr_v·v²)·(1+Crr_T·ΔT)·road_k\n  저온 타이어 (T<10°C): Crr_T 보정 최대 10~20% 추가\n  −20°C 적설 복합 페널티: 열간 건조 대비 최대 ×3.0",
                            keyPoints: lang==="en" ? [
                              `Current T_amb=${curP.T_amb}°C → Crr_T correction ${curP.T_amb<15?"active — cold penalty applies":"minimal (>15°C)"}`,
                              "Snow + Cold: worst-case scenario — motor derating + Crr×3 → range cut by 40–50%",
                              "Chart uses current T_amb for Crr(v,T) — change T_amb and re-run to see cold shift",
                            ] : [
                              `현재 T_amb=${curP.T_amb}°C → Crr_T 보정 ${curP.T_amb<15?"활성 — 저온 페널티 적용 중":"미미 (>15°C)"}`,
                              "적설 + 저온: 최악 조건 — 모터 디레이팅 + Crr×3 → 항속거리 40~50% 감소",
                              "차트는 현재 T_amb 기준 Crr(v,T) 사용 — T_amb 변경 후 재시뮬하면 저온 이동 확인",
                            ],
                            actions: lang==="en" ? [
                              "Set T_amb=−20°C + road_condition=snow → re-run: observe RE3 shift upward",
                              "Tyre pressure drops ~0.1 bar per 10°C → winter tyre pressure check critical",
                              "Pre-heating tyre via heat pump reduces cold Crr penalty — quantify break-even",
                            ] : [
                              "T_amb=−20°C + road_condition=snow 설정 후 재시뮬 → RE3 상방 이동 확인",
                              "타이어 공기압 10°C당 약 0.1 bar 감소 → 겨울철 공기압 점검 필수",
                              "히트펌프 타이어 예열 → 저온 Crr 페널티 감소 → 손익분기 정량화",
                            ],
                          },
                        ];
                        return (
                          <RLKnowHowPanel
                            sectionTitle={lang==="en"?"💡 Analysis Know-How — RE3: Road Condition F_roll":"💡 분석 노하우 — RE3: 노면 조건별 구름저항"}
                            khs={RE3_KH} activeId={re3KhId} onHwClick={handleRe3} lang={lang}>
                            {re3KhId===1 && (
                              <RLInlineKpi color="#22c55e"
                                title={lang==="en"?"🛣️ Road Condition Crr Multipliers":"🛣️ 노면 조건별 Crr 배율"}
                                hint={lang==="en"?"F_roll(cond) = F_roll(dry) × road_k":"F_roll(cond) = F_roll(dry) × road_k"}
                                items={[
                                  [lang==="en"?"Dry (road_k)":"건조 (road_k)", "1.00", "×"],
                                  [lang==="en"?"Wet (road_k)":"습윤 (road_k)", "1.15", "×"],
                                  [lang==="en"?"Ice (road_k)":"빙판 (road_k)", "1.80", "×"],
                                  [lang==="en"?"Snow (road_k)":"적설 (road_k)", "2.50", "×"],
                                ]} />
                            )}
                            {re3KhId===2 && (
                              <RLInlineKpi color="#fb923c"
                                title={lang==="en"?"🌡️ Cold+Condition Combined Effect":"🌡️ 저온+노면 복합 효과"}
                                hint={lang==="en"?`T_amb=${curP.T_amb}°C · road_condition=${curP.road_condition||"dry"}`:`T_amb=${curP.T_amb}°C · road_condition=${curP.road_condition||"dry"}`}
                                items={[
                                  [lang==="en"?"T_amb":"T_amb", curP.T_amb, "°C"],
                                  [lang==="en"?"Road Condition":"노면 조건", curP.road_condition||"dry", ""],
                                  [lang==="en"?"road_k":"road_k", ({dry:"1.00",wet:"1.15",snow:"2.50",ice:"1.80"})[curP.road_condition||"dry"], "×"],
                                ]} />
                            )}
                          </RLKnowHowPanel>
                        );
                      })()}
                    </RLGraphCard>

                    {/* ── RE4: 측풍 추가 공기저항 vs 풍향각 ── */}
                    <RLGraphCard
                      title={lang==="en"?"RE4 | Crosswind Extra Drag vs Wind Angle (v=100 km/h)":"RE4 | 측풍 추가 공기저항 vs 풍향각 (v=100 km/h)"}
                      subtitle=""
                      graphId="RE4"
                      axisPanel={<AxesPanel axX={axRE4.axX} setAxX={axRE4.setAxX} axY={axRE4.axY} setAxY={axRE4.setAxY} xUnit="°" yUnit="N" hideY={axRE4.hideY} setHideY={axRE4.setHideY} />}>
                      {/* RE4 헤더 배지 */}
                      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:4,flexWrap:"wrap",gap:4}}>
                        {[
                          {l:"CdA="+(curP.CdA_v3||curP.CdA||0.65).toFixed(3),c:"#60a5fa"},
                          {l:"Ka="+(curP.K_aero||1.0).toFixed(3),c:"#a78bfa"},
                          {l:"λ="+cwLatFactor.toFixed(2),c:"#38bdf8"},
                          cwEnabled && {l:"💨"+cwVWind.toFixed(0)+"m/s@"+cwAngle.toFixed(0)+"°",c:"#38bdf8"},
                        ].filter(Boolean).map((b,i)=>(
                          <span key={i} style={{fontSize:9,fontWeight:700,padding:"2px 5px",borderRadius:4,whiteSpace:"nowrap",background:b.c+"22",color:b.c,border:"1px solid "+b.c+"55"}}>{b.l}</span>
                        ))}
                      </div>
                      <CB width="100%" height={220}>
                        <ComposedChart data={re4Rows} margin={{top:5,right:10,left:0,bottom:20}}>
                          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gridLine} />
                          <XAxis dataKey="angle_deg" stroke={COLORS.textMuted} tick={{fontSize:9}} type="number"
                            domain={axRE4.domX} ticks={[0,15,30,45,60,75,90]}
                            label={{value:lang==="en"?"Wind Angle (°)":"풍향각 (°)",position:"insideBottom",offset:0,fontSize:10,fill:COLORS.textMuted}} />
                          <YAxis hide={axRE4.hideY} stroke={COLORS.textMuted} tick={{fontSize:9}} domain={axRE4.domY}
                            label={{value:lang==="en"?"ΔF_aero (N)":"추가 공기저항 (N)",angle:-90,position:"insideLeft",dx:10,fontSize:10,fill:COLORS.text,style:{textAnchor:"middle"}}} />
                          <Tooltip content={<CT />} />
                          <Legend wrapperStyle={{fontSize:10}} />
                          {/* [Fix-RE4-windSpeeds] 동적 windSpeeds 기반 Line 렌더 — 현재 cwVWind 포함 */}
                          {re4WindSpeeds.map((v_w, idx) => {
                            const isCurrent = cwEnabled && cwVWind > 0 && Math.round(cwVWind) === v_w;
                            const palette = [COLORS.gridLine, "#60a5fa", "#38bdf8", "#f97316", "#a78bfa", "#22c55e"];
                            const col = v_w === 0 ? COLORS.gridLine : palette[1 + (idx % (palette.length - 1))];
                            return (
                              <Line key={v_w} type="monotone" dataKey={`w${v_w}`}
                                stroke={isCurrent ? "#facc15" : col}
                                strokeWidth={isCurrent ? 2 : (v_w === 0 ? 1 : 1.5)}
                                dot={false}
                                strokeDasharray={v_w === 0 ? "2 2" : undefined}
                                name={v_w === 0 ? (lang==="en"?"0 m/s (no wind)":"무풍 (0 m/s)") : `${v_w} m/s${isCurrent ? (lang==="en"?" ★current":"★현재") : ""}`} />
                            );
                          })}
                          {/* 현재 설정 풍향각 표시 */}
                          {cwEnabled && cwVWind > 0 && (
                            <ReferenceLine x={cwAngle} stroke="#38bdf8" strokeDasharray="4 2" strokeWidth={1.5}
                              label={{value:lang==="en"?`Current ${cwAngle.toFixed(0)}°`:`현재 ${cwAngle.toFixed(0)}°`,position:"insideTopLeft",fill:"#38bdf8",fontSize:9,fontWeight:700}} />
                          )}
                          {/* λ=0 기준선 */}
                          <ReferenceLine y={0} stroke={COLORS.textMuted} strokeDasharray="3 1" />
                        </ComposedChart>
                      </CB>
                      {(function(){
                        const re4_delta_at_90_w10 = re4Rows.find(d=>d.angle_deg===90)?.w10?.toFixed(0) || "—";
                        var RE4_KH = [
                          { id:1, icon:"💨", color:"#38bdf8",
                            tag: lang==="en" ? "Crosswind ΔF_aero: Lateral Factor λ Effect" : "측풍 ΔF_aero: 횡방향 계수 λ 영향",
                            title: lang==="en" ? "Crosswind Extra Drag vs Wind Angle at 100 km/h" : "100 km/h에서의 측풍 추가 공기저항 vs 풍향각",
                            formula: lang==="en"
                              ? `v_eff = √((v+v_w·cos β)² + λ·(v_w·sin β)²)  λ=${cwLatFactor.toFixed(2)}\nΔF_aero = ½·ρ·CdA·Ka·(v_eff²−v²)  [N]\n  β: wind angle (0°=headwind, 90°=crosswind)\n  @90°, 10 m/s → ΔF_aero ≈ ${re4_delta_at_90_w10} N`
                              : `v_eff = √((v+v_w·cos β)² + λ·(v_w·sin β)²)  λ=${cwLatFactor.toFixed(2)}\nΔF_aero = ½·ρ·CdA·Ka·(v_eff²−v²)  [N]\n  β: 풍향각 (0°=정면풍, 90°=완전 측풍)\n  @90°, 10 m/s → ΔF_aero ≈ ${re4_delta_at_90_w10} N`,
                            keyPoints: lang==="en" ? [
                              `λ(crosswind_lat_factor)=${cwLatFactor.toFixed(2)}: lateral wind contribution to effective drag`,
                              "At 90°: pure crosswind — only lateral term active → ΔF depends on λ",
                              "At 0° headwind: v_eff = v + v_wind → maximum drag increase (additive, not lateral)",
                            ] : [
                              `λ(crosswind_lat_factor)=${cwLatFactor.toFixed(2)}: 횡방향 바람의 유효 항력 기여 계수`,
                              "90°: 완전 측풍 — 횡방향 항 만 활성 → ΔF는 λ에 비례",
                              "0° 정면풍: v_eff = v + v_wind → 최대 항력 증가 (가산, 횡방향 아님)",
                            ],
                            actions: lang==="en" ? [
                              "Enable crosswind_enable + set wind_angle_deg=90 → observe F_aero increase on RE1",
                              "Tune λ(crosswind_lat_factor) from 0.1 to 0.5 — compare RE4 curve slope",
                              "Cross-check: RE4 ΔF @current angle vs delta_F_wind field in simulation data",
                            ] : [
                              "crosswind_enable 활성 + wind_angle_deg=90 설정 → RE1 F_aero 증가 관찰",
                              "λ(crosswind_lat_factor) 0.1~0.5 조정 → RE4 곡선 기울기 변화 비교",
                              "검증: RE4 ΔF @현재 풍향각 vs 시뮬 데이터 delta_F_wind 필드 대조",
                            ],
                          },
                          { id:2, icon:"🔢", color:"#a78bfa",
                            tag: lang==="en" ? "Headwind vs Crosswind: Which Hurts More?" : "정면풍 vs 측풍: 어느 쪽이 더 불리한가?",
                            title: lang==="en" ? "Wind Angle Effect: Headwind Dominates, Crosswind via λ" : "풍향각 영향: 정면풍이 주도, 측풍은 λ 통해 반영",
                            formula: lang==="en"
                              ? "Headwind (0°): ΔF = ½ρ·CdA·Ka·[(v+v_w)²−v²] ≈ ρ·CdA·Ka·v·v_w (dominant)\nCrosswind (90°): ΔF = ½ρ·CdA·Ka·λ·v_w² (λ-limited)\nBreakeven angle: β* where ΔF_cw(β*)=ΔF_hw/2"
                              : "정면풍 (0°): ΔF = ½ρ·CdA·Ka·[(v+v_w)²−v²] ≈ ρ·CdA·Ka·v·v_w (지배적)\n측풍 (90°): ΔF = ½ρ·CdA·Ka·λ·v_w² (λ에 의해 제한)\n손익분기 풍향: β* where ΔF_cw(β*)=ΔF_hw/2",
                            keyPoints: lang==="en" ? [
                              "Headwind (0°) is always worst for energy — velocity adds linearly to vehicle speed",
                              `Crosswind (90°) effect = λ×v_w² term only — with λ=${cwLatFactor.toFixed(2)}, much smaller than headwind`,
                              "Oblique wind (30–60°) has both components — common highway scenario",
                            ] : [
                              "정면풍(0°)이 에너지에 가장 불리 — 속도가 차량 속도에 선형 추가",
                              `측풍(90°) 효과 = λ×v_w² 항만 — λ=${cwLatFactor.toFixed(2)}로 정면풍보다 훨씬 작음`,
                              "사선풍(30~60°): 두 성분 혼합 — 고속도로 실제 상황에 가장 빈번",
                            ],
                            actions: lang==="en" ? [
                              "Real highway: prevailing crosswind 45–70° → check ΔF at these angles on RE4",
                              "CdA improvement priority: reduces headwind drag proportionally (ΔF ∝ CdA)",
                              "Vehicle yaw stability (Cs·β_yaw): lateral force from crosswind also affects stability — beyond energy",
                            ] : [
                              "실제 고속도로: 주요 측풍 45~70° → RE4에서 해당 각도의 ΔF 확인",
                              "CdA 개선: 정면풍 항력을 비례 감소 (ΔF ∝ CdA) → 고속 주행 시 우선 ROI",
                              "차량 요 안정성(Cs·β_yaw): 측풍의 횡력 → 안정성에도 영향 (에너지 외 고려 필요)",
                            ],
                          },
                        ];
                        return (
                          <RLKnowHowPanel
                            sectionTitle={lang==="en"?"💡 Analysis Know-How — RE4: Crosswind Drag":"💡 분석 노하우 — RE4: 측풍 추가 공기저항"}
                            khs={RE4_KH} activeId={re4KhId} onHwClick={handleRe4} lang={lang}>
                            {re4KhId===1 && (
                              <RLInlineKpi color="#38bdf8"
                                title={lang==="en"?"💨 Crosswind Setup":"💨 측풍 설정"}
                                hint={lang==="en"?`λ=${cwLatFactor.toFixed(2)} · v_wind=${cwVWind.toFixed(1)} m/s · β=${cwAngle.toFixed(0)}°`:`λ=${cwLatFactor.toFixed(2)} · 풍속=${cwVWind.toFixed(1)} m/s · 풍향각=${cwAngle.toFixed(0)}°`}
                                items={[
                                  [lang==="en"?"Crosswind":"측풍 활성", cwEnabled?"ON":"OFF", ""],
                                  [lang==="en"?"lat_factor λ":"횡방향 계수 λ", cwLatFactor.toFixed(2), ""],
                                  [lang==="en"?"ΔF @90°,10m/s":"ΔF @90°,10m/s", re4_delta_at_90_w10, "N"],
                                ]} />
                            )}
                            {re4KhId===2 && (
                              <RLInlineKpi color="#a78bfa"
                                title={lang==="en"?"🔢 Wind Drag Comparison":"🔢 풍향별 항력 비교"}
                                hint={lang==="en"?"Headwind (0°) dominates; crosswind (90°) limited by λ":"정면풍(0°) 지배적; 측풍(90°)은 λ로 제한"}
                                items={[
                                  [lang==="en"?"ΔF @0°,10m/s":"ΔF @정면풍,10m/s", (re4Rows.find(d=>d.angle_deg===0)?.w10||0).toFixed(0), "N"],
                                  [lang==="en"?"ΔF @45°,10m/s":"ΔF @45°,10m/s", (re4Rows.find(d=>d.angle_deg===45)?.w10||0).toFixed(0), "N"],
                                  [lang==="en"?"ΔF @90°,10m/s":"ΔF @90°,10m/s", re4_delta_at_90_w10, "N"],
                                ]} />
                            )}
                          </RLKnowHowPanel>
                        );
                      })()}
                    </RLGraphCard>

                </div>
              </div>
            );
          })()}

          {/* ═══════════ [NEW] 파라미터 상호 영향 종합 매트릭스 ═══════════ */}
          {activeTab === "param_matrix" && (() => {
            const matrixParams = [
              { label: "차량 질량 ↑",           labelEn: "Vehicle Mass ↑" },
              { label: "공기저항계수 ↓",         labelEn: "Aero Drag Coeff. ↓" },
              { label: "구름저항계수 ↓",         labelEn: "Rolling Resist. ↓" },
              { label: "기어비 ↑",               labelEn: "Gear Ratio ↑" },
              { label: "배터리 내부저항 ↑",      labelEn: "Batt. Int. Resistance ↑" },
              { label: "토크 필터 시상수 ↓",     labelEn: "Torque Filter τ ↓" },
              { label: "배터리 디레이팅 온도 ↑", labelEn: "Batt. Derate Temp ↑" },
              { label: "최대 회생 출력 ↑",       labelEn: "Max Regen Power ↑" },
              { label: "스위칭 주파수 ↑",        labelEn: "Switching Freq. ↑" },
              { label: "외기 온도 ↓ (저온)",     labelEn: "Ambient Temp ↓ (Cold)" },
              { label: "BMS 방전 전류 한계 ↑",   labelEn: "BMS Discharge Current ↑" },
              { label: "K_aero 보정",            labelEn: "K_aero Calibration" },
              { label: "충전 목표 SOC ↓",        labelEn: "Charge Target SOC ↓" },
              { label: "OBC 효율 ↑",             labelEn: "OBC Efficiency ↑" },
              { label: "충전 프로파일 고출력",    labelEn: "High-Power Chg. Profile" },
            ];
            // 열 순서: 정속주행·발진·재가속·코스트·회생제동·에너지체인·충전시뮬·열관리·배터리건강·안전분석
            const sectionCols = lang === "en"
              ? ["Road Load","Launch","Re-Accel","Coast","Regen","Energy Chain","Chg. Sim.","Thermal","Batt. Health","Safety"]
              : ["정속주행","발진","재가속","코스트","회생제동","에너지체인","충전시뮬","열관리","배터리건강","안전분석"];
            const matrixDataKo = [
              // 기존 12행 — 충전시뮬 열(6번째) 추가
              ["⚠️전비↓",   "⚠️가속↓",   "⚠️출력↓",   "⚠️코스트↓", "—",       "⚠️손실↑",    "—",          "✅열완충",    "—",        "—"      ],
              ["✅전비↑",    "✅고속↑",    "✅추월↑",    "✅코스트↑",  "—",       "✅고속효율",  "—",          "⚠️냉각제약", "—",        "—"      ],
              ["✅저속효율", "—",          "—",          "✅코스트↑",  "—",       "✅E1개선",    "—",          "—",          "—",        "—"      ],
              ["⚠️최고속↓", "✅가속↑",    "—",          "—",          "—",       "⚠️철손↑",     "—",          "—",          "—",        "—"      ],
              ["⚠️전비↓",   "⚠️출력↓",   "⚠️출력↓",   "—",          "⚠️회생↓", "❌손실↑",     "❌충전시간↑", "⚠️발열↑",   "⚠️수명↓",  "—"      ],
              ["✅추종↑",    "✅발진↑",    "✅응답↑",    "—",          "⚠️저크↑", "—",           "—",          "—",          "—",        "—"      ],
              ["—",          "✅지연없음", "✅마진↑",    "—",          "—",       "—",           "✅CC유지",    "⚠️과열위험", "❌수명↓",  "⚠️ASIL" ],
              ["—",          "—",          "—",          "✅회수↑",    "✅회생↑", "✅E5개선",    "—",          "⚠️배터리↑",  "⚠️수명↓",  "✅SOC안정"],
              ["—",          "—",          "—",          "—",          "—",       "⚠️SW손실↑",  "—",          "⚠️Inv온도↑", "—",        "—"      ],
              ["⚠️Crr↑",    "⚠️derate",  "⚠️출력↓",   "—",          "⚠️회생↓", "❌전비↓",     "⚠️CC단축",   "⚠️Ri상승",   "⚠️열화↑",  "⚠️센서↑"],
              ["—",          "✅출력↑",    "✅출력↑",    "—",          "—",       "—",           "—",           "⚠️발열↑",   "❌수명↓",  "⚠️HARA" ],
              ["⚠️편향",     "—",          "⚠️편향",     "⚠️편향",     "—",       "⚠️교정필요",  "—",          "—",          "—",        "—"      ],
              // 신규 3행 — 충전 시뮬레이션 파라미터
              ["—",          "—",          "—",          "—",          "—",       "—",           "✅충전시간↓", "✅발열↓",    "✅SOH절감", "—"      ],
              ["—",          "—",          "—",          "—",          "—",       "✅AC손실↓",   "✅OBC발열↓", "✅온도↓",    "—",        "—"      ],
              ["—",          "—",          "—",          "—",          "—",       "⚠️peak전력↑", "✅충전속도↑", "⚠️발열↑",   "❌수명↓",  "⚠️ASIL" ],
            ];
            const matrixDataEn = [
              ["⚠️Effic.↓",   "⚠️Accel.↓",  "⚠️Power↓",  "⚠️Coast↓",  "—",        "⚠️Loss↑",     "—",           "✅Thermal buf.", "—",         "—"       ],
              ["✅Effic.↑",    "✅Top spd↑",  "✅Pass↑",    "✅Coast↑",   "—",        "✅Hi-spd eff.","—",           "⚠️Cool.limit",  "—",         "—"       ],
              ["✅Low-spd eff.","—",          "—",          "✅Coast↑",   "—",        "✅E1 impr.",   "—",           "—",             "—",         "—"       ],
              ["⚠️Top spd↓",  "✅Accel.↑",   "—",          "—",          "—",        "⚠️Iron loss↑","—",           "—",             "—",         "—"       ],
              ["⚠️Effic.↓",   "⚠️Power↓",   "⚠️Power↓",  "—",          "⚠️Regen↓", "❌Loss↑",      "❌Chg.time↑", "⚠️Heat↑",      "⚠️Life↓",   "—"       ],
              ["✅Tracking↑",  "✅Launch↑",   "✅Resp.↑",   "—",          "⚠️Jerk↑",  "—",           "—",           "—",             "—",         "—"       ],
              ["—",            "✅No delay",  "✅Margin↑",  "—",          "—",        "—",           "✅CC hold",   "⚠️Overheat",    "❌Life↓",   "⚠️ASIL"  ],
              ["—",            "—",           "—",          "✅Recov.↑",  "✅Regen↑", "✅E5 impr.",  "—",           "⚠️Batt.↑",      "⚠️Life↓",   "✅SOC stable"],
              ["—",            "—",           "—",          "—",          "—",        "⚠️SW loss↑",  "—",           "⚠️Inv temp↑",   "—",         "—"       ],
              ["⚠️Crr↑",      "⚠️Derate",    "⚠️Power↓",  "—",          "⚠️Regen↓", "❌Effic.↓",   "⚠️CC short",  "⚠️Ri↑",         "⚠️Degrad.↑","⚠️Sensor↑"],
              ["—",            "✅Power↑",    "✅Power↑",   "—",          "—",        "—",           "—",           "⚠️Heat↑",       "❌Life↓",   "⚠️HARA"  ],
              ["⚠️Bias",       "—",           "⚠️Bias",     "⚠️Bias",     "—",        "⚠️Cal.needed","—",           "—",             "—",         "—"       ],
              // new 3 rows — charging simulation parameters
              ["—",            "—",           "—",          "—",          "—",        "—",           "✅Chg.time↓", "✅Heat↓",       "✅SOH saving","—"      ],
              ["—",            "—",           "—",          "—",          "—",        "✅AC loss↓",  "✅OBC heat↓", "✅Temp↓",       "—",         "—"       ],
              ["—",            "—",           "—",          "—",          "—",        "⚠️Peak pwr↑", "✅Chg.spd↑",  "⚠️Heat↑",       "❌Life↓",   "⚠️ASIL"  ],
            ];
            const matrixData = lang === "en" ? matrixDataEn : matrixDataKo;
            const cellColor = (v) => {
              if (!v || v === "—") return COLORS.textMuted;
              if (v.startsWith("✅")) return "#22c55e";
              if (v.startsWith("⚠️")) return "#f59e0b";
              if (v.startsWith("❌")) return "#ef4444";
              return COLORS.text;
            };
            return (
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#8b5cf6", marginBottom: 4 }}>
                  {lang === "en" ? "🔢 Parameter Cross-Influence Matrix" : "🔢 파라미터 상호 영향 종합 매트릭스"}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>
                  {lang === "en"
                    ? "15 parameters × 10 simulation sections. ✅=Positive  ⚠️=Caution/Trade-off  ❌=Negative  —=No effect"
                    : "주요 15개 파라미터가 시뮬레이터 전체 섹션에 미치는 영향. ✅=긍정  ⚠️=주의/트레이드오프  ❌=부정  —=영향없음"}
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", fontSize: 10, minWidth: 700 }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "7px 10px", background: COLORS.cardBg, color: COLORS.textMuted,
                          border: `1px solid ${COLORS.border}`, textAlign: "left", fontWeight: 700, whiteSpace: "nowrap", minWidth: 130 }}>
                          {lang === "en" ? "Parameter ↓ / Section →" : "파라미터 ↓ / 섹션 →"}
                        </th>
                        {sectionCols.map(s => (
                          <th key={s} style={{ padding: "7px 8px", background: COLORS.cardBg, color: COLORS.textMuted,
                            border: `1px solid ${COLORS.border}`, textAlign: "center", fontWeight: 600, whiteSpace: "nowrap", minWidth: 72 }}>
                            {s}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matrixParams.map((p, ri) => (
                        <tr key={ri} style={{ background: ri % 2 === 0 ? "transparent" : COLORS.cardBg }}>
                          <td style={{ padding: "6px 10px", border: `1px solid ${COLORS.border}`,
                            fontWeight: 700, color: "#8b5cf6", fontFamily: "'JetBrains Mono','Fira Code',monospace",
                            whiteSpace: "nowrap", fontSize: 10 }}>
                            {lang === "en" ? p.labelEn : p.label}
                          </td>
                          {matrixData[ri].map((cell, ci) => (
                            <td key={ci} style={{ padding: "6px 8px", border: `1px solid ${COLORS.border}`,
                              textAlign: "center", color: cellColor(cell),
                              fontWeight: cell && cell !== "—" ? 700 : 400,
                              fontSize: cell === "—" ? 14 : 10 }}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 10, fontSize: 10, color: COLORS.textMuted, padding: "8px 12px",
                  background: COLORS.cardBg, borderRadius: 6 }}>
                  {lang === "en"
                    ? "Note: Arrows indicate direction of change (↑=increase, ↓=decrease). Effects are for the changed section relative to the baseline."
                    : "※ 화살표는 변화 방향(↑=증가, ↓=감소). 영향은 해당 섹션 기준 대비 변화 방향입니다."}
                </div>
              </div>
            );
          })()}

          {/* ═══════════ [NEW] 설계 권고사항 ═══════════ */}
          {activeTab === "design_reco" && (() => {
            const steps = [
              {
                step: "Step 1",
                goal: lang === "en" ? "Confirm Basic Vehicle Specs" : "차량 기본 사양 확정",
                params: "m, battery_energy, P_max_wheel, T_max_motor, gear_ratio, CdA, Crr",
                verify: lang === "en" ? "Verify with Road Load (R1~R5) results" : "정속주행(R1~R5) 결과로 검증",
                color: "#f59e0b",
              },
              {
                step: "Step 2",
                goal: lang === "en" ? "Set Thermal Model Parameters" : "열 모델 파라미터 설정",
                params: "C_th_batt/inv/motor, R_th_bc/ic/mc, T_derate 임계값, UA_rad",
                verify: lang === "en" ? "Verify with Thermal Mgmt (T1~T3) temperature history" : "열관리(T1~T3) 온도 이력 검증",
                color: "#0ea5e9",
              },
              {
                step: "Step 3",
                goal: lang === "en" ? "Refine Battery Model" : "배터리 모델 정밀화",
                params: "R_batt, Q_nom, soh_profile, 2RC 파라미터, ocv_lut_weight",
                verify: lang === "en" ? "Verify with Energy Chain (E1) loss decomposition" : "에너지체인(E1) 손실 분해 검증",
                color: "#0ea5e9",
              },
              {
                step: "Step 4",
                goal: lang === "en" ? "Charging Simulation Parameters" : "충전 시뮬레이션 파라미터 설정",
                params: "soc_target, T_amb_chg, charging_profile, R_int_T(SOC,T), eta_obc, bms_i_charge_max, v2g_dod",
                verify: lang === "en" ? "Verify with Charging (CH1~CH9): CC ratio, ΔSOH, peak temp, V2G SOC curve" : "충전탭(CH1~CH9) — CC구간 비율·ΔSOH·최고온도·V2G SOC 곡선 검증",
                color: "#f97316",
              },
              {
                step: "Step 5",
                goal: lang === "en" ? "Design Control Parameters" : "제어 파라미터 설계",
                params: "Kp_id/iq, Ki_id/iq, Kp_spd, τ_torque, dead_time, ctrl_dt",
                verify: lang === "en" ? "Verify FOC convergence and Launch (L1)" : "FOC 수렴성 및 발진(L1) 검증",
                color: "#8b5cf6",
              },
              {
                step: "Step 6",
                goal: lang === "en" ? "K-Factor Correlation Calibration" : "K-인자 상관 보정",
                params: "K_aero, K_roll, K_cu, K_fe, K_inv, K_batt_r, K_regen",
                verify: lang === "en" ? "Compare with measured data and calibrate" : "실측 데이터와 비교 후 보정",
                color: "#8b5cf6",
              },
              {
                step: "Step 7",
                goal: lang === "en" ? "Set Monte Carlo Uncertainty" : "Monte Carlo 불확실성 설정",
                params: "CdA σ, Crr σ, m σ, R_batt σ, aux_power σ, T_amb σ",
                verify: lang === "en" ? "Verify confidence interval in Sensitivity (S3)" : "민감도(S3) 신뢰구간 검증",
                color: "#8b5cf6",
              },
              {
                step: "Step 8",
                goal: lang === "en" ? "Safety Analysis Parameters" : "안전 분석 파라미터 설정",
                params: "S, E, C (HARA), pf_v_max, FTTI, sigma_v/soc/T (SOTIF)",
                verify: lang === "en" ? "Verify with Functional Safety & SOTIF sections" : "기능안전·SOTIF 섹션 결과 검증",
                color: "#10b981",
              },
            ];
            const tradeoffs = [
              { icon: "⚡", title: lang === "en" ? "Performance vs Ride Comfort" : "성능 vs 승차감",
                desc: lang === "en"
                  ? "τ_torque small (SPORT) → fast response but jerk increases. Provide driver mode selection."
                  : "τ_torque 작게(SPORT) → 빠른 응답이지만 저크 증가. 드라이브 모드 분리로 고객 선택 제공." },
              { icon: "🔋", title: lang === "en" ? "Energy Recovery vs Battery Life" : "에너지 회수 vs 배터리 수명",
                desc: lang === "en"
                  ? "P_max_regen high → regen improved but Ri loss·heat load increases. SOH linkage essential."
                  : "P_max_regen 높게 → 회생 향상이지만 Ri 손실·열 부하 증가. SOH 연동 필수." },
              { icon: "🌡️", title: lang === "en" ? "Output vs Thermal Safety" : "출력 vs 열 안전",
                desc: lang === "en"
                  ? "T_derate high → performance maintained but battery/motor thermal damage risk increases. Design cooling capacity together."
                  : "T_derate 높게 → 성능 유지이지만 배터리/모터 열 손상 위험 증가. 냉각 용량 함께 설계." },
              { icon: "🛡️", title: lang === "en" ? "Safety vs Cost" : "안전 vs 비용",
                desc: lang === "en"
                  ? "ASIL D → highest reliability but development/manufacturing/certification costs surge. Risk-based level determination essential."
                  : "ASIL D 등급 → 최고 신뢰성이지만 개발·제조·인증 비용 대폭 증가. 리스크 기반 등급 결정 필수." },
              { icon: "📊", title: lang === "en" ? "Model Precision vs Computation Speed" : "모델 정밀도 vs 계산 속도",
                desc: lang === "en"
                  ? "foc_current_pi_enable=true / thermal_3d_enable=true → accurate but simulation time greatly increases."
                  : "foc_current_pi_enable=true / thermal_3d_enable=true → 정확하지만 시뮬 시간 대폭 증가." },
              { icon: "⚡", title: lang === "en" ? "Charging Speed vs Battery Life" : "충전 속도 vs 배터리 수명",
                desc: lang === "en"
                  ? "350 kW DCFC cuts charge time 8× but accelerates ΔSOH per session proportionally. Check break-even via CC ratio KPI and ΔSOH comparison in the Charging tab."
                  : "350 kW 급속충전 → 충전 시간 8배 단축이지만 ΔSOH/세션도 비례 증가. CC구간 비율 KPI와 ΔSOH 비교 탭에서 손익분기 확인 필요." },
              { icon: "🎯", title: lang === "en" ? "Target SOC vs Range" : "충전 목표 SOC vs 항속거리",
                desc: lang === "en"
                  ? "80% SOC cutoff reduces ΔSOH 15~25% but shortens usable range. Determine by whether charging enters SEI growth acceleration zone (80~100%)."
                  : "SOC 80% 컷 → ΔSOH 15~25% 절감이지만 실항속 감소. SEI 성장 가속 구간(80~100%) 진입 여부로 손익 판단." },
              { icon: "💰", title: lang === "en" ? "V2G Revenue vs Battery Degradation" : "V2G 수익 vs 배터리 열화",
                desc: lang === "en"
                  ? "Wider DoD increases V2G revenue but accelerates cycle life degradation. Maintaining SOC 40~80% sweet spot is key to the break-even point."
                  : "DoD 넓힐수록 V2G 수익↑이지만 사이클 수명↓. SOC 40~80% 스위트스팟 유지가 손익분기의 핵심." },
            ];
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {/* 단계별 튜닝 순서 */}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#8b5cf6", marginBottom: 12 }}>
                    {lang === "en" ? "📋 Step-by-Step Parameter Tuning Order" : "📋 단계별 파라미터 튜닝 순서"}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>
                    {lang === "en"
                      ? "Following this order minimizes cross-section effects and builds a reliable simulation."
                      : "다음 순서로 파라미터를 조정하면 섹션 간 영향을 최소화하며 신뢰성 있는 시뮬을 구성할 수 있습니다."}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {steps.map((s, i) => (
                      <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start",
                        background: COLORS.cardBg, borderRadius: 8, padding: "10px 14px",
                        border: `1px solid ${COLORS.border}` }}>
                        <div style={{ minWidth: 56, height: 28, borderRadius: 5, background: s.color + "25",
                          border: `1.5px solid ${s.color}60`, display: "flex", alignItems: "center",
                          justifyContent: "center", fontSize: 10, fontWeight: 800, color: s.color, flexShrink: 0 }}>
                          {s.step}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 3 }}>{s.goal}</div>
                          <div style={{ fontSize: 10, color: s.color, fontFamily: "'JetBrains Mono','Fira Code',monospace",
                            marginBottom: 3, wordBreak: "break-word" }}>{s.params}</div>
                          <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                            ✓ {s.verify}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 핵심 트레이드오프 */}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#8b5cf6", marginBottom: 12 }}>
                    {lang === "en" ? "⚖️ Key Trade-off Summary" : "⚖️ 핵심 트레이드오프 요약"}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
                    {tradeoffs.map((t, i) => (
                      <div key={i} style={{ background: COLORS.cardBg, borderRadius: 8, padding: "12px 14px",
                        border: `1px solid ${COLORS.border}` }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
                          {t.icon} {t.title}
                        </div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6 }}>
                          {t.desc}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ═══════════ [DOMAIN-AUDIT] 도메인 완성도 감사 ═══════════ */}
          {activeTab === "domain_audit" && <DomainAuditPanel lang={lang} />}

          {/* ═══════════ [DEV-HISTORY] 개발 이력 ═══════════ */}
          {activeTab === "dev_history" && <DevHistoryPanel lang={lang} />}

          </React.Suspense>
          </ColLayoutContext.Provider>
        )}
      </div>

      {/* ═══════════ Footer ═══════════ */}
      <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: "6px 14px",
        display: "flex", justifyContent: "space-between", fontSize: 9,
        color: COLORS.textMuted, fontFamily: mono }}>
        <span>EV Dynamics Simulator EXTRA v17.0 | [V45] CAL Matrix(ISO/SAE 21434)·ISO 26262-6 SW V&V·Safety Triangle·SOTIF↔FTA Link·Pareto 3-Obj+Click Detail+Radar·PWA 3-Tier Cache+BackgroundSync | [R-16] NSGA-II Pareto·AugLag+NelderMead | [R-15] DFMEA↔PFMEA·FTA·TARA | dt=5ms</span>
        <span>{paramsA.shortName} vs {paramsB.shortName} | Grade: {paramsA.grade_deg}° | Road: {paramsA.road_condition} | T_amb: {paramsA.T_amb}°C</span>
      </div>
    </div>
    </RLKpiOpenContext.Provider>
    </PopupContext.Provider>
    </LangContext.Provider>
    </ThemeContext.Provider>
  );
}

// ════════════════════════════════════════════════════════════════
// §  [R-13-UI] 인터랙티브 차트 Pinch-Zoom · 대시보드 레이아웃 저장/불러오기 · PWA
// ════════════════════════════════════════════════════════════════

// ── Pinch-Zoom 훅 ────────────────────────────────────────────
// 터치 이벤트로 차트 컨테이너의 확대/축소 지원
function usePinchZoom(ref) {
  const [scale, setScale] = React.useState(1);
  const touchRef = React.useRef({ dist: null, scale: 1 });

  React.useEffect(() => {
    const el = ref?.current;
    if (!el) return;

    function getDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function onTouchStart(e) {
      if (e.touches.length === 2) {
        touchRef.current.dist  = getDistance(e.touches);
        touchRef.current.scale = scale;
      }
    }
    function onTouchMove(e) {
      if (e.touches.length === 2 && touchRef.current.dist !== null) {
        e.preventDefault();
        const newDist  = getDistance(e.touches);
        const ratio    = newDist / touchRef.current.dist;
        const newScale = Math.max(0.5, Math.min(4.0, touchRef.current.scale * ratio));
        setScale(newScale);
      }
    }
    function onTouchEnd() { touchRef.current.dist = null; }

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, [ref, scale]);

  return [scale, setScale];
}

// ── Pinch-Zoom 래퍼 컴포넌트 ────────────────────────────────

// ── PWA 서비스 워커 등록 (오프라인 지원) ─────────────────────
// [V41-Fix-SW] Blob URL → /sw.js 정적 파일 우선 시도 (CSP 호환성 확보)
//   ① /sw.js 존재 시: 정적 파일 등록 (CSP script-src 제한 없이 동작)
//   ② /sw.js 미존재 시: Blob URL fallback (개발·임베드 환경)
(function registerPWA() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  const SW_VERSION = 'ev-sim-v20'; // [V45] 버전 업

  // ── [V45] 오프라인 캐싱 확장: 정적 자산 목록 및 캐싱 전략 고도화 ──────────
  // 캐시 전략: Shell(앱셸) = CacheFirst, API/CDN = NetworkFirst + fallback
  const SHELL_ASSETS = [
    '/',
    '/index.html',
    '/manifest.json',
    '/favicon.ico',
  ];
  const CDN_PATTERNS = [
    'cdnjs.cloudflare.com',
    'unpkg.com',
    'cdn.jsdelivr.net',
  ];

  // ① 정적 /sw.js 우선 시도 (CSP-safe)
  navigator.serviceWorker.register('/sw.js').then(reg => {
    console.log('[EV-Sim v20] PWA Service Worker (/sw.js) 등록 완료');
    // [V45] 백그라운드 동기화: 파라미터 변경 감지 → 서버 저장 (future use)
    if ('sync' in reg) {
      reg.sync.register('params-sync').catch(() => {/* BackgroundSync 미지원 환경 */});
    }
    // 새 SW 발견 시 즉시 활성화 안내
    reg.addEventListener('updatefound', () => {
      const newWorker = reg.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('[EV-Sim v20] 새 버전 설치됨. 새로고침하면 업데이트 적용.');
          }
        });
      }
    });
  }).catch(() => {
    // ② /sw.js 없음 → Blob URL fallback (고도화된 캐싱 전략 적용)
    const swCode = `
      const CACHE_NAME = '${SW_VERSION}';
      const SHELL_ASSETS = ${JSON.stringify(SHELL_ASSETS)};
      const CDN_PATTERNS = ${JSON.stringify(CDN_PATTERNS)};

      // Install: 앱셸 사전 캐싱
      self.addEventListener('install', e => {
        e.waitUntil(
          caches.open(CACHE_NAME).then(c =>
            c.addAll(SHELL_ASSETS).catch(() => {
              // 일부 자산 실패 허용 (개발 환경 대응)
              return Promise.all(SHELL_ASSETS.map(url => c.add(url).catch(()=>{})));
            })
          )
        );
        self.skipWaiting();
      });

      // Activate: 이전 캐시 정리
      self.addEventListener('activate', e => {
        e.waitUntil(
          caches.keys().then(keys =>
            Promise.all(keys.filter(k => k !== CACHE_NAME).map(k => caches.delete(k)))
          ).then(() => self.clients.claim())
        );
      });

      // Fetch: 리소스 종류별 캐싱 전략 분기
      self.addEventListener('fetch', e => {
        const url = new URL(e.request.url);

        // CDN 자산: Cache First (변경 빈도 낮음)
        if (CDN_PATTERNS.some(p => url.hostname.includes(p))) {
          e.respondWith(
            caches.match(e.request).then(cached => {
              if (cached) return cached;
              return fetch(e.request).then(resp => {
                if (resp && resp.status === 200) {
                  const clone = resp.clone();
                  caches.open(CACHE_NAME + '-cdn').then(c => c.put(e.request, clone));
                }
                return resp;
              }).catch(() => cached || new Response('Offline', { status: 503 }));
            })
          );
          return;
        }

        // HTML/JS 앱셸: Network First + Cache Fallback (최신성 우선)
        if (e.request.mode === 'navigate' || url.pathname.endsWith('.js') || url.pathname.endsWith('.jsx')) {
          e.respondWith(
            fetch(e.request).then(resp => {
              if (resp && resp.status === 200 && resp.type === 'basic') {
                const clone = resp.clone();
                caches.open(CACHE_NAME).then(c => c.put(e.request, clone));
              }
              return resp;
            }).catch(() =>
              caches.match(e.request).then(cached => cached ||
                caches.match('/').then(shell => shell || new Response('Offline', { status: 503 }))
              )
            )
          );
          return;
        }

        // 기타: Stale-While-Revalidate (캐시 반환 + 백그라운드 업데이트)
        e.respondWith(
          caches.open(CACHE_NAME).then(cache =>
            cache.match(e.request).then(cached => {
              const fetchPromise = fetch(e.request).then(resp => {
                if (resp && resp.status === 200) cache.put(e.request, resp.clone());
                return resp;
              }).catch(() => cached);
              return cached || fetchPromise;
            })
          )
        );
      });

      // [V45] BackgroundSync: 파라미터 저장 큐 처리 (future: 백엔드 연동 대비)
      self.addEventListener('sync', e => {
        if (e.tag === 'params-sync') {
          e.waitUntil(
            self.clients.matchAll().then(clients => {
              clients.forEach(c => c.postMessage({ type: 'SYNC_COMPLETE', tag: e.tag }));
            })
          );
        }
      });

      // [V45] Push 알림 수신 핸들러 (미래 기능 대비 skeleton)
      self.addEventListener('push', e => {
        if (!e.data) return;
        const data = e.data.json().catch(() => ({ title: 'EV-Sim', body: e.data.text() }));
        e.waitUntil(
          data.then(d =>
            self.registration.showNotification(d.title || 'EV-Sim', {
              body: d.body || '',
              icon: '/favicon.ico',
              badge: '/favicon.ico',
            })
          ).catch(() => {})
        );
      });
    `;
    try {
      const blob = new Blob([swCode], { type: 'application/javascript' });
      const swUrl = URL.createObjectURL(blob);
      navigator.serviceWorker.register(swUrl).then(reg => {
        console.log('[EV-Sim v20] PWA Service Worker (Blob + 확장 캐싱) 등록 완료');
        // [V45] 메시지 수신 (SW ↔ 앱 통신)
        navigator.serviceWorker.addEventListener('message', e => {
          if (e.data?.type === 'SYNC_COMPLETE') {
            console.log('[EV-Sim v20] BackgroundSync 완료:', e.data.tag);
          }
        });
      }).catch(e => console.warn('[EV-Sim v20] SW 등록 실패:', e));
    } catch(e) { /* Blob SW는 일부 환경에서 차단됨 — 무시 */ }
  });

  // [V45] Web App Manifest 동적 주입 (manifest.json 없는 환경 대응)
  if (!document.querySelector('link[rel="manifest"]')) {
    const manifest = {
      name: 'Vehicle Engineering Simulation Platform',
      short_name: 'EV-Sim',
      description: 'Vehicle Engineering Simulation Platform',
      start_url: '/',
      display: 'standalone',
      background_color: '#0f172a',
      theme_color: '#38bdf8',
      icons: [
        { src: '/favicon.ico', sizes: '64x64', type: 'image/x-icon' },
      ],
      categories: ['education', 'utilities', 'productivity'],
      shortcuts: [
        { name: '주행 시뮬레이션', url: '/?tab=driving',     icons: [{ src: '/favicon.ico', sizes: '64x64' }] },
        { name: '기능 안전',       url: '/?tab=safety',      icons: [{ src: '/favicon.ico', sizes: '64x64' }] },
        { name: 'DOE/Pareto',       url: '/?tab=doe',         icons: [{ src: '/favicon.ico', sizes: '64x64' }] },
      ],
    };
    try {
      const mBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
      const mUrl  = URL.createObjectURL(mBlob);
      const link  = document.createElement('link');
      link.rel = 'manifest'; link.href = mUrl;
      document.head.appendChild(link);
      console.log('[EV-Sim v20] Web App Manifest 동적 주입 완료');
    } catch(e) {}
  }
})();

// ── Three.js CDN 동적 로드 (3D 토크맵용) ────────────────────
(function loadThreeJS() {
  if (typeof THREE !== 'undefined') return;
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  s.async = true;
  document.head.appendChild(s);
})();

// ── 모바일 뷰포트 메타 자동 주입 ─────────────────────────────
(function injectViewportMeta() {
  if (document.querySelector('meta[name="viewport"]')) return;
  const m = document.createElement('meta');
  m.name = 'viewport';
  m.content = 'width=device-width, initial-scale=1.0, maximum-scale=5.0';
  document.head.appendChild(m);
})();

// [BugFix7] Wrap with ErrorBoundary so render errors are visible on screen
export default function EVSimulator() {
  return (
    <ErrorBoundary>
      <EVSimulatorInner />
    </ErrorBoundary>
  );
}